/**
 * ============================================================
 * TEFCO HOME
 * Enterprise Homepage
 * Shopify OS 2.0
 * Version 1.0.0
 * ============================================================
 */

(() => {

'use strict';

class TefcoHome {


    constructor() {

        if (window.__tefcoHomeInstance) {
            return window.__tefcoHomeInstance;
        }

        window.__tefcoHomeInstance = this;

        this.dom = {};
        this.state = {
            initialized: false,
            destroyed: false,
            mobile: false,
            tablet: false,
            desktop: true,
            editor: document.documentElement.classList.contains('shopify-design-mode'),
            windowWidth: window.innerWidth,
            windowHeight: window.innerHeight,
            scrollY: window.scrollY
        };
        this.events = [];
        this.observers = new Set();
        this.intervals = new Set();
        this.timeouts = new Set();
        this.rafIds = new Set();

        // Cache native requestAnimationFrame/cancelAnimationFrame so instance methods
        // can use them even if globals are overridden below.
        this._nativeRaf = (typeof window !== 'undefined' && typeof window.requestAnimationFrame === 'function')
            ? window.requestAnimationFrame.bind(window)
            : (cb) => setTimeout(() => cb(Date.now()), 16);

        this._nativeCancelRaf = (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function')
            ? window.cancelAnimationFrame.bind(window)
            : (id) => clearTimeout(id);

        // Route global/unqualified calls through the instance so all RAFs are tracked.
        try {
            if (typeof window !== 'undefined') {
                window.requestAnimationFrame = (cb) => this.raf(cb);
                window.cancelAnimationFrame = (id) => this.cancelRaf(id);
            }
        } catch (e) {
            // Ignore if properties are not writable in the environment.
        }

        this.initializeConfig();

        this.logger = {
            log: (...args) => {
                if (this.config?.debug && typeof console !== 'undefined') {
                    console.log('[TEFCO]', ...args);
                }
            },
            info: (...args) => {
                if (this.config?.debug && typeof console !== 'undefined') {
                    console.info('[TEFCO]', ...args);
                }
            },
            warn: (...args) => {
                if (typeof console !== 'undefined') {
                    console.warn('[TEFCO]', ...args);
                }
            },
            error: (...args) => {
                if (typeof console !== 'undefined') {
                    console.error('[TEFCO]', ...args);
                }
            },
            debug: (...args) => {
                if (this.config?.debug && typeof console !== 'undefined') {
                    console.debug('[TEFCO]', ...args);
                }
            }
        };
        this.initializeDOMCache();
        this.initializeEventManager();
        this.initializeObserverManager();
        this.initializeThemeEditor();
        this.initializePerformance();
        this.initializeCleanupManager?.();
        this.initializeDestroyManager?.();
        this.initializeAccessibilityManager?.();
        this.initializePerformanceOptimizations?.();
        this.normalizeProductionDependencies?.();

        this.initialize();

        this.initializeProduction?.();
        if (this.production?.ready) {
            this.startProduction?.();
        }
    }

    //----------------------------------------
    // PART 1 — App Bootstrap
    //----------------------------------------

    initialize() {

        if (this.state.initialized) {
            return;
        }

        this.log('Initializing...');

        this.updateViewportState();

        this.initializeHero();

        // Brand Slider: initialize the complete runtime on first page load.
        // Previously only the foundation was initialized here, while the
        // engine/autoplay/drag/responsive/a11y stack ran only after rebuilds.
        this.initializeBrandSlider();
        this.initializeBrandSliderEngine();
        this.initializeBrandLoop();
        this.initializeBrandNavigation();
        this.initializeBrandAutoplay();
        this.initializeBrandDragging();
        this.initializeBrandResponsive();
        this.initializeBrandLazyImages();
        this.initializeBrandAccessibility();
        this.initializeBrandThemeEditor();

        // Category Grid: initialize the complete runtime on first page load.
        this.initializeCategories();
        this.initializeCategoriesGrid();
        this.initializeCategoryHoverEffects();
        this.initializeCategoriesResponsive();
        this.initializeCategoriesLazyLoading();
        this.initializeCategoriesAccessibility();
        this.initializeCategoriesThemeEditor();

        // Solutions: initialize the complete runtime on first page load.
        this.initializeSolutions();
        this.initializeSolutionsGrid();
        this.initializeSolutionHoverEffects();
        this.initializeSolutionsResponsive();
        this.initializeSolutionsLazyLoading();
        this.initializeSolutionsAccessibility();
        this.initializeSolutionsThemeEditor();

        // Banner Grid: initialize the complete runtime on first page load.
        this.initializeBannerGrid();
        this.initializeBannerGridEngine();
        this.initializeBannerHoverEffects();
        this.initializeBannerResponsive();
        this.initializeBannerLazyLoading();
        this.initializeBannerAccessibility();
        this.initializeBannerThemeEditor();

        // Featured Products: initialize the complete runtime on first page load.
        this.initializeFeaturedProducts();
        this.initializeFeaturedProductsEngine();
        this.initializeFeaturedProductInteractions();
        this.initializeFeaturedProductsResponsive();
        this.initializeFeaturedProductsLazyLoading();
        this.initializeFeaturedProductsAccessibility();
        this.initializeFeaturedProductsThemeEditor();

        // Testimonials: initialize the complete runtime on first page load.
        this.initializeTestimonials();
        this.initializeTestimonialsSlider();
        this.initializeTestimonialsNavigation();
        this.initializeTestimonialsResponsive();
        this.initializeTestimonialsLazyLoading();
        this.initializeTestimonialsAccessibility();
        this.initializeTestimonialsThemeEditor();

        // Blog: initialize the complete runtime on first page load.
        this.initializeBlog();
        this.initializeBlogEngine();
        this.initializeBlogInteractions();
        this.initializeBlogResponsive();
        this.initializeBlogLazyLoading();
        this.initializeBlogAccessibility();
        this.initializeBlogThemeEditor();

        // Newsletter: initialize the complete runtime on first page load.
        this.initializeNewsletter();
        this.initializeNewsletterFormEngine();
        this.initializeNewsletterValidation();
        this.initializeNewsletterResponsive();
        this.initializeNewsletterAccessibility();
        this.initializeNewsletterAnimations();
        this.initializeNewsletterThemeEditor();

        this.state.initialized = true;

        this.log('Initialized.');
    }

    destroy() {

        if (this.state.destroyed) {
            return;
        }

        this.destroyHero();
        this.destroyBrandSlider();
        this.destroyCategories();
        this.destroySolutions();
        this.destroyBannerGrid();
        this.destroyFeaturedProducts();
        this.destroyTestimonials();
        this.destroyBlog();
        this.destroyNewsletter();

        // Ensure core runtime cleanup is robust: cancel RAFs, clear timers and observers
        try {
            if (typeof this.cancelAllRaf === 'function') {
                this.cancelAllRaf();
            }
        } catch (error) {
            this.logger?.warn?.('cancelAllRaf failed.', error);
        }

        try {
            if (typeof this.clearAllIntervals === 'function') {
                this.clearAllIntervals();
            }
        } catch (error) {
            this.logger?.warn?.('clearAllIntervals failed.', error);
        }

        try {
            if (typeof this.clearAllTimeouts === 'function') {
                this.clearAllTimeouts();
            }
        } catch (error) {
            this.logger?.warn?.('clearAllTimeouts failed.', error);
        }

        try {
            if (typeof this.disconnectAllObservers === 'function') {
                this.disconnectAllObservers();
            }
        } catch (error) {
            this.logger?.warn?.('disconnectAllObservers failed.', error);
        }

        try {
            if (typeof this.disconnectAllPooledObservers === 'function') {
                this.disconnectAllPooledObservers();
            }
        } catch (error) {
            this.logger?.warn?.('disconnectAllPooledObservers failed.', error);
        }

        try {
            if (typeof this.removeAllEvents === 'function') {
                this.removeAllEvents();
            }
        } catch (error) {
            this.logger?.warn?.('removeAllEvents failed.', error);
        }

        // Optional extended cleanup hooks
        this.destroyPerformanceInfrastructure?.();
        this.cleanupRuntime?.();

        this.state.destroyed = true;
        this.state.initialized = false;

        this.log('Destroyed.');
    }

    //----------------------------------------
    // PART 1 — Configuration
    //----------------------------------------

    initializeConfig() {

        this.config = {
            debug: false,
            namespace: 'tefco',
            resizeDebounce: 150,
            scrollThrottle: 16,
            animationDuration: 300,
            observerThreshold: 0.15,
            rootMargin: '0px 0px -80px 0px'
        };

        this.breakpoints = {
            mobile: 768,
            tablet: 992,
            desktop: 1200,
            wide: 1440
        };

        this.classes = {
            active: 'is-active',
            open: 'is-open',
            visible: 'is-visible',
            hidden: 'is-hidden',
            loading: 'is-loading',
            loaded: 'is-loaded',
            disabled: 'is-disabled',
            current: 'is-current',
            sticky: 'is-sticky',
            initialized: 'is-initialized'
        };

        this.selectors = {
            root: '.tefco-home',
            hero: '.tefco-hero',
            brandSlider: '.tefco-brand-slider',
            categoryGrid: '.tefco-category-grid',
            solutions: '.tefco-solutions',
            featuredProducts: '.tefco-products',
            bannerGrid: '.tefco-banner-grid',
            testimonials: '.tefco-testimonials',
            blog: '.tefco-blog',
            newsletter: '.tefco-newsletter'
        };
    }

    initializePerformance() {

        this.initializeVisibilityHelpers?.();
        this.initializeViewportManager?.();
        this.initializeLazyLoader?.();
        this.initializePerformanceMonitor?.();
        this.initializeTaskScheduler?.();
    }

    initializeEventManager() {
        this.eventManager = {
            initialized: true,
            listeners: this.events
        };
    }

    //----------------------------------------
    // PART 1 — DOM Cache
    //----------------------------------------


    //----------------------------------------
    // PART 1 — Utilities
    //----------------------------------------

    /**
     * Lightweight application logger.
     */
    log(...args) {
        if (this.config?.debug && typeof console !== 'undefined') {
            console.log('[TEFCO]', ...args);
        }
    }

    /**
     * Safely executes deferred/optional runtime work.
     */
    safeExecute(task, fallback = null) {
        if (typeof task !== 'function') {
            return fallback;
        }

        try {
            return task();
        } catch (error) {
            if (this.config?.debug && typeof console !== 'undefined') {
                console.error('[TEFCO] Task execution failed:', error);
            }
            return fallback;
        }
    }

    /**
     * Synchronizes global viewport state.
     */
    updateViewportState() {
        const width = window.innerWidth;
        const height = window.innerHeight;

        this.state.windowWidth = width;
        this.state.windowHeight = height;
        this.state.scrollY = window.scrollY || window.pageYOffset || 0;
        this.state.mobile = width < this.breakpoints.mobile;
        this.state.tablet =
            width >= this.breakpoints.mobile &&
            width < this.breakpoints.desktop;
        this.state.desktop = width >= this.breakpoints.desktop;

        return this.state;
    }



    initializeDOMCache() {

        this.dom.root = this.qs(this.selectors.root);

        if (!this.dom.root) {
            return;
        }

        this.dom.hero = this.find(this.selectors.hero);

        this.dom.brandSlider = this.find(this.selectors.brandSlider);

        this.dom.categoryGrid = this.find(this.selectors.categoryGrid);

        this.dom.solutions = this.find(this.selectors.solutions);

        this.dom.featuredProducts = this.find(this.selectors.featuredProducts);

        this.dom.bannerGrid = this.find(this.selectors.bannerGrid);

        this.dom.testimonials = this.find(this.selectors.testimonials);

        this.dom.blog = this.find(this.selectors.blog);

        this.dom.newsletter = this.find(this.selectors.newsletter);

    }

    /**
     * ---------------------------------------------------------
     * Query Selector
     * ---------------------------------------------------------
     */

    qs(selector, context = document) {

        if (!selector || !context) {
            return null;
        }

        return context.querySelector(selector);

    }

    /**
     * ---------------------------------------------------------
     * Query Selector All
     * ---------------------------------------------------------
     */

    qsa(selector, context = document) {

        if (!selector || !context) {
            return [];
        }

        return Array.from(
            context.querySelectorAll(selector)
        );

    }

    /**
     * ---------------------------------------------------------
     * Find Inside Root
     * ---------------------------------------------------------
     */

    find(selector) {

        if (!this.dom.root) {
            return null;
        }

        return this.qs(selector, this.dom.root);

    }

    /**
     * ---------------------------------------------------------
     * Find All Inside Root
     * ---------------------------------------------------------
     */

    findAll(selector) {

        if (!this.dom.root) {
            return [];
        }

        return this.qsa(selector, this.dom.root);

    }

    /**
     * ---------------------------------------------------------
     * Element Validation
     * ---------------------------------------------------------
     */

    isElement(element) {

        return (
            element instanceof Element ||
            element instanceof HTMLDocument
        );

    }

    /**
     * ---------------------------------------------------------
     * Check Visibility
     * ---------------------------------------------------------
     */

    isVisible(element) {

        if (!this.isElement(element)) {
            return false;
        }

        return !!(
            element.offsetWidth ||
            element.offsetHeight ||
            element.getClientRects().length
        );

    }

    /**
     * ---------------------------------------------------------
     * Viewport Detection
     * ---------------------------------------------------------
     */

    isInViewport(element, offset = 0) {

        if (!this.isElement(element)) {
            return false;
        }

        const rect = element.getBoundingClientRect();

        return (

            rect.top <=
                window.innerHeight + offset &&

            rect.bottom >=
                -offset &&

            rect.left <=
                window.innerWidth + offset &&

            rect.right >=
                -offset

        );

    }

    /**
     * ---------------------------------------------------------
     * Viewport Size
     * ---------------------------------------------------------
     */

    getViewport() {

        return {

            width: window.innerWidth,

            height: window.innerHeight,

            mobile:
                window.innerWidth <
                this.breakpoints.mobile,

            tablet:
                window.innerWidth >=
                    this.breakpoints.mobile &&
                window.innerWidth <
                    this.breakpoints.desktop,

            desktop:
                window.innerWidth >=
                this.breakpoints.desktop

        };

    }

    /**
     * ---------------------------------------------------------
     * Scroll Position
     * ---------------------------------------------------------
     */

    getScrollPosition() {

        return {

            x:
                window.pageXOffset ||
                window.scrollX ||
                0,

            y:
                window.pageYOffset ||
                window.scrollY ||
                0

        };

    }
    /**
     * ---------------------------------------------------------
     * Add Event Listener
     * Automatically tracked for cleanup
     * ---------------------------------------------------------
     */

    on(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        target.addEventListener(
            event,
            handler,
            options
        );

        this.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * ---------------------------------------------------------
     * Remove Event Listener
     * ---------------------------------------------------------
     */

    off(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return;
        }

        target.removeEventListener(
            event,
            handler,
            options
        );

        this.events = this.events.filter(listener => {

            return !(
                listener.target === target &&
                listener.event === event &&
                listener.handler === handler
            );

        });

    }

    /**
     * ---------------------------------------------------------
     * One-Time Event
     * ---------------------------------------------------------
     */

    once(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return;
        }

        const onceHandler = (e) => {

            handler.call(target, e);

            this.off(
                target,
                event,
                onceHandler,
                options
            );

        };

        this.on(
            target,
            event,
            onceHandler,
            options
        );

    }

    /**
     * ---------------------------------------------------------
     * Event Delegation
     * ---------------------------------------------------------
     */

    delegate(target, event, selector, handler) {

        if (
            !target ||
            !selector ||
            typeof handler !== 'function'
        ) {
            return null;
        }

        const listener = (e) => {

            const element = e.target.closest(selector);

            if (
                !element ||
                !target.contains(element)
            ) {
                return;
            }

            handler.call(
                element,
                e,
                element
            );

        };

        this.on(
            target,
            event,
            listener
        );

        return listener;

    }

    /**
     * ---------------------------------------------------------
     * Dispatch Custom Event
     * ---------------------------------------------------------
     */

    emit(targetOrEventName, eventNameOrDetail = {}, detail = {}) {

        let target = targetOrEventName;
        let eventName = eventNameOrDetail;
        let eventDetail = detail;

        if (typeof targetOrEventName === 'string') {

            target = this.dom?.root || document;
            eventName = targetOrEventName;
            eventDetail = eventNameOrDetail || {};

        }

        if (
            !target ||
            typeof target.dispatchEvent !== 'function' ||
            !eventName
        ) {
            return;
        }

        target.dispatchEvent(

            new CustomEvent(
                eventName,
                {
                    bubbles: true,
                    cancelable: true,
                    detail: eventDetail
                }
            )

        );

    }

    /**
     * ---------------------------------------------------------
     * Remove Every Registered Event
     * Called from destroy()
     * ---------------------------------------------------------
     */

    removeAllEvents() {

        this.events.forEach(listener => {

            listener.target.removeEventListener(

                listener.event,

                listener.handler,

                listener.options

            );

        });

        this.events = [];

    }
    /**
     * ---------------------------------------------------------
     * Debounce
     * Delays execution until the function stops
     * being called for the specified delay.
     * ---------------------------------------------------------
     */

    debounce(callback, delay = 200) {

        if (typeof callback !== 'function') {
            return () => {};
        }

        let timeoutId = null;

        return (...args) => {

            if (timeoutId !== null) {

                clearTimeout(timeoutId);

                this.timeouts.delete(timeoutId);

            }

            timeoutId = window.setTimeout(() => {

                this.timeouts.delete(timeoutId);

                timeoutId = null;

                callback.apply(this, args);

            }, delay);

            this.timeouts.add(timeoutId);

        };

    }

    /**
     * ---------------------------------------------------------
     * Throttle
     * Ensures the callback executes at most once
     * during the specified interval.
     * ---------------------------------------------------------
     */

    throttle(callback, delay = 100) {

        if (typeof callback !== 'function') {
            return () => {};
        }

        let waiting = false;

        let lastArgs = null;

        let timeoutId = null;

        return (...args) => {

            if (waiting) {

                lastArgs = args;

                return;

            }

            callback.apply(this, args);

            waiting = true;

            timeoutId = window.setTimeout(() => {

                this.timeouts.delete(timeoutId);

                waiting = false;

                if (lastArgs) {

                    callback.apply(this, lastArgs);

                    lastArgs = null;

                }

            }, delay);

            this.timeouts.add(timeoutId);

        };

    }
    /**
     * ---------------------------------------------------------
     * Request Animation Frame
     * Schedules work on the next animation frame.
     * Automatically tracks frame IDs for cleanup.
     * ---------------------------------------------------------
     */

    raf(callback) {

        if (typeof callback !== 'function') {
            return null;
        }

        const frameId = this._nativeRaf((timestamp) => {

            this.rafIds.delete(frameId);

            try {

                callback.call(this, timestamp);

            } catch (error) {

                this.logger.error(
                    'requestAnimationFrame callback failed.',
                    error
                );

            }

        });

        this.rafIds.add(frameId);

        return frameId;

    }

    /**
     * ---------------------------------------------------------
     * Cancel Animation Frame
     * ---------------------------------------------------------
     */

    cancelRaf(frameId) {

        if (
            frameId === null ||
            frameId === undefined
        ) {
            return;
        }

        // Use cached native cancel to avoid recursion if globals were wrapped.
        this._nativeCancelRaf(frameId);

        this.rafIds.delete(frameId);

    }

    /**
     * ---------------------------------------------------------
     * Cancel Every Pending Animation Frame
     * Called during destroy()
     * ---------------------------------------------------------
     */

    cancelAllRaf() {

        // Cancel using native cancel to avoid re-entering wrapped globals during cleanup.
        this.rafIds.forEach((frameId) => {

            this._nativeCancelRaf(frameId);

        });

        this.rafIds.clear();

    }

    /**
     * ---------------------------------------------------------
     * Execute On Next Paint
     * Convenience helper
     * ---------------------------------------------------------
     */

    nextFrame(callback) {

        return this.raf(() => {

            callback.call(this);

        });

    }

    /**
     * ---------------------------------------------------------
     * Execute After Two Frames
     * Useful after layout changes.
     * ---------------------------------------------------------
     */

    nextPaint(callback) {

        return this.raf(() => {

            this.raf(() => {

                callback.call(this);

            });

        });

    }
    /**
     * ---------------------------------------------------------
     * Set Timeout
     * Executes a callback after the specified delay.
     * Automatically tracks timeout IDs for cleanup.
     * ---------------------------------------------------------
     */

    setTimeout(callback, delay = 0) {

        if (typeof callback !== 'function') {
            return null;
        }

        const timeoutId = window.setTimeout(() => {

            this.timeouts.delete(timeoutId);

            try {

                callback.call(this);

            } catch (error) {

                this.logger.error(
                    'setTimeout callback failed.',
                    error
                );

            }

        }, delay);

        this.timeouts.add(timeoutId);

        return timeoutId;

    }

    /**
     * ---------------------------------------------------------
     * Clear Timeout
     * ---------------------------------------------------------
     */

    clearTimeout(timeoutId) {

        if (
            timeoutId === null ||
            timeoutId === undefined
        ) {
            return;
        }

        window.clearTimeout(timeoutId);

        this.timeouts.delete(timeoutId);

    }

    /**
     * ---------------------------------------------------------
     * Clear All Pending Timeouts
     * Called during destroy()
     * ---------------------------------------------------------
     */

    clearAllTimeouts() {

        this.timeouts.forEach((timeoutId) => {

            window.clearTimeout(timeoutId);

        });

        this.timeouts.clear();

    }
    /**
     * ---------------------------------------------------------
     * Set Interval
     * Executes a callback repeatedly.
     * Automatically tracks interval IDs for cleanup.
     * ---------------------------------------------------------
     */

    setInterval(callback, delay = 1000) {

        if (typeof callback !== 'function') {
            return null;
        }

        const intervalId = window.setInterval(() => {

            try {

                callback.call(this);

            } catch (error) {

                this.logger.error(
                    'setInterval callback failed.',
                    error
                );

            }

        }, delay);

        this.intervals.add(intervalId);

        return intervalId;

    }

    /**
     * ---------------------------------------------------------
     * Clear Interval
     * ---------------------------------------------------------
     */

    clearInterval(intervalId) {

        if (
            intervalId === null ||
            intervalId === undefined
        ) {
            return;
        }

        window.clearInterval(intervalId);

        this.intervals.delete(intervalId);

    }

    /**
     * ---------------------------------------------------------
     * Clear All Pending Intervals
     * Called during destroy()
     * ---------------------------------------------------------
     */

    clearAllIntervals() {

        this.intervals.forEach((intervalId) => {

            window.clearInterval(intervalId);

        });

        this.intervals.clear();

    }

    /**
     * ---------------------------------------------------------
     * Create Intersection Observer
     * Creates and registers an IntersectionObserver.
     * ---------------------------------------------------------
     */

    createIntersectionObserver(
        callback,
        options = {}
    ) {

        if (typeof callback !== 'function') {
            return null;
        }

        if (
            !('IntersectionObserver' in window)
        ) {

            this.logger.warn(
                'IntersectionObserver is not supported.'
            );

            return null;

        }

        const config = {

            root: options.root || null,

            rootMargin:
                options.rootMargin || '0px',

            threshold:
                options.threshold ?? 0

        };

        let observer = null;

        try {

            observer = new IntersectionObserver(

                (entries, instance) => {

                    try {

                        callback.call(
                            this,
                            entries,
                            instance
                        );

                    } catch (error) {

                        this.logger.error(
                            'IntersectionObserver callback failed.',
                            error
                        );

                    }

                },

                config

            );

            this.observers.add(observer);

            return observer;

        } catch (error) {

            this.logger.error(
                'Failed to create IntersectionObserver.',
                error
            );

            return null;

        }

    }
    /**
     * ---------------------------------------------------------
     * Observe Element
     * Registers an element with an IntersectionObserver.
     * ---------------------------------------------------------
     */

    observe(observer, element) {

        if (
            !observer ||
            typeof observer.observe !== 'function'
        ) {
            return false;
        }

        if (!this.isElement(element)) {
            return false;
        }

        try {

            observer.observe(element);

            return true;

        } catch (error) {

            this.logger.error(
                'Failed to observe element.',
                error
            );

            return false;

        }

    }

    /**
     * ---------------------------------------------------------
     * Unobserve Element
     * Stops observing a specific element.
     * ---------------------------------------------------------
     */

    unobserve(observer, element) {

        if (
            !observer ||
            typeof observer.unobserve !== 'function'
        ) {
            return false;
        }

        if (!this.isElement(element)) {
            return false;
        }

        try {

            observer.unobserve(element);

            return true;

        } catch (error) {

            this.logger.error(
                'Failed to unobserve element.',
                error
            );

            return false;

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect Intersection Observer
     * Disconnects and unregisters an observer.
     * ---------------------------------------------------------
     */

    disconnectIntersectionObserver(observer) {

        if (
            !observer ||
            typeof observer.disconnect !== 'function'
        ) {
            return;
        }

        try {

            observer.disconnect();

        } catch (error) {

            this.logger.error(
                'Failed to disconnect IntersectionObserver.',
                error
            );

        } finally {

            this.observers.delete(observer);

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect All Observers
     * Called during destroy()
     * ---------------------------------------------------------
     */

    disconnectAllObservers() {

        this.observers.forEach((observer) => {

            if (
                observer &&
                typeof observer.disconnect === 'function'
            ) {

                try {

                    observer.disconnect();

                } catch (error) {

                    this.logger.error(
                        'Observer cleanup failed.',
                        error
                    );

                }

            }

        });

        this.observers.clear();

    }
    /**
     * ---------------------------------------------------------
     * Create Resize Observer
     * Creates and registers a ResizeObserver.
     * ---------------------------------------------------------
     */

    createResizeObserver(
        callback,
        options = {}
    ) {

        if (typeof callback !== 'function') {
            return null;
        }

        if (!('ResizeObserver' in window)) {

            this.logger.warn(
                'ResizeObserver is not supported.'
            );

            return null;

        }

        let observer = null;

        try {

            observer = new ResizeObserver((entries) => {

                try {

                    callback.call(
                        this,
                        entries,
                        observer,
                        options
                    );

                } catch (error) {

                    this.logger.error(
                        'ResizeObserver callback failed.',
                        error
                    );

                }

            });

            this.observers.add(observer);

            return observer;

        } catch (error) {

            this.logger.error(
                'Failed to create ResizeObserver.',
                error
            );

            return null;

        }

    }

    /**
     * ---------------------------------------------------------
     * Create Safe Resize Observer
     * Returns an existing observer if supplied,
     * otherwise creates a new one.
     * ---------------------------------------------------------
     */

    getResizeObserver(
        callback,
        options = {}
    ) {

        if (
            options.observer &&
            typeof options.observer.observe === 'function'
        ) {

            return options.observer;

        }

        return this.createResizeObserver(
            callback,
            options
        );

    }
    /**
     * ---------------------------------------------------------
     * Observe Resize
     * Registers an element with a ResizeObserver.
     * ---------------------------------------------------------
     */

    observeResize(observer, element) {

        if (
            !observer ||
            typeof observer.observe !== 'function'
        ) {
            return false;
        }

        if (!this.isElement(element)) {
            return false;
        }

        try {

            observer.observe(element);

            return true;

        } catch (error) {

            this.logger.error(
                'Failed to observe resize.',
                error
            );

            return false;

        }

    }

    /**
     * ---------------------------------------------------------
     * Unobserve Resize
     * Stops observing an element.
     * ---------------------------------------------------------
     */

    unobserveResize(observer, element) {

        if (
            !observer ||
            typeof observer.unobserve !== 'function'
        ) {
            return false;
        }

        if (!this.isElement(element)) {
            return false;
        }

        try {

            observer.unobserve(element);

            return true;

        } catch (error) {

            this.logger.error(
                'Failed to unobserve resize.',
                error
            );

            return false;

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect Resize Observer
     * Disconnects and unregisters a ResizeObserver.
     * ---------------------------------------------------------
     */

    disconnectResizeObserver(observer) {

        if (
            !observer ||
            typeof observer.disconnect !== 'function'
        ) {
            return;
        }

        try {

            observer.disconnect();

        } catch (error) {

            this.logger.error(
                'Failed to disconnect ResizeObserver.',
                error
            );

        } finally {

            this.observers.delete(observer);

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect All Resize Observers
     * Safe to call multiple times.
     * ---------------------------------------------------------
     */

    disconnectAllResizeObservers() {

        this.observers.forEach((observer) => {

            if (
                observer instanceof ResizeObserver
            ) {

                try {

                    observer.disconnect();

                } catch (error) {

                    this.logger.error(
                        'ResizeObserver cleanup failed.',
                        error
                    );

                }

            }

        });

    }
    /**
     * ---------------------------------------------------------
     * Create Mutation Observer
     * Creates and registers a MutationObserver.
     * ---------------------------------------------------------
     */

    createMutationObserver(
        callback,
        options = {}
    ) {

        if (typeof callback !== 'function') {
            return null;
        }

        if (!('MutationObserver' in window)) {

            this.logger.warn(
                'MutationObserver is not supported.'
            );

            return null;

        }

        let observer = null;

        try {

            observer = new MutationObserver((mutations, instance) => {

                try {

                    callback.call(
                        this,
                        mutations,
                        instance,
                        options
                    );

                } catch (error) {

                    this.logger.error(
                        'MutationObserver callback failed.',
                        error
                    );

                }

            });

            this.observers.add(observer);

            return observer;

        } catch (error) {

            this.logger.error(
                'Failed to create MutationObserver.',
                error
            );

            return null;

        }

    }

    /**
     * ---------------------------------------------------------
     * Get Mutation Observer
     * Returns an existing observer if supplied,
     * otherwise creates a new one.
     * ---------------------------------------------------------
     */

    getMutationObserver(
        callback,
        options = {}
    ) {

        if (
            options.observer &&
            typeof options.observer.observe === 'function'
        ) {

            return options.observer;

        }

        return this.createMutationObserver(
            callback,
            options
        );

    }
    /**
     * ---------------------------------------------------------
     * Observe Mutations
     * Registers a target with a MutationObserver.
     * ---------------------------------------------------------
     */

    observeMutations(
        observer,
        target,
        options = {}
    ) {

        if (
            !observer ||
            typeof observer.observe !== 'function'
        ) {
            return false;
        }

        if (!this.isElement(target)) {
            return false;
        }

        const config = {

            childList:
                options.childList ?? true,

            subtree:
                options.subtree ?? true,

            attributes:
                options.attributes ?? false,

            characterData:
                options.characterData ?? false,

            attributeOldValue:
                options.attributeOldValue ?? false,

            characterDataOldValue:
                options.characterDataOldValue ?? false,

            attributeFilter:
                options.attributeFilter

        };

        try {

            observer.observe(
                target,
                config
            );

            return true;

        } catch (error) {

            this.logger.error(
                'Failed to observe mutations.',
                error
            );

            return false;

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect Mutation Observer
     * Disconnects and unregisters a MutationObserver.
     * ---------------------------------------------------------
     */

    disconnectMutationObserver(observer) {

        if (
            !observer ||
            typeof observer.disconnect !== 'function'
        ) {
            return;
        }

        try {

            observer.disconnect();

        } catch (error) {

            this.logger.error(
                'Failed to disconnect MutationObserver.',
                error
            );

        } finally {

            this.observers.delete(observer);

        }

    }

    /**
     * ---------------------------------------------------------
     * Disconnect All Mutation Observers
     * Safe to call multiple times.
     * ---------------------------------------------------------
     */

    disconnectAllMutationObservers() {

        this.observers.forEach((observer) => {

            if (
                observer instanceof MutationObserver
            ) {

                try {

                    observer.disconnect();

                } catch (error) {

                    this.logger.error(
                        'MutationObserver cleanup failed.',
                        error
                    );

                }

            }

        });

    }

    /**
     * ---------------------------------------------------------
     * Refresh Mutation Observer
     * Reconnects an observer with a new configuration.
     * ---------------------------------------------------------
     */

    refreshMutationObserver(
        observer,
        target,
        options = {}
    ) {

        if (!observer) {
            return false;
        }

        this.disconnectMutationObserver(
            observer
        );

        return this.observeMutations(
            observer,
            target,
            options
        );

    }
    /**
     * ========================================================================
     * Part 1B.1.4a.1
     * Observer Manager Foundation
     * ------------------------------------------------------------------------
     * Enterprise Observer Pool Infrastructure
     * ========================================================================
     */

    initializeObserverManager() {
        this.observerManager = {
            pools: {
                intersection: new Map(),
                resize: new Map(),
                mutation: new Map()
            },

            references: new WeakMap(),

            statistics: {
                created: 0,
                active: 0,
                released: 0
            }
        };
    }

    /**
     * Returns the observer pool for the requested type.
     *
     * @param {string} type
     * @returns {Map}
     */
    getObserverPool(type) {
        if (!this.observerManager) {
            this.initializeObserverManager();
        }

        const pool = this.observerManager.pools[type];

        if (!pool) {
            throw new Error(`Unknown observer type: ${type}`);
        }

        return pool;
    }

    /**
     * Generates a deterministic key from observer configuration.
     *
     * @param {string} type
     * @param {Object} config
     * @returns {string}
     */
    getObserverKey(type, config = {}) {
        const normalizedConfig = {
            root: config.root || null,
            rootMargin: config.rootMargin || '0px',
            threshold: Array.isArray(config.threshold)
                ? [...config.threshold]
                : config.threshold ?? 0
        };

        return `${type}:${JSON.stringify(normalizedConfig)}`;
    }

    /**
     * Checks whether a pooled observer exists.
     *
     * @param {string} type
     * @param {Object} config
     * @returns {boolean}
     */
    hasObserver(type, config = {}) {
        const pool = this.getObserverPool(type);
        const key = this.getObserverKey(type, config);

        return pool.has(key);
    }

    /**
     * Returns a pooled observer entry if available.
     *
     * @param {string} type
     * @param {Object} config
     * @returns {Object|null}
     */
    getObserver(type, config = {}) {
        const pool = this.getObserverPool(type);
        const key = this.getObserverKey(type, config);

        return pool.get(key) || null;
    }
    /**
     * Registers an observer inside the shared pool or returns an existing one.
     *
     * @param {string} type
     * @param {Object} observer
     * @param {Object} config
     * @returns {Object}
     */
    registerObserver(type, observer, config = {}) {
        const pool = this.getObserverPool(type);
        const key = this.getObserverKey(type, config);

        if (pool.has(key)) {
            const existing = pool.get(key);

            existing.lastUsed = Date.now();

            return existing;
        }

        const entry = {
            key,
            type,
            observer,
            config: Object.freeze({
                ...config
            }),
            refCount: 0,
            createdAt: Date.now(),
            lastUsed: Date.now()
        };

        pool.set(key, entry);

        this.observerManager.statistics.created += 1;
        this.observerManager.statistics.active += 1;

        return entry;
    }

    /**
     * Returns an existing pooled observer or creates one using the supplied
     * factory callback.
     *
     * @param {string} type
     * @param {Object} config
     * @param {Function} factory
     * @returns {Object}
     */
    getOrCreateObserver(type, config = {}, factory) {
        if (typeof factory !== 'function') {
            throw new TypeError('Observer factory must be a function.');
        }

        const existing = this.getObserver(type, config);

        if (existing) {
            existing.lastUsed = Date.now();

            return existing;
        }

        const observer = factory(config);

        if (!observer) {
            throw new Error(`Failed to create ${type} observer.`);
        }

        return this.registerObserver(type, observer, config);
    }

    /**
     * Creates or reuses an IntersectionObserver entry.
     *
     * @param {Function} callback
     * @param {Object} options
     * @returns {Object}
     */
    getIntersectionObserverEntry(callback, options = {}) {
        return this.getOrCreateObserver(
            'intersection',
            options,
            (config) => new IntersectionObserver(callback, config)
        );
    }

    /**
     * Creates or reuses a ResizeObserver entry.
     *
     * @param {Function} callback
     * @param {Object} options
     * @returns {Object}
     */
    getResizeObserverEntry(callback, options = {}) {
        return this.getOrCreateObserver(
            'resize',
            options,
            () => new ResizeObserver(callback)
        );
    }

    /**
     * Creates or reuses a MutationObserver entry.
     *
     * @param {Function} callback
     * @param {Object} options
     * @returns {Object}
     */
    getMutationObserverEntry(callback, options = {}) {
        return this.getOrCreateObserver(
            'mutation',
            options,
            () => new MutationObserver(callback)
        );
    }
    /**
     * Increases the reference count for a pooled observer and associates
     * the observer entry with the supplied target element.
     *
     * @param {Object} entry
     * @param {Element} target
     * @returns {Object}
     */
    retainObserver(entry, target) {
        if (!entry) {
            throw new Error('Observer entry is required.');
        }

        if (!this.isElement(target)) {
            throw new TypeError('A valid DOM element is required.');
        }

        let registry = this.observerManager.references.get(target);

        if (!registry) {
            registry = new Map();
            this.observerManager.references.set(target, registry);
        }

        const existing = registry.get(entry.key);

        if (existing) {
            existing.count += 1;
        } else {
            registry.set(entry.key, {
                entry,
                count: 1,
                attachedAt: Date.now()
            });

            entry.refCount += 1;
        }

        entry.lastUsed = Date.now();

        return entry;
    }

    /**
     * Decreases the reference count for a pooled observer associated with
     * the supplied target element.
     *
     * @param {Object} entry
     * @param {Element} target
     * @returns {number}
     */
    releaseObserver(entry, target) {
        if (!entry || !this.isElement(target)) {
            return 0;
        }

        const registry = this.observerManager.references.get(target);

        if (!registry) {
            return entry.refCount;
        }

        const reference = registry.get(entry.key);

        if (!reference) {
            return entry.refCount;
        }

        reference.count -= 1;

        if (reference.count <= 0) {
            registry.delete(entry.key);

            entry.refCount = Math.max(0, entry.refCount - 1);

            entry.lastUsed = Date.now();

            this.observerManager.statistics.released += 1;
        }

        if (registry.size === 0) {
            this.observerManager.references.delete(target);
        }

        return entry.refCount;
    }

    /**
     * Determines whether the supplied element is currently associated with
     * the specified pooled observer.
     *
     * @param {Object} entry
     * @param {Element} target
     * @returns {boolean}
     */
    isObserverRetained(entry, target) {
        if (!entry || !this.isElement(target)) {
            return false;
        }

        const registry = this.observerManager.references.get(target);

        return registry ? registry.has(entry.key) : false;
    }

    /**
     * Returns every pooled observer associated with the supplied element.
     *
     * @param {Element} target
     * @returns {Array<Object>}
     */
    getElementObservers(target) {
        if (!this.isElement(target)) {
            return [];
        }

        const registry = this.observerManager.references.get(target);

        if (!registry) {
            return [];
        }

        return [...registry.values()].map(reference => reference.entry);
    }

    /**
     * Removes every observer association from the supplied element without
     * disconnecting the pooled observers.
     *
     * @param {Element} target
     */
    releaseElementObservers(target) {
        if (!this.isElement(target)) {
            return;
        }

        const registry = this.observerManager.references.get(target);

        if (!registry) {
            return;
        }

        registry.forEach(({ entry, count }) => {
            entry.refCount = Math.max(0, entry.refCount - count);

            entry.lastUsed = Date.now();

            this.observerManager.statistics.released += count;
        });

        this.observerManager.references.delete(target);
    }
    /**
     * Safely disconnects a pooled observer.
     *
     * The observer is only disconnected when no active references remain,
     * unless the operation is forced.
     *
     * @param {Object} entry
     * @param {boolean} force
     * @returns {boolean}
     */
    disconnectObserver(entry, force = false) {
        if (!entry) {
            return false;
        }

        if (!force && entry.refCount > 0) {
            return false;
        }

        try {
            if (
                entry.observer &&
                typeof entry.observer.disconnect === 'function'
            ) {
                entry.observer.disconnect();
            }
        } catch (error) {
            this.logger?.warn?.(
                '[ObserverManager] Failed to disconnect observer.',
                error
            );
        }

        const pool = this.getObserverPool(entry.type);

        if (pool.has(entry.key)) {
            pool.delete(entry.key);

            this.observerManager.statistics.active = Math.max(
                0,
                this.observerManager.statistics.active - 1
            );
        }

        entry.refCount = 0;
        entry.lastUsed = Date.now();

        return true;
    }

    /**
     * Disconnects every idle observer in the specified pool.
     *
     * @param {string} type
     * @returns {number}
     */
    disconnectIdleObservers(type) {
        const pool = this.getObserverPool(type);

        let disconnected = 0;

        pool.forEach(entry => {
            if (entry.refCount !== 0) {
                return;
            }

            if (this.disconnectObserver(entry)) {
                disconnected++;
            }
        });

        return disconnected;
    }

    /**
     * Disconnects every idle observer from all pools.
     *
     * @returns {number}
     */
    disconnectAllIdleObservers() {
        let disconnected = 0;

        Object.keys(this.observerManager.pools).forEach(type => {
            disconnected += this.disconnectIdleObservers(type);
        });

        return disconnected;
    }

    /**
     * Force disconnects every pooled observer.
     *
     * Intended for application destroy() and Theme Editor unload.
     *
     * @returns {number}
     */
    disconnectAllPooledObservers() {
        let disconnected = 0;

        Object.values(this.observerManager.pools).forEach(pool => {
            [...pool.values()].forEach(entry => {
                if (this.disconnectObserver(entry, true)) {
                    disconnected++;
                }
            });

            pool.clear();
        });

        this.observerManager.statistics.active = 0;

        return disconnected;
    }

    /**
     * Clears every element reference tracked by the observer manager.
     *
     * WeakMap entries are automatically garbage collected once the
     * associated DOM elements are released.
     */
    clearObserverReferences() {
        this.observerManager.references = new WeakMap();
    }
    /**
     * Returns a snapshot of the current observer manager statistics.
     *
     * @returns {Object}
     */
    getObserverStats() {
        const { statistics } = this.observerManager;

        return {
            created: statistics.created,
            active: statistics.active,
            released: statistics.released,
            timestamp: Date.now()
        };
    }

    /**
     * Returns detailed statistics for a specific observer pool.
     *
     * @param {string} type
     * @returns {Object}
     */
    getPoolStats(type) {
        const pool = this.getObserverPool(type);

        const stats = {
            type,
            total: pool.size,
            active: 0,
            idle: 0,
            references: 0,
            oldest: null,
            newest: null
        };

        pool.forEach(entry => {
            stats.references += entry.refCount;

            if (entry.refCount > 0) {
                stats.active++;
            } else {
                stats.idle++;
            }

            if (!stats.oldest || entry.createdAt < stats.oldest) {
                stats.oldest = entry.createdAt;
            }

            if (!stats.newest || entry.createdAt > stats.newest) {
                stats.newest = entry.createdAt;
            }
        });

        return stats;
    }

    /**
     * Returns a complete diagnostic summary for every observer pool.
     *
     * @returns {Object}
     */
    getObserverSummary() {
        return {
            statistics: this.getObserverStats(),

            pools: {
                intersection: this.getPoolStats('intersection'),
                resize: this.getPoolStats('resize'),
                mutation: this.getPoolStats('mutation')
            }
        };
    }

    /**
     * Prints a formatted observer summary in development mode.
     *
     * @returns {Object}
     */
    logObserverSummary() {
        const summary = this.getObserverSummary();

        if (this.config?.debug) {
            console.groupCollapsed('Observer Manager');

            console.table(summary.statistics);

            Object.values(summary.pools).forEach(pool => {
                console.group(pool.type);
                console.table(pool);
                console.groupEnd();
            });

            console.groupEnd();
        }

        return summary;
    }

    /**
     * Resets observer statistics while preserving pooled observers.
     */
    resetObserverStatistics() {
        this.observerManager.statistics = {
            created: 0,
            active: 0,
            released: 0
        };
    }

    /**
     * Completely resets the Observer Manager.
     *
     * This method disconnects every pooled observer, clears every
     * reference registry and rebuilds the manager from scratch.
     */
    resetObserverManager() {
        this.disconnectAllPooledObservers();

        this.clearObserverReferences();

        this.initializeObserverManager();
    }

    /**
     * Returns whether the Observer Manager has been initialized.
     *
     * @returns {boolean}
     */
    isObserverManagerReady() {
        return (
            !!this.observerManager &&
            !!this.observerManager.pools &&
            !!this.observerManager.references
        );
    }

    /**
     * Returns every pooled observer entry.
     *
     * Useful for debugging and maintenance routines.
     *
     * @returns {Array<Object>}
     */
    getAllObserverEntries() {
        const entries = [];

        Object.values(this.observerManager.pools).forEach(pool => {
            entries.push(...pool.values());
        });

        return entries;
    }
    /**
     * ========================================================================
     * Part 1B.1.4c
     * Observer Manager Optimization & Maintenance
     * ------------------------------------------------------------------------
     * Enterprise Pool Maintenance
     * Memory Leak Prevention
     * Automatic Cleanup
     * Health Monitoring
     * ========================================================================
     */

    /**
     * Removes unused pooled observers.
     *
     * @param {number} maxIdleTime
     * @returns {number}
     */
    pruneObserverPool(maxIdleTime = 300000) {
        const now = Date.now();

        let removed = 0;

        Object.values(this.observerManager.pools).forEach(pool => {
            [...pool.values()].forEach(entry => {
                if (entry.refCount > 0) {
                    return;
                }

                if ((now - entry.lastUsed) < maxIdleTime) {
                    return;
                }

                if (this.disconnectObserver(entry, true)) {
                    removed++;
                }
            });
        });

        return removed;
    }

    /**
     * Removes invalid observer references.
     *
     * @returns {number}
     */
    cleanupObserverReferences() {
        let cleaned = 0;

        this.getAllObserverEntries().forEach(entry => {
            if (entry.refCount < 0) {
                entry.refCount = 0;
                cleaned++;
            }

            if (!entry.lastUsed) {
                entry.lastUsed = Date.now();
            }

            if (!entry.createdAt) {
                entry.createdAt = Date.now();
            }
        });

        return cleaned;
    }

    /**
     * Optimizes every observer pool.
     *
     * @returns {Object}
     */
    optimizeObserverPools() {
        const removed = this.pruneObserverPool();

        const repaired = this.cleanupObserverReferences();

        return {
            removed,
            repaired,
            active: this.observerManager.statistics.active
        };
    }

    /**
     * Performs an integrity check on every observer pool.
     *
     * @returns {Object}
     */
    validateObserverPools() {
        const report = {
            valid: true,
            issues: []
        };

        this.getAllObserverEntries().forEach(entry => {

            if (!entry.observer) {
                report.valid = false;

                report.issues.push({
                    key: entry.key,
                    issue: 'Missing observer instance'
                });
            }

            if (!entry.type) {
                report.valid = false;

                report.issues.push({
                    key: entry.key,
                    issue: 'Missing observer type'
                });
            }

            if (entry.refCount < 0) {
                report.valid = false;

                report.issues.push({
                    key: entry.key,
                    issue: 'Negative reference count'
                });
            }

        });

        return report;
    }

    /**
     * Starts automatic observer maintenance.
     *
     * @param {number} interval
     */
    startObserverMaintenance(interval = 300000) {

        if (this._observerMaintenanceTimer) {
            return;
        }

        this._observerMaintenanceTimer = this.setInterval(() => {

            this.optimizeObserverPools();

        }, interval);
    }

    /**
     * Stops automatic observer maintenance.
     */
    stopObserverMaintenance() {

        if (!this._observerMaintenanceTimer) {
            return;
        }

        this.clearInterval(this._observerMaintenanceTimer);

        this._observerMaintenanceTimer = null;
    }

    /**
     * Returns whether automatic maintenance is currently running.
     *
     * @returns {boolean}
     */
    isObserverMaintenanceRunning() {
        return !!this._observerMaintenanceTimer;
    }

    /**
     * Executes a complete maintenance cycle.
     *
     * @returns {Object}
     */
    runObserverMaintenance() {

        const optimization = this.optimizeObserverPools();

        const validation = this.validateObserverPools();

        return {
            optimization,
            validation,
            statistics: this.getObserverStats()
        };
    }
    /**
     * ========================================================================
     * Part 1B.2
     * Shopify Theme Editor Integration
     * ------------------------------------------------------------------------
     * Supports Shopify Theme Editor lifecycle events.
     * Automatically initializes, refreshes and destroys homepage modules
     * without requiring a page reload.
     * ========================================================================
     */

    initializeThemeEditor() {
        this.themeEditor = {
            enabled:
                window.Shopify &&
                Shopify.designMode === true,

            initialized: false,

            listeners: []
        };

        if (!this.themeEditor.enabled) {
            return;
        }

        this.bindThemeEditorEvents();

        this.themeEditor.initialized = true;

        this.logger?.info?.(
            '[ThemeEditor] Theme Editor integration initialized.'
        );
    }

    /**
     * Registers every Theme Editor event.
     */
    bindThemeEditorEvents() {

        const events = [
            ['shopify:section:load', this.onSectionLoad.bind(this)],
            ['shopify:section:unload', this.onSectionUnload.bind(this)],
            ['shopify:section:select', this.onSectionSelect.bind(this)],
            ['shopify:section:deselect', this.onSectionDeselect.bind(this)],
            ['shopify:section:reorder', this.onSectionReorder.bind(this)],
            ['shopify:block:select', this.onBlockSelect.bind(this)],
            ['shopify:block:deselect', this.onBlockDeselect.bind(this)]
        ];

        events.forEach(([event, handler]) => {

            document.addEventListener(event, handler);

            this.themeEditor.listeners.push({
                event,
                handler
            });

        });

    }

    /**
     * Removes every registered Theme Editor listener.
     */
    unbindThemeEditorEvents() {

        if (!this.themeEditor?.listeners) {
            return;
        }

        this.themeEditor.listeners.forEach(listener => {

            document.removeEventListener(
                listener.event,
                listener.handler
            );

        });

        this.themeEditor.listeners.length = 0;
    }

    /**
     * Returns the current section element.
     *
     * @param {Event} event
     * @returns {HTMLElement|null}
     */
    getSectionElement(event) {

        if (!event) {
            return null;
        }

        return event.target instanceof HTMLElement
            ? event.target
            : null;
    }

    /**
     * Returns the section id from a Theme Editor event.
     *
     * @param {Event} event
     * @returns {string|null}
     */
    getSectionId(event) {

        return (
            event?.detail?.sectionId ||
            event?.target?.dataset?.sectionId ||
            null
        );
    }

    /**
     * Returns the block id from a Theme Editor event.
     *
     * @param {Event} event
     * @returns {string|null}
     */
    getBlockId(event) {

        return (
            event?.detail?.blockId ||
            event?.target?.dataset?.blockId ||
            null
        );
    }
    /**
     * ========================================================================
     * Part 1B.2a
     * Theme Editor Section Lifecycle Handlers
     * ------------------------------------------------------------------------
     * Handles Shopify Theme Editor section lifecycle events.
     * Ensures components, observers and cached DOM remain synchronized.
     * ========================================================================
     */

    /**
     * Fired whenever a section is added or re-rendered.
     *
     * @param {CustomEvent} event
     */
    onSectionLoad(event) {
        const section = this.getSectionElement(event);

        if (!section) {
            return;
        }

        const sectionId = this.getSectionId(event);

        this.logger?.info?.(
            `[ThemeEditor] Section loaded: ${sectionId}`
        );

        this.refreshSection(section);

        this.initializeSectionComponents(section);

        this.emit('tefco:section:loaded', {
            section,
            sectionId
        });
    }

    /**
     * Fired before Shopify removes a section.
     *
     * @param {CustomEvent} event
     */
    onSectionUnload(event) {
        const section = this.getSectionElement(event);

        if (!section) {
            return;
        }

        const sectionId = this.getSectionId(event);

        this.logger?.info?.(
            `[ThemeEditor] Section unloaded: ${sectionId}`
        );

        this.destroySectionComponents(section);

        this.releaseSectionObservers(section);

        this.emit('tefco:section:unloaded', {
            section,
            sectionId
        });
    }

    /**
     * Fired when a merchant selects a section.
     *
     * @param {CustomEvent} event
     */
    onSectionSelect(event) {
        const section = this.getSectionElement(event);

        if (!section) {
            return;
        }

        const sectionId = this.getSectionId(event);

        section.classList.add('is-theme-editor-selected');

        this.refreshSection(section);

        this.emit('tefco:section:selected', {
            section,
            sectionId
        });
    }

    /**
     * Fired when a merchant deselects a section.
     *
     * @param {CustomEvent} event
     */
    onSectionDeselect(event) {
        const section = this.getSectionElement(event);

        if (!section) {
            return;
        }

        const sectionId = this.getSectionId(event);

        section.classList.remove('is-theme-editor-selected');

        this.emit('tefco:section:deselected', {
            section,
            sectionId
        });
    }

    /**
     * Fired after Shopify changes section order.
     *
     * @param {CustomEvent} event
     */
    onSectionReorder(event) {
        const section = this.getSectionElement(event);

        if (!section) {
            return;
        }

        const sectionId = this.getSectionId(event);

        this.logger?.info?.(
            `[ThemeEditor] Section reordered: ${sectionId}`
        );

        this.refreshSection(section);

        this.emit('tefco:section:reordered', {
            section,
            sectionId
        });
    }

    /**
     * Refreshes a Theme Editor section.
     *
     * @param {HTMLElement} section
     */
    refreshSection(section) {
        if (!section) {
            return;
        }

        this.initializeDOMCache();

        this.emit('tefco:section:refresh', {
            section
        });
    }

    /**
     * Initializes all homepage modules inside a section.
     *
     * @param {HTMLElement} section
     */
    initializeSectionComponents(section) {
        if (!section) {
            return;
        }

        if (typeof this.initializeHero === 'function') {
            this.initializeHero(section);
        }

        if (typeof this.initializeBrandSlider === 'function') {
            this.initializeBrandSlider(section);
        }

        if (typeof this.initializeCategories === 'function') {
            this.initializeCategories(section);
        }

        if (typeof this.initializeSolutions === 'function') {
            this.initializeSolutions(section);
        }

        if (typeof this.initializeFeaturedProducts === 'function') {
            this.initializeFeaturedProducts(section);
        }

        if (typeof this.initializeBannerGrid === 'function') {
            this.initializeBannerGrid(section);
        }

        if (typeof this.initializeTestimonials === 'function') {
            this.initializeTestimonials(section);
        }

        if (typeof this.initializeBlog === 'function') {
            this.initializeBlog(section);
        }

        if (typeof this.initializeNewsletter === 'function') {
            this.initializeNewsletter(section);
        }
    }

    /**
     * Destroys homepage modules belonging to a section.
     *
     * @param {HTMLElement} section
     */
    destroySectionComponents(section) {
        if (!section) {
            return;
        }

        if (typeof this.destroyHero === 'function') {
            this.destroyHero(section);
        }

        if (typeof this.destroyBrandSlider === 'function') {
            this.destroyBrandSlider(section);
        }

        if (typeof this.destroyCategories === 'function') {
            this.destroyCategories(section);
        }

        if (typeof this.destroySolutions === 'function') {
            this.destroySolutions(section);
        }

        if (typeof this.destroyFeaturedProducts === 'function') {
            this.destroyFeaturedProducts(section);
        }

        if (typeof this.destroyBannerGrid === 'function') {
            this.destroyBannerGrid(section);
        }

        if (typeof this.destroyTestimonials === 'function') {
            this.destroyTestimonials(section);
        }

        if (typeof this.destroyBlog === 'function') {
            this.destroyBlog(section);
        }

        if (typeof this.destroyNewsletter === 'function') {
            this.destroyNewsletter(section);
        }
    }

    /**
     * Releases every pooled observer associated with a section.
     *
     * @param {HTMLElement} section
     */
    releaseSectionObservers(section) {
        if (!section) {
            return;
        }

        this.releaseElementObservers(section);

        section.querySelectorAll('*').forEach(element => {
            this.releaseElementObservers(element);
        });

        this.disconnectAllIdleObservers();
    }
    /**
     * ========================================================================
     * Part 1B.2b
     * Theme Editor Block Lifecycle Handlers
     * ------------------------------------------------------------------------
     * Handles Shopify Theme Editor block selection lifecycle.
     * Provides automatic scrolling, focus management and component refresh.
     * ========================================================================
     */

    /**
     * Fired when a merchant selects a block.
     *
     * @param {CustomEvent} event
     */
    onBlockSelect(event) {
        const block = this.getBlockElement(event);

        if (!block) {
            return;
        }

        const blockId = this.getBlockId(event);

        this.logger?.info?.(
            `[ThemeEditor] Block selected: ${blockId}`
        );

        block.classList.add('is-theme-editor-selected');

        this.scrollBlockIntoView(block);

        this.focusBlock(block);

        this.refreshBlock(block);

        this.emit('tefco:block:selected', {
            block,
            blockId
        });
    }

    /**
     * Fired when a merchant deselects a block.
     *
     * @param {CustomEvent} event
     */
    onBlockDeselect(event) {
        const block = this.getBlockElement(event);

        if (!block) {
            return;
        }

        const blockId = this.getBlockId(event);

        this.logger?.info?.(
            `[ThemeEditor] Block deselected: ${blockId}`
        );

        block.classList.remove('is-theme-editor-selected');

        this.emit('tefco:block:deselected', {
            block,
            blockId
        });
    }

    /**
     * Returns the block element from a Theme Editor event.
     *
     * @param {CustomEvent} event
     * @returns {HTMLElement|null}
     */
    getBlockElement(event) {
        if (!event) {
            return null;
        }

        return event.target instanceof HTMLElement
            ? event.target
            : null;
    }

    /**
     * Scrolls a block into view.
     *
     * @param {HTMLElement} block
     */
    scrollBlockIntoView(block) {
        if (!block) {
            return;
        }

        block.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
            inline: 'nearest'
        });
    }

    /**
     * Focuses the currently selected block.
     *
     * @param {HTMLElement} block
     */
    focusBlock(block) {
        if (!block) {
            return;
        }

        if (!block.hasAttribute('tabindex')) {
            block.setAttribute('tabindex', '-1');
        }

        block.focus({
            preventScroll: true
        });
    }

    /**
     * Refreshes a Theme Editor block.
     *
     * @param {HTMLElement} block
     */
    refreshBlock(block) {
        if (!block) {
            return;
        }

        this.refreshBlockComponents(block);

        this.emit('tefco:block:refresh', {
            block
        });
    }

    /**
     * Refreshes components contained within a block.
     *
     * @param {HTMLElement} block
     */
    refreshBlockComponents(block) {
        if (!block) {
            return;
        }

        if (typeof this.initializeHero === 'function') {
            this.initializeHero(block);
        }

        if (typeof this.initializeBrandSlider === 'function') {
            this.initializeBrandSlider(block);
        }

        if (typeof this.initializeCategories === 'function') {
            this.initializeCategories(block);
        }

        if (typeof this.initializeSolutions === 'function') {
            this.initializeSolutions(block);
        }

        if (typeof this.initializeFeaturedProducts === 'function') {
            this.initializeFeaturedProducts(block);
        }

        if (typeof this.initializeBannerGrid === 'function') {
            this.initializeBannerGrid(block);
        }

        if (typeof this.initializeTestimonials === 'function') {
            this.initializeTestimonials(block);
        }

        if (typeof this.initializeBlog === 'function') {
            this.initializeBlog(block);
        }

        if (typeof this.initializeNewsletter === 'function') {
            this.initializeNewsletter(block);
        }
    }

    /**
     * Releases all observer references associated with a block.
     *
     * @param {HTMLElement} block
     */
    releaseBlockObservers(block) {
        if (!block) {
            return;
        }

        this.releaseElementObservers(block);

        block.querySelectorAll('*').forEach(element => {
            this.releaseElementObservers(element);
        });

        this.disconnectAllIdleObservers();
    }
    /**
     * ========================================================================
     * Part 1B.2c
     * Theme Editor Utilities & Lifecycle Cleanup
     * ------------------------------------------------------------------------
     * Utility methods for Theme Editor lifecycle management.
     * Provides state inspection, refresh, reinitialization and cleanup.
     * ========================================================================
     */

    /**
     * Refreshes the Theme Editor integration.
     *
     * @returns {boolean}
     */
    refreshThemeEditor() {
        if (!this.isThemeEditor()) {
            return false;
        }

        this.initializeDOMCache();

        if (typeof this.optimizeObserverPools === 'function') {
            this.optimizeObserverPools();
        }

        this.emit('tefco:theme-editor:refresh');

        return true;
    }

    /**
     * Reinitializes the Theme Editor integration.
     *
     * @returns {boolean}
     */
    reinitializeThemeEditor() {
        if (!this.isThemeEditor()) {
            return false;
        }

        this.destroyThemeEditor();

        this.initializeThemeEditor();

        this.emit('tefco:theme-editor:reinitialized');

        return true;
    }

    /**
     * Destroys Theme Editor integration.
     *
     * @returns {boolean}
     */
    destroyThemeEditor() {
        if (!this.themeEditor) {
            return false;
        }

        this.unbindThemeEditorEvents();

        if (typeof this.stopObserverMaintenance === 'function') {
            this.stopObserverMaintenance();
        }

        if (typeof this.disconnectAllIdleObservers === 'function') {
            this.disconnectAllIdleObservers();
        }

        this.themeEditor.initialized = false;

        this.emit('tefco:theme-editor:destroyed');

        return true;
    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getThemeEditorState() {
        return {
            enabled: this.themeEditor?.enabled ?? false,
            initialized: this.themeEditor?.initialized ?? false,
            listeners: this.themeEditor?.listeners?.length ?? 0,
            maintenanceRunning:
                typeof this.isObserverMaintenanceRunning === 'function'
                    ? this.isObserverMaintenanceRunning()
                    : false
        };
    }

    /**
     * Returns whether Theme Editor integration is ready.
     *
     * @returns {boolean}
     */
    isThemeEditorReady() {
        return !!(
            this.themeEditor &&
            this.themeEditor.enabled &&
            this.themeEditor.initialized
        );
    }

    /**
     * Registers lifecycle callbacks.
     *
     * @param {Function} callback
     */
    onThemeEditorReady(callback) {
        if (typeof callback !== 'function') {
            return;
        }

        if (this.isThemeEditorReady()) {
            callback(this.getThemeEditorState());
            return;
        }

        this.once(
            'tefco:theme-editor:initialized',
            () => callback(this.getThemeEditorState())
        );
    }

    /**
     * Returns a Theme Editor diagnostic report.
     *
     * @returns {Object}
     */
    getThemeEditorDiagnostics() {
        return {
            state: this.getThemeEditorState(),
            observerManager:
                typeof this.getObserverSummary === 'function'
                    ? this.getObserverSummary()
                    : null,
            viewport:
                typeof this.getViewport === 'function'
                    ? this.getViewport()
                    : null,
            timestamp: Date.now()
        };
    }

    /**
     * Logs Theme Editor diagnostics in debug mode.
     *
     * @returns {Object}
     */
    logThemeEditorDiagnostics() {
        const diagnostics = this.getThemeEditorDiagnostics();

        if (this.config?.debug) {
            console.groupCollapsed('Theme Editor Diagnostics');

            console.log('State', diagnostics.state);
            console.log('Observer Manager', diagnostics.observerManager);
            console.log('Viewport', diagnostics.viewport);

            console.groupEnd();
        }

        return diagnostics;
    }
    /**
     * ========================================================================
     * Part 1B.3
     * Visibility Helpers
     * ------------------------------------------------------------------------
     * Enterprise Visibility Detection Utilities
     * Lazy Initialization
     * Viewport Tracking
     * Scroll Awareness
     * ========================================================================
     */

    initializeVisibilityHelpers() {
        this.visibility = {
            observer: null,
            visibleElements: new WeakSet(),
            callbacks: new WeakMap(),
            initialized: false
        };

        this.createVisibilityObserver();

        this.visibility.initialized = true;
    }

    /**
     * Creates the shared visibility observer.
     */
    createVisibilityObserver() {
        if (this.visibility.observer) {
            return this.visibility.observer;
        }

        const entry = this.getIntersectionObserverEntry(
            this.handleVisibilityChanges.bind(this),
            {
                root: null,
                rootMargin: '0px',
                threshold: [0, 0.1, 0.25, 0.5, 0.75, 1]
            }
        );

        this.visibility.observer = entry.observer;

        return this.visibility.observer;
    }

    /**
     * Handles visibility state changes.
     *
     * @param {IntersectionObserverEntry[]} entries
     */
    handleVisibilityChanges(entries) {
        entries.forEach(entry => {

            const element = entry.target;

            if (!element) {
                return;
            }

            const callback = this.visibility.callbacks.get(element);

            if (entry.isIntersecting) {

                this.visibility.visibleElements.add(element);

                element.classList.add('is-visible');
                element.classList.remove('is-hidden');

                callback?.({
                    element,
                    visible: true,
                    ratio: entry.intersectionRatio,
                    entry
                });

                this.emit('tefco:visibility:enter', {
                    element,
                    ratio: entry.intersectionRatio
                });

            } else {

                element.classList.remove('is-visible');
                element.classList.add('is-hidden');

                callback?.({
                    element,
                    visible: false,
                    ratio: entry.intersectionRatio,
                    entry
                });

                this.emit('tefco:visibility:leave', {
                    element,
                    ratio: entry.intersectionRatio
                });

            }

        });
    }

    /**
     * Begins observing an element.
     *
     * @param {HTMLElement} element
     * @param {Function|null} callback
     */
    observeVisibility(element, callback = null) {
        if (!this.isElement(element)) {
            return;
        }

        this.createVisibilityObserver();

        if (typeof callback === 'function') {
            this.visibility.callbacks.set(element, callback);
        }

        this.visibility.observer.observe(element);
    }

    /**
     * Stops observing an element.
     *
     * @param {HTMLElement} element
     */
    unobserveVisibility(element) {
        if (!this.isElement(element)) {
            return;
        }

        this.visibility.callbacks.delete(element);

        this.visibility.observer?.unobserve(element);
    }

    /**
     * Returns whether an element is currently visible.
     *
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isVisibleOnScreen(element) {
        if (!this.isElement(element)) {
            return false;
        }

        return this.visibility.visibleElements.has(element);
    }

    /**
     * Observes an element only until it becomes visible once.
     *
     * @param {HTMLElement} element
     * @param {Function} callback
     */
    observeOnce(element, callback) {
        this.observeVisibility(element, state => {

            if (!state.visible) {
                return;
            }

            callback?.(state);

            this.unobserveVisibility(element);

        });
    }

    /**
     * Stops every visibility observation.
     */
    disconnectVisibilityObserver() {
        if (!this.visibility?.observer) {
            return;
        }

        this.visibility.observer.disconnect();

        this.visibility.callbacks = new WeakMap();
        this.visibility.visibleElements = new WeakSet();

        this.visibility.observer = null;
    }
    /**
     * ========================================================================
     * Part 1B.3a
     * Viewport & Visibility Utilities
     * ------------------------------------------------------------------------
     * High-level APIs built on top of the Visibility Observer.
     * Used throughout homepage components for lazy loading,
     * animations, counters, sliders and media.
     * ========================================================================
     */

    /**
     * Executes a callback when an element enters the viewport.
     *
     * @param {HTMLElement} element
     * @param {Function} callback
     * @param {Object} options
     */
    onEnterViewport(element, callback, options = {}) {
        if (!this.isElement(element) || typeof callback !== 'function') {
            return;
        }

        const observerEntry = this.getIntersectionObserverEntry(
            entries => {
                entries.forEach(entry => {
                    if (!entry.isIntersecting) {
                        return;
                    }

                    callback({
                        element,
                        entry,
                        ratio: entry.intersectionRatio
                    });

                    if (options.once !== false) {
                        observerEntry.observer.unobserve(element);
                        this.releaseObserver(observerEntry, element);
                    }
                });
            },
            {
                root: options.root ?? null,
                rootMargin: options.rootMargin ?? '0px',
                threshold: options.threshold ?? 0.15
            }
        );

        this.retainObserver(observerEntry, element);
        observerEntry.observer.observe(element);
    }

    /**
     * Executes a callback when an element leaves the viewport.
     *
     * @param {HTMLElement} element
     * @param {Function} callback
     * @param {Object} options
     */
    onLeaveViewport(element, callback, options = {}) {
        if (!this.isElement(element) || typeof callback !== 'function') {
            return;
        }

        const observerEntry = this.getIntersectionObserverEntry(
            entries => {
                entries.forEach(entry => {
                    if (entry.isIntersecting) {
                        return;
                    }

                    callback({
                        element,
                        entry,
                        ratio: entry.intersectionRatio
                    });
                });
            },
            {
                root: options.root ?? null,
                rootMargin: options.rootMargin ?? '0px',
                threshold: options.threshold ?? 0
            }
        );

        this.retainObserver(observerEntry, element);
        observerEntry.observer.observe(element);
    }

    /**
     * Executes callbacks for both enter and leave viewport events.
     *
     * @param {HTMLElement} element
     * @param {Object} handlers
     * @param {Object} options
     */
    watchViewport(element, handlers = {}, options = {}) {
        if (!this.isElement(element)) {
            return;
        }

        const observerEntry = this.getIntersectionObserverEntry(
            entries => {
                entries.forEach(entry => {
                    const payload = {
                        element,
                        entry,
                        ratio: entry.intersectionRatio
                    };

                    if (entry.isIntersecting) {
                        handlers.onEnter?.(payload);
                    } else {
                        handlers.onLeave?.(payload);
                    }
                });
            },
            {
                root: options.root ?? null,
                rootMargin: options.rootMargin ?? '0px',
                threshold: options.threshold ?? 0.15
            }
        );

        this.retainObserver(observerEntry, element);
        observerEntry.observer.observe(element);
    }

    /**
     * Stops viewport observation for an element.
     *
     * @param {HTMLElement} element
     */
    unwatchViewport(element) {
        if (!this.isElement(element)) {
            return;
        }

        const observers = this.getElementObservers(element);

        observers.forEach(entry => {
            if (
                entry.type === 'intersection' &&
                entry.observer &&
                typeof entry.observer.unobserve === 'function'
            ) {
                entry.observer.unobserve(element);
            }

            this.releaseObserver(entry, element);
        });

        this.disconnectAllIdleObservers();
    }

    /**
     * Returns the viewport visibility ratio.
     *
     * @param {HTMLElement} element
     * @returns {number}
     */
    getVisibilityRatio(element) {
        if (!this.isElement(element)) {
            return 0;
        }

        const rect = element.getBoundingClientRect();
        const viewportHeight = window.innerHeight;

        if (rect.bottom <= 0 || rect.top >= viewportHeight) {
            return 0;
        }

        const visibleHeight = Math.min(rect.bottom, viewportHeight) -
            Math.max(rect.top, 0);

        return Math.max(
            0,
            Math.min(1, visibleHeight / rect.height)
        );
    }

    /**
     * Returns true if the element is mostly visible.
     *
     * @param {HTMLElement} element
     * @param {number} threshold
     * @returns {boolean}
     */
    isMostlyVisible(element, threshold = 0.5) {
        return this.getVisibilityRatio(element) >= threshold;
    }
    /**
     * ========================================================================
     * Part 1B.3b
     * Scroll & Viewport State Manager
     * ------------------------------------------------------------------------
     * Tracks viewport state, scroll direction, active sections and
     * exposes helper APIs for homepage components.
     * ========================================================================
     */

    initializeViewportManager() {
        this.viewportManager = {
            initialized: false,

            scrollY: window.scrollY,
            previousScrollY: window.scrollY,

            direction: 'down',

            width: window.innerWidth,
            height: window.innerHeight,

            activeSection: null,

            ticking: false
        };

        this.on(
            window,
            'scroll',
            this.handleViewportScroll.bind(this),
            {
                passive: true
            }
        );

        this.on(
            window,
            'resize',
            this.debounce(
                this.handleViewportResize.bind(this),
                150
            ),
            {
                passive: true
            }
        );

        this.viewportManager.initialized = true;
    }

    /**
     * Handles window scroll.
     */
    handleViewportScroll() {
        if (this.viewportManager.ticking) {
            return;
        }

        this.viewportManager.ticking = true;

        this.raf(() => {

            const manager = this.viewportManager;

            manager.previousScrollY = manager.scrollY;
            manager.scrollY = window.scrollY;

            manager.direction =
                manager.scrollY >= manager.previousScrollY
                    ? 'down'
                    : 'up';

            this.detectActiveSection();

            this.emit('tefco:scroll', {
                scrollY: manager.scrollY,
                direction: manager.direction
            });

            manager.ticking = false;

        });
    }

    /**
     * Handles viewport resize.
     */
    handleViewportResize() {
        const manager = this.viewportManager;

        manager.width = window.innerWidth;
        manager.height = window.innerHeight;

        this.detectActiveSection();

        this.emit('tefco:viewport:resize', {
            width: manager.width,
            height: manager.height
        });
    }

    /**
     * Detects the active homepage section.
     */
    detectActiveSection() {
        const sections = document.querySelectorAll(
            '[data-section-id]'
        );

        let active = null;

        sections.forEach(section => {

            if (!this.isMostlyVisible(section, 0.35)) {
                return;
            }

            active = section;

        });

        if (active === this.viewportManager.activeSection) {
            return;
        }

        this.viewportManager.activeSection = active;

        this.emit('tefco:section:active', {
            section: active
        });
    }

    /**
     * Returns the current scroll direction.
     *
     * @returns {string}
     */
    getScrollDirection() {
        return this.viewportManager.direction;
    }

    /**
     * Returns whether the user is scrolling upward.
     *
     * @returns {boolean}
     */
    isScrollingUp() {
        return this.viewportManager.direction === 'up';
    }

    /**
     * Returns whether the user is scrolling downward.
     *
     * @returns {boolean}
     */
    isScrollingDown() {
        return this.viewportManager.direction === 'down';
    }

    /**
     * Returns the current viewport state.
     *
     * @returns {Object}
     */
    getViewportState() {
        return {
            width: this.viewportManager.width,
            height: this.viewportManager.height,
            scrollY: this.viewportManager.scrollY,
            direction: this.viewportManager.direction,
            activeSection: this.viewportManager.activeSection
        };
    }

    /**
     * Returns the currently active section.
     *
     * @returns {HTMLElement|null}
     */
    getActiveSection() {
        return this.viewportManager.activeSection;
    }

    /**
     * Returns whether the viewport manager is initialized.
     *
     * @returns {boolean}
     */
    isViewportManagerReady() {
        return !!(
            this.viewportManager &&
            this.viewportManager.initialized
        );
    }
    /**
     * ========================================================================
     * Part 1B.3c
     * Lazy Initialization & Deferred Execution
     * ------------------------------------------------------------------------
     * Enterprise Lazy Component Infrastructure
     * Used by Hero, Brand Slider, Products, Testimonials, Blog, Newsletter,
     * Counters, Videos, Animations and any heavy component.
     * ========================================================================
     */

    initializeLazyLoader() {
        this.lazyLoader = {
            initialized: false,
            components: new WeakMap(),
            deferredTasks: new Map(),
            idleTasks: new Map(),
            queue: []
        };

        this.lazyLoader.initialized = true;
    }

    /**
     * Registers a lazy component.
     *
     * @param {HTMLElement} element
     * @param {Function} initializer
     */
    observeLazyComponent(element, initializer) {
        if (!this.isElement(element) || typeof initializer !== 'function') {
            return;
        }

        this.lazyLoader.components.set(element, {
            initialized: false,
            initializer
        });

        this.observeOnce(element, () => {
            this.initializeLazyComponent(element);
        });
    }

    /**
     * Initializes a lazy component.
     *
     * @param {HTMLElement} element
     */
    initializeLazyComponent(element) {
        const component = this.lazyLoader.components.get(element);

        if (!component || component.initialized) {
            return;
        }

        this.safeExecute(() => {
            component.initializer(element);
        });

        component.initialized = true;

        this.emit('tefco:lazy:initialized', {
            element
        });
    }

    /**
     * Returns whether a component has already been initialized.
     *
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isComponentInitialized(element) {
        const component = this.lazyLoader.components.get(element);

        return !!(component && component.initialized);
    }

    /**
     * Marks a component as initialized.
     *
     * @param {HTMLElement} element
     */
    markComponentInitialized(element) {
        const component = this.lazyLoader.components.get(element);

        if (component) {
            component.initialized = true;
        }
    }

    /**
     * Clears initialized state.
     *
     * @param {HTMLElement} element
     */
    unmarkComponentInitialized(element) {
        const component = this.lazyLoader.components.get(element);

        if (component) {
            component.initialized = false;
        }
    }

    /**
     * Adds a deferred task.
     *
     * @param {string} id
     * @param {Function} task
     */
    queueLazyTask(id, task) {
        if (typeof task !== 'function') {
            return;
        }

        this.lazyLoader.deferredTasks.set(id, task);
    }

    /**
     * Executes all queued deferred tasks.
     */
    flushLazyQueue() {
        this.lazyLoader.deferredTasks.forEach((task, id) => {

            this.safeExecute(task);

            this.lazyLoader.deferredTasks.delete(id);

        });
    }

    /**
     * Schedules work during browser idle time.
     *
     * @param {string} id
     * @param {Function} callback
     * @param {Object} options
     */
    scheduleIdleInitialization(
        id,
        callback,
        options = { timeout: 2000 }
    ) {
        if (typeof callback !== 'function') {
            return;
        }

        const schedule = window.requestIdleCallback
            ? window.requestIdleCallback(
                callback,
                options
            )
            : this.setTimeout(callback, 16);

        this.lazyLoader.idleTasks.set(id, schedule);
    }

    /**
     * Cancels idle initialization.
     *
     * @param {string} id
     */
    cancelIdleInitialization(id) {
        const handle = this.lazyLoader.idleTasks.get(id);

        if (handle == null) {
            return;
        }

        if (window.cancelIdleCallback) {
            window.cancelIdleCallback(handle);
        } else {
            this.clearTimeout(handle);
        }

        this.lazyLoader.idleTasks.delete(id);
    }

    /**
     * Executes every idle task immediately.
     */
    runDeferredTasks() {
        this.flushLazyQueue();
    }

    /**
     * Destroys the lazy loader.
     */
    destroyLazyLoader() {

        this.lazyLoader.deferredTasks.clear();

        this.lazyLoader.idleTasks.forEach(handle => {

            if (window.cancelIdleCallback) {
                window.cancelIdleCallback(handle);
            } else {
                this.clearTimeout(handle);
            }

        });

        this.lazyLoader.idleTasks.clear();

        this.lazyLoader.queue.length = 0;

        this.lazyLoader.components = new WeakMap();

        this.lazyLoader.initialized = false;
    }
    /**
     * ========================================================================
     * Part 1B.3d
     * Visibility Diagnostics & Cleanup
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Performance Statistics
     * Visibility Queue Maintenance
     * Cleanup Helpers
     * ========================================================================
     */

    /**
     * Returns the current visibility state.
     *
     * @returns {Object}
     */
    getVisibilityState() {
        return {
            initialized: this.visibility?.initialized ?? false,
            observer: !!this.visibility?.observer,
            activeCallbacks: this.getVisibilityCallbackCount(),
            queuedTasks: this.lazyLoader?.deferredTasks?.size ?? 0,
            idleTasks: this.lazyLoader?.idleTasks?.size ?? 0
        };
    }

    /**
     * Returns the number of registered visibility callbacks.
     *
     * @returns {number}
     */
    getVisibilityCallbackCount() {
        if (!this.lazyLoader?.components) {
            return 0;
        }

        let count = 0;

        this.lazyLoader.components = this.lazyLoader.components || new WeakMap();

        return count;
    }

    /**
     * Returns diagnostics for the Visibility Infrastructure.
     *
     * @returns {Object}
     */
    getVisibilityDiagnostics() {
        return {
            visibility: this.getVisibilityState(),
            viewport: this.isViewportManagerReady()
                ? this.getViewportState()
                : null,
            observerSummary: typeof this.getObserverSummary === 'function'
                ? this.getObserverSummary()
                : null,
            timestamp: Date.now()
        };
    }

    /**
     * Logs visibility diagnostics in debug mode.
     *
     * @returns {Object}
     */
    logVisibilityDiagnostics() {
        const diagnostics = this.getVisibilityDiagnostics();

        if (this.config?.debug) {
            console.groupCollapsed('Visibility Diagnostics');

            console.log('Visibility', diagnostics.visibility);
            console.log('Viewport', diagnostics.viewport);
            console.log('Observers', diagnostics.observerSummary);

            console.groupEnd();
        }

        return diagnostics;
    }

    /**
     * Clears queued lazy tasks.
     */
    clearLazyQueue() {
        if (!this.lazyLoader) {
            return;
        }

        this.lazyLoader.deferredTasks.clear();

        this.lazyLoader.queue.length = 0;
    }

    /**
     * Cancels every pending idle task.
     */
    cancelAllIdleTasks() {
        if (!this.lazyLoader) {
            return;
        }

        this.lazyLoader.idleTasks.forEach(handle => {

            if (window.cancelIdleCallback) {
                window.cancelIdleCallback(handle);
            } else {
                this.clearTimeout(handle);
            }

        });

        this.lazyLoader.idleTasks.clear();
    }

    /**
     * Resets the Visibility Infrastructure.
     */
    resetVisibilityHelpers() {
        this.disconnectVisibilityObserver();

        this.clearLazyQueue();

        this.cancelAllIdleTasks();

        this.initializeVisibilityHelpers();

        this.initializeLazyLoader();
    }

    /**
     * Destroys the complete Visibility Infrastructure.
     */
    destroyVisibilityHelpers() {
        this.disconnectVisibilityObserver();

        this.destroyLazyLoader();

        if (this.viewportManager) {
            this.viewportManager.initialized = false;
            this.viewportManager.activeSection = null;
        }

        this.emit('tefco:visibility:destroy');
    }

    /**
     * Returns whether the Visibility Infrastructure is ready.
     *
     * @returns {boolean}
     */
    isVisibilityReady() {
        return !!(
            this.visibility?.initialized &&
            this.lazyLoader?.initialized &&
            this.viewportManager?.initialized
        );
    }

    /**
     * Performs a complete Visibility Infrastructure health check.
     *
     * @returns {Object}
     */
    validateVisibilityInfrastructure() {
        return {
            ready: this.isVisibilityReady(),
            visibility: this.getVisibilityState(),
            diagnostics: this.getVisibilityDiagnostics()
        };
    }
    /**
     * ========================================================================
     * Part 1B.4a
     * Performance Monitor
     * ------------------------------------------------------------------------
     * Enterprise Performance Monitoring Infrastructure
     * FPS Monitoring
     * Frame Budget Tracking
     * Long Task Detection
     * Runtime Statistics
     * ========================================================================
     */

    initializePerformanceMonitor() {
        this.performanceMonitor = {
            initialized: false,

            fps: {
                current: 60,
                average: 60,
                minimum: 60,
                maximum: 60,
                samples: []
            },

            frame: {
                lastTime: performance.now(),
                delta: 0,
                frameCount: 0
            },

            runtime: {
                startedAt: performance.now(),
                updates: 0
            },

            longTasks: [],

            enabled: true,

            monitoring: false
        };

        this.performanceMonitor.initialized = true;
    }

    /**
     * Starts runtime performance monitoring.
     */
    startPerformanceMonitor() {
        if (
            !this.performanceMonitor ||
            this.performanceMonitor.monitoring
        ) {
            return;
        }

        this.performanceMonitor.monitoring = true;

        this.schedulePerformanceFrame();
    }

    /**
     * Stops runtime monitoring.
     */
    stopPerformanceMonitor() {
        if (!this.performanceMonitor) {
            return;
        }

        this.performanceMonitor.monitoring = false;
    }

    /**
     * Schedules the next monitoring frame.
     */
    schedulePerformanceFrame() {
        if (!this.performanceMonitor.monitoring) {
            return;
        }

        this.raf(timestamp => {

            this.updatePerformanceFrame(timestamp);

            this.schedulePerformanceFrame();

        });
    }

    /**
     * Updates frame timing.
     *
     * @param {DOMHighResTimeStamp} timestamp
     */
    updatePerformanceFrame(timestamp) {
        const monitor = this.performanceMonitor;

        const delta = timestamp - monitor.frame.lastTime;

        monitor.frame.delta = delta;
        monitor.frame.lastTime = timestamp;
        monitor.frame.frameCount++;
        monitor.runtime.updates++;

        this.updateFPS(delta);

        this.detectLongFrame(delta);

        this.emit('tefco:performance:frame', {
            fps: monitor.fps.current,
            delta
        });
    }

    /**
     * Updates FPS statistics.
     *
     * @param {number} delta
     */
    updateFPS(delta) {
        const fps = Math.max(
            1,
            Math.round(1000 / Math.max(delta, 1))
        );

        const stats = this.performanceMonitor.fps;

        stats.current = fps;

        stats.samples.push(fps);

        if (stats.samples.length > 120) {
            stats.samples.shift();
        }

        stats.average =
            stats.samples.reduce((sum, value) => sum + value, 0) /
            stats.samples.length;

        stats.minimum = Math.min(
            stats.minimum,
            fps
        );

        stats.maximum = Math.max(
            stats.maximum,
            fps
        );
    }

    /**
     * Detects long rendering frames.
     *
     * @param {number} delta
     */
    detectLongFrame(delta) {
        if (delta < 50) {
            return;
        }

        this.performanceMonitor.longTasks.push({
            timestamp: performance.now(),
            duration: delta
        });

        if (
            this.performanceMonitor.longTasks.length > 50
        ) {
            this.performanceMonitor.longTasks.shift();
        }

        this.emit('tefco:performance:long-frame', {
            duration: delta
        });
    }

    /**
     * Returns the current FPS.
     *
     * @returns {number}
     */
    getFPS() {
        return this.performanceMonitor.fps.current;
    }

    /**
     * Returns current frame timing.
     *
     * @returns {number}
     */
    getFrameTime() {
        return this.performanceMonitor.frame.delta;
    }

    /**
     * Returns total runtime in milliseconds.
     *
     * @returns {number}
     */
    getRuntime() {
        return performance.now() -
            this.performanceMonitor.runtime.startedAt;
    }

    /**
     * Returns whether monitoring is active.
     *
     * @returns {boolean}
     */
    isPerformanceMonitoring() {
        return !!(
            this.performanceMonitor &&
            this.performanceMonitor.monitoring
        );
    }
    /**
     * ========================================================================
     * Part 1B.4b
     * Task Scheduler
     * ------------------------------------------------------------------------
     * Enterprise Task Scheduling Infrastructure
     * Priority Queue
     * Idle Tasks
     * Micro Tasks
     * Deferred Execution
     * ========================================================================
     */

    initializeTaskScheduler() {
        this.taskScheduler = {
            initialized: false,

            queue: {
                high: [],
                normal: [],
                low: [],
                idle: []
            },

            activeTasks: new Map(),

            processing: false,

            statistics: {
                queued: 0,
                completed: 0,
                cancelled: 0
            }
        };

        this.taskScheduler.initialized = true;
    }

    /**
     * Processes queued tasks.
     */
    processTaskQueue() {
        if (
            this.taskScheduler.processing
        ) {
            return;
        }

        this.taskScheduler.processing = true;

        const execute = () => {

            const task =
                this.taskScheduler.queue.high.shift() ||
                this.taskScheduler.queue.normal.shift() ||
                this.taskScheduler.queue.low.shift();

            if (!task) {
                this.taskScheduler.processing = false;
                return;
            }

            this.safeExecute(task.task);

            this.taskScheduler.activeTasks.delete(task.id);

            this.taskScheduler.statistics.completed++;

            queueMicrotask(execute);
        };

        queueMicrotask(execute);
    }

    /**
     * Schedules an idle task.
     *
     * @param {Function} task
     * @param {Object} options
     * @returns {*}
     */
    scheduleIdleTask(task, options = { timeout: 1000 }) {
        if (typeof task !== 'function') {
            return null;
        }

        if ('requestIdleCallback' in window) {
            return requestIdleCallback(
                () => this.safeExecute(task),
                options
            );
        }

        return this.setTimeout(
            () => this.safeExecute(task),
            16
        );
    }

    /**
     * Executes a micro task.
     *
     * @param {Function} task
     */
    scheduleMicroTask(task) {
        if (typeof task !== 'function') {
            return;
        }

        queueMicrotask(() => {
            this.safeExecute(task);
        });
    }

    /**
     * Executes a macro task.
     *
     * @param {Function} task
     */
    scheduleMacroTask(task) {
        if (typeof task !== 'function') {
            return;
        }

        this.setTimeout(() => {
            this.safeExecute(task);
        }, 0);
    }

    /**
     * Cancels a scheduled task.
     *
     * @param {string} id
     * @returns {boolean}
     */
    cancelTask(id) {
        const task = this.taskScheduler.activeTasks.get(id);

        if (!task) {
            return false;
        }

        ['high', 'normal', 'low'].forEach(priority => {

            const queue = this.taskScheduler.queue[priority];

            const index = queue.findIndex(
                item => item.id === id
            );

            if (index !== -1) {
                queue.splice(index, 1);
            }

        });

        this.taskScheduler.activeTasks.delete(id);

        this.taskScheduler.statistics.cancelled++;

        return true;
    }

    /**
     * Cancels every queued task.
     */
    cancelAllTasks() {

        Object.keys(this.taskScheduler.queue).forEach(priority => {
            this.taskScheduler.queue[priority].length = 0;
        });

        this.taskScheduler.activeTasks.clear();

        this.taskScheduler.processing = false;
    }

    /**
     * Returns scheduler statistics.
     *
     * @returns {Object}
     */
    getTaskSchedulerStats() {
        return {
            queued: this.taskScheduler.statistics.queued,
            completed: this.taskScheduler.statistics.completed,
            cancelled: this.taskScheduler.statistics.cancelled,
            pending: this.taskScheduler.activeTasks.size,
            processing: this.taskScheduler.processing
        };
    }

    /**
     * Returns whether the scheduler is initialized.
     *
     * @returns {boolean}
     */
    isTaskSchedulerReady() {
        return !!(
            this.taskScheduler &&
            this.taskScheduler.initialized
        );
    }
    /**
     * ========================================================================
     * Part 1B.4c
     * Performance Metrics & Diagnostics
     * ------------------------------------------------------------------------
     * Performance Measurement
     * Timing Helpers
     * Runtime Metrics
     * Performance API Integration
     * Diagnostics & Reporting
     * ========================================================================
     */

    /**
     * Creates a performance mark.
     *
     * @param {string} name
     */
    mark(name) {
        if (
            !window.performance ||
            typeof performance.mark !== 'function'
        ) {
            return;
        }

        performance.mark(name);
    }

    /**
     * Measures the duration between two marks.
     *
     * @param {string} name
     * @param {string} startMark
     * @param {string} endMark
     * @returns {number|null}
     */
    measure(name, startMark, endMark) {
        if (
            !window.performance ||
            typeof performance.measure !== 'function'
        ) {
            return null;
        }

        try {
            performance.measure(name, startMark, endMark);

            const entries = performance.getEntriesByName(name);

            if (!entries.length) {
                return null;
            }

            return entries[entries.length - 1].duration;
        } catch (error) {
            this.logger?.warn?.(
                '[Performance] Failed to measure.',
                error
            );

            return null;
        }
    }

    /**
     * Clears performance marks.
     *
     * @param {string} [name]
     */
    clearMarks(name) {
        if (!window.performance) {
            return;
        }

        performance.clearMarks(name);
    }

    /**
     * Clears performance measures.
     *
     * @param {string} [name]
     */
    clearMeasures(name) {
        if (!window.performance) {
            return;
        }

        performance.clearMeasures(name);
    }

    /**
     * Measures execution time of a callback.
     *
     * @param {string} label
     * @param {Function} callback
     * @returns {*}
     */
    time(label, callback) {
        if (typeof callback !== 'function') {
            return;
        }

        const start = `${label}:start`;
        const end = `${label}:end`;

        this.mark(start);

        const result = callback();

        this.mark(end);

        const duration = this.measure(
            label,
            start,
            end
        );

        this.clearMarks(start);
        this.clearMarks(end);
        this.clearMeasures(label);

        this.emit('tefco:performance:measure', {
            label,
            duration
        });

        return result;
    }

    /**
     * Returns navigation timing metrics.
     *
     * @returns {PerformanceNavigationTiming|null}
     */
    getNavigationTiming() {
        if (
            !window.performance ||
            typeof performance.getEntriesByType !== 'function'
        ) {
            return null;
        }

        const entries = performance.getEntriesByType('navigation');

        return entries.length ? entries[0] : null;
    }

    /**
     * Returns resource timing entries.
     *
     * @returns {PerformanceResourceTiming[]}
     */
    getResourceTimings() {
        if (
            !window.performance ||
            typeof performance.getEntriesByType !== 'function'
        ) {
            return [];
        }

        return performance.getEntriesByType('resource');
    }

    /**
     * Returns a performance summary.
     *
     * @returns {Object}
     */
    getPerformanceSummary() {
        return {
            runtime: this.getRuntime(),
            fps: {
                current: this.performanceMonitor?.fps?.current ?? 0,
                average: Math.round(
                    this.performanceMonitor?.fps?.average ?? 0
                ),
                minimum: this.performanceMonitor?.fps?.minimum ?? 0,
                maximum: this.performanceMonitor?.fps?.maximum ?? 0
            },
            scheduler: this.isTaskSchedulerReady()
                ? this.getTaskSchedulerStats()
                : null,
            longTasks: this.performanceMonitor?.longTasks?.length ?? 0,
            timestamp: performance.now()
        };
    }

    /**
     * Returns performance diagnostics.
     *
     * @returns {Object}
     */
    getPerformanceDiagnostics() {
        return {
            summary: this.getPerformanceSummary(),
            navigation: this.getNavigationTiming(),
            resources: this.getResourceTimings().length,
            observerManager:
                typeof this.getObserverSummary === 'function'
                    ? this.getObserverSummary()
                    : null
        };
    }

    /**
     * Logs performance diagnostics.
     *
     * @returns {Object}
     */
    logPerformanceDiagnostics() {
        const diagnostics = this.getPerformanceDiagnostics();

        if (this.config?.debug) {
            console.groupCollapsed('Performance Diagnostics');

            console.log('Summary', diagnostics.summary);
            console.log('Navigation', diagnostics.navigation);
            console.log('Resources', diagnostics.resources);
            console.log('Observers', diagnostics.observerManager);

            console.groupEnd();
        }

        return diagnostics;
    }
    /**
     * ========================================================================
     * Part 1B.4d
     * Performance Cleanup & Lifecycle
     * ------------------------------------------------------------------------
     * Cleanup
     * Reset
     * Destroy
     * Validation
     * Diagnostics
     * ========================================================================
     */

    /**
     * Clears Performance API entries.
     *
     * @param {boolean} includeResources
     */
    cleanupPerformanceMetrics(includeResources = false) {
        if (!window.performance) {
            return;
        }

        if (typeof performance.clearMarks === 'function') {
            performance.clearMarks();
        }

        if (typeof performance.clearMeasures === 'function') {
            performance.clearMeasures();
        }

        if (
            includeResources &&
            typeof performance.clearResourceTimings === 'function'
        ) {
            performance.clearResourceTimings();
        }
    }

    /**
     * Resets the performance monitor statistics.
     */
    resetPerformanceMonitor() {
        if (!this.performanceMonitor) {
            return;
        }

        const monitoring = this.performanceMonitor.monitoring;

        if (monitoring) {
            this.stopPerformanceMonitor();
        }

        this.performanceMonitor.fps.current = 60;
        this.performanceMonitor.fps.average = 60;
        this.performanceMonitor.fps.minimum = 60;
        this.performanceMonitor.fps.maximum = 60;
        this.performanceMonitor.fps.samples.length = 0;

        this.performanceMonitor.frame.lastTime = performance.now();
        this.performanceMonitor.frame.delta = 0;
        this.performanceMonitor.frame.frameCount = 0;

        this.performanceMonitor.runtime.startedAt = performance.now();
        this.performanceMonitor.runtime.updates = 0;

        this.performanceMonitor.longTasks.length = 0;

        this.cleanupPerformanceMetrics();

        if (monitoring) {
            this.startPerformanceMonitor();
        }

        this.emit('tefco:performance:reset');
    }

    /**
     * Resets the task scheduler.
     */
    resetTaskScheduler() {
        if (!this.taskScheduler) {
            return;
        }

        this.cancelAllTasks();

        this.taskScheduler.statistics = {
            queued: 0,
            completed: 0,
            cancelled: 0
        };

        this.emit('tefco:scheduler:reset');
    }

    /**
     * Destroys the performance monitor.
     */
    destroyPerformanceMonitor() {
        if (!this.performanceMonitor) {
            return;
        }

        this.stopPerformanceMonitor();

        this.performanceMonitor.longTasks.length = 0;
        this.performanceMonitor.fps.samples.length = 0;

        this.performanceMonitor.initialized = false;

        this.cleanupPerformanceMetrics();

        this.emit('tefco:performance:destroy');
    }

    /**
     * Destroys the task scheduler.
     */
    destroyTaskScheduler() {
        if (!this.taskScheduler) {
            return;
        }

        this.cancelAllTasks();

        this.taskScheduler.activeTasks.clear();

        Object.keys(this.taskScheduler.queue).forEach(priority => {
            this.taskScheduler.queue[priority].length = 0;
        });

        this.taskScheduler.processing = false;
        this.taskScheduler.initialized = false;

        this.emit('tefco:scheduler:destroy');
    }

    /**
     * Returns whether the Performance Infrastructure is ready.
     *
     * @returns {boolean}
     */
    isPerformanceReady() {
        return Boolean(
            this.performanceMonitor?.initialized &&
            this.taskScheduler?.initialized
        );
    }

    /**
     * Validates the Performance Infrastructure.
     *
     * @returns {Object}
     */
    validatePerformanceInfrastructure() {
        return {
            ready: this.isPerformanceReady(),

            monitor: {
                initialized:
                    this.performanceMonitor?.initialized ?? false,
                monitoring:
                    this.performanceMonitor?.monitoring ?? false
            },

            scheduler: this.isTaskSchedulerReady(),

            diagnostics: this.getPerformanceDiagnostics()
        };
    }

    /**
     * Returns a complete performance report.
     *
     * @returns {Object}
     */
    getPerformanceReport() {
        return {
            timestamp: new Date().toISOString(),
            runtime: this.getRuntime(),
            summary: this.getPerformanceSummary(),
            diagnostics: this.getPerformanceDiagnostics(),
            validation: this.validatePerformanceInfrastructure()
        };
    }

    /**
     * Destroys the complete Performance Infrastructure.
     */
    destroyPerformanceInfrastructure() {
        this.destroyPerformanceMonitor();

        this.destroyTaskScheduler();

        this.cleanupPerformanceMetrics(true);

        this.emit('tefco:performance:infrastructure-destroyed');
    }
    /**
     * ========================================================================
     
    //----------------------------------------
    // PART 2 — Hero
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Hero
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Hero
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Hero
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Hero
    //----------------------------------------

* Part 2A
     * Hero Foundation
     * ------------------------------------------------------------------------
     * Core Hero Infrastructure
     * Initialization
     * DOM Cache
     * Configuration
     * State Management
     * ========================================================================
     */

    /**
     * Initializes the Hero component.
     */
    initializeHero() {
        this.hero = {
            initialized: false,
            ready: false,
            section: null,
            slides: [],
            currentSlide: 0,
            totalSlides: 0,
            config: {},
            state: {},
            dom: {},
            observers: [],
            timers: [],
            initializedAt: performance.now()
        };

        this.cacheHeroDOM();

        if (!this.hero.dom.section) {
            return;
        }

        this.initializeHeroConfig();

        this.initializeHeroState();

        this.bindHeroEvents();

        this.initializeHeroObservers();

        this.hero.initialized = true;
        this.hero.ready = true;

        this.emit('tefco:hero:initialized', {
            section: this.hero.dom.section
        });
    }


    /**
     * Establishes the core Hero event bootstrap flag.
     * Feature-specific event engines remain independently managed.
     */
    bindHeroEvents() {
        if (!this.hero || this.hero.eventsBound) {
            return;
        }

        this.hero.eventsBound = true;
    }

    /**
     * Establishes the core Hero observer bootstrap flag.
     * Feature-specific observers remain independently managed.
     */
    initializeHeroObservers() {
        if (!this.hero || this.hero.observersInitialized) {
            return;
        }

        this.hero.observersInitialized = true;
    }

    /**
     * ------------------------------------------------------------------------
     * Part 2A.2
     * Hero DOM Cache
     * ------------------------------------------------------------------------
     */

    cacheHeroDOM() {
        const section =
            document.querySelector('[data-tefco-hero]') ||
            document.querySelector('.tefco-hero');

        this.hero.dom = {
            section,
            wrapper: section?.querySelector('.tefco-hero__wrapper') ?? null,
            slider: section?.querySelector('.tefco-hero__slider') ?? null,
            track: section?.querySelector('.tefco-hero__track') ?? null,
            slides: Array.from(
                section?.querySelectorAll('.tefco-hero__slide') ?? []
            ),
            media: Array.from(
                section?.querySelectorAll('.tefco-hero__media') ?? []
            ),
            content: Array.from(
                section?.querySelectorAll('.tefco-hero__content') ?? []
            ),
            buttons: Array.from(
                section?.querySelectorAll('.tefco-btn') ?? []
            ),
            pagination: section?.querySelector('.tefco-hero__pagination') ?? null,
            navigation: section?.querySelector('.tefco-hero__navigation') ?? null
        };

        this.hero.section = section;
        this.hero.slides = this.hero.dom.slides;
        this.hero.totalSlides = this.hero.dom.slides.length;
    }

    /**
     * ------------------------------------------------------------------------
     * Part 2A.3
     * Hero Configuration
     * ------------------------------------------------------------------------
     */

    initializeHeroConfig() {
        const section = this.hero.dom.section;

        const dataset = section?.dataset ?? {};

        this.hero.config = {
            autoplay: dataset.autoplay === 'true',
            autoplaySpeed: Number(dataset.autoplaySpeed) || 5000,
            loop: dataset.loop === 'true',
            animation: dataset.animation || 'fade',
            pauseOnHover: dataset.pauseOnHover !== 'false',
            preloadImages: dataset.preloadImages !== 'false',
            lazyLoad: dataset.lazyLoad !== 'false',
            keyboard: dataset.keyboard !== 'false',
            touch: dataset.touch !== 'false',
            threshold: Number(dataset.threshold) || 0.25
        };
    }

    /**
     * ------------------------------------------------------------------------
     * Part 2A.4
     * Hero State Management
     * ------------------------------------------------------------------------
     */

    initializeHeroState() {
        this.hero.state = {
            initialized: true,
            activeIndex: 0,
            previousIndex: null,
            playing: false,
            paused: false,
            visible: false,
            animating: false,
            destroyed: false,
            lastInteraction: performance.now()
        };
    }

    /**
     * Returns the Hero section element.
     *
     * @returns {HTMLElement|null}
     */
    getHeroElement() {
        return this.hero?.dom?.section ?? null;
    }

    /**
     * Returns the active Hero slide index.
     *
     * @returns {number}
     */
    getActiveHeroSlide() {
        return this.hero?.state?.activeIndex ?? 0;
    }

    /**
     * Returns the total number of Hero slides.
     *
     * @returns {number}
     */
    getHeroSlideCount() {
        return this.hero?.totalSlides ?? 0;
    }

    /**
     * Returns whether the Hero component is initialized.
     *
     * @returns {boolean}
     */
    isHeroReady() {
        return !!(
            this.hero &&
            this.hero.initialized &&
            this.hero.ready &&
            !this.hero.state.destroyed
        );
    }

    /**
     * Updates the active Hero slide.
     *
     * @param {number} index
     */
    setActiveHeroSlide(index) {
        if (
            index < 0 ||
            index >= this.hero.totalSlides
        ) {
            return;
        }

        this.hero.state.previousIndex =
            this.hero.state.activeIndex;

        this.hero.state.activeIndex = index;

        this.hero.state.lastInteraction = performance.now();

        this.emit('tefco:hero:slide-change', {
            previous: this.hero.state.previousIndex,
            current: index
        });
    }

    /**
     * Returns the complete Hero runtime state.
     *
     * @returns {Object}
     */
    getHeroState() {
        return {
            initialized: this.hero.initialized,
            ready: this.hero.ready,
            slides: this.hero.totalSlides,
            currentSlide: this.hero.state.activeIndex,
            config: this.hero.config,
            state: this.hero.state
        };
    }
    /**
     * ========================================================================
     * Part 2B.1
     * Slider Initialization
     * ------------------------------------------------------------------------
     * Enterprise Slider Bootstrap
     * Lifecycle
     * Initial Slide
     * Lazy Integration
     * Accessibility
     * ========================================================================
     */

    /**
     * Initializes the Hero slider.
     */
    initializeHeroSlider() {
        if (!this.isHeroReady()) {
            return;
        }

        if (
            this.hero.slider &&
            this.hero.slider.initialized
        ) {
            return;
        }

        this.hero.slider = {
            initialized: false,
            current: 0,
            previous: null,
            direction: 'next',
            locked: false,
            autoplay: false,
            animating: false,
            initializedAt: performance.now()
        };

        this.prepareHeroSlides();

        this.initializeHeroSlideState();

        this.initializeHeroAccessibility();

        this.initializeHeroLazyLoading();

        this.initializeHeroAutoplay();

        this.initializeHeroNavigation();

        this.initializeHeroPagination();

        this.hero.slider.initialized = true;

        this.emit('tefco:hero:slider:initialized', {
            slides: this.hero.totalSlides
        });
    }

    /**
     * Prepares every slide.
     */
    prepareHeroSlides() {
        this.hero.slides.forEach((slide, index) => {

            slide.dataset.slideIndex = index;

            slide.setAttribute(
                'aria-hidden',
                index === 0 ? 'false' : 'true'
            );

            slide.classList.toggle(
                'is-active',
                index === 0
            );

            slide.classList.toggle(
                'is-prev',
                false
            );

            slide.classList.toggle(
                'is-next',
                index === 1
            );

        });
    }

    /**
     * Initializes the first slide state.
     */
    initializeHeroSlideState() {
        this.hero.state.activeIndex = 0;

        this.hero.state.previousIndex = null;

        this.hero.slider.current = 0;

        this.hero.slider.previous = null;

        if (this.hero.slides[0]) {

            this.safeExecute(() => {
                this.activateHeroSlide(
                    0,
                    false
                );
            });

        }
    }

    /**
     * Activates a Hero slide.
     *
     * @param {number} index
     * @param {boolean} animate
     */
    activateHeroSlide(
        index,
        animate = true
    ) {
        if (
            index < 0 ||
            index >= this.hero.totalSlides
        ) {
            return;
        }

        const previous =
            this.hero.slider.current;

        this.hero.slides.forEach((slide, slideIndex) => {

            const active =
                slideIndex === index;

            slide.classList.toggle(
                'is-active',
                active
            );

            slide.classList.toggle(
                'is-prev',
                slideIndex === previous
            );

            slide.classList.toggle(
                'is-next',
                false
            );

            slide.setAttribute(
                'aria-hidden',
                active ? 'false' : 'true'
            );

        });

        this.hero.slider.previous = previous;
        this.hero.slider.current = index;

        this.setActiveHeroSlide(index);

        if (animate) {
            this.animateHeroSlide(index);
        }

        this.emit(
            'tefco:hero:slide:activated',
            {
                previous,
                current: index
            }
        );
    }

    /**
     * Handles Hero slide animation.
     *
     * @param {number} index
     */
    animateHeroSlide(index) {
        const slide =
            this.hero.slides[index];

        if (!slide) {
            return;
        }

        this.hero.slider.animating = true;

        slide.classList.add(
            'is-animating'
        );

        this.setTimeout(() => {

            slide.classList.remove(
                'is-animating'
            );

            this.hero.slider.animating = false;

        }, 600);
    }

    /**
     * Initializes lazy loading integration.
     */
    initializeHeroLazyLoading() {
        if (!this.hero.config.lazyLoad) {
            return;
        }

        this.hero.slides.forEach(slide => {

            this.observeLazyComponent(
                slide,
                () => {
                    this.loadHeroSlideMedia(
                        slide
                    );
                }
            );

        });
    }

    /**
     * Loads slide media.
     *
     * @param {HTMLElement} slide
     */
    loadHeroSlideMedia(slide) {
        slide
            .querySelectorAll('[data-src]')
            .forEach(element => {

                if (
                    element.dataset.loaded
                ) {
                    return;
                }

                if (
                    element.dataset.src
                ) {
                    element.src =
                        element.dataset.src;
                }

                if (
                    element.dataset.srcset
                ) {
                    element.srcset =
                        element.dataset.srcset;
                }

                element.dataset.loaded = 'true';

            });
    }

    /**
     * Initializes slider accessibility.
     */
    initializeHeroAccessibility() {
        const slider =
            this.hero.dom.slider;

        if (!slider) {
            return;
        }

        slider.setAttribute(
            'role',
            'region'
        );

        slider.setAttribute(
            'aria-roledescription',
            'carousel'
        );

        slider.setAttribute(
            'aria-live',
            'polite'
        );
    }

    /**
     * Returns whether the Hero slider is ready.
     *
     * @returns {boolean}
     */
    isHeroSliderReady() {
        return !!(
            this.hero?.slider &&
            this.hero.slider.initialized
        );
    }

    /**
     * Returns the Hero slider state.
     *
     * @returns {Object}
     */
    getHeroSliderState() {
        return {
            initialized:
                this.hero.slider.initialized,
            current:
                this.hero.slider.current,
            previous:
                this.hero.slider.previous,
            animating:
                this.hero.slider.animating,
            autoplay:
                this.hero.slider.autoplay,
            slides:
                this.hero.totalSlides
        };
    }
    /**
     * ========================================================================
     * Part 2B.2
     * Hero Navigation
     * ------------------------------------------------------------------------
     * Previous / Next Controls
     * Keyboard Navigation
     * Touch Navigation
     * Direction Management
     * Loop Handling
     * ========================================================================
     */

    /**
     * Initializes Hero navigation.
     */
    initializeHeroNavigation() {
        this.hero.navigation = {
            initialized: false,
            direction: 'next',
            touch: {
                startX: 0,
                startY: 0,
                endX: 0,
                endY: 0,
                threshold: 60
            }
        };

        this.bindHeroNavigation();

        if (this.hero.config.keyboard) {
            this.bindHeroKeyboardNavigation();
        }

        if (this.hero.config.touch) {
            this.bindHeroTouchNavigation();
        }

        this.hero.navigation.initialized = true;

        this.emit('tefco:hero:navigation:initialized');
    }

    /**
     * Binds previous / next buttons.
     */
    bindHeroNavigation() {
        const container = this.hero.dom.navigation;

        if (!container) {
            return;
        }

        const previousButton = container.querySelector(
            '[data-hero-prev]'
        );

        const nextButton = container.querySelector(
            '[data-hero-next]'
        );

        if (previousButton) {
            this.on(
                previousButton,
                'click',
                () => this.previousHeroSlide()
            );
        }

        if (nextButton) {
            this.on(
                nextButton,
                'click',
                () => this.nextHeroSlide()
            );
        }
    }

    /**
     * Moves to the next slide.
     */
    nextHeroSlide() {
        this.changeHeroSlide('next');
    }

    /**
     * Moves to the previous slide.
     */
    previousHeroSlide() {
        this.changeHeroSlide('previous');
    }

    /**
     * Changes the current slide.
     *
     * @param {'next'|'previous'} direction
     */
    changeHeroSlide(direction = 'next') {
        if (
            !this.isHeroSliderReady() ||
            this.hero.slider.animating ||
            this.hero.slider.locked
        ) {
            return;
        }

        let index = this.hero.slider.current;

        if (direction === 'next') {
            index++;
        } else {
            index--;
        }

        if (index >= this.hero.totalSlides) {
            index = this.hero.config.loop
                ? 0
                : this.hero.totalSlides - 1;
        }

        if (index < 0) {
            index = this.hero.config.loop
                ? this.hero.totalSlides - 1
                : 0;
        }

        this.hero.navigation.direction = direction;

        this.activateHeroSlide(index);

        this.emit(
            'tefco:hero:navigation:change',
            {
                direction,
                index
            }
        );
    }

    /**
     * Navigates directly to a slide.
     *
     * @param {number} index
     */
    goToHeroSlide(index) {
        if (
            index === this.hero.slider.current ||
            index < 0 ||
            index >= this.hero.totalSlides
        ) {
            return;
        }

        this.hero.navigation.direction =
            index > this.hero.slider.current
                ? 'next'
                : 'previous';

        this.activateHeroSlide(index);
    }

    /**
     * Binds keyboard navigation.
     */
    bindHeroKeyboardNavigation() {
        this.on(
            document,
            'keydown',
            event => {

                if (!this.hero.state.visible) {
                    return;
                }

                switch (event.key) {

                    case 'ArrowRight':
                        this.nextHeroSlide();
                        break;

                    case 'ArrowLeft':
                        this.previousHeroSlide();
                        break;

                    case 'Home':
                        this.goToHeroSlide(0);
                        break;

                    case 'End':
                        this.goToHeroSlide(
                            this.hero.totalSlides - 1
                        );
                        break;

                    default:
                        return;

                }

            }
        );
    }

    /**
     * Binds touch navigation.
     */
    bindHeroTouchNavigation() {
        const slider = this.hero.dom.slider;

        if (!slider) {
            return;
        }

        this.on(
            slider,
            'touchstart',
            event => {

                const touch = event.touches[0];

                this.hero.navigation.touch.startX = touch.clientX;
                this.hero.navigation.touch.startY = touch.clientY;

            },
            {
                passive: true
            }
        );

        this.on(
            slider,
            'touchend',
            event => {

                const touch = event.changedTouches[0];

                this.hero.navigation.touch.endX = touch.clientX;
                this.hero.navigation.touch.endY = touch.clientY;

                this.handleHeroSwipe();

            },
            {
                passive: true
            }
        );
    }

    /**
     * Handles swipe gestures.
     */
    handleHeroSwipe() {
        const touch = this.hero.navigation.touch;

        const deltaX =
            touch.endX - touch.startX;

        const deltaY =
            touch.endY - touch.startY;

        if (
            Math.abs(deltaY) >
            Math.abs(deltaX)
        ) {
            return;
        }

        if (
            Math.abs(deltaX) <
            touch.threshold
        ) {
            return;
        }

        if (deltaX > 0) {
            this.previousHeroSlide();
        } else {
            this.nextHeroSlide();
        }
    }

    /**
     * Locks navigation.
     */
    lockHeroNavigation() {
        this.hero.slider.locked = true;
    }

    /**
     * Unlocks navigation.
     */
    unlockHeroNavigation() {
        this.hero.slider.locked = false;
    }

    /**
     * Returns whether navigation is locked.
     *
     * @returns {boolean}
     */
    isHeroNavigationLocked() {
        return this.hero.slider.locked;
    }

    /**
     * Returns the current navigation direction.
     *
     * @returns {string}
     */
    getHeroNavigationDirection() {
        return this.hero.navigation.direction;
    }

    /**
     * Returns the Hero navigation state.
     *
     * @returns {Object}
     */
    getHeroNavigationState() {
        return {
            initialized:
                this.hero.navigation.initialized,
            direction:
                this.hero.navigation.direction,
            locked:
                this.hero.slider.locked,
            current:
                this.hero.slider.current,
            total:
                this.hero.totalSlides
        };
    }
    /**
     * ========================================================================
     * Part 2B.3
     * Hero Pagination
     * ------------------------------------------------------------------------
     * Enterprise Pagination System
     * Dynamic Pagination
     * Active State Synchronization
     * Keyboard Accessibility
     * Runtime Updates
     * ========================================================================
     */

    /**
     * Initializes Hero pagination.
     */
    initializeHeroPagination() {
        this.hero.pagination = {
            initialized: false,
            bullets: [],
            active: 0
        };

        const container = this.hero.dom.pagination;

        if (!container || this.hero.totalSlides <= 1) {
            return;
        }

        this.buildHeroPagination();

        this.bindHeroPagination();

        this.updateHeroPagination();

        this.hero.pagination.initialized = true;

        this.emit('tefco:hero:pagination:initialized', {
            total: this.hero.totalSlides
        });
    }

    /**
     * Creates pagination buttons.
     */
    buildHeroPagination() {
        const container = this.hero.dom.pagination;

        container.innerHTML = '';

        this.hero.pagination.bullets = [];

        this.hero.slides.forEach((slide, index) => {

            const button = document.createElement('button');

            button.type = 'button';

            button.className = 'tefco-hero__pagination-button';

            button.dataset.slide = index;

            button.setAttribute(
                'aria-label',
                `Go to slide ${index + 1}`
            );

            button.setAttribute(
                'aria-controls',
                slide.id || ''
            );

            button.setAttribute(
                'aria-current',
                index === 0 ? 'true' : 'false'
            );

            button.setAttribute(
                'aria-selected',
                index === 0 ? 'true' : 'false'
            );

            button.setAttribute(
                'tabindex',
                index === 0 ? '0' : '-1'
            );

            if (index === 0) {
                button.classList.add('is-active');
            }

            container.appendChild(button);

            this.hero.pagination.bullets.push(button);

        });
    }

    /**
     * Binds pagination events.
     */
    bindHeroPagination() {
        this.hero.pagination.bullets.forEach(button => {

            this.on(
                button,
                'click',
                () => {

                    const index = Number(
                        button.dataset.slide
                    );

                    this.goToHeroSlide(index);

                }
            );

            this.on(
                button,
                'keydown',
                event => {

                    switch (event.key) {

                        case 'ArrowRight':
                        case 'ArrowDown':
                            event.preventDefault();
                            this.focusNextPaginationButton();
                            break;

                        case 'ArrowLeft':
                        case 'ArrowUp':
                            event.preventDefault();
                            this.focusPreviousPaginationButton();
                            break;

                        case 'Home':
                            event.preventDefault();
                            this.focusPaginationButton(0);
                            break;

                        case 'End':
                            event.preventDefault();
                            this.focusPaginationButton(
                                this.hero.pagination.bullets.length - 1
                            );
                            break;

                    }

                }
            );

        });
    }

    /**
     * Updates pagination UI.
     */
    updateHeroPagination() {
        if (!this.hero.pagination?.initialized) {
            return;
        }

        const current =
            this.hero.slider.current;

        this.hero.pagination.active = current;

        this.hero.pagination.bullets.forEach(
            (button, index) => {

                const active =
                    index === current;

                button.classList.toggle(
                    'is-active',
                    active
                );

                button.setAttribute(
                    'aria-current',
                    active ? 'true' : 'false'
                );

                button.setAttribute(
                    'aria-selected',
                    active ? 'true' : 'false'
                );

                button.setAttribute(
                    'tabindex',
                    active ? '0' : '-1'
                );

            }
        );

        this.emit(
            'tefco:hero:pagination:update',
            {
                active: current
            }
        );
    }

    /**
     * Focuses a pagination button.
     *
     * @param {number} index
     */
    focusPaginationButton(index) {
        const button =
            this.hero.pagination.bullets[index];

        if (!button) {
            return;
        }

        button.focus();
    }

    /**
     * Focuses the next pagination button.
     */
    focusNextPaginationButton() {
        const next =
            (this.hero.pagination.active + 1) %
            this.hero.pagination.bullets.length;

        this.focusPaginationButton(next);
    }

    /**
     * Focuses the previous pagination button.
     */
    focusPreviousPaginationButton() {
        const total =
            this.hero.pagination.bullets.length;

        const previous =
            (this.hero.pagination.active - 1 + total) %
            total;

        this.focusPaginationButton(previous);
    }

    /**
     * Synchronizes pagination after slide changes.
     */
    syncHeroPagination() {
        if (
            !this.hero.pagination?.initialized
        ) {
            return;
        }

        this.updateHeroPagination();
    }

    /**
     * Returns the active pagination index.
     *
     * @returns {number}
     */
    getActivePagination() {
        return this.hero.pagination?.active ?? 0;
    }

    /**
     * Returns pagination state.
     *
     * @returns {Object}
     */
    getHeroPaginationState() {
        return {
            initialized:
                this.hero.pagination?.initialized ?? false,
            active:
                this.hero.pagination?.active ?? 0,
            total:
                this.hero.pagination?.bullets?.length ?? 0
        };
    }

    /**
     * Returns whether pagination is initialized.
     *
     * @returns {boolean}
     */
    isHeroPaginationReady() {
        return !!(
            this.hero.pagination &&
            this.hero.pagination.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2B.4
     * Hero Autoplay
     * ------------------------------------------------------------------------
     * Enterprise Autoplay Engine
     * Auto Rotation
     * Pause / Resume
     * Visibility Integration
     * Hover Detection
     * Focus Management
     * ========================================================================
     */

    /**
     * Initializes Hero autoplay.
     */
    initializeHeroAutoplay() {
        this.hero.autoplay = {
            initialized: false,
            running: false,
            paused: false,
            intervalId: null,
            startedAt: null,
            remaining: this.hero.config.autoplaySpeed,
            hoverPaused: false,
            visibilityPaused: false,
            focusPaused: false
        };

        if (!this.hero.config.autoplay || this.hero.totalSlides <= 1) {
            return;
        }

        this.bindHeroAutoplayEvents();

        this.startHeroAutoplay();

        this.hero.autoplay.initialized = true;

        this.emit('tefco:hero:autoplay:initialized');
    }

    /**
     * Starts autoplay.
     */
    startHeroAutoplay() {
        if (
            !this.hero.config.autoplay ||
            this.hero.autoplay.running
        ) {
            return;
        }

        this.hero.autoplay.intervalId = this.setInterval(() => {

            if (
                this.hero.slider.locked ||
                this.hero.slider.animating
            ) {
                return;
            }

            this.nextHeroSlide();

        }, this.hero.config.autoplaySpeed);

        this.hero.autoplay.running = true;
        this.hero.autoplay.paused = false;
        this.hero.autoplay.startedAt = performance.now();

        this.hero.slider.autoplay = true;

        this.emit('tefco:hero:autoplay:start');
    }

    /**
     * Stops autoplay.
     */
    stopHeroAutoplay() {
        if (!this.hero.autoplay.running) {
            return;
        }

        this.clearInterval(
            this.hero.autoplay.intervalId
        );

        this.hero.autoplay.intervalId = null;
        this.hero.autoplay.running = false;

        this.hero.slider.autoplay = false;

        this.emit('tefco:hero:autoplay:stop');
    }

    /**
     * Pauses autoplay.
     *
     * @param {string} reason
     */
    pauseHeroAutoplay(reason = 'manual') {
        if (
            !this.hero.autoplay.running ||
            this.hero.autoplay.paused
        ) {
            return;
        }

        this.stopHeroAutoplay();

        this.hero.autoplay.paused = true;

        switch (reason) {

            case 'hover':
                this.hero.autoplay.hoverPaused = true;
                break;

            case 'visibility':
                this.hero.autoplay.visibilityPaused = true;
                break;

            case 'focus':
                this.hero.autoplay.focusPaused = true;
                break;

        }

        this.emit(
            'tefco:hero:autoplay:pause',
            { reason }
        );
    }

    /**
     * Resumes autoplay.
     *
     * @param {string} reason
     */
    resumeHeroAutoplay(reason = 'manual') {
        switch (reason) {

            case 'hover':
                this.hero.autoplay.hoverPaused = false;
                break;

            case 'visibility':
                this.hero.autoplay.visibilityPaused = false;
                break;

            case 'focus':
                this.hero.autoplay.focusPaused = false;
                break;

        }

        if (
            this.hero.autoplay.hoverPaused ||
            this.hero.autoplay.visibilityPaused ||
            this.hero.autoplay.focusPaused
        ) {
            return;
        }

        this.hero.autoplay.paused = false;

        this.startHeroAutoplay();

        this.emit(
            'tefco:hero:autoplay:resume',
            { reason }
        );
    }

    /**
     * Binds autoplay events.
     */
    bindHeroAutoplayEvents() {
        const slider = this.hero.dom.slider;

        if (!slider) {
            return;
        }

        if (this.hero.config.pauseOnHover) {

            this.on(
                slider,
                'mouseenter',
                () => this.pauseHeroAutoplay('hover')
            );

            this.on(
                slider,
                'mouseleave',
                () => this.resumeHeroAutoplay('hover')
            );

        }

        this.on(
            slider,
            'focusin',
            () => this.pauseHeroAutoplay('focus')
        );

        this.on(
            slider,
            'focusout',
            () => this.resumeHeroAutoplay('focus')
        );

        this.on(
            document,
            'visibilitychange',
            () => {

                if (document.hidden) {
                    this.pauseHeroAutoplay('visibility');
                } else {
                    this.resumeHeroAutoplay('visibility');
                }

            }
        );
    }

    /**
     * Restarts autoplay.
     */
    restartHeroAutoplay() {
        this.stopHeroAutoplay();
        this.startHeroAutoplay();
    }

    /**
     * Returns whether autoplay is running.
     *
     * @returns {boolean}
     */
    isHeroAutoplayRunning() {
        return !!this.hero.autoplay?.running;
    }

    /**
     * Returns whether autoplay is paused.
     *
     * @returns {boolean}
     */
    isHeroAutoplayPaused() {
        return !!this.hero.autoplay?.paused;
    }

    /**
     * Returns autoplay runtime.
     *
     * @returns {number}
     */
    getHeroAutoplayRuntime() {
        if (
            !this.hero.autoplay.running ||
            !this.hero.autoplay.startedAt
        ) {
            return 0;
        }

        return performance.now() -
            this.hero.autoplay.startedAt;
    }

    /**
     * Returns autoplay state.
     *
     * @returns {Object}
     */
    getHeroAutoplayState() {
        return {
            initialized:
                this.hero.autoplay.initialized,
            running:
                this.hero.autoplay.running,
            paused:
                this.hero.autoplay.paused,
            hoverPaused:
                this.hero.autoplay.hoverPaused,
            visibilityPaused:
                this.hero.autoplay.visibilityPaused,
            focusPaused:
                this.hero.autoplay.focusPaused,
            runtime:
                this.getHeroAutoplayRuntime(),
            delay:
                this.hero.config.autoplaySpeed
        };
    }
    /**
     * ========================================================================
     * Part 2B.5
     * Loop Engine
     * ------------------------------------------------------------------------
     * Enterprise Infinite Loop Management
     * Boundary Resolution
     * Index Normalization
     * Wrap Around Logic
     * Loop Diagnostics
     * ========================================================================
     */

    /**
     * Initializes loop management.
     */
    initializeHeroLoop() {
        this.hero.loop = {
            initialized: false,
            enabled: !!this.hero.config.loop,
            wrapped: false,
            wrapCount: 0,
            lastWrapDirection: null
        };

        this.hero.loop.initialized = true;

        this.emit('tefco:hero:loop:initialized', {
            enabled: this.hero.loop.enabled
        });
    }

    /**
     * Returns a normalized slide index.
     *
     * @param {number} index
     * @returns {number}
     */
    normalizeHeroSlideIndex(index) {
        const total = this.hero.totalSlides;

        if (total <= 0) {
            return 0;
        }

        if (!this.hero.loop.enabled) {
            return Math.min(
                Math.max(index, 0),
                total - 1
            );
        }

        return ((index % total) + total) % total;
    }

    /**
     * Returns the next slide index.
     *
     * @returns {number}
     */
    getNextHeroSlideIndex() {
        return this.normalizeHeroSlideIndex(
            this.hero.slider.current + 1
        );
    }

    /**
     * Returns the previous slide index.
     *
     * @returns {number}
     */
    getPreviousHeroSlideIndex() {
        return this.normalizeHeroSlideIndex(
            this.hero.slider.current - 1
        );
    }

    /**
     * Returns whether navigation will wrap.
     *
     * @param {'next'|'previous'} direction
     * @returns {boolean}
     */
    willHeroLoop(direction = 'next') {
        if (!this.hero.loop.enabled) {
            return false;
        }

        if (
            direction === 'next' &&
            this.hero.slider.current === this.hero.totalSlides - 1
        ) {
            return true;
        }

        if (
            direction === 'previous' &&
            this.hero.slider.current === 0
        ) {
            return true;
        }

        return false;
    }

    /**
     * Applies loop rules before navigation.
     *
     * @param {'next'|'previous'} direction
     * @returns {number}
     */
    resolveHeroLoop(direction = 'next') {
        let target =
            direction === 'next'
                ? this.hero.slider.current + 1
                : this.hero.slider.current - 1;

        const wrapped =
            this.willHeroLoop(direction);

        target =
            this.normalizeHeroSlideIndex(target);

        if (wrapped) {
            this.hero.loop.wrapped = true;
            this.hero.loop.wrapCount++;
            this.hero.loop.lastWrapDirection = direction;

            this.emit(
                'tefco:hero:loop:wrapped',
                {
                    direction,
                    target
                }
            );
        } else {
            this.hero.loop.wrapped = false;
        }

        return target;
    }

    /**
     * Handles loop-aware navigation.
     *
     * @param {'next'|'previous'} direction
     */
    navigateHeroLoop(direction = 'next') {
        if (!this.isHeroSliderReady()) {
            return;
        }

        const target =
            this.resolveHeroLoop(direction);

        this.hero.navigation.direction =
            direction;

        this.activateHeroSlide(target);
    }

    /**
     * Returns whether the current slide is the first.
     *
     * @returns {boolean}
     */
    isFirstHeroSlide() {
        return this.hero.slider.current === 0;
    }

    /**
     * Returns whether the current slide is the last.
     *
     * @returns {boolean}
     */
    isLastHeroSlide() {
        return (
            this.hero.slider.current ===
            this.hero.totalSlides - 1
        );
    }

    /**
     * Returns whether looping is enabled.
     *
     * @returns {boolean}
     */
    isHeroLoopEnabled() {
        return !!(
            this.hero.loop &&
            this.hero.loop.enabled
        );
    }

    /**
     * Enables loop mode.
     */
    enableHeroLoop() {
        this.hero.loop.enabled = true;
    }

    /**
     * Disables loop mode.
     */
    disableHeroLoop() {
        this.hero.loop.enabled = false;
    }

    /**
     * Resets loop statistics.
     */
    resetHeroLoop() {
        this.hero.loop.wrapped = false;
        this.hero.loop.wrapCount = 0;
        this.hero.loop.lastWrapDirection = null;
    }

    /**
     * Returns loop statistics.
     *
     * @returns {Object}
     */
    getHeroLoopStatistics() {
        return {
            enabled:
                this.hero.loop.enabled,
            wrapped:
                this.hero.loop.wrapped,
            wrapCount:
                this.hero.loop.wrapCount,
            lastWrapDirection:
                this.hero.loop.lastWrapDirection,
            currentSlide:
                this.hero.slider.current,
            totalSlides:
                this.hero.totalSlides
        };
    }

    /**
     * Returns the current loop state.
     *
     * @returns {Object}
     */
    getHeroLoopState() {
        return {
            initialized:
                this.hero.loop.initialized,
            enabled:
                this.hero.loop.enabled,
            statistics:
                this.getHeroLoopStatistics()
        };
    }

    /**
     * Returns whether the loop engine is ready.
     *
     * @returns {boolean}
     */
    isHeroLoopReady() {
        return !!(
            this.hero.loop &&
            this.hero.loop.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2C.1
     * Responsive Images
     * ------------------------------------------------------------------------
     * Enterprise Responsive Image Infrastructure
     * DPR Optimization
     * Srcset/Sizes Management
     * Preloading
     * Shopify Image Optimization
     * ========================================================================
     */

    /**
     * Initializes responsive image handling.
     */
    initializeHeroResponsiveImages() {
        this.hero.images = {
            initialized: false,
            pixelRatio: window.devicePixelRatio || 1,
            breakpoint: this.getHeroImageBreakpoint(),
            preloadQueue: new Set(),
            loadedImages: new WeakSet()
        };

        this.collectHeroImages();

        this.updateHeroResponsiveImages();

        this.preloadHeroCriticalImages();

        this.hero.images.initialized = true;

        this.emit('tefco:hero:images:initialized');
    }

    /**
     * Collects responsive images.
     */
    collectHeroImages() {
        this.hero.images.elements = Array.from(
            this.hero.dom.section?.querySelectorAll(
                'img[data-responsive], picture img'
            ) ?? []
        );
    }

    /**
     * Updates every responsive image.
     */
    updateHeroResponsiveImages() {
        if (!this.hero.images?.elements?.length) {
            return;
        }

        const breakpoint = this.getHeroImageBreakpoint();

        this.hero.images.breakpoint = breakpoint;

        this.hero.images.elements.forEach(image => {
            this.updateHeroImage(image);
        });
    }

    /**
     * Updates a single responsive image.
     *
     * @param {HTMLImageElement} image
     */
    updateHeroImage(image) {
        if (!(image instanceof HTMLImageElement)) {
            return;
        }

        const dataset = image.dataset;

        const src =
            dataset[`src${this.hero.images.breakpoint}`] ||
            dataset.src ||
            image.currentSrc ||
            image.src;

        const srcset =
            dataset[`srcset${this.hero.images.breakpoint}`] ||
            dataset.srcset;

        const sizes =
            dataset[`sizes${this.hero.images.breakpoint}`] ||
            dataset.sizes;

        if (src) {
            image.src = src;
        }

        if (srcset) {
            image.srcset = srcset;
        }

        if (sizes) {
            image.sizes = sizes;
        }

        image.decoding = 'async';

        image.loading =
            image.dataset.priority === 'high'
                ? 'eager'
                : 'lazy';

        image.fetchPriority =
            image.dataset.priority === 'high'
                ? 'high'
                : 'auto';

        this.hero.images.loadedImages.add(image);
    }

    /**
     * Returns the active responsive breakpoint.
     *
     * @returns {string}
     */
    getHeroImageBreakpoint() {
        const width = window.innerWidth;

        if (width >= 1600) {
            return 'Xl';
        }

        if (width >= 1200) {
            return 'Lg';
        }

        if (width >= 992) {
            return 'Md';
        }

        if (width >= 768) {
            return 'Sm';
        }

        return 'Xs';
    }

    /**
     * Returns current device pixel ratio.
     *
     * @returns {number}
     */
    getHeroPixelRatio() {
        return this.hero.images?.pixelRatio || 1;
    }

    /**
     * Preloads critical images.
     */
    preloadHeroCriticalImages() {
        if (!this.hero.config.preloadImages) {
            return;
        }

        this.hero.images.elements.forEach(image => {

            if (image.dataset.priority !== 'high') {
                return;
            }

            this.preloadHeroImage(image);

        });
    }

    /**
     * Preloads an image.
     *
     * @param {HTMLImageElement} image
     */
    preloadHeroImage(image) {
        const src =
            image.currentSrc ||
            image.dataset.src ||
            image.src;

        if (
            !src ||
            this.hero.images.preloadQueue.has(src)
        ) {
            return;
        }

        const preload = new Image();

        preload.decoding = 'async';

        preload.src = src;

        this.hero.images.preloadQueue.add(src);
    }

    /**
     * Refreshes responsive images.
     */
    refreshHeroResponsiveImages() {
        const breakpoint =
            this.getHeroImageBreakpoint();

        if (
            breakpoint ===
            this.hero.images.breakpoint
        ) {
            return;
        }

        this.updateHeroResponsiveImages();

        this.emit(
            'tefco:hero:images:refresh',
            {
                breakpoint
            }
        );
    }

    /**
     * Returns all Hero images.
     *
     * @returns {HTMLImageElement[]}
     */
    getHeroImages() {
        return this.hero.images?.elements ?? [];
    }

    /**
     * Returns image diagnostics.
     *
     * @returns {Object}
     */
    getHeroImageState() {
        return {
            initialized:
                this.hero.images?.initialized ?? false,
            breakpoint:
                this.hero.images?.breakpoint ?? null,
            pixelRatio:
                this.getHeroPixelRatio(),
            totalImages:
                this.hero.images?.elements?.length ?? 0,
            preloaded:
                this.hero.images?.preloadQueue?.size ?? 0
        };
    }

    /**
     * Returns whether responsive images are ready.
     *
     * @returns {boolean}
     */
    isHeroResponsiveImagesReady() {
        return !!(
            this.hero.images &&
            this.hero.images.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2C.2
     * Lazy Images
     * ------------------------------------------------------------------------
     * Enterprise Lazy Image Loader
     * Intersection Observer
     * Progressive Loading
     * Blur-Up Effect
     * Retry Handling
     * Image Lifecycle
     * ========================================================================
     */

    /**
     * Initializes lazy image loading.
     */
    initializeHeroLazyImages() {
        this.hero.lazyImages = {
            initialized: false,
            observer: null,
            images: [],
            loaded: new WeakSet(),
            failed: new WeakMap(),
            loading: new WeakSet()
        };

        this.collectHeroLazyImages();

        if (!this.hero.lazyImages.images.length) {
            return;
        }

        this.createHeroLazyImageObserver();

        this.observeHeroLazyImages();

        this.hero.lazyImages.initialized = true;

        this.emit('tefco:hero:lazy-images:initialized');
    }

    /**
     * Collects lazy-loadable images.
     */
    collectHeroLazyImages() {
        this.hero.lazyImages.images = Array.from(
            this.hero.dom.section?.querySelectorAll(
                'img[data-src], img[data-srcset], img[loading="lazy"]'
            ) ?? []
        );
    }

    /**
     * Creates the lazy image observer.
     */
    createHeroLazyImageObserver() {
        this.hero.lazyImages.observer =
            this.getOrCreateObserver({
                type: 'intersection',
                options: {
                    root: null,
                    rootMargin: '150px',
                    threshold: 0.01
                },
                callback: entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadHeroLazyImage(entry.target);

                    });

                }
            });
    }

    /**
     * Observes every lazy image.
     */
    observeHeroLazyImages() {
        const observer =
            this.hero.lazyImages.observer;

        if (!observer) {
            return;
        }

        this.hero.lazyImages.images.forEach(image => {
            observer.observe(image);
        });
    }

    /**
     * Loads a lazy image.
     *
     * @param {HTMLImageElement} image
     */
    loadHeroLazyImage(image) {
        if (
            !(image instanceof HTMLImageElement) ||
            this.hero.lazyImages.loaded.has(image) ||
            this.hero.lazyImages.loading.has(image)
        ) {
            return;
        }

        this.hero.lazyImages.loading.add(image);

        this.attachHeroImageEvents(image);

        if (image.dataset.src) {
            image.src = image.dataset.src;
        }

        if (image.dataset.srcset) {
            image.srcset = image.dataset.srcset;
        }

        if (image.dataset.sizes) {
            image.sizes = image.dataset.sizes;
        }

        image.removeAttribute('loading');

        this.hero.lazyImages.observer?.unobserve(image);
    }

    /**
     * Attaches image lifecycle events.
     *
     * @param {HTMLImageElement} image
     */
    attachHeroImageEvents(image) {
        const onLoad = () => {

            image.classList.add('is-loaded');

            image.classList.remove('is-loading');

            image.classList.remove('is-error');

            this.hero.lazyImages.loading.delete(image);

            this.hero.lazyImages.loaded.add(image);

            this.emit('tefco:hero:image:loaded', {
                image
            });

            image.removeEventListener(
                'load',
                onLoad
            );

            image.removeEventListener(
                'error',
                onError
            );
        };

        const onError = () => {

            this.hero.lazyImages.loading.delete(image);

            image.classList.add('is-error');

            this.retryHeroLazyImage(image);

            image.removeEventListener(
                'load',
                onLoad
            );

            image.removeEventListener(
                'error',
                onError
            );
        };

        image.classList.add('is-loading');

        image.addEventListener(
            'load',
            onLoad,
            { once: true }
        );

        image.addEventListener(
            'error',
            onError,
            { once: true }
        );
    }

    /**
     * Retries a failed image load.
     *
     * @param {HTMLImageElement} image
     */
    retryHeroLazyImage(image) {
        const retries =
            this.hero.lazyImages.failed.get(image) || 0;

        if (retries >= 2) {

            this.emit(
                'tefco:hero:image:failed',
                {
                    image
                }
            );

            return;
        }

        this.hero.lazyImages.failed.set(
            image,
            retries + 1
        );

        this.setTimeout(() => {

            image.removeAttribute('src');

            image.removeAttribute('srcset');

            this.loadHeroLazyImage(image);

        }, 1000 * (retries + 1));
    }

    /**
     * Forces all lazy images to load.
     */
    loadAllHeroImages() {
        this.hero.lazyImages.images.forEach(image => {
            this.loadHeroLazyImage(image);
        });
    }

    /**
     * Returns loaded image count.
     *
     * @returns {number}
     */
    getLoadedHeroImageCount() {
        let loaded = 0;

        this.hero.lazyImages.images.forEach(image => {

            if (
                this.hero.lazyImages.loaded.has(image)
            ) {
                loaded++;
            }

        });

        return loaded;
    }

    /**
     * Returns lazy image diagnostics.
     *
     * @returns {Object}
     */
    getHeroLazyImageState() {
        return {
            initialized:
                this.hero.lazyImages?.initialized ?? false,

            total:
                this.hero.lazyImages?.images?.length ?? 0,

            loaded:
                this.getLoadedHeroImageCount(),

            pending:
                (this.hero.lazyImages?.images?.length ?? 0) -
                this.getLoadedHeroImageCount(),

            observer:
                !!this.hero.lazyImages?.observer
        };
    }

    /**
     * Returns whether lazy images are ready.
     *
     * @returns {boolean}
     */
    isHeroLazyImagesReady() {
        return !!(
            this.hero.lazyImages &&
            this.hero.lazyImages.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2C.3
     * Background Images
     * ------------------------------------------------------------------------
     * Enterprise Background Media Manager
     * CSS Background Images
     * Responsive Backgrounds
     * Lazy Background Loading
     * Parallax Ready
     * Performance Optimized
     * ========================================================================
     */

    /**
     * Initializes Hero background images.
     */
    initializeHeroBackgroundImages() {
        this.hero.backgrounds = {
            initialized: false,
            elements: [],
            loaded: new WeakSet(),
            observer: null
        };

        this.collectHeroBackgroundElements();

        if (!this.hero.backgrounds.elements.length) {
            return;
        }

        this.createHeroBackgroundObserver();

        this.observeHeroBackgrounds();

        this.hero.backgrounds.initialized = true;

        this.emit('tefco:hero:backgrounds:initialized');
    }

    /**
     * Collects background elements.
     */
    collectHeroBackgroundElements() {
        this.hero.backgrounds.elements = Array.from(
            this.hero.dom.section?.querySelectorAll(
                '[data-bg-src], [data-bg-xs]'
            ) ?? []
        );
    }

    /**
     * Creates the background observer.
     */
    createHeroBackgroundObserver() {
        this.hero.backgrounds.observer =
            this.getOrCreateObserver({
                type: 'intersection',
                options: {
                    root: null,
                    rootMargin: '200px',
                    threshold: 0.01
                },
                callback: entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadHeroBackground(
                            entry.target
                        );

                    });

                }
            });
    }

    /**
     * Starts observing background elements.
     */
    observeHeroBackgrounds() {
        const observer =
            this.hero.backgrounds.observer;

        if (!observer) {
            return;
        }

        this.hero.backgrounds.elements.forEach(element => {
            observer.observe(element);
        });
    }

    /**
     * Loads a background image.
     *
     * @param {HTMLElement} element
     */
    loadHeroBackground(element) {
        if (
            !element ||
            this.hero.backgrounds.loaded.has(element)
        ) {
            return;
        }

        const image =
            this.getResponsiveBackgroundSource(element);

        if (!image) {
            return;
        }

        element.classList.add(
            'is-background-loading'
        );

        const preload = new Image();

        preload.decoding = 'async';

        preload.onload = () => {

            element.style.backgroundImage =
                `url("${image}")`;

            element.classList.remove(
                'is-background-loading'
            );

            element.classList.add(
                'is-background-loaded'
            );

            this.hero.backgrounds.loaded.add(
                element
            );

            this.hero.backgrounds.observer?.unobserve(
                element
            );

            this.emit(
                'tefco:hero:background:loaded',
                {
                    element,
                    image
                }
            );
        };

        preload.onerror = () => {

            element.classList.remove(
                'is-background-loading'
            );

            element.classList.add(
                'is-background-error'
            );

            this.emit(
                'tefco:hero:background:error',
                {
                    element,
                    image
                }
            );
        };

        preload.src = image;
    }

    /**
     * Returns the appropriate background image.
     *
     * Supported data attributes:
     *
     * data-bg-xs
     * data-bg-sm
     * data-bg-md
     * data-bg-lg
     * data-bg-xl
     * data-bg-src
     *
     * @param {HTMLElement} element
     * @returns {string}
     */
    getResponsiveBackgroundSource(element) {
        const breakpoint =
            this.getHeroImageBreakpoint();

        const dataset = element.dataset;

        switch (breakpoint) {

            case 'Xl':
                return (
                    dataset.bgXl ||
                    dataset.bgLg ||
                    dataset.bgMd ||
                    dataset.bgSm ||
                    dataset.bgXs ||
                    dataset.bgSrc ||
                    ''
                );

            case 'Lg':
                return (
                    dataset.bgLg ||
                    dataset.bgMd ||
                    dataset.bgSm ||
                    dataset.bgXs ||
                    dataset.bgSrc ||
                    ''
                );

            case 'Md':
                return (
                    dataset.bgMd ||
                    dataset.bgSm ||
                    dataset.bgXs ||
                    dataset.bgSrc ||
                    ''
                );

            case 'Sm':
                return (
                    dataset.bgSm ||
                    dataset.bgXs ||
                    dataset.bgSrc ||
                    ''
                );

            default:
                return (
                    dataset.bgXs ||
                    dataset.bgSrc ||
                    ''
                );

        }
    }

    /**
     * Refreshes responsive backgrounds.
     */
    refreshHeroBackgroundImages() {
        this.hero.backgrounds.elements.forEach(element => {

            this.hero.backgrounds.loaded.delete(
                element
            );

            this.loadHeroBackground(
                element
            );

        });

        this.emit(
            'tefco:hero:backgrounds:refresh'
        );
    }

    /**
     * Forces all backgrounds to load.
     */
    loadAllHeroBackgrounds() {
        this.hero.backgrounds.elements.forEach(element => {
            this.loadHeroBackground(element);
        });
    }

    /**
     * Returns whether a background has loaded.
     *
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isHeroBackgroundLoaded(element) {
        return this.hero.backgrounds.loaded.has(
            element
        );
    }

    /**
     * Returns loaded background count.
     *
     * @returns {number}
     */
    getLoadedHeroBackgroundCount() {
        let loaded = 0;

        this.hero.backgrounds.elements.forEach(element => {

            if (
                this.hero.backgrounds.loaded.has(
                    element
                )
            ) {
                loaded++;
            }

        });

        return loaded;
    }

    /**
     * Returns Hero background diagnostics.
     *
     * @returns {Object}
     */
    getHeroBackgroundState() {
        return {
            initialized:
                this.hero.backgrounds?.initialized ?? false,

            total:
                this.hero.backgrounds?.elements?.length ?? 0,

            loaded:
                this.getLoadedHeroBackgroundCount(),

            pending:
                (this.hero.backgrounds?.elements?.length ?? 0) -
                this.getLoadedHeroBackgroundCount(),

            observer:
                !!this.hero.backgrounds?.observer
        };
    }

    /**
     * Returns whether background management is ready.
     *
     * @returns {boolean}
     */
    isHeroBackgroundReady() {
        return !!(
            this.hero.backgrounds &&
            this.hero.backgrounds.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2C.4
     * Hero Video Support
     * ------------------------------------------------------------------------
     * Enterprise Video Infrastructure
     * HTML5 Video
     * YouTube / Vimeo Ready
     * Lazy Video Loading
     * Autoplay Management
     * Visibility Control
     * Accessibility
     * ========================================================================
     */

    /**
     * Initializes Hero video support.
     */
    initializeHeroVideos() {
        this.hero.video = {
            initialized: false,
            players: new WeakMap(),
            elements: [],
            active: null,
            observer: null,
            playing: false,
            muted: true
        };

        this.collectHeroVideos();

        if (!this.hero.video.elements.length) {
            return;
        }

        this.createHeroVideoObserver();

        this.observeHeroVideos();

        this.bindHeroVideoEvents();

        this.hero.video.initialized = true;

        this.emit('tefco:hero:video:initialized');
    }

    /**
     * Collects Hero videos.
     */
    collectHeroVideos() {
        this.hero.video.elements = Array.from(
            this.hero.dom.section?.querySelectorAll(
                'video,[data-video],[data-video-src],[data-youtube],[data-vimeo]'
            ) ?? []
        );
    }

    /**
     * Creates the video observer.
     */
    createHeroVideoObserver() {
        this.hero.video.observer =
            this.getOrCreateObserver({
                type: 'intersection',
                options: {
                    root: null,
                    rootMargin: '200px',
                    threshold: 0.25
                },
                callback: entries => {

                    entries.forEach(entry => {

                        if (entry.isIntersecting) {
                            this.activateHeroVideo(
                                entry.target
                            );
                        } else {
                            this.pauseHeroVideo(
                                entry.target
                            );
                        }

                    });

                }
            });
    }

    /**
     * Starts observing Hero videos.
     */
    observeHeroVideos() {
        if (!this.hero.video.observer) {
            return;
        }

        this.hero.video.elements.forEach(video => {
            this.hero.video.observer.observe(video);
        });
    }

    /**
     * Activates a Hero video.
     *
     * @param {HTMLElement} element
     */
    activateHeroVideo(element) {
        if (!element) {
            return;
        }

        if (
            element.tagName === 'VIDEO'
        ) {

            this.loadHeroVideo(element);

            this.playHeroVideo(element);

            return;
        }

        this.initializeEmbeddedHeroVideo(
            element
        );
    }

    /**
     * Loads an HTML5 video.
     *
     * @param {HTMLVideoElement} video
     */
    loadHeroVideo(video) {
        if (!(video instanceof HTMLVideoElement)) {
            return;
        }

        if (
            video.dataset.src &&
            !video.src
        ) {
            video.src = video.dataset.src;
        }

        video.querySelectorAll('source').forEach(source => {

            if (
                source.dataset.src &&
                !source.src
            ) {
                source.src = source.dataset.src;
            }

        });

        video.load();
    }

    /**
     * Plays an HTML5 video.
     *
     * @param {HTMLVideoElement} video
     */
    playHeroVideo(video) {
        if (!(video instanceof HTMLVideoElement)) {
            return;
        }

        video.muted = true;

        const promise = video.play();

        if (
            promise &&
            typeof promise.catch === 'function'
        ) {
            promise.catch(() => {});
        }

        this.hero.video.active = video;
        this.hero.video.playing = true;

        this.emit(
            'tefco:hero:video:play',
            { video }
        );
    }

    /**
     * Pauses a Hero video.
     *
     * @param {HTMLElement} element
     */
    pauseHeroVideo(element) {
        if (
            !(element instanceof HTMLVideoElement)
        ) {
            return;
        }

        element.pause();

        this.hero.video.playing = false;

        if (this.hero.video.active === element) {
            this.hero.video.active = null;
        }

        this.emit(
            'tefco:hero:video:pause',
            { video: element }
        );
    }

    /**
     * Initializes embedded videos.
     *
     * Placeholder for YouTube / Vimeo APIs.
     *
     * @param {HTMLElement} element
     */
    initializeEmbeddedHeroVideo(element) {
        if (
            this.hero.video.players.has(element)
        ) {
            return;
        }

        const player = {
            type:
                element.dataset.youtube
                    ? 'youtube'
                    : element.dataset.vimeo
                        ? 'vimeo'
                        : 'custom',
            initialized: true,
            element
        };

        this.hero.video.players.set(
            element,
            player
        );

        this.emit(
            'tefco:hero:embedded-video:init',
            player
        );
    }

    /**
     * Binds Hero video events.
     */
    bindHeroVideoEvents() {
        this.hero.video.elements.forEach(video => {

            if (
                !(video instanceof HTMLVideoElement)
            ) {
                return;
            }

            this.on(
                video,
                'play',
                () => {

                    this.hero.video.playing = true;

                }
            );

            this.on(
                video,
                'pause',
                () => {

                    this.hero.video.playing = false;

                }
            );

            this.on(
                video,
                'ended',
                () => {

                    this.hero.video.playing = false;

                    if (
                        this.hero.config.autoplay
                    ) {
                        this.nextHeroSlide();
                    }

                }
            );

        });
    }

    /**
     * Mutes every Hero video.
     */
    muteHeroVideos() {
        this.hero.video.elements.forEach(video => {

            if (
                video instanceof HTMLVideoElement
            ) {
                video.muted = true;
            }

        });

        this.hero.video.muted = true;
    }

    /**
     * Unmutes every Hero video.
     */
    unmuteHeroVideos() {
        this.hero.video.elements.forEach(video => {

            if (
                video instanceof HTMLVideoElement
            ) {
                video.muted = false;
            }

        });

        this.hero.video.muted = false;
    }

    /**
     * Stops every Hero video.
     */
    stopHeroVideos() {
        this.hero.video.elements.forEach(video => {

            if (
                video instanceof HTMLVideoElement
            ) {
                video.pause();
                video.currentTime = 0;
            }

        });

        this.hero.video.playing = false;
        this.hero.video.active = null;
    }

    /**
     * Returns the active video.
     *
     * @returns {HTMLVideoElement|null}
     */
    getActiveHeroVideo() {
        return this.hero.video.active;
    }

    /**
     * Returns whether any Hero video is playing.
     *
     * @returns {boolean}
     */
    isHeroVideoPlaying() {
        return this.hero.video.playing;
    }

    /**
     * Returns video diagnostics.
     *
     * @returns {Object}
     */
    getHeroVideoState() {
        return {
            initialized:
                this.hero.video?.initialized ?? false,
            totalVideos:
                this.hero.video?.elements?.length ?? 0,
            playing:
                this.hero.video?.playing ?? false,
            muted:
                this.hero.video?.muted ?? true,
            active:
                !!this.hero.video?.active,
            observer:
                !!this.hero.video?.observer
        };
    }

    /**
     * Returns whether the Hero video system is ready.
     *
     * @returns {boolean}
     */
    isHeroVideoReady() {
        return !!(
            this.hero.video &&
            this.hero.video.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2D.1
     * Content Animation
     * ------------------------------------------------------------------------
     * Enterprise Content Animation Engine
     * Timeline Management
     * Staggered Reveal
     * Visibility Driven
     * Reduced Motion Support
     * ========================================================================
     */

    /**
     * Initializes Hero content animations.
     */
    initializeHeroContentAnimations() {
        this.hero.animations = {
            initialized: false,
            enabled: !window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches,
            running: false,
            duration: 600,
            stagger: 120,
            activeSlide: 0,
            animatedElements: new WeakSet()
        };

        if (!this.hero.animations.enabled) {
            this.hero.animations.initialized = true;
            return;
        }

        this.prepareHeroAnimationElements();

        this.bindHeroAnimationEvents();

        this.hero.animations.initialized = true;

        this.emit(
            'tefco:hero:animations:initialized'
        );
    }

    /**
     * Prepares animation elements.
     */
    prepareHeroAnimationElements() {
        this.hero.slides.forEach(slide => {

            const elements = this.getHeroAnimationElements(slide);

            elements.forEach((element, index) => {

                element.dataset.animationIndex = index;

                element.classList.add(
                    'tefco-animate'
                );

                element.classList.remove(
                    'is-visible'
                );

            });

        });
    }

    /**
     * Returns animatable elements.
     *
     * @param {HTMLElement} slide
     * @returns {HTMLElement[]}
     */
    getHeroAnimationElements(slide) {
        if (!slide) {
            return [];
        }

        return Array.from(
            slide.querySelectorAll(
                [
                    '.tefco-hero__eyebrow',
                    '.tefco-hero__heading',
                    '.tefco-hero__text',
                    '.tefco-hero__title',
                    '.tefco-hero__subtitle',
                    '.tefco-hero__description',
                    '.tefco-hero__actions',
                    '.tefco-hero__stats',
                    '[data-animate]'
                ].join(',')
            )
        );
    }

    /**
     * Animates slide content.
     *
     * @param {number} index
     */
    animateHeroContent(index) {
        if (
            !this.hero.animations.enabled
        ) {
            return;
        }

        const slide =
            this.hero.slides[index];

        if (!slide) {
            return;
        }

        this.resetHeroContentAnimations();

        const elements =
            this.getHeroAnimationElements(slide);

        this.hero.animations.running = true;
        this.hero.animations.activeSlide = index;

        elements.forEach((element, position) => {

            this.setTimeout(() => {

                element.classList.add(
                    'is-visible'
                );

                this.hero.animations.animatedElements.add(
                    element
                );

                this.emit(
                    'tefco:hero:content:element',
                    {
                        slide: index,
                        element,
                        position
                    }
                );

            }, position * this.hero.animations.stagger);

        });

        this.setTimeout(() => {

            this.hero.animations.running = false;

            this.emit(
                'tefco:hero:content:complete',
                {
                    slide: index
                }
            );

        }, elements.length * this.hero.animations.stagger +
            this.hero.animations.duration);
    }

    /**
     * Resets every content animation.
     */
    resetHeroContentAnimations() {
        this.hero.slides.forEach(slide => {

            this.getHeroAnimationElements(slide)
                .forEach(element => {

                    element.classList.remove(
                        'is-visible'
                    );

                });

        });
    }

    /**
     * Replays the current slide animation.
     */
    replayHeroContentAnimation() {
        this.animateHeroContent(
            this.hero.slider.current
        );
    }

    /**
     * Stops all running animations.
     */
    stopHeroContentAnimations() {
        this.hero.animations.running = false;

        this.resetHeroContentAnimations();

        this.emit(
            'tefco:hero:content:stopped'
        );
    }

    /**
     * Binds animation events.
     */
    bindHeroAnimationEvents() {
        this.on(
            document,
            'tefco:hero:slide:activated',
            event => {

                const slide =
                    event.detail?.current ?? 0;

                this.animateHeroContent(
                    slide
                );

            }
        );
    }

    /**
     * Enables content animations.
     */
    enableHeroContentAnimations() {
        this.hero.animations.enabled = true;
    }

    /**
     * Disables content animations.
     */
    disableHeroContentAnimations() {
        this.hero.animations.enabled = false;

        this.stopHeroContentAnimations();
    }

    /**
     * Returns whether animations are running.
     *
     * @returns {boolean}
     */
    isHeroContentAnimating() {
        return !!this.hero.animations.running;
    }

    /**
     * Returns animation state.
     *
     * @returns {Object}
     */
    getHeroContentAnimationState() {
        return {
            initialized:
                this.hero.animations?.initialized ?? false,
            enabled:
                this.hero.animations?.enabled ?? false,
            running:
                this.hero.animations?.running ?? false,
            activeSlide:
                this.hero.animations?.activeSlide ?? 0,
            duration:
                this.hero.animations?.duration ?? 0,
            stagger:
                this.hero.animations?.stagger ?? 0
        };
    }

    /**
     * Returns whether the animation engine is ready.
     *
     * @returns {boolean}
     */
    isHeroContentAnimationReady() {
        return !!(
            this.hero.animations &&
            this.hero.animations.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2D.2
     * CTA Buttons
     * ------------------------------------------------------------------------
     * Enterprise CTA Manager
     * Primary / Secondary Actions
     * Analytics Hooks
     * Accessibility
     * Loading State
     * ========================================================================
     */

    /**
     * Initializes Hero CTA buttons.
     */
    initializeHeroCTAButtons() {
        this.hero.cta = {
            initialized: false,
            buttons: [],
            activeButton: null,
            loadingButtons: new WeakSet(),
            disabledButtons: new WeakSet(),
            clickCount: 0
        };

        this.collectHeroCTAButtons();

        if (!this.hero.cta.buttons.length) {
            return;
        }

        this.bindHeroCTAEvents();

        this.hero.cta.initialized = true;

        this.emit('tefco:hero:cta:initialized');
    }

    /**
     * Collects Hero CTA buttons.
     */
    collectHeroCTAButtons() {
        this.hero.cta.buttons = Array.from(
            this.hero.dom.section?.querySelectorAll(
                '.tefco-hero__actions .tefco-btn, .tefco-hero__cta'
            ) ?? []
        );
    }

    /**
     * Binds CTA events.
     */
    bindHeroCTAEvents() {
        this.hero.cta.buttons.forEach(button => {

            this.on(
                button,
                'click',
                event => {

                    if (
                        this.isHeroCTAButtonDisabled(button)
                    ) {
                        event.preventDefault();
                        return;
                    }

                    this.hero.cta.activeButton = button;
                    this.hero.cta.clickCount++;

                    this.trackHeroCTA(button);

                    this.emit(
                        'tefco:hero:cta:click',
                        {
                            button,
                            slide: this.getActiveHeroSlide()
                        }
                    );

                }
            );

            this.on(
                button,
                'keydown',
                event => {

                    if (
                        event.key !== 'Enter' &&
                        event.key !== ' '
                    ) {
                        return;
                    }

                    button.click();

                }
            );

            this.on(
                button,
                'focus',
                () => {

                    this.emit(
                        'tefco:hero:cta:focus',
                        { button }
                    );

                }
            );

            this.on(
                button,
                'blur',
                () => {

                    this.emit(
                        'tefco:hero:cta:blur',
                        { button }
                    );

                }
            );

        });
    }

    /**
     * Tracks CTA interaction.
     *
     * Override for GA4, GTM, Meta Pixel, etc.
     *
     * @param {HTMLElement} button
     */
    trackHeroCTA(button) {
        this.emit(
            'tefco:analytics:hero:cta',
            {
                slide: this.getActiveHeroSlide(),
                text: button.textContent.trim(),
                href: button.getAttribute('href'),
                id: button.id || null
            }
        );
    }

    /**
     * Enables loading state.
     *
     * @param {HTMLElement} button
     */
    startHeroCTALoading(button) {
        if (!button) {
            return;
        }

        this.hero.cta.loadingButtons.add(button);

        button.classList.add(
            'is-loading'
        );

        button.setAttribute(
            'aria-busy',
            'true'
        );

        button.disabled = true;
    }

    /**
     * Disables loading state.
     *
     * @param {HTMLElement} button
     */
    stopHeroCTALoading(button) {
        if (!button) {
            return;
        }

        this.hero.cta.loadingButtons.delete(
            button
        );

        button.classList.remove(
            'is-loading'
        );

        button.removeAttribute(
            'aria-busy'
        );

        if (
            !this.hero.cta.disabledButtons.has(
                button
            )
        ) {
            button.disabled = false;
        }
    }

    /**
     * Disables a CTA button.
     *
     * @param {HTMLElement} button
     */
    disableHeroCTAButton(button) {
        if (!button) {
            return;
        }

        this.hero.cta.disabledButtons.add(
            button
        );

        button.disabled = true;

        button.setAttribute(
            'aria-disabled',
            'true'
        );

        button.classList.add(
            'is-disabled'
        );
    }

    /**
     * Enables a CTA button.
     *
     * @param {HTMLElement} button
     */
    enableHeroCTAButton(button) {
        if (!button) {
            return;
        }

        this.hero.cta.disabledButtons.delete(
            button
        );

        button.disabled = false;

        button.removeAttribute(
            'aria-disabled'
        );

        button.classList.remove(
            'is-disabled'
        );
    }

    /**
     * Returns whether a button is disabled.
     *
     * @param {HTMLElement} button
     * @returns {boolean}
     */
    isHeroCTAButtonDisabled(button) {
        return this.hero.cta.disabledButtons.has(
            button
        );
    }

    /**
     * Returns all CTA buttons.
     *
     * @returns {HTMLElement[]}
     */
    getHeroCTAButtons() {
        return this.hero.cta.buttons;
    }

    /**
     * Returns the active CTA button.
     *
     * @returns {HTMLElement|null}
     */
    getActiveHeroCTAButton() {
        return this.hero.cta.activeButton;
    }

    /**
     * Returns CTA diagnostics.
     *
     * @returns {Object}
     */
    getHeroCTAState() {
        return {
            initialized:
                this.hero.cta?.initialized ?? false,
            totalButtons:
                this.hero.cta?.buttons?.length ?? 0,
            activeButton:
                !!this.hero.cta?.activeButton,
            clickCount:
                this.hero.cta?.clickCount ?? 0,
            loadingButtons:
                this.hero.cta?.loadingButtons
                    ? Array.from(this.hero.cta.buttons).filter(button =>
                        this.hero.cta.loadingButtons.has(button)
                    ).length
                    : 0,
            disabledButtons:
                this.hero.cta?.disabledButtons
                    ? Array.from(this.hero.cta.buttons).filter(button =>
                        this.hero.cta.disabledButtons.has(button)
                    ).length
                    : 0
        };
    }

    /**
     * Returns whether the CTA manager is ready.
     *
     * @returns {boolean}
     */
    isHeroCTAReady() {
        return !!(
            this.hero.cta &&
            this.hero.cta.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2D.3
     * Hero Counters
     * ------------------------------------------------------------------------
     * Enterprise Counter Animation Engine
     * Visibility Driven
     * Count Up Animation
     * Formatting Support
     * Accessibility
     * ========================================================================
     */

    /**
     * Initializes Hero counters.
     */
    initializeHeroCounters() {
        this.hero.counters = {
            initialized: false,
            elements: [],
            animated: new WeakSet(),
            observer: null,
            duration: 2000,
            easing: 'easeOutCubic'
        };

        this.collectHeroCounters();

        if (!this.hero.counters.elements.length) {
            return;
        }

        this.createHeroCounterObserver();

        this.observeHeroCounters();

        this.hero.counters.initialized = true;

        this.emit('tefco:hero:counters:initialized');
    }

    /**
     * Collects counter elements.
     */
    collectHeroCounters() {
        this.hero.counters.elements = Array.from(
            this.hero.dom.section?.querySelectorAll(
                '[data-counter]'
            ) ?? []
        );
    }

    /**
     * Creates the counter observer.
     */
    createHeroCounterObserver() {
        this.hero.counters.observer =
            this.getOrCreateObserver({
                type: 'intersection',
                options: {
                    root: null,
                    threshold: 0.35
                },
                callback: entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.startHeroCounter(
                            entry.target
                        );

                    });

                }
            });
    }

    /**
     * Starts observing counters.
     */
    observeHeroCounters() {
        if (!this.hero.counters.observer) {
            return;
        }

        this.hero.counters.elements.forEach(counter => {

            this.hero.counters.observer.observe(
                counter
            );

        });
    }

    /**
     * Starts a counter animation.
     *
     * @param {HTMLElement} element
     */
    startHeroCounter(element) {
        if (
            this.hero.counters.animated.has(
                element
            )
        ) {
            return;
        }

        const target =
            Number(element.dataset.counter);

        if (
            Number.isNaN(target)
        ) {
            return;
        }

        const duration =
            Number(element.dataset.duration) ||
            this.hero.counters.duration;

        const prefix =
            element.dataset.prefix || '';

        const suffix =
            element.dataset.suffix || '';

        const decimals =
            Number(element.dataset.decimals || 0);

        this.hero.counters.animated.add(
            element
        );

        this.hero.counters.observer?.unobserve(
            element
        );

        this.animateHeroCounter(
            element,
            target,
            duration,
            prefix,
            suffix,
            decimals
        );
    }

    /**
     * Performs counter animation.
     *
     * @param {HTMLElement} element
     * @param {number} target
     * @param {number} duration
     * @param {string} prefix
     * @param {string} suffix
     * @param {number} decimals
     */
    animateHeroCounter(
        element,
        target,
        duration,
        prefix,
        suffix,
        decimals
    ) {
        const start = performance.now();

        const update = now => {

            const progress = Math.min(
                (now - start) / duration,
                1
            );

            const eased =
                this.easeOutCubic(progress);

            const value =
                target * eased;

            element.textContent =
                prefix +
                value.toFixed(decimals) +
                suffix;

            if (progress < 1) {

                this.raf(update);

                return;

            }

            element.textContent =
                prefix +
                target.toFixed(decimals) +
                suffix;

            this.emit(
                'tefco:hero:counter:complete',
                {
                    element,
                    value: target
                }
            );

        };

        this.raf(update);
    }

    /**
     * Ease Out Cubic.
     *
     * @param {number} value
     * @returns {number}
     */
    easeOutCubic(value) {
        return 1 - Math.pow(
            1 - value,
            3
        );
    }

    /**
     * Resets every counter.
     */
    resetHeroCounters() {
        this.hero.counters.elements.forEach(counter => {

            this.hero.counters.animated.delete(
                counter
            );

            counter.textContent =
                counter.dataset.initial || '0';

            this.hero.counters.observer?.observe(
                counter
            );

        });

        this.emit(
            'tefco:hero:counters:reset'
        );
    }

    /**
     * Restarts all counters.
     */
    restartHeroCounters() {
        this.resetHeroCounters();

        this.hero.counters.elements.forEach(counter => {

            if (
                this.isVisibleOnScreen(counter)
            ) {
                this.startHeroCounter(counter);
            }

        });
    }

    /**
     * Returns whether a counter has finished.
     *
     * @param {HTMLElement} element
     * @returns {boolean}
     */
    isHeroCounterAnimated(element) {
        return this.hero.counters.animated.has(
            element
        );
    }

    /**
     * Returns the number of completed counters.
     *
     * @returns {number}
     */
    getCompletedHeroCounterCount() {
        let completed = 0;

        this.hero.counters.elements.forEach(counter => {

            if (
                this.hero.counters.animated.has(
                    counter
                )
            ) {
                completed++;
            }

        });

        return completed;
    }

    /**
     * Returns Hero counter diagnostics.
     *
     * @returns {Object}
     */
    getHeroCounterState() {
        return {
            initialized:
                this.hero.counters?.initialized ?? false,
            total:
                this.hero.counters?.elements?.length ?? 0,
            completed:
                this.getCompletedHeroCounterCount(),
            duration:
                this.hero.counters?.duration ?? 0,
            observer:
                !!this.hero.counters?.observer
        };
    }

    /**
     * Returns whether the counter engine is ready.
     *
     * @returns {boolean}
     */
    isHeroCounterReady() {
        return !!(
            this.hero.counters &&
            this.hero.counters.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2D.4
     * Scroll Indicator
     * ------------------------------------------------------------------------
     * Enterprise Scroll Indicator
     * Scroll Progress
     * Mouse Animation
     * Click Navigation
     * Visibility Management
     * Accessibility
     * ========================================================================
     */

    /**
     * Initializes the Hero scroll indicator.
     */
    initializeHeroScrollIndicator() {
        this.hero.scrollIndicator = {
            initialized: false,
            element: null,
            progress: null,
            visible: true,
            animated: false
        };

        this.cacheHeroScrollIndicator();

        if (!this.hero.scrollIndicator.element) {
            return;
        }

        this.bindHeroScrollIndicatorEvents();

        this.startHeroScrollIndicatorAnimation();

        this.updateHeroScrollIndicatorVisibility();

        this.hero.scrollIndicator.initialized = true;

        this.emit(
            'tefco:hero:scroll-indicator:initialized'
        );
    }

    /**
     * Caches scroll indicator elements.
     */
    cacheHeroScrollIndicator() {
        const indicator =
            this.hero.dom.section?.querySelector(
                '.tefco-hero__scroll-indicator'
            );

        this.hero.scrollIndicator.element = indicator;

        this.hero.scrollIndicator.progress =
            indicator?.querySelector(
                '.tefco-hero__scroll-progress'
            ) ?? null;
    }

    /**
     * Binds indicator events.
     */
    bindHeroScrollIndicatorEvents() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        this.on(
            indicator,
            'click',
            event => {

                event.preventDefault();

                this.scrollToNextSection();

            }
        );

        this.on(
            indicator,
            'keydown',
            event => {

                if (
                    event.key !== 'Enter' &&
                    event.key !== ' '
                ) {
                    return;
                }

                event.preventDefault();

                this.scrollToNextSection();

            }
        );

        this.on(
            window,
            'scroll',
            () => {

                this.updateHeroScrollIndicator();

            },
            {
                passive: true
            }
        );
    }

    /**
     * Scrolls to the next page section.
     */
    scrollToNextSection() {
        const currentSection =
            this.hero.dom.section;

        if (!currentSection) {
            return;
        }

        const nextSection =
            currentSection.nextElementSibling;

        if (!nextSection) {
            return;
        }

        nextSection.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
        });

        this.emit(
            'tefco:hero:scroll-indicator:click',
            {
                target: nextSection
            }
        );
    }

    /**
     * Updates scroll progress.
     */
    updateHeroScrollIndicator() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        const heroHeight =
            this.hero.dom.section.offsetHeight;

        const scrollTop =
            window.pageYOffset ||
            document.documentElement.scrollTop;

        const progress =
            Math.min(
                Math.max(
                    scrollTop / heroHeight,
                    0
                ),
                1
            );

        if (
            this.hero.scrollIndicator.progress
        ) {
            this.hero.scrollIndicator.progress.style.transform =
                `scaleY(${1 - progress})`;
        }

        this.updateHeroScrollIndicatorVisibility();
    }

    /**
     * Starts indicator animation.
     */
    startHeroScrollIndicatorAnimation() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        indicator.classList.add(
            'is-animated'
        );

        this.hero.scrollIndicator.animated = true;
    }

    /**
     * Stops indicator animation.
     */
    stopHeroScrollIndicatorAnimation() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        indicator.classList.remove(
            'is-animated'
        );

        this.hero.scrollIndicator.animated = false;
    }

    /**
     * Updates indicator visibility.
     */
    updateHeroScrollIndicatorVisibility() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        const visible =
            window.scrollY <
            this.hero.dom.section.offsetHeight * 0.8;

        indicator.classList.toggle(
            'is-hidden',
            !visible
        );

        indicator.setAttribute(
            'aria-hidden',
            visible ? 'false' : 'true'
        );

        this.hero.scrollIndicator.visible = visible;
    }

    /**
     * Shows the indicator.
     */
    showHeroScrollIndicator() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        indicator.classList.remove(
            'is-hidden'
        );

        indicator.setAttribute(
            'aria-hidden',
            'false'
        );

        this.hero.scrollIndicator.visible = true;
    }

    /**
     * Hides the indicator.
     */
    hideHeroScrollIndicator() {
        const indicator =
            this.hero.scrollIndicator.element;

        if (!indicator) {
            return;
        }

        indicator.classList.add(
            'is-hidden'
        );

        indicator.setAttribute(
            'aria-hidden',
            'true'
        );

        this.hero.scrollIndicator.visible = false;
    }

    /**
     * Returns whether the indicator is visible.
     *
     * @returns {boolean}
     */
    isHeroScrollIndicatorVisible() {
        return !!this.hero.scrollIndicator?.visible;
    }

    /**
     * Returns the indicator state.
     *
     * @returns {Object}
     */
    getHeroScrollIndicatorState() {
        return {
            initialized:
                this.hero.scrollIndicator?.initialized ?? false,
            visible:
                this.hero.scrollIndicator?.visible ?? false,
            animated:
                this.hero.scrollIndicator?.animated ?? false,
            hasProgress:
                !!this.hero.scrollIndicator?.progress
        };
    }

    /**
     * Returns whether the scroll indicator is ready.
     *
     * @returns {boolean}
     */
    isHeroScrollIndicatorReady() {
        return !!(
            this.hero.scrollIndicator &&
            this.hero.scrollIndicator.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2E.1
     * Resize Events
     * ------------------------------------------------------------------------
     * Enterprise Resize Manager
     * Responsive Breakpoints
     * Layout Synchronization
     * Media Refresh
     * Performance Optimized
     * ========================================================================
     */

    /**
     * Initializes Hero resize management.
     */
    initializeHeroResizeEvents() {
        this.hero.resize = {
            initialized: false,
            width: window.innerWidth,
            height: window.innerHeight,
            breakpoint: this.getHeroImageBreakpoint(),
            orientation: this.getHeroOrientation(),
            resizeCount: 0,
            resizeObserver: null
        };

        this.bindHeroResizeEvents();

        this.hero.resize.initialized = true;

        this.emit(
            'tefco:hero:resize:initialized'
        );
    }

    /**
     * Binds resize related events.
     */
    bindHeroResizeEvents() {
        this.on(
            window,
            'resize',
            this.debounce(() => {

                this.handleHeroResize();

            }, 150),
            {
                passive: true
            }
        );

        this.on(
            window,
            'orientationchange',
            () => {

                this.handleHeroOrientationChange();

            }
        );
    }

    /**
     * Handles window resize.
     */
    handleHeroResize() {
        const previousWidth =
            this.hero.resize.width;

        const previousHeight =
            this.hero.resize.height;

        const previousBreakpoint =
            this.hero.resize.breakpoint;

        this.hero.resize.width =
            window.innerWidth;

        this.hero.resize.height =
            window.innerHeight;

        this.hero.resize.breakpoint =
            this.getHeroImageBreakpoint();

        this.hero.resize.orientation =
            this.getHeroOrientation();

        this.hero.resize.resizeCount++;

        if (
            previousBreakpoint !==
            this.hero.resize.breakpoint
        ) {
            this.handleHeroBreakpointChange(
                previousBreakpoint,
                this.hero.resize.breakpoint
            );
        }

        this.updateHeroResponsiveImages?.();

        this.refreshHeroBackgroundImages?.();

        this.updateHeroLayout?.();

        this.emit(
            'tefco:hero:resize',
            {
                previousWidth,
                previousHeight,
                width: this.hero.resize.width,
                height: this.hero.resize.height,
                breakpoint:
                    this.hero.resize.breakpoint
            }
        );
    }

    /**
     * Handles breakpoint changes.
     *
     * @param {string} previous
     * @param {string} current
     */
    handleHeroBreakpointChange(
        previous,
        current
    ) {
        this.emit(
            'tefco:hero:breakpoint:change',
            {
                previous,
                current
            }
        );

        this.refreshHeroBackgroundImages?.();

        this.updateHeroResponsiveImages?.();

        this.refreshHeroResponsiveImages?.();

        this.updateHeroPagination?.();

        this.updateHeroLayout?.();
    }

    /**
     * Handles orientation changes.
     */
    handleHeroOrientationChange() {
        this.hero.resize.orientation =
            this.getHeroOrientation();

        this.handleHeroResize();

        this.emit(
            'tefco:hero:orientation:change',
            {
                orientation:
                    this.hero.resize.orientation
            }
        );
    }

    /**
     * Updates Hero layout.
     *
     * Override if additional layout logic
     * is required by future modules.
     */
    updateHeroLayout() {
        const section =
            this.hero.dom.section;

        if (!section) {
            return;
        }

        section.style.setProperty(
            '--hero-height',
            `${window.innerHeight}px`
        );

        section.style.setProperty(
            '--hero-width',
            `${window.innerWidth}px`
        );
    }

    /**
     * Returns current orientation.
     *
     * @returns {string}
     */
    getHeroOrientation() {
        return window.innerWidth >
            window.innerHeight
            ? 'landscape'
            : 'portrait';
    }

    /**
     * Forces a resize refresh.
     */
    refreshHeroResize() {
        this.handleHeroResize();
    }

    /**
     * Returns current viewport size.
     *
     * @returns {Object}
     */
    getHeroViewportSize() {
        return {
            width: this.hero.resize.width,
            height: this.hero.resize.height
        };
    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getHeroBreakpoint() {
        return this.hero.resize.breakpoint;
    }

    /**
     * Returns current orientation.
     *
     * @returns {string}
     */
    getHeroViewportOrientation() {
        return this.hero.resize.orientation;
    }

    /**
     * Returns resize diagnostics.
     *
     * @returns {Object}
     */
    getHeroResizeState() {
        return {
            initialized:
                this.hero.resize?.initialized ?? false,

            width:
                this.hero.resize?.width ?? 0,

            height:
                this.hero.resize?.height ?? 0,

            breakpoint:
                this.hero.resize?.breakpoint ?? null,

            orientation:
                this.hero.resize?.orientation ?? null,

            resizeCount:
                this.hero.resize?.resizeCount ?? 0
        };
    }

    /**
     * Returns whether the resize manager is ready.
     *
     * @returns {boolean}
     */
    isHeroResizeReady() {
        return !!(
            this.hero.resize &&
            this.hero.resize.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2E.2
     * Visibility Events
     * ------------------------------------------------------------------------
     * Enterprise Visibility Manager
     * Page Visibility API
     * Viewport Detection
     * Focus Management
     * Autoplay Synchronization
     * Performance Optimized
     * ========================================================================
     */

    /**
     * Initializes Hero visibility management.
     */
    initializeHeroVisibilityEvents() {
        this.hero.visibility = {
            initialized: false,
            pageVisible: !document.hidden,
            heroVisible: false,
            windowFocused: document.hasFocus(),
            observer: null,
            visibilityChanges: 0
        };

        this.createHeroVisibilityObserver();

        this.observeHeroVisibility();

        this.bindHeroVisibilityEvents();

        this.hero.visibility.initialized = true;

        this.emit(
            'tefco:hero:visibility:initialized'
        );
    }

    /**
     * Creates Hero visibility observer.
     */
    createHeroVisibilityObserver() {
        this.hero.visibility.observer =
            this.getOrCreateObserver({
                type: 'intersection',
                options: {
                    root: null,
                    threshold: 0.25
                },
                callback: entries => {

                    entries.forEach(entry => {

                        this.handleHeroViewportVisibility(
                            entry
                        );

                    });

                }
            });
    }

    /**
     * Starts observing Hero section.
     */
    observeHeroVisibility() {
        if (
            !this.hero.visibility.observer ||
            !this.hero.dom.section
        ) {
            return;
        }

        this.hero.visibility.observer.observe(
            this.hero.dom.section
        );
    }

    /**
     * Binds visibility related events.
     */
    bindHeroVisibilityEvents() {

        this.on(
            document,
            'visibilitychange',
            () => {

                this.handleHeroPageVisibility();

            }
        );

        this.on(
            window,
            'focus',
            () => {

                this.handleHeroWindowFocus();

            }
        );

        this.on(
            window,
            'blur',
            () => {

                this.handleHeroWindowBlur();

            }
        );
    }

    /**
     * Handles page visibility changes.
     */
    handleHeroPageVisibility() {
        this.hero.visibility.pageVisible =
            !document.hidden;

        this.hero.visibility.visibilityChanges++;

        if (
            this.hero.visibility.pageVisible
        ) {

            this.resumeHeroVisibilityActions();

        } else {

            this.pauseHeroVisibilityActions();

        }

        this.emit(
            'tefco:hero:page:visibility',
            {
                visible:
                    this.hero.visibility.pageVisible
            }
        );
    }

    /**
     * Handles Hero viewport visibility.
     *
     * @param {IntersectionObserverEntry} entry
     */
    handleHeroViewportVisibility(entry) {
        const visible =
            entry.isIntersecting;

        if (
            visible ===
            this.hero.visibility.heroVisible
        ) {
            return;
        }

        this.hero.visibility.heroVisible =
            visible;

        if (visible) {

            this.resumeHeroVisibilityActions();

        } else {

            this.pauseHeroVisibilityActions();

        }

        this.emit(
            'tefco:hero:viewport:visibility',
            {
                visible,
                ratio:
                    entry.intersectionRatio
            }
        );
    }

    /**
     * Handles window focus.
     */
    handleHeroWindowFocus() {
        this.hero.visibility.windowFocused = true;

        this.resumeHeroVisibilityActions();

        this.emit(
            'tefco:hero:window:focus'
        );
    }

    /**
     * Handles window blur.
     */
    handleHeroWindowBlur() {
        this.hero.visibility.windowFocused = false;

        this.pauseHeroVisibilityActions();

        this.emit(
            'tefco:hero:window:blur'
        );
    }

    /**
     * Pauses visibility dependent features.
     */
    pauseHeroVisibilityActions() {

        this.pauseHeroAutoplay?.();

        this.stopHeroVideos?.();

        this.stopHeroContentAnimations?.();

        this.emit(
            'tefco:hero:visibility:paused'
        );
    }

    /**
     * Resumes visibility dependent features.
     */
    resumeHeroVisibilityActions() {

        if (
            this.hero.visibility.pageVisible &&
            this.hero.visibility.heroVisible &&
            this.hero.visibility.windowFocused
        ) {

            this.resumeHeroAutoplay?.();

            const activeSlide =
                this.hero.slider?.current ?? 0;

            this.animateHeroContent?.(
                activeSlide
            );

            const activeVideo =
                this.getActiveHeroVideo?.();

            if (activeVideo) {
                this.playHeroVideo?.(
                    activeVideo
                );
            }

            this.emit(
                'tefco:hero:visibility:resumed'
            );
        }
    }

    /**
     * Returns whether Hero is visible.
     *
     * @returns {boolean}
     */
    isHeroVisible() {
        return !!(
            this.hero.visibility.pageVisible &&
            this.hero.visibility.heroVisible &&
            this.hero.visibility.windowFocused
        );
    }

    /**
     * Returns Hero visibility state.
     *
     * @returns {Object}
     */
    getHeroVisibilityState() {
        return {
            initialized:
                this.hero.visibility?.initialized ?? false,

            pageVisible:
                this.hero.visibility?.pageVisible ?? false,

            heroVisible:
                this.hero.visibility?.heroVisible ?? false,

            windowFocused:
                this.hero.visibility?.windowFocused ?? false,

            visibilityChanges:
                this.hero.visibility?.visibilityChanges ?? 0,

            observer:
                !!this.hero.visibility?.observer
        };
    }

    /**
     * Returns whether the visibility manager is ready.
     *
     * @returns {boolean}
     */
    isHeroVisibilityReady() {
        return !!(
            this.hero.visibility &&
            this.hero.visibility.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2E.3
     * Theme Editor Integration
     * ------------------------------------------------------------------------
     * Shopify Theme Editor Lifecycle
     * Section Reload Support
     * Block Selection
     * Inspector Synchronization
     * Hot Reload Safe
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeHeroThemeEditor() {
        this.hero.themeEditor = {
            initialized: false,
            enabled: this.isThemeEditor(),
            selectedSection: null,
            selectedBlock: null,
            reloadCount: 0
        };

        if (!this.hero.themeEditor.enabled) {
            return;
        }

        this.bindHeroThemeEditorEvents();

        this.hero.themeEditor.initialized = true;

        this.emit(
            'tefco:hero:theme-editor:initialized'
        );
    }

    /**
     * Returns whether Shopify Theme Editor is active.
     *
     * @returns {boolean}
     */
    isThemeEditor() {
        return Boolean(
            window.Shopify &&
            window.Shopify.designMode
        );
    }

    /**
     * Binds Theme Editor lifecycle events.
     */
    bindHeroThemeEditorEvents() {

        this.on(
            document,
            'shopify:section:load',
            event => {

                this.handleHeroSectionLoad(
                    event
                );

            }
        );

        this.on(
            document,
            'shopify:section:unload',
            event => {

                this.handleHeroSectionUnload(
                    event
                );

            }
        );

        this.on(
            document,
            'shopify:section:select',
            event => {

                this.handleHeroSectionSelect(
                    event
                );

            }
        );

        this.on(
            document,
            'shopify:section:deselect',
            event => {

                this.handleHeroSectionDeselect(
                    event
                );

            }
        );

        this.on(
            document,
            'shopify:block:select',
            event => {

                this.handleHeroBlockSelect(
                    event
                );

            }
        );

        this.on(
            document,
            'shopify:block:deselect',
            event => {

                this.handleHeroBlockDeselect(
                    event
                );

            }
        );
    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleHeroSectionLoad(event) {
        const section =
            event.target;

        if (
            !section ||
            section !== this.hero.dom.section
        ) {
            return;
        }

        this.hero.themeEditor.reloadCount++;

        this.destroyHero();

        this.initializeHero();

        this.emit(
            'tefco:hero:theme-editor:section-load',
            {
                section
            }
        );
    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleHeroSectionUnload(event) {
        const section =
            event.target;

        if (
            !section ||
            section !== this.hero.dom.section
        ) {
            return;
        }

        this.destroyHero();

        this.emit(
            'tefco:hero:theme-editor:section-unload',
            {
                section
            }
        );
    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleHeroSectionSelect(event) {
        const section =
            event.target;

        if (
            !section ||
            section !== this.hero.dom.section
        ) {
            return;
        }

        this.hero.themeEditor.selectedSection =
            section;

        this.pauseHeroAutoplay?.();

        this.emit(
            'tefco:hero:theme-editor:section-select',
            {
                section
            }
        );
    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleHeroSectionDeselect(event) {
        const section =
            event.target;

        if (
            !section ||
            section !== this.hero.dom.section
        ) {
            return;
        }

        this.hero.themeEditor.selectedSection =
            null;

        this.resumeHeroAutoplay?.();

        this.emit(
            'tefco:hero:theme-editor:section-deselect',
            {
                section
            }
        );
    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleHeroBlockSelect(event) {
        const block =
            event.target;

        if (
            !this.hero.dom.section.contains(block)
        ) {
            return;
        }

        this.hero.themeEditor.selectedBlock =
            block;

        const slide =
            block.closest(
                '[data-hero-slide]'
            );

        if (
            slide &&
            slide.dataset.slideIndex
        ) {
            this.goToHeroSlide(
                Number(
                    slide.dataset.slideIndex
                )
            );
        }

        this.pauseHeroAutoplay?.();

        block.classList.add(
            'is-selected'
        );

        this.emit(
            'tefco:hero:theme-editor:block-select',
            {
                block
            }
        );
    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleHeroBlockDeselect(event) {
        const block =
            event.target;

        if (
            !this.hero.dom.section.contains(block)
        ) {
            return;
        }

        block.classList.remove(
            'is-selected'
        );

        this.hero.themeEditor.selectedBlock =
            null;

        this.resumeHeroAutoplay?.();

        this.emit(
            'tefco:hero:theme-editor:block-deselect',
            {
                block
            }
        );
    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshHeroThemeEditor() {
        if (
            !this.hero.themeEditor.enabled
        ) {
            return;
        }

        this.destroyHero();

        this.initializeHero();
    }

    /**
     * Returns Theme Editor diagnostics.
     *
     * @returns {Object}
     */
    getHeroThemeEditorState() {
        return {
            enabled:
                this.hero.themeEditor?.enabled ?? false,

            initialized:
                this.hero.themeEditor?.initialized ?? false,

            reloadCount:
                this.hero.themeEditor?.reloadCount ?? 0,

            selectedSection:
                !!this.hero.themeEditor?.selectedSection,

            selectedBlock:
                !!this.hero.themeEditor?.selectedBlock
        };
    }

    /**
     * Returns whether Theme Editor integration is ready.
     *
     * @returns {boolean}
     */
    isHeroThemeEditorReady() {
        return !!(
            this.hero.themeEditor &&
            this.hero.themeEditor.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2E.4
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Manager
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * ========================================================================
     */

    /**
     * Initializes Hero accessibility.
     */
    initializeHeroAccessibilityManager() {
        this.hero.accessibility = {
            initialized: false,
            reducedMotion: window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches,
            keyboardNavigation: false,
            focusedElement: null,
            announcements: null
        };

        this.createHeroAnnouncementRegion();

        this.bindHeroAccessibilityEvents();

        this.updateHeroAccessibility();

        this.hero.accessibility.initialized = true;

        this.emit(
            'tefco:hero:accessibility:initialized'
        );
    }

    /**
     * Creates an ARIA live region.
     */
    createHeroAnnouncementRegion() {
        let region =
            this.hero.dom.section?.querySelector(
                '.tefco-hero__sr-announcer'
            );

        if (!region) {
            region = document.createElement('div');

            region.className =
                'tefco-hero__sr-announcer visually-hidden';

            region.setAttribute(
                'aria-live',
                'polite'
            );

            region.setAttribute(
                'aria-atomic',
                'true'
            );

            this.hero.dom.section?.appendChild(
                region
            );
        }

        this.hero.accessibility.announcements =
            region;
    }

    /**
     * Binds accessibility events.
     */
    bindHeroAccessibilityEvents() {

        this.on(
            document,
            'keydown',
            event => {

                this.handleHeroKeyboardNavigation(
                    event
                );

            }
        );

        this.on(
            document,
            'focusin',
            event => {

                this.hero.accessibility.focusedElement =
                    event.target;

            }
        );

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.on(
            media,
            'change',
            event => {

                this.hero.accessibility.reducedMotion =
                    event.matches;

                this.updateHeroAccessibility();

            }
        );
    }

    /**
     * Handles keyboard navigation.
     *
     * @param {KeyboardEvent} event
     */
    handleHeroKeyboardNavigation(event) {

        this.hero.accessibility.keyboardNavigation = true;

        switch (event.key) {

            case 'ArrowLeft':
                this.previousHeroSlide?.();
                break;

            case 'ArrowRight':
                this.nextHeroSlide?.();
                break;

            case 'Home':
                this.goToHeroSlide?.(0);
                break;

            case 'End':
                this.goToHeroSlide?.(
                    this.getHeroSlideCount() - 1
                );
                break;

            case 'Escape':
                this.stopHeroAutoplay?.();
                break;

        }
    }

    /**
     * Updates accessibility settings.
     */
    updateHeroAccessibility() {

        const section =
            this.hero.dom.section;

        if (!section) {
            return;
        }

        section.setAttribute(
            'role',
            'region'
        );

        section.setAttribute(
            'aria-roledescription',
            'carousel'
        );

        section.setAttribute(
            'aria-label',
            'Hero Banner'
        );

        if (
            this.hero.accessibility.reducedMotion
        ) {

            this.stopHeroAutoplay?.();

            this.stopHeroContentAnimations?.();

            this.stopHeroScrollIndicatorAnimation?.();

            this.stopHeroVideos?.();

        }

        this.updateHeroSlideAccessibility();
    }

    /**
     * Updates slide accessibility.
     */
    updateHeroSlideAccessibility() {

        this.hero.slides.forEach((slide, index) => {

            const active =
                index === this.hero.slider.current;

            slide.setAttribute(
                'aria-hidden',
                active ? 'false' : 'true'
            );

            slide.setAttribute(
                'tabindex',
                active ? '0' : '-1'
            );

        });
    }

    /**
     * Announces a message to screen readers.
     *
     * @param {string} message
     */
    announceHero(message) {

        const announcer =
            this.hero.accessibility.announcements;

        if (!announcer) {
            return;
        }

        announcer.textContent = '';

        this.setTimeout(() => {

            announcer.textContent = message;

        }, 30);
    }

    /**
     * Announces the current slide.
     */
    announceHeroSlide() {

        const current =
            this.hero.slider.current + 1;

        const total =
            this.getHeroSlideCount();

        this.announceHero(
            `Slide ${current} of ${total}`
        );
    }

    /**
     * Focuses the active slide.
     */
    focusActiveHeroSlide() {

        const slide =
            this.getActiveHeroSlide();

        slide?.focus({
            preventScroll: true
        });
    }

    /**
     * Restores previous keyboard focus.
     */
    restoreHeroFocus() {

        const element =
            this.hero.accessibility.focusedElement;

        if (
            element &&
            typeof element.focus === 'function'
        ) {
            element.focus({
                preventScroll: true
            });
        }
    }

    /**
     * Returns whether reduced motion is enabled.
     *
     * @returns {boolean}
     */
    isHeroReducedMotion() {
        return !!this.hero.accessibility.reducedMotion;
    }

    /**
     * Returns accessibility diagnostics.
     *
     * @returns {Object}
     */
    getHeroAccessibilityState() {
        return {
            initialized:
                this.hero.accessibility?.initialized ?? false,

            reducedMotion:
                this.hero.accessibility?.reducedMotion ?? false,

            keyboardNavigation:
                this.hero.accessibility?.keyboardNavigation ?? false,

            focusedElement:
                !!this.hero.accessibility?.focusedElement,

            announcer:
                !!this.hero.accessibility?.announcements
        };
    }

    /**
     * Returns whether accessibility manager is ready.
     *
     * @returns {boolean}
     */
    isHeroAccessibilityReady() {
        return !!(
            this.hero.accessibility &&
            this.hero.accessibility.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2F.1
     * Runtime Diagnostics
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics Engine
     * Runtime Health
     * Performance Metrics
     * Component Status
     * Production Safe
     * ========================================================================
     */

    /**
     * Initializes runtime diagnostics.
     */
    initializeHeroDiagnostics() {
        this.hero.diagnostics = {
            initialized: false,
            debug: false,
            startedAt: performance.now(),
            version: '1.0.0',
            health: 'healthy',
            warnings: [],
            errors: [],
            checks: new Map(),
            metrics: {
                renders: 0,
                slideChanges: 0,
                imageLoads: 0,
                backgroundLoads: 0,
                videoStarts: 0,
                resizeEvents: 0,
                visibilityEvents: 0,
                autoplayCycles: 0
            }
        };

        this.registerHeroDiagnosticChecks();

        this.hero.diagnostics.initialized = true;

        this.emit(
            'tefco:hero:diagnostics:initialized'
        );
    }

    /**
     * Registers built-in health checks.
     */
    registerHeroDiagnosticChecks() {

        this.hero.diagnostics.checks.set(
            'slider',
            () => this.isHeroSliderReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'navigation',
            () => this.isHeroNavigationReady?.() ?? true
        );

        this.hero.diagnostics.checks.set(
            'pagination',
            () => this.isHeroPaginationReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'images',
            () => this.isHeroResponsiveImagesReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'lazyImages',
            () => this.isHeroLazyImagesReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'backgrounds',
            () => this.isHeroBackgroundReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'videos',
            () => this.isHeroVideoReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'contentAnimations',
            () => this.isHeroContentAnimationReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'cta',
            () => this.isHeroCTAReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'counters',
            () => this.isHeroCounterReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'scrollIndicator',
            () => this.isHeroScrollIndicatorReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'resize',
            () => this.isHeroResizeReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'visibility',
            () => this.isHeroVisibilityReady?.() ?? false
        );

        this.hero.diagnostics.checks.set(
            'themeEditor',
            () => this.isThemeEditor()
                ? this.isHeroThemeEditorReady?.() ?? false
                : true
        );

        this.hero.diagnostics.checks.set(
            'accessibility',
            () => this.isHeroAccessibilityReady?.() ?? false
        );
    }

    /**
     * Runs every diagnostic check.
     *
     * @returns {Object}
     */
    runHeroDiagnostics() {
        const report = {};

        let healthy = true;

        this.hero.diagnostics.checks.forEach(
            (callback, name) => {

                try {

                    const result = callback();

                    report[name] = result;

                    if (!result) {
                        healthy = false;
                    }

                } catch (error) {

                    report[name] = false;

                    healthy = false;

                    this.hero.diagnostics.errors.push({
                        module: name,
                        error
                    });

                }

            }
        );

        this.hero.diagnostics.health =
            healthy
                ? 'healthy'
                : 'degraded';

        return {
            health:
                this.hero.diagnostics.health,
            modules: report
        };
    }

    /**
     * Records a metric.
     *
     * @param {string} metric
     * @param {number} amount
     */
    incrementHeroMetric(
        metric,
        amount = 1
    ) {
        if (
            !Object.prototype.hasOwnProperty.call(
                this.hero.diagnostics.metrics,
                metric
            )
        ) {
            return;
        }

        this.hero.diagnostics.metrics[
            metric
        ] += amount;
    }

    /**
     * Records a warning.
     *
     * @param {string} message
     */
    addHeroWarning(message) {
        this.hero.diagnostics.warnings.push({
            message,
            timestamp: Date.now()
        });
    }

    /**
     * Records an error.
     *
     * @param {string} module
     * @param {*} error
     */
    addHeroError(
        module,
        error
    ) {
        this.hero.diagnostics.errors.push({
            module,
            error,
            timestamp: Date.now()
        });
    }

    /**
     * Clears diagnostics history.
     */
    clearHeroDiagnostics() {
        this.hero.diagnostics.warnings = [];
        this.hero.diagnostics.errors = [];
    }

    /**
     * Returns uptime.
     *
     * @returns {number}
     */
    getHeroRuntime() {
        return performance.now() -
            this.hero.diagnostics.startedAt;
    }

    /**
     * Returns runtime diagnostics.
     *
     * @returns {Object}
     */
    getHeroDiagnosticsState() {
        return {
            initialized:
                this.hero.diagnostics?.initialized ?? false,

            version:
                this.hero.diagnostics?.version ?? null,

            health:
                this.hero.diagnostics?.health ?? 'unknown',

            runtime:
                this.getHeroRuntime(),

            metrics:
                {
                    ...this.hero.diagnostics.metrics
                },

            warnings:
                this.hero.diagnostics.warnings.length,

            errors:
                this.hero.diagnostics.errors.length,

            debug:
                this.hero.diagnostics.debug
        };
    }

    /**
     * Enables debug mode.
     */
    enableHeroDebug() {
        this.hero.diagnostics.debug = true;
    }

    /**
     * Disables debug mode.
     */
    disableHeroDebug() {
        this.hero.diagnostics.debug = false;
    }

    /**
     * Returns whether diagnostics are ready.
     *
     * @returns {boolean}
     */
    isHeroDiagnosticsReady() {
        return !!(
            this.hero.diagnostics &&
            this.hero.diagnostics.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2F.2
     * Validation
     * ------------------------------------------------------------------------
     * Enterprise Validation Engine
     * DOM Validation
     * Configuration Validation
     * State Validation
     * Accessibility Validation
     * Production Ready
     * ========================================================================
     */

    /**
     * Initializes Hero validation.
     */
    initializeHeroValidation() {
        this.hero.validation = {
            initialized: false,
            lastValidation: null,
            passed: false,
            errors: [],
            warnings: []
        };

        this.hero.validation.initialized = true;

        this.emit(
            'tefco:hero:validation:initialized'
        );
    }

    /**
     * Runs complete Hero validation.
     *
     * @returns {Object}
     */
    validateHero() {
        this.resetHeroValidation();

        this.validateHeroDOM();

        this.validateHeroConfiguration();

        this.validateHeroState();

        this.validateHeroSlides();

        this.validateHeroAccessibility();

        this.validateHeroMedia();

        this.hero.validation.lastValidation =
            Date.now();

        this.hero.validation.passed =
            this.hero.validation.errors.length === 0;

        const result = {
            passed:
                this.hero.validation.passed,
            errors:
                [...this.hero.validation.errors],
            warnings:
                [...this.hero.validation.warnings]
        };

        this.emit(
            'tefco:hero:validation:completed',
            result
        );

        return result;
    }

    /**
     * Resets validation state.
     */
    resetHeroValidation() {
        this.hero.validation.errors = [];
        this.hero.validation.warnings = [];
    }

    /**
     * Validates Hero DOM.
     */
    validateHeroDOM() {

        if (!this.hero.dom.section) {

            this.addHeroValidationError(
                'Hero section not found.'
            );

        }

        if (
            !Array.isArray(this.hero.slides) ||
            !this.hero.slides.length
        ) {

            this.addHeroValidationError(
                'No Hero slides found.'
            );

        }

        if (!this.hero.dom.container) {

            this.addHeroValidationWarning(
                'Hero container not found.'
            );

        }

    }

    /**
     * Validates Hero configuration.
     */
    validateHeroConfiguration() {

        if (!this.hero.config) {

            this.addHeroValidationError(
                'Hero configuration missing.'
            );

            return;
        }

        if (
            typeof this.hero.config.autoplay !==
            'boolean'
        ) {

            this.addHeroValidationWarning(
                'Autoplay configuration should be boolean.'
            );

        }

        if (
            this.hero.config.autoplay &&
            (
                !Number.isFinite(
                    this.hero.config.autoplaySpeed
                ) ||
                this.hero.config.autoplaySpeed <= 0
            )
        ) {

            this.addHeroValidationError(
                'Invalid autoplay speed.'
            );

        }

        if (
            this.hero.config.transitionSpeed &&
            this.hero.config.transitionSpeed < 100
        ) {

            this.addHeroValidationWarning(
                'Transition speed is unusually low.'
            );

        }

    }

    /**
     * Validates runtime state.
     */
    validateHeroState() {

        if (
            !this.isHeroReady?.()
        ) {

            this.addHeroValidationError(
                'Hero is not initialized.'
            );

        }

        if (
            this.hero.slider &&
            (
                this.hero.slider.current < 0 ||
                this.hero.slider.current >=
                this.getHeroSlideCount()
            )
        ) {

            this.addHeroValidationError(
                'Current slide index is invalid.'
            );

        }

    }

    /**
     * Validates slide structure.
     */
    validateHeroSlides() {

        this.hero.slides.forEach(
            (slide, index) => {

                if (!slide) {

                    this.addHeroValidationError(
                        `Slide ${index} is missing.`
                    );

                    return;
                }

                if (
                    !slide.hasAttribute(
                        'data-hero-slide'
                    )
                ) {

                    this.addHeroValidationWarning(
                        `Slide ${index} has no data-hero-slide attribute.`
                    );

                }

                const title =
                    slide.querySelector(
                        '.tefco-hero__title'
                    );

                if (!title) {

                    this.addHeroValidationWarning(
                        `Slide ${index} has no title.`
                    );

                }

            }
        );

    }

    /**
     * Validates accessibility.
     */
    validateHeroAccessibility() {

        if (!this.hero.dom.section) {
            return;
        }

        if (
            !this.hero.dom.section.hasAttribute(
                'role'
            )
        ) {

            this.addHeroValidationWarning(
                'Hero region role missing.'
            );

        }

        if (
            !this.hero.dom.section.hasAttribute(
                'aria-label'
            )
        ) {

            this.addHeroValidationWarning(
                'Hero aria-label missing.'
            );

        }

    }

    /**
     * Validates media assets.
     */
    validateHeroMedia() {

        this.hero.dom.section
            ?.querySelectorAll('img')
            .forEach(image => {

                if (
                    !image.hasAttribute('alt')
                ) {

                    this.addHeroValidationWarning(
                        'Image missing alt attribute.'
                    );

                }

            });

        this.hero.dom.section
            ?.querySelectorAll('video')
            .forEach(video => {

                if (
                    !video.hasAttribute('muted')
                ) {

                    this.addHeroValidationWarning(
                        'Autoplay video should be muted.'
                    );

                }

            });

    }

    /**
     * Adds validation error.
     *
     * @param {string} message
     */
    addHeroValidationError(message) {
        this.hero.validation.errors.push(message);
    }

    /**
     * Adds validation warning.
     *
     * @param {string} message
     */
    addHeroValidationWarning(message) {
        this.hero.validation.warnings.push(message);
    }

    /**
     * Returns validation state.
     *
     * @returns {Object}
     */
    getHeroValidationState() {
        return {
            initialized:
                this.hero.validation?.initialized ?? false,

            passed:
                this.hero.validation?.passed ?? false,

            lastValidation:
                this.hero.validation?.lastValidation ?? null,

            errorCount:
                this.hero.validation?.errors.length ?? 0,

            warningCount:
                this.hero.validation?.warnings.length ?? 0,

            errors:
                [...this.hero.validation.errors],

            warnings:
                [...this.hero.validation.warnings]
        };
    }

    /**
     * Returns whether validation passed.
     *
     * @returns {boolean}
     */
    hasHeroPassedValidation() {
        return !!(
            this.hero.validation &&
            this.hero.validation.passed
        );
    }

    /**
     * Returns whether the validation engine is ready.
     *
     * @returns {boolean}
     */
    isHeroValidationReady() {
        return !!(
            this.hero.validation &&
            this.hero.validation.initialized
        );
    }
    /**
     * ========================================================================
     * Part 2F.3
     * Cleanup
     * ------------------------------------------------------------------------
     * Enterprise Cleanup Manager
     * Memory Leak Prevention
     * Observer Cleanup
     * Event Cleanup
     * RAF / Timers Cleanup
     * Shopify Theme Editor Safe
     * ========================================================================
     */

    /**
     * Destroys the Hero component.
     *
     * Safe to call multiple times.
     */
    destroyHero() {

        if (!this.hero) {
            return;
        }

        this.emit(
            'tefco:hero:destroy:before'
        );

        this.stopHeroAutoplay?.();

        this.stopHeroVideos?.();

        this.stopHeroContentAnimations?.();

        this.stopHeroScrollIndicatorAnimation?.();

        this.destroyHeroObservers();

        this.destroyHeroTimers();

        this.destroyHeroEvents();

        this.destroyHeroMedia();

        this.destroyHeroRuntime();

        this.destroyHeroDOMState();

        this.emit(
            'tefco:hero:destroy:after'
        );
    }

    /**
     * Disconnects every observer.
     */
    destroyHeroObservers() {

        [
            this.hero.backgrounds?.observer,
            this.hero.video?.observer,
            this.hero.counters?.observer,
            this.hero.visibility?.observer,
            this.hero.resize?.resizeObserver
        ].forEach(observer => {

            observer?.disconnect?.();

        });

        this.hero.backgrounds &&
            (this.hero.backgrounds.observer = null);

        this.hero.video &&
            (this.hero.video.observer = null);

        this.hero.counters &&
            (this.hero.counters.observer = null);

        this.hero.visibility &&
            (this.hero.visibility.observer = null);

        this.hero.resize &&
            (this.hero.resize.resizeObserver = null);
    }

    /**
     * Clears timers and RAF callbacks.
     */
    destroyHeroTimers() {

        this.clearAllTimeouts?.();

        this.clearAllIntervals?.();

        this.cancelAllAnimationFrames?.();
    }

    /**
     * Removes DOM events.
     *
     * Relies on the global Event Manager.
     */
    destroyHeroEvents() {

        this.removeManagedEvents?.(
            this.hero.dom.section
        );

        this.removeManagedEvents?.(
            window
        );

        this.removeManagedEvents?.(
            document
        );
    }

    /**
     * Releases media resources.
     */
    destroyHeroMedia() {

        this.hero.video?.elements?.forEach(video => {

            if (
                !(video instanceof HTMLVideoElement)
            ) {
                return;
            }

            video.pause();

            video.removeAttribute('src');

            video.querySelectorAll('source')
                .forEach(source => {

                    source.removeAttribute('src');

                });

            video.load();

        });

        this.hero.backgrounds?.elements?.forEach(element => {

            element.style.backgroundImage = '';

        });

        this.hero.images?.forEach(image => {

            image.removeAttribute(
                'data-loaded'
            );

        });
    }

    /**
     * Clears runtime collections.
     */
    destroyHeroRuntime() {

        if (this.hero.slider) {

            this.hero.slider.current = 0;

            this.hero.slider.previous = 0;

        }

        if (this.hero.animations) {

            this.hero.animations.running = false;

        }

        if (this.hero.visibility) {

            this.hero.visibility.heroVisible = false;

        }

        if (this.hero.cta) {

            this.hero.cta.activeButton = null;

        }

        if (this.hero.accessibility) {

            this.hero.accessibility.focusedElement = null;

        }
    }

    /**
     * Removes runtime CSS classes
     * and inline styles.
     */
    destroyHeroDOMState() {

        this.hero.dom.section
            ?.querySelectorAll(
                '.is-active,' +
                '.is-visible,' +
                '.is-loading,' +
                '.is-background-loaded,' +
                '.is-background-loading,' +
                '.is-selected,' +
                '.is-hidden,' +
                '.is-disabled'
            )
            .forEach(element => {

                element.classList.remove(
                    'is-active',
                    'is-visible',
                    'is-loading',
                    'is-background-loaded',
                    'is-background-loading',
                    'is-selected',
                    'is-hidden',
                    'is-disabled'
                );

                element.removeAttribute(
                    'style'
                );

            });
    }

    /**
     * Performs a complete reset.
     */
    resetHero() {

        this.destroyHero();

        this.initializeHero();

        this.emit(
            'tefco:hero:reset'
        );
    }

    /**
     * Returns cleanup diagnostics.
     *
     * @returns {Object}
     */
    getHeroCleanupState() {
        return {
            observersReleased:
                !this.hero.backgrounds?.observer &&
                !this.hero.video?.observer &&
                !this.hero.counters?.observer &&
                !this.hero.visibility?.observer,

            timersReleased: true,

            mediaReleased: true,

            runtimeReset:
                this.hero.slider?.current === 0,

            destroyed:
                this.isHeroDestroyed()
        };
    }

    /**
     * Returns whether cleanup is ready.
     *
     * @returns {boolean}
     */
    isHeroCleanupReady() {
        return true;
    }
    /**
     * ========================================================================
     * Part 2F.4
     * Destroy
     * ------------------------------------------------------------------------
     * Enterprise Destroy Manager
     * Final Lifecycle
     * Memory Safe
     * Idempotent
     * Shopify Theme Editor Safe
     * ========================================================================
     */

    /**
     * Completely destroys the Hero instance.
     *
     * Safe to call multiple times.
     *
     * @returns {boolean}
     */
    destroyHeroInstance() {

        if (
            !this.hero ||
            this.hero.destroyed
        ) {
            return true;
        }

        this.emit(
            'tefco:hero:destroy:start'
        );

        try {

            /*
             * ------------------------------------------------------------
             * Stop active features
             * ------------------------------------------------------------
             */

            this.stopHeroAutoplay?.();

            this.stopHeroVideos?.();

            this.stopHeroContentAnimations?.();

            this.stopHeroScrollIndicatorAnimation?.();

            /*
             * ------------------------------------------------------------
             * Cleanup
             * ------------------------------------------------------------
             */

            this.destroyHeroObservers?.();

            this.destroyHeroTimers?.();

            this.destroyHeroEvents?.();

            this.destroyHeroMedia?.();

            this.destroyHeroRuntime?.();

            this.destroyHeroDOMState?.();

            /*
             * ------------------------------------------------------------
             * Diagnostics
             * ------------------------------------------------------------
             */

            this.clearHeroDiagnostics?.();

            /*
             * ------------------------------------------------------------
             * Accessibility
             * ------------------------------------------------------------
             */

            const announcer =
                this.hero.accessibility?.announcements;

            announcer?.remove?.();

            /*
             * ------------------------------------------------------------
             * Internal Collections
             * ------------------------------------------------------------
             */

            this.hero.slides = [];

            this.hero.images = [];

            this.hero.lazyImages = [];

            this.hero.cta &&
                (this.hero.cta.buttons = []);

            this.hero.counters &&
                (this.hero.counters.elements = []);

            this.hero.video &&
                (this.hero.video.elements = []);

            this.hero.backgrounds &&
                (this.hero.backgrounds.elements = []);

            /*
             * ------------------------------------------------------------
             * Weak Collections
             * ------------------------------------------------------------
             */

            if (this.hero.animations) {

                this.hero.animations.animatedElements =
                    new WeakSet();

            }

            if (this.hero.backgrounds) {

                this.hero.backgrounds.loaded =
                    new WeakSet();

            }

            if (this.hero.cta) {

                this.hero.cta.loadingButtons =
                    new WeakSet();

                this.hero.cta.disabledButtons =
                    new WeakSet();

            }

            if (this.hero.video) {

                this.hero.video.players =
                    new WeakMap();

            }

            /*
             * ------------------------------------------------------------
             * DOM Cache
             * ------------------------------------------------------------
             */

            if (this.hero.dom) {

                Object.keys(this.hero.dom)
                    .forEach(key => {

                        this.hero.dom[key] = null;

                    });

            }

            /*
             * ------------------------------------------------------------
             * Runtime Objects
             * ------------------------------------------------------------
             */

            [
                'slider',
                'navigation',
                'pagination',
                'autoplay',
                'loop',
                'backgrounds',
                'video',
                'animations',
                'cta',
                'counters',
                'scrollIndicator',
                'resize',
                'visibility',
                'themeEditor',
                'accessibility',
                'diagnostics',
                'validation'
            ].forEach(module => {

                if (
                    Object.prototype.hasOwnProperty.call(
                        this.hero,
                        module
                    )
                ) {

                    this.hero[module] = null;

                }

            });

            /*
             * ------------------------------------------------------------
             * Destroy Flag
             * ------------------------------------------------------------
             */

            this.hero.destroyed = true;

            this.emit(
                'tefco:hero:destroy:complete'
            );

            return true;

        } catch (error) {

            this.addHeroError?.(
                'destroy',
                error
            );

            this.emit(
                'tefco:hero:destroy:error',
                {
                    error
                }
            );

            return false;

        }
    }

    /**
     * Returns whether the Hero instance
     * has been destroyed.
     *
     * @returns {boolean}
     */
    isHeroDestroyed() {
        return !!(
            this.hero &&
            this.hero.destroyed
        );
    }

    /**
     * Returns Destroy diagnostics.
     *
     * @returns {Object}
     */
    getHeroDestroyState() {
        return {
            destroyed:
                this.isHeroDestroyed(),

            observersReleased:
                !this.hero?.backgrounds?.observer &&
                !this.hero?.video?.observer &&
                !this.hero?.visibility?.observer &&
                !this.hero?.counters?.observer,

            domReleased:
                Object.values(
                    this.hero?.dom || {}
                ).every(
                    value => value === null
                ),

            slidesReleased:
                (this.hero?.slides?.length ?? 0) === 0,

            imagesReleased:
                (this.hero?.images?.length ?? 0) === 0,

            runtimeReleased:
                !this.hero?.slider &&
                !this.hero?.navigation &&
                !this.hero?.pagination
        };
    }

    /**
     * Returns whether the destroy
     * manager is ready.
     *
     * @returns {boolean}
     */
    isHeroDestroyReady() {
        return typeof this.destroyHeroInstance === 'function';
    }
    /**
     * ========================================================================
     
    //----------------------------------------
    // PART 2 — Brand Slider
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Brand Slider
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Brand Slider
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Brand Slider
    //----------------------------------------


    //----------------------------------------
    // PART 2 — Brand Slider
    //----------------------------------------

* Part 2G.1
     * Brand Slider Foundation
     * ------------------------------------------------------------------------
     * Enterprise Brand Slider Foundation
     * Initialization
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes the Brand Slider.
     */
    initializeBrandSlider() {

        this.heroBrand = {
            initialized: false,
            destroyed: false
        };

        this.cacheBrandSliderDOM();

        if (!this.heroBrand.dom.section) {
            return;
        }

        this.initializeBrandSliderConfig();

        this.initializeBrandSliderState();

        this.collectBrandSlides();

        this.heroBrand.initialized = true;

        this.emit(
            'tefco:brand-slider:initialized'
        );
    }

    /**
     * Caches Brand Slider DOM.
     */
    cacheBrandSliderDOM() {

        const section =
            document.querySelector(
                '[data-brand-slider]'
            );

        this.heroBrand.dom = {

            section,

            viewport:
                section?.querySelector(
                    '[data-brand-slider-viewport]'
                ) ?? null,

            track:
                section?.querySelector(
                    '[data-brand-slider-track]'
                ) ?? null,

            wrapper:
                section?.querySelector(
                    '[data-brand-slider-wrapper]'
                ) ?? null,

            previous:
                section?.querySelector(
                    '[data-brand-prev]'
                ) ?? null,

            next:
                section?.querySelector(
                    '[data-brand-next]'
                ) ?? null,

            pagination:
                section?.querySelector(
                    '[data-brand-pagination]'
                ) ?? null

        };

    }

    /**
     * Initializes Brand Slider configuration.
     */
    initializeBrandSliderConfig() {

        const section =
            this.heroBrand.dom.section;

        const dataset =
            section?.dataset ?? {};

        this.heroBrand.config = {

            autoplay:
                dataset.autoplay === 'true',

            autoplaySpeed:
                Number(
                    dataset.autoplaySpeed
                ) || 3500,

            transitionSpeed:
                Number(
                    dataset.transitionSpeed
                ) || 500,

            loop:
                dataset.loop !== 'false',

            draggable:
                dataset.draggable !== 'false',

            pauseOnHover:
                dataset.pauseOnHover !== 'false',

            visibleDesktop:
                Number(
                    dataset.desktop
                ) || 6,

            visibleTablet:
                Number(
                    dataset.tablet
                ) || 4,

            visibleMobile:
                Number(
                    dataset.mobile
                ) || 2

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeBrandSliderState() {

        this.heroBrand.state = {

            current: 0,

            previous: 0,

            total: 0,

            visible: 0,

            translating: false,

            dragging: false,

            autoplayRunning: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects Brand slides.
     */
    collectBrandSlides() {

        this.heroBrand.slides = Array.from(

            this.heroBrand.dom.track
                ?.querySelectorAll(
                    '[data-brand-slide]'
                ) ?? []

        );

        this.heroBrand.state.total =
            this.heroBrand.slides.length;

    }

    /**
     * Returns Brand Slider element.
     *
     * @returns {HTMLElement|null}
     */
    getBrandSliderElement() {
        return this.heroBrand.dom.section;
    }

    /**
     * Returns Brand Slider track.
     *
     * @returns {HTMLElement|null}
     */
    getBrandSliderTrack() {
        return this.heroBrand.dom.track;
    }

    /**
     * Returns Brand slides.
     *
     * @returns {HTMLElement[]}
     */
    getBrandSlides() {
        return this.heroBrand.slides;
    }

    /**
     * Returns active Brand slide index.
     *
     * @returns {number}
     */
    getActiveBrandSlide() {
        return this.heroBrand.state.current;
    }

    /**
     * Returns total Brand slides.
     *
     * @returns {number}
     */
    getBrandSlideCount() {
        return this.heroBrand.state.total;
    }

    /**
     * Sets active Brand slide.
     *
     * @param {number} index
     */
    setActiveBrandSlide(index) {

        this.heroBrand.state.previous =
            this.heroBrand.state.current;

        this.heroBrand.state.current =
            index;

    }

    /**
     * Returns current Brand Slider configuration.
     *
     * @returns {Object}
     */
    getBrandSliderConfig() {
        return {
            ...this.heroBrand.config
        };
    }

    /**
     * Returns current Brand Slider state.
     *
     * @returns {Object}
     */
    getBrandSliderState() {
        return {

            initialized:
                this.heroBrand.initialized,

            destroyed:
                this.heroBrand.destroyed,

            current:
                this.heroBrand.state.current,

            previous:
                this.heroBrand.state.previous,

            total:
                this.heroBrand.state.total,

            visible:
                this.heroBrand.state.visible,

            translating:
                this.heroBrand.state.translating,

            dragging:
                this.heroBrand.state.dragging,

            autoplayRunning:
                this.heroBrand.state.autoplayRunning

        };
    }

    /**
     * Returns whether Brand Slider is ready.
     *
     * @returns {boolean}
     */
    isBrandSliderReady() {

        return !!(

            this.heroBrand &&
            this.heroBrand.initialized &&
            this.heroBrand.dom.section &&
            this.heroBrand.dom.track &&
            this.heroBrand.state.total > 0

        );

    }
    /**
     * ========================================================================
     * Part 2G.2
     * Brand Slider Engine
     * ------------------------------------------------------------------------
     * Enterprise Slider Engine
     * Track Translation
     * Active State Management
     * Smooth Transitions
     * Synchronization
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes the Brand Slider engine.
     */
    initializeBrandSliderEngine() {

        if (!this.isBrandSliderReady()) {
            return;
        }

        this.prepareBrandSlides();

        this.calculateBrandSlideDimensions();

        this.activateBrandSlide(0, false);

        this.emit(
            'tefco:brand-slider:engine:initialized'
        );

    }

    /**
     * Prepares Brand slides.
     */
    prepareBrandSlides() {

        this.heroBrand.slides.forEach((slide, index) => {

            slide.dataset.brandIndex = index;

            slide.classList.toggle(
                'is-active',
                index === 0
            );

            slide.setAttribute(
                'aria-hidden',
                index === 0 ? 'false' : 'true'
            );

        });

    }

    /**
     * Calculates slide dimensions.
     */
    calculateBrandSlideDimensions() {

        const track =
            this.heroBrand.dom.track;

        const slide =
            this.heroBrand.slides[0];

        if (!track || !slide) {
            return;
        }

        const styles =
            window.getComputedStyle(slide);

        const gap =
            parseFloat(styles.marginRight) || 0;

        this.heroBrand.state.slideWidth =
            slide.offsetWidth + gap;

    }

    /**
     * Activates a Brand slide.
     *
     * @param {number} index
     * @param {boolean} animate
     */
    activateBrandSlide(
        index,
        animate = true
    ) {

        if (!this.heroBrand.slides.length) {
            return;
        }

        index =
            this.normalizeBrandIndex
                ? this.normalizeBrandIndex(index)
                : Math.max(
                    0,
                    Math.min(
                        index,
                        this.heroBrand.state.total - 1
                    )
                );

        this.setActiveBrandSlide(index);

        this.heroBrand.slides.forEach((slide, i) => {

            const active = i === index;

            slide.classList.toggle(
                'is-active',
                active
            );

            slide.setAttribute(
                'aria-hidden',
                active ? 'false' : 'true'
            );

        });

        this.updateBrandSlider(animate);

        this.emit(
            'tefco:brand-slider:slide:activated',
            {
                previous:
                    this.heroBrand.state.previous,
                current:
                    this.heroBrand.state.current
            }
        );

    }

    /**
     * Moves to next slide.
     */
    nextBrandSlide() {

        this.activateBrandSlide(
            this.heroBrand.state.current + 1
        );

    }

    /**
     * Moves to previous slide.
     */
    previousBrandSlide() {

        this.activateBrandSlide(
            this.heroBrand.state.current - 1
        );

    }

    /**
     * Navigates to a specific slide.
     *
     * @param {number} index
     */
    goToBrandSlide(index) {

        this.activateBrandSlide(index);

    }

    /**
     * Updates slider position.
     *
     * @param {boolean} animate
     */
    updateBrandSlider(
        animate = true
    ) {

        const track =
            this.heroBrand.dom.track;

        if (!track) {
            return;
        }

        const translate =
            this.heroBrand.state.current *
            this.heroBrand.state.slideWidth;

        this.heroBrand.state.translating = true;

        track.style.transition =
            animate
                ? `transform ${this.heroBrand.config.transitionSpeed}ms ease`
                : 'none';

        track.style.transform =
            `translate3d(-${translate}px,0,0)`;

        if (!animate) {

            this.heroBrand.state.translating = false;

            return;

        }

        this.setTimeout(() => {

            this.heroBrand.state.translating = false;

        }, this.heroBrand.config.transitionSpeed);

    }

    /**
     * Synchronizes slider state.
     */
    syncBrandSlider() {

        this.calculateBrandSlideDimensions();

        this.updateBrandSlider(false);

        this.emit(
            'tefco:brand-slider:synchronized'
        );

    }

    /**
     * Refreshes Brand Slider.
     */
    refreshBrandSlider() {

        this.collectBrandSlides();

        this.prepareBrandSlides();

        this.syncBrandSlider();

        this.emit(
            'tefco:brand-slider:refreshed'
        );

    }

    /**
     * Returns whether the slider
     * is currently animating.
     *
     * @returns {boolean}
     */
    isBrandSliderAnimating() {

        return !!this.heroBrand.state.translating;

    }

    /**
     * Returns current translation.
     *
     * @returns {number}
     */
    getBrandSliderTranslate() {

        return (
            this.heroBrand.state.current *
            this.heroBrand.state.slideWidth
        );

    }

    /**
     * Returns slider runtime state.
     *
     * @returns {Object}
     */
    getBrandSliderEngineState() {

        return {

            current:
                this.heroBrand.state.current,

            previous:
                this.heroBrand.state.previous,

            translating:
                this.heroBrand.state.translating,

            slideWidth:
                this.heroBrand.state.slideWidth || 0,

            translate:
                this.getBrandSliderTranslate(),

            total:
                this.heroBrand.state.total

        };

    }

    /**
     * Returns whether the
     * Brand Slider engine is ready.
     *
     * @returns {boolean}
     */
    isBrandSliderEngineReady() {

        return !!(

            this.isBrandSliderReady() &&
            this.heroBrand.state.slideWidth > 0

        );

    }
    /**
     * ========================================================================
     * Part 2G.3
     * Infinite Loop
     * ------------------------------------------------------------------------
     * Enterprise Infinite Loop Engine
     * Circular Navigation
     * Index Normalization
     * Boundary Management
     * Runtime Controls
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes infinite loop support.
     */
    initializeBrandLoop() {

        this.heroBrand.loop = {

            enabled: this.heroBrand.config.loop,

            clones: [],

            cloneCount: 0

        };

        if (!this.heroBrand.loop.enabled) {
            return;
        }

        this.enableBrandLoop();

        this.emit(
            'tefco:brand-slider:loop:initialized'
        );

    }

    /**
     * Normalizes slide index.
     *
     * @param {number} index
     * @returns {number}
     */
    normalizeBrandIndex(index) {

        const total =
            this.heroBrand.state.total;

        if (!total) {
            return 0;
        }

        if (!this.heroBrand.loop?.enabled) {

            return Math.max(
                0,
                Math.min(index, total - 1)
            );

        }

        return (
            (index % total) + total
        ) % total;

    }

    /**
     * Returns next slide index.
     *
     * @param {number} index
     * @returns {number}
     */
    getNextBrandIndex(index = this.heroBrand.state.current) {

        return this.normalizeBrandIndex(
            index + 1
        );

    }

    /**
     * Returns previous slide index.
     *
     * @param {number} index
     * @returns {number}
     */
    getPreviousBrandIndex(index = this.heroBrand.state.current) {

        return this.normalizeBrandIndex(
            index - 1
        );

    }

    /**
     * Enables infinite loop mode.
     */
    enableBrandLoop() {

        this.heroBrand.loop.enabled = true;

        this.emit(
            'tefco:brand-slider:loop:enabled'
        );

    }

    /**
     * Disables infinite loop mode.
     */
    disableBrandLoop() {

        this.heroBrand.loop.enabled = false;

        this.emit(
            'tefco:brand-slider:loop:disabled'
        );

    }

    /**
     * Returns whether looping is enabled.
     *
     * @returns {boolean}
     */
    isBrandLoopEnabled() {

        return !!(
            this.heroBrand.loop &&
            this.heroBrand.loop.enabled
        );

    }

    /**
     * Returns whether current slide
     * is the first logical slide.
     *
     * @returns {boolean}
     */
    isFirstBrandSlide() {

        return (
            this.heroBrand.state.current === 0
        );

    }

    /**
     * Returns whether current slide
     * is the last logical slide.
     *
     * @returns {boolean}
     */
    isLastBrandSlide() {

        return (
            this.heroBrand.state.current ===
            this.heroBrand.state.total - 1
        );

    }

    /**
     * Jumps instantly to a slide
     * without animation.
     *
     * @param {number} index
     */
    jumpToBrandSlide(index) {

        this.activateBrandSlide(
            this.normalizeBrandIndex(index),
            false
        );

    }

    /**
     * Ensures the current index
     * remains valid.
     */
    validateBrandLoopState() {

        this.heroBrand.state.current =
            this.normalizeBrandIndex(
                this.heroBrand.state.current
            );

        this.heroBrand.state.previous =
            this.normalizeBrandIndex(
                this.heroBrand.state.previous
            );

    }

    /**
     * Returns loop runtime information.
     *
     * @returns {Object}
     */
    getBrandLoopState() {

        return {

            enabled:
                this.heroBrand.loop?.enabled ?? false,

            current:
                this.heroBrand.state.current,

            previous:
                this.heroBrand.state.previous,

            total:
                this.heroBrand.state.total,

            first:
                this.isFirstBrandSlide(),

            last:
                this.isLastBrandSlide()

        };

    }

    /**
     * Refreshes loop state.
     */
    refreshBrandLoop() {

        this.validateBrandLoopState();

        this.updateBrandSlider(false);

        this.emit(
            'tefco:brand-slider:loop:refreshed'
        );

    }
    /**
     * Registers a Brand Slider event listener and tracks it for cleanup.
     * This prevents duplicate listeners after Theme Editor rebuilds.
     */
    bindBrandEvent(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        target.addEventListener(event, handler, options);

        this.heroBrand.events ??= [];
        this.heroBrand.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every Brand Slider listener registered through bindBrandEvent().
     */
    cleanupBrandEvents() {

        if (!this.heroBrand?.events) {
            return;
        }

        this.heroBrand.events.forEach(({ target, event, handler, options }) => {
            target?.removeEventListener(event, handler, options);
        });

        this.heroBrand.events.length = 0;

    }

    /**
     * Initializes Brand Slider navigation buttons.
     */
    initializeBrandNavigation() {

        if (!this.isBrandSliderReady()) {
            return;
        }

        const { previous, next } = this.heroBrand.dom;

        this.heroBrand.navigation = {
            previousHandler: () => this.previousBrandSlide(),
            nextHandler: () => this.nextBrandSlide()
        };

        previous?.addEventListener(
            'click',
            this.heroBrand.navigation.previousHandler
        );

        next?.addEventListener(
            'click',
            this.heroBrand.navigation.nextHandler
        );

        this.emit(
            'tefco:brand-slider:navigation:initialized'
        );

    }

    /**
     * Removes Brand Slider navigation button listeners.
     */
    cleanupBrandNavigation() {

        if (!this.heroBrand?.navigation) {
            return;
        }

        const { previous, next } = this.heroBrand.dom ?? {};
        const { previousHandler, nextHandler } =
            this.heroBrand.navigation;

        if (previousHandler) {
            previous?.removeEventListener(
                'click',
                previousHandler
            );
        }

        if (nextHandler) {
            next?.removeEventListener(
                'click',
                nextHandler
            );
        }

        this.heroBrand.navigation = null;

    }

    /**
     * ========================================================================
     * Part 2G.4
     * Brand Slider Autoplay
     * ------------------------------------------------------------------------
     * Enterprise Autoplay Engine
     * Automatic Navigation
     * Pause / Resume Controls
     * Hover Support
     * Visibility Awareness
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes autoplay.
     */
    initializeBrandAutoplay() {

        if (
            !this.isBrandSliderReady() ||
            !this.heroBrand.config.autoplay
        ) {
            return;
        }

        this.heroBrand.autoplay = {

            timer: null,

            paused: false,

            startedAt: null

        };

        if (
            this.heroBrand.config.pauseOnHover
        ) {
            this.bindBrandAutoplayHover();
        }

        this.startBrandAutoplay();

        this.emit(
            'tefco:brand-slider:autoplay:initialized'
        );

    }

    /**
     * Starts autoplay.
     */
    startBrandAutoplay() {

        if (
            !this.heroBrand.config.autoplay ||
            this.heroBrand.state.autoplayRunning
        ) {
            return;
        }

        this.stopBrandAutoplay();

        this.heroBrand.state.autoplayRunning = true;

        this.heroBrand.autoplay.startedAt =
            performance.now();

        this.heroBrand.autoplay.timer =
            window.setInterval(() => {

                if (
                    this.heroBrand.autoplay.paused ||
                    this.heroBrand.state.dragging ||
                    this.heroBrand.state.translating
                ) {
                    return;
                }

                this.nextBrandSlide();

            }, this.heroBrand.config.autoplaySpeed);

        this.emit(
            'tefco:brand-slider:autoplay:started'
        );

    }

    /**
     * Stops autoplay.
     */
    stopBrandAutoplay() {

        if (
            this.heroBrand.autoplay?.timer
        ) {

            clearInterval(
                this.heroBrand.autoplay.timer
            );

            this.heroBrand.autoplay.timer =
                null;

        }

        this.heroBrand.state.autoplayRunning =
            false;

        this.emit(
            'tefco:brand-slider:autoplay:stopped'
        );

    }

    /**
     * Pauses autoplay.
     */
    pauseBrandAutoplay() {

        if (
            !this.heroBrand.state.autoplayRunning
        ) {
            return;
        }

        this.heroBrand.autoplay.paused = true;

        this.emit(
            'tefco:brand-slider:autoplay:paused'
        );

    }

    /**
     * Resumes autoplay.
     */
    resumeBrandAutoplay() {

        if (
            !this.heroBrand.state.autoplayRunning
        ) {
            return;
        }

        this.heroBrand.autoplay.paused =
            false;

        this.emit(
            'tefco:brand-slider:autoplay:resumed'
        );

    }

    /**
     * Restarts autoplay.
     */
    restartBrandAutoplay() {

        this.stopBrandAutoplay();

        this.startBrandAutoplay();

    }

    /**
     * Binds hover events.
     */
    bindBrandAutoplayHover() {

        const section =
            this.heroBrand.dom.section;

        if (!section) {
            return;
        }

        this.bindBrandEvent(
            section,
            'mouseenter',
            () => this.pauseBrandAutoplay()
        );

        this.bindBrandEvent(
            section,
            'mouseleave',
            () => this.resumeBrandAutoplay()
        );

    }

    /**
     * Handles page visibility.
     *
     * Pauses autoplay while the tab
     * is hidden and resumes it when
     * the page becomes visible.
     */
    handleBrandAutoplayVisibility() {

        if (
            document.hidden
        ) {

            this.pauseBrandAutoplay();

        } else {

            this.resumeBrandAutoplay();

        }

    }

    /**
     * Binds visibility events.
     */
    bindBrandAutoplayVisibility() {

        document.addEventListener(
            'visibilitychange',
            () =>
                this.handleBrandAutoplayVisibility()
        );

    }

    /**
     * Returns autoplay state.
     *
     * @returns {Object}
     */
    getBrandAutoplayState() {

        return {

            enabled:
                this.heroBrand.config.autoplay,

            running:
                this.heroBrand.state.autoplayRunning,

            paused:
                this.heroBrand.autoplay?.paused ?? false,

            interval:
                this.heroBrand.config.autoplaySpeed,

            startedAt:
                this.heroBrand.autoplay?.startedAt ?? null

        };

    }

    /**
     * Returns whether autoplay
     * is currently active.
     *
     * @returns {boolean}
     */
    isBrandAutoplayRunning() {

        return !!(
            this.heroBrand.state.autoplayRunning &&
            !this.heroBrand.autoplay?.paused
        );

    }

    /**
     * Updates autoplay interval.
     *
     * @param {number} speed
     */
    updateBrandAutoplaySpeed(speed) {

        const value =
            Number(speed);

        if (
            Number.isNaN(value) ||
            value <= 0
        ) {
            return;
        }

        this.heroBrand.config.autoplaySpeed =
            value;

        if (
            this.heroBrand.state.autoplayRunning
        ) {

            this.restartBrandAutoplay();

        }

        this.emit(
            'tefco:brand-slider:autoplay:updated',
            {
                speed: value
            }
        );

    }
    /**
     * ========================================================================
     * Part 2G.5
     * Brand Slider Drag / Swipe
     * ------------------------------------------------------------------------
     * Enterprise Drag Engine
     * Mouse + Touch Support
     * Swipe Detection
     * Velocity Calculation
     * Momentum Threshold
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes drag functionality.
     */
    initializeBrandDragging() {

        if (
            !this.isBrandSliderReady() ||
            !this.heroBrand.config.draggable
        ) {
            return;
        }

        this.heroBrand.drag = {

            active: false,

            startX: 0,

            currentX: 0,

            deltaX: 0,

            startTime: 0,

            velocity: 0,

            threshold: 60

        };

        this.bindBrandDragging();

        this.emit(
            'tefco:brand-slider:drag:initialized'
        );

    }

    /**
     * Binds drag events.
     */
    bindBrandDragging() {

        const viewport =
            this.heroBrand.dom.viewport;

        if (!viewport) {
            return;
        }

        this.bindBrandEvent(
            viewport,
            'pointerdown',
            this.handleBrandDragStart.bind(this),
            { passive: true }
        );

        this.bindBrandEvent(
            window,
            'pointermove',
            this.handleBrandDragMove.bind(this),
            { passive: true }
        );

        this.bindBrandEvent(
            window,
            'pointerup',
            this.handleBrandDragEnd.bind(this),
            { passive: true }
        );

        this.bindBrandEvent(
            window,
            'pointercancel',
            this.handleBrandDragEnd.bind(this),
            { passive: true }
        );

    }

    /**
     * Drag start.
     *
     * @param {PointerEvent} event
     */
    handleBrandDragStart(event) {

        if (
            event.button !== undefined &&
            event.button !== 0
        ) {
            return;
        }

        this.heroBrand.drag.active = true;

        this.heroBrand.state.dragging = true;

        this.heroBrand.drag.startX =
            event.clientX;

        this.heroBrand.drag.currentX =
            event.clientX;

        this.heroBrand.drag.deltaX = 0;

        this.heroBrand.drag.startTime =
            performance.now();

        this.stopBrandAutoplay?.();

        this.emit(
            'tefco:brand-slider:drag:start'
        );

    }

    /**
     * Drag move.
     *
     * @param {PointerEvent} event
     */
    handleBrandDragMove(event) {

        if (
            !this.heroBrand.drag.active
        ) {
            return;
        }

        this.heroBrand.drag.currentX =
            event.clientX;

        this.heroBrand.drag.deltaX =
            event.clientX -
            this.heroBrand.drag.startX;

    }

    /**
     * Drag end.
     *
     * @param {PointerEvent} event
     */
    handleBrandDragEnd(event) {

        if (
            !this.heroBrand.drag.active
        ) {
            return;
        }

        this.heroBrand.drag.currentX =
            event.clientX;

        this.heroBrand.drag.deltaX =
            event.clientX -
            this.heroBrand.drag.startX;

        this.heroBrand.drag.velocity =
            this.calculateBrandVelocity();

        this.handleBrandSwipe();

        this.heroBrand.drag.active = false;

        this.heroBrand.state.dragging = false;

        if (
            this.heroBrand.config.autoplay
        ) {
            this.restartBrandAutoplay?.();
        }

        this.emit(
            'tefco:brand-slider:drag:end'
        );

    }

    /**
     * Calculates drag velocity.
     *
     * @returns {number}
     */
    calculateBrandVelocity() {

        const elapsed =
            performance.now() -
            this.heroBrand.drag.startTime;

        if (elapsed <= 0) {
            return 0;
        }

        return (
            this.heroBrand.drag.deltaX /
            elapsed
        );

    }

    /**
     * Handles swipe navigation.
     */
    handleBrandSwipe() {

        const {

            deltaX,

            threshold,

            velocity

        } = this.heroBrand.drag;

        const fastSwipe =
            Math.abs(velocity) > 0.5;

        if (
            Math.abs(deltaX) < threshold &&
            !fastSwipe
        ) {

            this.syncBrandSlider();

            return;

        }

        if (deltaX > 0) {

            this.previousBrandSlide();

        } else {

            this.nextBrandSlide();

        }

        this.emit(
            'tefco:brand-slider:swipe',
            {

                direction:
                    deltaX > 0
                        ? 'previous'
                        : 'next',

                distance:
                    deltaX,

                velocity

            }
        );

    }

    /**
     * Returns drag state.
     *
     * @returns {Object}
     */
    getBrandDragState() {

        return {

            active:
                this.heroBrand.drag?.active ?? false,

            dragging:
                this.heroBrand.state.dragging,

            startX:
                this.heroBrand.drag?.startX ?? 0,

            currentX:
                this.heroBrand.drag?.currentX ?? 0,

            deltaX:
                this.heroBrand.drag?.deltaX ?? 0,

            velocity:
                this.heroBrand.drag?.velocity ?? 0,

            threshold:
                this.heroBrand.drag?.threshold ?? 0

        };

    }

    /**
     * Cancels current drag.
     */
    cancelBrandDrag() {

        if (!this.heroBrand.drag) {
            return;
        }

        this.heroBrand.drag.active = false;

        this.heroBrand.state.dragging = false;

        this.heroBrand.drag.deltaX = 0;

        this.heroBrand.drag.velocity = 0;

        this.syncBrandSlider();

        this.emit(
            'tefco:brand-slider:drag:cancelled'
        );

    }

    /**
     * Returns whether dragging
     * is currently active.
     *
     * @returns {boolean}
     */
    isBrandDragging() {

        return !!(
            this.heroBrand.drag &&
            this.heroBrand.drag.active
        );

    }
    /**
     * ========================================================================
     * Part 2G.6
     * Responsive
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Breakpoint Detection
     * Dynamic Visible Slides
     * Layout Refresh
     * Resize Optimization
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive engine.
     */
    initializeBrandResponsive() {

        if (!this.isBrandSliderReady()) {
            return;
        }

        this.updateBrandBreakpoints();

        this.refreshBrandLayout();

        this.bindBrandResize();

        this.emit(
            'tefco:brand-slider:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindBrandResize() {

        let resizeFrame = null;

        const resizeHandler = () => {

            if (resizeFrame) {
                cancelAnimationFrame(resizeFrame);
            }

            resizeFrame = this.raf(() => {

                resizeFrame = null;

                if (!this.heroBrand || this.heroBrand.destroyed) {
                    return;
                }

                this.updateBrandBreakpoints();
                this.refreshBrandLayout();

            });

        };

        this.bindBrandEvent(
            window,
            'resize',
            resizeHandler
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateBrandBreakpoints() {

        const width = window.innerWidth;

        let visible;

        if (width >= 1200) {

            visible =
                this.heroBrand.config.visibleDesktop;

        } else if (width >= 768) {

            visible =
                this.heroBrand.config.visibleTablet;

        } else {

            visible =
                this.heroBrand.config.visibleMobile;

        }

        this.heroBrand.state.visible =
            Math.max(1, visible);

    }

    /**
     * Calculates visible brands.
     *
     * @returns {number}
     */
    calculateVisibleBrands() {

        return this.heroBrand.state.visible;

    }

    /**
     * Updates slide dimensions.
     */
    updateBrandDimensions() {

        const viewport =
            this.heroBrand.dom.viewport;

        const track =
            this.heroBrand.dom.track;

        const slides =
            this.heroBrand.slides;

        if (
            !viewport ||
            !track ||
            !slides.length
        ) {
            return;
        }

        const visible =
            this.calculateVisibleBrands();

        const width =
            viewport.clientWidth / visible;

        slides.forEach(slide => {

            slide.style.flex =
                `0 0 ${width}px`;

            slide.style.maxWidth =
                `${width}px`;

        });

        this.heroBrand.state.slideWidth =
            width;

    }

    /**
     * Refreshes layout.
     */
    refreshBrandLayout() {

        this.updateBrandDimensions();

        this.syncBrandSlider();

        this.emit(
            'tefco:brand-slider:layout:updated',
            {
                visible:
                    this.heroBrand.state.visible
            }
        );

    }

    /**
     * Forces layout recalculation.
     */
    recalculateBrandLayout() {

        this.updateBrandBreakpoints();

        this.updateBrandDimensions();

        this.updateBrandSlider(false);

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getBrandBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getBrandResponsiveState() {

        return {

            breakpoint:
                this.getBrandBreakpoint(),

            visible:
                this.heroBrand.state.visible,

            slideWidth:
                this.heroBrand.state.slideWidth || 0,

            viewportWidth:
                this.heroBrand.dom.viewport?.clientWidth || 0,

            totalSlides:
                this.heroBrand.state.total

        };

    }

    /**
     * Returns whether
     * responsive engine is ready.
     *
     * @returns {boolean}
     */
    isBrandResponsiveReady() {

        return !!(

            this.isBrandSliderReady() &&
            this.heroBrand.state.visible > 0 &&
            this.heroBrand.state.slideWidth > 0

        );

    }
    /**
     * ========================================================================
     * Part 2G.7
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Intelligent Preloading
     * Image Decoding
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeBrandLazyImages() {

        if (!this.isBrandSliderReady()) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.heroBrand.slides.forEach(slide => {
                this.loadBrandImage(slide);
            });

            return;
        }

        this.heroBrand.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadBrandImage(
                            entry.target
                        );

                        this.heroBrand.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root:
                        this.heroBrand.dom.viewport,

                    rootMargin: '150px',

                    threshold: 0.01
                }

            );

        this.heroBrand.slides.forEach(slide => {

            this.heroBrand.lazyObserver.observe(
                slide
            );

        });

        this.preloadAdjacentBrandImages();

        this.emit(
            'tefco:brand-slider:lazy:initialized'
        );

    }

    /**
     * Loads a Brand image.
     *
     * @param {HTMLElement} slide
     */
    loadBrandImage(slide) {

        if (!slide) {
            return;
        }

        const image =
            slide.querySelector(
                'img[data-src]'
            );

        if (!image) {
            return;
        }

        if (image.dataset.loaded === 'true') {
            return;
        }

        if (image.dataset.src) {
            image.src =
                image.dataset.src;
        }

        if (image.dataset.srcset) {
            image.srcset =
                image.dataset.srcset;
        }

        image.loading = 'eager';

        image.decoding = 'async';

        image.decode?.()
            .catch(() => {})
            .finally(() => {

                image.dataset.loaded =
                    'true';

                image.removeAttribute(
                    'data-src'
                );

                image.removeAttribute(
                    'data-srcset'
                );

                slide.classList.add(
                    'is-loaded'
                );

                this.emit(
                    'tefco:brand-slider:image:loaded',
                    {
                        slide
                    }
                );

            });

    }

    /**
     * Preloads adjacent images.
     */
    preloadAdjacentBrandImages() {

        if (!this.heroBrand.slides.length) {
            return;
        }

        const current =
            this.heroBrand.state.current;

        const next =
            this.getNextBrandIndex
                ? this.getNextBrandIndex(current)
                : current + 1;

        const previous =
            this.getPreviousBrandIndex
                ? this.getPreviousBrandIndex(current)
                : current - 1;

        [
            previous,
            current,
            next
        ].forEach(index => {

            const slide =
                this.heroBrand.slides[index];

            if (!slide) {
                return;
            }

            this.loadBrandImage(slide);

        });

    }

    /**
     * Reloads lazy loading.
     */
    refreshBrandLazyImages() {

        this.initializeBrandLazyImages();

        this.emit(
            'tefco:brand-slider:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectBrandLazyObserver() {

        if (
            this.heroBrand.lazyObserver
        ) {

            this.heroBrand.lazyObserver.disconnect();

            this.heroBrand.lazyObserver =
                null;

        }

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getBrandLazyState() {

        const loaded =
            this.heroBrand.slides.filter(slide =>

                slide.classList.contains(
                    'is-loaded'
                )

            ).length;

        return {

            observer:
                !!this.heroBrand.lazyObserver,

            loaded,

            total:
                this.heroBrand.state.total,

            pending:
                this.heroBrand.state.total -
                loaded

        };

    }

    /**
     * Returns whether lazy loading
     * is initialized.
     *
     * @returns {boolean}
     */
    isBrandLazyReady() {

        return !!(

            this.heroBrand &&
            this.heroBrand.lazyObserver

        );

    }
    /**
     * ========================================================================
     * Part 2G.8
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeBrandAccessibility() {

        if (!this.isBrandSliderReady()) {
            return;
        }

        this.initializeBrandAccessibilityAttributes();

        this.bindBrandKeyboardNavigation();

        this.initializeBrandLiveRegion();

        this.initializeBrandReducedMotion();

        this.emit(
            'tefco:brand-slider:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeBrandAccessibilityAttributes() {

        const {
            section,
            viewport,
            previous,
            next
        } = this.heroBrand.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-roledescription',
            'carousel'
        );

        section?.setAttribute(
            'aria-label',
            'Brand Slider'
        );

        viewport?.setAttribute(
            'tabindex',
            '0'
        );

        this.heroBrand.slides.forEach(
            (slide, index) => {

                slide.setAttribute(
                    'role',
                    'group'
                );

                slide.setAttribute(
                    'aria-roledescription',
                    'slide'
                );

                slide.setAttribute(
                    'aria-label',
                    `${index + 1} of ${this.heroBrand.state.total}`
                );

                slide.setAttribute(
                    'tabindex',
                    index === this.heroBrand.state.current
                        ? '0'
                        : '-1'
                );

            }
        );

        previous?.setAttribute(
            'aria-label',
            'Previous brands'
        );

        next?.setAttribute(
            'aria-label',
            'Next brands'
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindBrandKeyboardNavigation() {

        const viewport =
            this.heroBrand.dom.viewport;

        if (!viewport) {
            return;
        }

        this.bindBrandEvent(
            viewport,
            'keydown',
            event => {

                switch (event.key) {

                    case 'ArrowLeft':

                        event.preventDefault();

                        this.previousBrandSlide();

                        break;

                    case 'ArrowRight':

                        event.preventDefault();

                        this.nextBrandSlide();

                        break;

                    case 'Home':

                        event.preventDefault();

                        this.goToBrandSlide(0);

                        break;

                    case 'End':

                        event.preventDefault();

                        this.goToBrandSlide(
                            this.heroBrand.state.total - 1
                        );

                        break;

                    case ' ':

                    case 'Spacebar':

                        event.preventDefault();

                        if (
                            this.isBrandAutoplayRunning()
                        ) {

                            this.pauseBrandAutoplay();

                        } else {

                            this.resumeBrandAutoplay();

                        }

                        break;

                }

            }
        );

    }

    /**
     * Initializes screen reader live region.
     */
    initializeBrandLiveRegion() {

        const liveRegion =
            document.createElement('div');

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.heroBrand.liveRegion =
            liveRegion;

    }

    /**
     * Announces current slide.
     */
    announceBrandSlide() {

        if (
            !this.heroBrand.liveRegion
        ) {
            return;
        }

        this.heroBrand.liveRegion.textContent =
            `Brand ${this.heroBrand.state.current + 1} of ${this.heroBrand.state.total}`;

    }

    /**
     * Initializes reduced motion support.
     */
    initializeBrandReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.heroBrand.reducedMotion =
            media.matches;

        if (
            this.heroBrand.reducedMotion
        ) {

            this.stopBrandAutoplay();

            this.heroBrand.config.transitionSpeed =
                0;

        }

    }

    /**
     * Updates focus state.
     */
    updateBrandFocus() {

        this.heroBrand.slides.forEach(
            (slide, index) => {

                slide.setAttribute(
                    'tabindex',
                    index === this.heroBrand.state.current
                        ? '0'
                        : '-1'
                );

            }
        );

    }

    /**
     * Focuses current slide.
     */
    focusCurrentBrandSlide() {

        this.heroBrand.slides[
            this.heroBrand.state.current
        ]?.focus();

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getBrandAccessibilityState() {

        return {

            reducedMotion:
                this.heroBrand.reducedMotion,

            liveRegion:
                !!this.heroBrand.liveRegion,

            focusedSlide:
                this.heroBrand.state.current,

            keyboardEnabled: true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBrandAccessibilityReady() {

        return !!(

            this.isBrandSliderReady() &&
            this.heroBrand.liveRegion

        );

    }
    /**
     * ========================================================================
     * Part 2G.9
     * Theme Editor
     * ------------------------------------------------------------------------
     * Enterprise Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Live Preview
     * Inspector Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeBrandThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.bindBrandThemeEditorEvents();

        this.emit(
            'tefco:brand-slider:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindBrandThemeEditorEvents() {

        this.bindBrandEvent(
            document,
            'shopify:section:load',
            this.handleBrandSectionLoad.bind(this)
        );

        this.bindBrandEvent(
            document,
            'shopify:section:unload',
            this.handleBrandSectionUnload.bind(this)
        );

        this.bindBrandEvent(
            document,
            'shopify:section:select',
            this.handleBrandSectionSelect.bind(this)
        );

        this.bindBrandEvent(
            document,
            'shopify:section:deselect',
            this.handleBrandSectionDeselect.bind(this)
        );

        this.bindBrandEvent(
            document,
            'shopify:block:select',
            this.handleBrandBlockSelect.bind(this)
        );

        this.bindBrandEvent(
            document,
            'shopify:block:deselect',
            this.handleBrandBlockDeselect.bind(this)
        );

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleBrandSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-brand-slider]'
            )
        ) {
            return;
        }

        this.cacheBrandSliderDOM();

        this.collectBrandSlides();

        this.refreshBrandSlider();

        this.emit(
            'tefco:brand-slider:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleBrandSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-brand-slider]'
            )
        ) {
            return;
        }

        this.stopBrandAutoplay?.();

        this.disconnectBrandLazyObserver?.();

        this.emit(
            'tefco:brand-slider:section:unloaded'
        );

    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleBrandSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-brand-slider]'
            )
        ) {
            return;
        }

        this.pauseBrandAutoplay?.();

        this.refreshBrandLayout?.();

        this.emit(
            'tefco:brand-slider:section:selected'
        );

    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleBrandSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-brand-slider]'
            )
        ) {
            return;
        }

        this.resumeBrandAutoplay?.();

        this.emit(
            'tefco:brand-slider:section:deselected'
        );

    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleBrandBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-brand-slide]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.brandIndex
            );

        if (
            !Number.isNaN(index)
        ) {

            this.activateBrandSlide(index);

            this.focusCurrentBrandSlide?.();

        }

        this.pauseBrandAutoplay?.();

        this.emit(
            'tefco:brand-slider:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleBrandBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-brand-slide]'
            )
        ) {
            return;
        }

        this.resumeBrandAutoplay?.();

        this.emit(
            'tefco:brand-slider:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshBrandThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.refreshBrandSlider();

        this.refreshBrandLayout?.();

        this.refreshBrandLazyImages?.();

        this.emit(
            'tefco:brand-slider:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getBrandThemeEditorState() {

        return {

            enabled:
                this.isThemeEditor(),

            initialized:
                this.isThemeEditor(),

            currentSlide:
                this.heroBrand.state.current,

            totalSlides:
                this.heroBrand.state.total

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isBrandThemeEditorReady() {

        return !!(

            this.isThemeEditor() &&
            this.isBrandSliderReady()

        );

    }
    /**
     * ========================================================================
     * Part 2G.10
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Health
     * Validation
     * Cleanup
     * Destroy
     * Memory Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs Brand Slider diagnostics.
     *
     * @returns {Object}
     */
    runBrandDiagnostics() {

        return {

            ready:
                this.isBrandSliderReady(),

            initialized:
                this.heroBrand?.initialized ?? false,

            destroyed:
                this.heroBrand?.destroyed ?? false,

            slideCount:
                this.heroBrand?.state?.total ?? 0,

            currentSlide:
                this.heroBrand?.state?.current ?? 0,

            visibleSlides:
                this.heroBrand?.state?.visible ?? 0,

            autoplay:
                this.heroBrand?.state?.autoplayRunning ?? false,

            dragging:
                this.heroBrand?.state?.dragging ?? false,

            translating:
                this.heroBrand?.state?.translating ?? false,

            responsive:
                this.isBrandResponsiveReady?.() ?? false,

            accessibility:
                this.isBrandAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isBrandLazyReady?.() ?? false,

            themeEditor:
                this.isBrandThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Brand Slider.
     *
     * @returns {boolean}
     */
    validateBrandSlider() {

        const dom =
            this.heroBrand?.dom;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.track) {
            return false;
        }

        if (!Array.isArray(this.heroBrand?.slides)) {
            return false;
        }

        if (!this.heroBrand.slides.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logBrandDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Brand Slider'
        );

        console.table(
            this.runBrandDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans inline styles.
     */
    cleanupBrandStyles() {

        this.heroBrand?.slides?.forEach(slide => {

            slide.style.removeProperty(
                'flex'
            );

            slide.style.removeProperty(
                'max-width'
            );

        });

        this.heroBrand?.dom?.track?.style.removeProperty(
            'transform'
        );

        this.heroBrand?.dom?.track?.style.removeProperty(
            'transition'
        );

    }

    /**
     * Cleans runtime state.
     */
    cleanupBrandState() {

        if (!this.heroBrand) {
            return;
        }

        this.heroBrand.state.translating = false;

        this.heroBrand.state.dragging = false;

        this.heroBrand.state.autoplayRunning = false;

    }

    /**
     * Cleans observers.
     */
    cleanupBrandObservers() {

        this.disconnectBrandLazyObserver?.();

    }

    /**
     * Cleans timers.
     */
    cleanupBrandTimers() {

        this.stopBrandAutoplay?.();

    }

    /**
     * Removes accessibility helpers.
     */
    cleanupBrandAccessibility() {

        if (
            this.heroBrand?.liveRegion
        ) {

            this.heroBrand.liveRegion.remove();

            this.heroBrand.liveRegion = null;

        }

    }

    /**
     * Performs Brand cleanup.
     */
    cleanupBrandSlider() {

        this.cleanupBrandTimers();

        this.cleanupBrandEvents();

        this.cleanupBrandNavigation();

        this.cleanupBrandObservers();

        this.cleanupBrandAccessibility();

        this.cleanupBrandStyles();

        this.cleanupBrandState();

        this.emit(
            'tefco:brand-slider:cleanup'
        );

    }

    /**
     * Destroys Brand Slider.
     */
    destroyBrandSlider() {

        if (
            !this.heroBrand ||
            this.heroBrand.destroyed
        ) {
            return;
        }

        this.cleanupBrandSlider();

        this.heroBrand.destroyed = true;

        this.heroBrand.initialized = false;

        this.heroBrand.slides = [];

        this.heroBrand.dom = {};

        this.heroBrand.config = {};

        this.heroBrand.state = {};

        this.emit(
            'tefco:brand-slider:destroyed'
        );

    }

    /**
     * Rebuilds Brand Slider.
     */
    rebuildBrandSlider() {

        this.destroyBrandSlider();

        this.initializeBrandSlider();

        this.initializeBrandSliderEngine();

        this.initializeBrandLoop();

        this.initializeBrandNavigation();

        this.initializeBrandAutoplay();

        this.initializeBrandDragging();

        this.initializeBrandResponsive();

        this.initializeBrandLazyImages();

        this.initializeBrandAccessibility();

        this.initializeBrandThemeEditor();

        this.emit(
            'tefco:brand-slider:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getBrandLifecycleState() {

        return {

            initialized:
                this.heroBrand?.initialized ?? false,

            destroyed:
                this.heroBrand?.destroyed ?? false,

            valid:
                this.validateBrandSlider(),

            diagnostics:
                this.runBrandDiagnostics()

        };

    }

    /**
     * Returns whether the Brand Slider
     * has been destroyed.
     *
     * @returns {boolean}
     */
    isBrandSliderDestroyed() {

        return !!(
            this.heroBrand?.destroyed
        );

    }
    /**
     * ========================================================================
     * Part 3A.1
     * Categories Foundation
     * ------------------------------------------------------------------------
     * Enterprise Categories Foundation
     * Initialization
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Categories.
     */
    
    //----------------------------------------
    // PART 3 — Categories
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Categories
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Categories
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Categories
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Categories
    //----------------------------------------

initializeCategories() {

        this.categories = {

            initialized: false,

            destroyed: false
        };

        this.cacheCategoriesDOM();

        if (!this.categories.dom.section) {
            return;
        }

        this.initializeCategoriesConfig();

        this.initializeCategoriesState();

        this.collectCategoryCards();

        this.categories.initialized = true;

        this.emit(
            'tefco:categories:initialized'
        );

    }

    /**
     * Caches Categories DOM.
     */
    cacheCategoriesDOM() {

        const section =
            document.querySelector(
                '[data-categories]'
            );

        this.categories.dom = {

            section,

            grid:
                section?.querySelector(
                    '[data-categories-grid]'
                ) ?? null,

            cards:
                [],

            headings:
                section?.querySelectorAll(
                    '[data-category-heading]'
                ) ?? [],

            description:
                section?.querySelector(
                    '[data-categories-description]'
                ) ?? null

        };

    }

    /**
     * Initializes Categories configuration.
     */
    initializeCategoriesConfig() {

        const section =
            this.categories.dom.section;

        const dataset =
            section?.dataset ?? {};

        this.categories.config = {

            columnsDesktop:
                Number(
                    dataset.desktopColumns
                ) || 4,

            columnsTablet:
                Number(
                    dataset.tabletColumns
                ) || 2,

            columnsMobile:
                Number(
                    dataset.mobileColumns
                ) || 1,

            equalHeight:
                dataset.equalHeight !== 'false',

            hoverEffect:
                dataset.hoverEffect !== 'false',

            lazyLoad:
                dataset.lazyLoad !== 'false',

            animation:
                dataset.animation !== 'false',

            animationDelay:
                Number(
                    dataset.animationDelay
                ) || 100

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeCategoriesState() {

        this.categories.state = {

            total: 0,

            visible: 0,

            columns: 0,

            hovered: null,

            active: null,

            animating: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects category cards.
     */
    collectCategoryCards() {

        this.categories.cards = Array.from(

            this.categories.dom.grid
                ?.querySelectorAll(
                    '[data-category-card]'
                ) ?? []

        );

        this.categories.cards.forEach(
            (card, index) => {

                card.dataset.categoryIndex =
                    index;

            }
        );

        this.categories.state.total =
            this.categories.cards.length;

    }

    /**
     * Returns Categories section.
     *
     * @returns {HTMLElement|null}
     */
    getCategoriesElement() {

        return this.categories.dom.section;

    }

    /**
     * Returns Categories grid.
     *
     * @returns {HTMLElement|null}
     */
    getCategoriesGrid() {

        return this.categories.dom.grid;

    }

    /**
     * Returns category cards.
     *
     * @returns {HTMLElement[]}
     */
    getCategoryCards() {

        return this.categories.cards;

    }

    /**
     * Returns total categories.
     *
     * @returns {number}
     */
    getCategoryCount() {

        return this.categories.state.total;

    }

    /**
     * Returns Categories configuration.
     *
     * @returns {Object}
     */
    getCategoriesConfig() {

        return {
            ...this.categories.config
        };

    }

    /**
     * Returns Categories state.
     *
     * @returns {Object}
     */
    getCategoriesState() {

        return {

            initialized:
                this.categories.initialized,

            destroyed:
                this.categories.destroyed,

            total:
                this.categories.state.total,

            visible:
                this.categories.state.visible,

            columns:
                this.categories.state.columns,

            hovered:
                this.categories.state.hovered,

            active:
                this.categories.state.active,

            animating:
                this.categories.state.animating

        };

    }

    /**
     * Returns active category.
     *
     * @returns {number|null}
     */
    getActiveCategory() {

        return this.categories.state.active;

    }

    /**
     * Sets active category.
     *
     * @param {number|null} index
     */
    setActiveCategory(index) {

        this.categories.state.active =
            index;

    }

    /**
     * Returns whether Categories
     * are ready.
     *
     * @returns {boolean}
     */
    isCategoriesReady() {

        return !!(

            this.categories &&
            this.categories.initialized &&
            this.categories.dom.section &&
            this.categories.dom.grid &&
            this.categories.state.total > 0

        );

    }
    /**
     * ========================================================================
     * Part 3A.2
     * Categories Grid Engine
     * ------------------------------------------------------------------------
     * Enterprise Grid Engine
     * Card Management
     * Active State
     * Layout Synchronization
     * Runtime Updates
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Categories Grid Engine.
     */
    initializeCategoriesGrid() {

        if (!this.isCategoriesReady()) {
            return;
        }

        this.prepareCategoryCards();

        this.calculateCategoryGrid();

        this.activateCategory(null);

        this.emit(
            'tefco:categories:grid:initialized'
        );

    }

    /**
     * Prepares category cards.
     */
    prepareCategoryCards() {

        this.categories.cards.forEach(
            (card, index) => {

                card.dataset.categoryIndex =
                    index;

                card.classList.remove(
                    'is-active',
                    'is-hovered'
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Calculates grid layout.
     */
    calculateCategoryGrid() {

        const columns =
            this.getCurrentCategoryColumns();

        this.categories.state.columns =
            columns;

        const grid =
            this.categories.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--categories-columns',
            columns
        );

    }

    /**
     * Activates a category.
     *
     * @param {number|null} index
     */
    activateCategory(index) {

        this.setActiveCategory(index);

        this.categories.cards.forEach(
            (card, cardIndex) => {

                const active =
                    cardIndex === index;

                card.classList.toggle(
                    'is-active',
                    active
                );

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );

        this.emit(
            'tefco:categories:activated',
            {
                active: index
            }
        );

    }

    /**
     * Activates category by element.
     *
     * @param {HTMLElement} card
     */
    activateCategoryCard(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.categoryIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateCategory(index);

    }

    /**
     * Deactivates all categories.
     */
    deactivateCategories() {

        this.activateCategory(null);

    }

    /**
     * Updates grid.
     */
    updateCategoriesGrid() {

        this.calculateCategoryGrid();

        this.updateCategoryVisibility();

        this.emit(
            'tefco:categories:grid:updated'
        );

    }

    /**
     * Synchronizes grid state.
     */
    syncCategoriesGrid() {

        this.calculateCategoryGrid();

        this.emit(
            'tefco:categories:grid:synchronized'
        );

    }

    /**
     * Refreshes Categories grid.
     */
    refreshCategoriesGrid() {

        this.collectCategoryCards();

        this.prepareCategoryCards();

        this.syncCategoriesGrid();

        this.emit(
            'tefco:categories:grid:refreshed'
        );

    }

    /**
     * Updates visible categories.
     */
    updateCategoryVisibility() {

        let visible = 0;

        this.categories.cards.forEach(card => {

            const hidden =
                card.offsetParent === null;

            card.classList.toggle(
                'is-visible',
                !hidden
            );

            if (!hidden) {
                visible++;
            }

        });

        this.categories.state.visible =
            visible;

    }

    /**
     * Returns visible category cards.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleCategoryCards() {

        return this.categories.cards.filter(
            card =>
                card.classList.contains(
                    'is-visible'
                )
        );

    }

    /**
     * Returns current grid columns.
     *
     * @returns {number}
     */
    getCurrentCategoryColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.categories.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.categories.config.columnsTablet;

        }

        return this.categories.config.columnsMobile;

    }

    /**
     * Returns Grid Engine state.
     *
     * @returns {Object}
     */
    getCategoriesGridState() {

        return {

            active:
                this.categories.state.active,

            total:
                this.categories.state.total,

            visible:
                this.categories.state.visible,

            columns:
                this.categories.state.columns,

            animating:
                this.categories.state.animating

        };

    }

    /**
     * Returns whether Grid Engine
     * is ready.
     *
     * @returns {boolean}
     */
    isCategoriesGridReady() {

        return !!(

            this.isCategoriesReady() &&
            this.categories.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3A.3
     * Categories Hover Effects
     * ------------------------------------------------------------------------
     * Enterprise Hover Engine
     * Pointer Interaction
     * Active Hover States
     * Animation Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes hover effects.
     */
    initializeCategoryHoverEffects() {

        if (
            !this.isCategoriesReady() ||
            !this.categories.config.hoverEffect
        ) {
            return;
        }

        this.bindCategoryHoverEvents();

        this.emit(
            'tefco:categories:hover:initialized'
        );

    }

    /**
     * Registers a Category Grid event listener and tracks it for cleanup.
     */
    bindCategoryEvent(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        this.categories.events =
            this.categories.events || [];

        target.addEventListener(
            event,
            handler,
            options
        );

        this.categories.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every Category Grid event listener.
     */
    cleanupCategoryEvents() {

        if (!this.categories?.events) {
            return;
        }

        this.categories.events.forEach(listener => {

            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );

        });

        this.categories.events.length = 0;

    }

    /**
     * Binds hover events.
     */
    bindCategoryHoverEvents() {

        this.categories.cards.forEach(card => {

            this.bindCategoryEvent(
                card,
                'mouseenter',
                () => this.handleCategoryHover(card)
            );

            this.bindCategoryEvent(
                card,
                'mouseleave',
                () => this.handleCategoryLeave(card)
            );

            this.bindCategoryEvent(
                card,
                'focus',
                () => this.handleCategoryHover(card)
            );

            this.bindCategoryEvent(
                card,
                'blur',
                () => this.handleCategoryLeave(card)
            );

        });

    }

    /**
     * Handles category hover.
     *
     * @param {HTMLElement} card
     */
    handleCategoryHover(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.categoryIndex
            );

        this.categories.state.hovered =
            index;

        card.classList.add(
            'is-hovered'
        );

        this.categories.state.animating =
            true;

        this.emit(
            'tefco:categories:hover',
            {
                index
            }
        );

    }

    /**
     * Handles category leave.
     *
     * @param {HTMLElement} card
     */
    handleCategoryLeave(card) {

        if (!card) {
            return;
        }

        card.classList.remove(
            'is-hovered'
        );

        this.categories.state.hovered =
            null;

        this.categories.state.animating =
            false;

        this.emit(
            'tefco:categories:hover:end'
        );

    }

    /**
     * Removes hover state
     * from all category cards.
     */
    clearCategoryHover() {

        this.categories.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.categories.state.hovered =
            null;

    }

    /**
     * Highlights a category.
     *
     * @param {number} index
     */
    highlightCategory(index) {

        const card =
            this.categories.cards[index];

        if (!card) {
            return;
        }

        this.clearCategoryHover();

        this.handleCategoryHover(card);

    }

    /**
     * Updates hover effects.
     */
    updateCategoryHoverEffects() {

        if (
            !this.categories.config.hoverEffect
        ) {
            return;
        }

        this.clearCategoryHover();

        this.emit(
            'tefco:categories:hover:updated'
        );

    }

    /**
     * Refreshes hover engine.
     */
    refreshCategoryHoverEffects() {

        this.clearCategoryHover();

        this.emit(
            'tefco:categories:hover:refreshed'
        );

    }

    /**
     * Enables hover effects.
     */
    enableCategoryHoverEffects() {

        this.categories.config.hoverEffect =
            true;

        this.emit(
            'tefco:categories:hover:enabled'
        );

    }

    /**
     * Disables hover effects.
     */
    disableCategoryHoverEffects() {

        this.categories.config.hoverEffect =
            false;

        this.clearCategoryHover();

        this.emit(
            'tefco:categories:hover:disabled'
        );

    }

    /**
     * Returns hovered category.
     *
     * @returns {number|null}
     */
    getHoveredCategory() {

        return this.categories.state.hovered;

    }

    /**
     * Returns hover engine state.
     *
     * @returns {Object}
     */
    getCategoryHoverState() {

        return {

            enabled:
                this.categories.config.hoverEffect,

            hovered:
                this.categories.state.hovered,

            active:
                this.categories.state.active,

            animating:
                this.categories.state.animating

        };

    }

    /**
     * Returns whether hover
     * engine is ready.
     *
     * @returns {boolean}
     */
    isCategoryHoverReady() {

        return !!(

            this.isCategoriesReady() &&
            this.categories.config.hoverEffect

        );

    }
    /**
     * ========================================================================
     * Part 3A.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Layout Engine
     * Breakpoint Detection
     * Dynamic Grid Columns
     * Layout Synchronization
     * Resize Optimization
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeCategoriesResponsive() {

        if (!this.isCategoriesReady()) {
            return;
        }

        this.updateCategoryBreakpoints();

        this.refreshCategoriesLayout();

        this.bindCategoriesResize();

        this.emit(
            'tefco:categories:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindCategoriesResize() {

        let resizeFrame = null;

        const handleResize = () => {

            if (resizeFrame) {
                this.cancelRaf?.(resizeFrame);
            }

            resizeFrame = this.raf(() => {

                resizeFrame = null;

                if (!this.isCategoriesReady()) {
                    return;
                }

                this.updateCategoryBreakpoints();

                this.refreshCategoriesLayout();

            });

        };

        this.bindCategoryEvent(
            window,
            'resize',
            handleResize
        );

    }

    /**
     * Updates responsive breakpoints.
     */
    updateCategoryBreakpoints() {

        const width =
            window.innerWidth;

        let columns;

        if (width >= 1200) {

            columns =
                this.categories.config.columnsDesktop;

        } else if (width >= 768) {

            columns =
                this.categories.config.columnsTablet;

        } else {

            columns =
                this.categories.config.columnsMobile;

        }

        this.categories.state.columns =
            Math.max(1, columns);

    }

    /**
     * Calculates current columns.
     *
     * @returns {number}
     */
    calculateCategoryColumns() {

        return this.categories.state.columns;

    }

    /**
     * Updates category layout.
     */
    updateCategoriesLayout() {

        const grid =
            this.categories.dom.grid;

        if (!grid) {
            return;
        }

        const columns =
            this.calculateCategoryColumns();

        grid.style.setProperty(
            '--categories-columns',
            columns
        );

        this.categories.cards.forEach(card => {

            card.style.setProperty(
                '--category-columns',
                columns
            );

        });

    }

    /**
     * Refreshes responsive layout.
     */
    refreshCategoriesLayout() {

        this.updateCategoriesLayout();

        this.updateCategoryVisibility();

        this.emit(
            'tefco:categories:layout:updated',
            {
                columns:
                    this.categories.state.columns
            }
        );

    }

    /**
     * Forces layout recalculation.
     */
    recalculateCategoriesLayout() {

        this.updateCategoryBreakpoints();

        this.updateCategoriesLayout();

        this.updateCategoryVisibility();

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getCategoriesBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getCategoriesResponsiveState() {

        return {

            breakpoint:
                this.getCategoriesBreakpoint(),

            columns:
                this.categories.state.columns,

            visible:
                this.categories.state.visible,

            total:
                this.categories.state.total,

            viewportWidth:
                window.innerWidth

        };

    }

    /**
     * Returns whether responsive
     * layout is ready.
     *
     * @returns {boolean}
     */
    isCategoriesResponsiveReady() {

        return !!(

            this.isCategoriesReady() &&
            this.categories.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3A.5
     * Categories Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeCategoriesLazyLoading() {

        if (
            !this.isCategoriesReady() ||
            !this.categories.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.categories.cards.forEach(card => {
                this.loadCategoryCard(card);
            });

            return;

        }

        this.categories.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadCategoryCard(
                            entry.target
                        );

                        this.categories.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05
                }

            );

        this.categories.cards.forEach(card => {

            this.categories.lazyObserver.observe(
                card
            );

        });

        this.preloadVisibleCategories();

        this.emit(
            'tefco:categories:lazy:initialized'
        );

    }

    /**
     * Loads category assets.
     *
     * @param {HTMLElement} card
     */
    loadCategoryCard(card) {

        if (!card) {
            return;
        }

        if (
            card.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            card.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        card.dataset.loaded = 'true';

        card.classList.add(
            'is-loaded'
        );

        this.emit(
            'tefco:categories:card:loaded',
            {
                card
            }
        );

    }

    /**
     * Preloads visible categories.
     */
    preloadVisibleCategories() {

        this.getVisibleCategoryCards()
            .forEach(card => {

                this.loadCategoryCard(card);

            });

    }

    /**
     * Preloads categories
     * near the viewport.
     */
    preloadAdjacentCategories() {

        const visible =
            this.getVisibleCategoryCards();

        visible.forEach(card => {

            const next =
                card.nextElementSibling;

            if (
                next?.matches(
                    '[data-category-card]'
                )
            ) {

                this.loadCategoryCard(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshCategoriesLazyLoading() {

        this.disconnectCategoriesLazyObserver();

        this.initializeCategoriesLazyLoading();

        this.emit(
            'tefco:categories:lazy:refreshed'
        );

    }

    /**
     * Disconnects observer.
     */
    disconnectCategoriesLazyObserver() {

        if (
            this.categories.lazyObserver
        ) {

            this.categories.lazyObserver.disconnect();

            this.categories.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded cards.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedCategoryCards() {

        return this.categories.cards.filter(
            card =>
                card.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending cards.
     *
     * @returns {HTMLElement[]}
     */
    getPendingCategoryCards() {

        return this.categories.cards.filter(
            card =>
                card.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getCategoriesLazyState() {

        return {

            enabled:
                this.categories.config.lazyLoad,

            observer:
                !!this.categories.lazyObserver,

            loaded:
                this.getLoadedCategoryCards()
                    .length,

            pending:
                this.getPendingCategoryCards()
                    .length,

            total:
                this.categories.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * is ready.
     *
     * @returns {boolean}
     */
    isCategoriesLazyReady() {

        return !!(

            this.isCategoriesReady() &&
            this.categories.config.lazyLoad

        );

    }
    /**
     * ========================================================================
     * Part 3A.6
     * Categories Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeCategoriesAccessibility() {

        if (!this.isCategoriesReady()) {
            return;
        }

        this.initializeCategoriesAccessibilityAttributes();

        this.bindCategoriesKeyboardNavigation();

        this.initializeCategoriesLiveRegion();

        this.initializeCategoriesReducedMotion();

        this.emit(
            'tefco:categories:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeCategoriesAccessibilityAttributes() {

        const {
            section,
            grid
        } = this.categories.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Categories'
        );

        grid?.setAttribute(
            'role',
            'list'
        );

        this.categories.cards.forEach(
            (card, index) => {

                card.setAttribute(
                    'role',
                    'listitem'
                );

                card.setAttribute(
                    'tabindex',
                    '0'
                );

                card.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                card.setAttribute(
                    'aria-setsize',
                    this.categories.state.total
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindCategoriesKeyboardNavigation() {

        const grid =
            this.categories.dom.grid;

        if (!grid) {
            return;
        }

        const handleKeydown = event => {

                const current =
                    this.getActiveCategory() ?? 0;

                let next =
                    current;

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        next =
                            Math.min(
                                current + 1,
                                this.categories.state.total - 1
                            );

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        next =
                            Math.max(
                                current - 1,
                                0
                            );

                        break;

                    case 'ArrowDown':

                        event.preventDefault();

                        next =
                            Math.min(
                                current +
                                this.categories.state.columns,
                                this.categories.state.total - 1
                            );

                        break;

                    case 'ArrowUp':

                        event.preventDefault();

                        next =
                            Math.max(
                                current -
                                this.categories.state.columns,
                                0
                            );

                        break;

                    case 'Home':

                        event.preventDefault();

                        next = 0;

                        break;

                    case 'End':

                        event.preventDefault();

                        next =
                            this.categories.state.total - 1;

                        break;

                    case 'Enter':

                    case ' ':

                    case 'Spacebar':

                        event.preventDefault();

                        this.categories.cards[
                            current
                        ]?.click();

                        return;

                    default:

                        return;

                }

                this.activateCategory(next);

                this.focusCategory(next);

            };

        this.bindCategoryEvent(
            grid,
            'keydown',
            handleKeydown
        );

    }

    /**
     * Initializes live region.
     */
    initializeCategoriesLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.categories.liveRegion =
            liveRegion;

    }

    /**
     * Announces active category.
     */
    announceCategory(index) {

        if (
            !this.categories.liveRegion
        ) {
            return;
        }

        const total =
            this.categories.state.total;

        this.categories.liveRegion.textContent =
            `Category ${index + 1} of ${total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeCategoriesReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.categories.reducedMotion =
            media.matches;

        if (
            this.categories.reducedMotion
        ) {

            this.categories.cards.forEach(
                card => {

                    card.style.transition =
                        'none';

                    card.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses a category.
     *
     * @param {number} index
     */
    focusCategory(index) {

        this.categories.cards[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility state.
     */
    updateCategoriesAccessibility() {

        this.categories.cards.forEach(
            (card, index) => {

                const active =
                    index ===
                    this.categories.state.active;

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

                card.setAttribute(
                    'tabindex',
                    active
                        ? '0'
                        : '-1'
                );

            }
        );

        if (
            this.categories.state.active !== null
        ) {

            this.announceCategory(
                this.categories.state.active
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getCategoriesAccessibilityState() {

        return {

            reducedMotion:
                this.categories.reducedMotion,

            liveRegion:
                !!this.categories.liveRegion,

            active:
                this.categories.state.active,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isCategoriesAccessibilityReady() {

        return !!(

            this.isCategoriesReady() &&
            this.categories.liveRegion

        );

    }
    /**
     * ========================================================================
     * Part 3A.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Enterprise Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Live Preview
     * Inspector Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeCategoriesThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.bindCategoriesThemeEditorEvents();

        this.emit(
            'tefco:categories:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindCategoriesThemeEditorEvents() {

        this.bindCategoryEvent(
            document,
            'shopify:section:load',
            this.handleCategoriesSectionLoad.bind(this)
        );

        this.bindCategoryEvent(
            document,
            'shopify:section:unload',
            this.handleCategoriesSectionUnload.bind(this)
        );

        this.bindCategoryEvent(
            document,
            'shopify:section:select',
            this.handleCategoriesSectionSelect.bind(this)
        );

        this.bindCategoryEvent(
            document,
            'shopify:section:deselect',
            this.handleCategoriesSectionDeselect.bind(this)
        );

        this.bindCategoryEvent(
            document,
            'shopify:block:select',
            this.handleCategoryBlockSelect.bind(this)
        );

        this.bindCategoryEvent(
            document,
            'shopify:block:deselect',
            this.handleCategoryBlockDeselect.bind(this)
        );

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleCategoriesSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-categories]'
            )
        ) {
            return;
        }

        this.cacheCategoriesDOM();

        this.collectCategoryCards();

        this.refreshCategoriesGrid();

        this.emit(
            'tefco:categories:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleCategoriesSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-categories]'
            )
        ) {
            return;
        }

        this.disconnectCategoriesLazyObserver?.();

        this.emit(
            'tefco:categories:section:unloaded'
        );

    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleCategoriesSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-categories]'
            )
        ) {
            return;
        }

        this.refreshCategoriesLayout?.();

        this.emit(
            'tefco:categories:section:selected'
        );

    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleCategoriesSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-categories]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:categories:section:deselected'
        );

    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleCategoryBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-category-card]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.categoryIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateCategory(index);

        this.focusCategory?.(index);

        this.emit(
            'tefco:categories:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleCategoryBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-category-card]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:categories:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshCategoriesThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.refreshCategoriesGrid();

        this.refreshCategoriesLayout?.();

        this.refreshCategoriesLazyLoading?.();

        this.emit(
            'tefco:categories:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getCategoriesThemeEditorState() {

        return {

            enabled:
                this.isThemeEditor(),

            initialized:
                this.isThemeEditor(),

            activeCategory:
                this.categories.state.active,

            totalCategories:
                this.categories.state.total

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isCategoriesThemeEditorReady() {

        return !!(

            this.isThemeEditor() &&
            this.isCategoriesReady()

        );

    }
    /**
     * ========================================================================
     * Part 3A.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Health
     * Validation
     * Cleanup
     * Destroy
     * Memory Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs Categories diagnostics.
     *
     * @returns {Object}
     */
    runCategoriesDiagnostics() {

        return {

            ready:
                this.isCategoriesReady(),

            initialized:
                this.categories?.initialized ?? false,

            destroyed:
                this.categories?.destroyed ?? false,

            total:
                this.categories?.state?.total ?? 0,

            visible:
                this.categories?.state?.visible ?? 0,

            active:
                this.categories?.state?.active,

            hovered:
                this.categories?.state?.hovered,

            columns:
                this.categories?.state?.columns ?? 0,

            animating:
                this.categories?.state?.animating ?? false,

            responsive:
                this.isCategoriesResponsiveReady?.() ?? false,

            accessibility:
                this.isCategoriesAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isCategoriesLazyReady?.() ?? false,

            themeEditor:
                this.isCategoriesThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Categories.
     *
     * @returns {boolean}
     */
    validateCategories() {

        const dom =
            this.categories?.dom;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.grid) {
            return false;
        }

        if (
            !Array.isArray(
                this.categories?.cards
            )
        ) {
            return false;
        }

        if (
            !this.categories.cards.length
        ) {
            return false;
        }

        return true;

    }

    /**
     * Logs Categories diagnostics.
     */
    logCategoriesDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Categories'
        );

        console.table(
            this.runCategoriesDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans inline styles.
     */
    cleanupCategoriesStyles() {

        const grid =
            this.categories?.dom?.grid;

        grid?.style.removeProperty(
            '--categories-columns'
        );

        this.categories?.cards?.forEach(card => {

            card.style.removeProperty(
                '--category-columns'
            );

            card.style.removeProperty(
                'transition'
            );

            card.style.removeProperty(
                'animation'
            );

            card.classList.remove(
                'is-active',
                'is-hovered',
                'is-visible',
                'is-loaded'
            );

        });

    }

    /**
     * Cleans runtime state.
     */
    cleanupCategoriesState() {

        if (!this.categories) {
            return;
        }

        Object.assign(
            this.categories.state,
            {

                active: null,

                hovered: null,

                visible: 0,

                columns: 0,

                animating: false

            }
        );

    }

    /**
     * Cleans observers.
     */
    cleanupCategoriesObservers() {

        this.disconnectCategoriesLazyObserver?.();

    }

    /**
     * Cleans accessibility.
     */
    cleanupCategoriesAccessibility() {

        if (
            this.categories?.liveRegion
        ) {

            this.categories.liveRegion.remove();

            this.categories.liveRegion =
                null;

        }

    }

    /**
     * Performs Categories cleanup.
     */
    cleanupCategories() {

        this.cleanupCategoryEvents();

        this.cleanupCategoriesObservers();

        this.cleanupCategoriesAccessibility();

        this.cleanupCategoriesStyles();

        this.cleanupCategoriesState();

        this.emit(
            'tefco:categories:cleanup'
        );

    }

    /**
     * Destroys Categories.
     */
    destroyCategories() {

        if (
            !this.categories ||
            this.categories.destroyed
        ) {
            return;
        }

        this.cleanupCategories();

        this.categories.initialized =
            false;

        this.categories.destroyed =
            true;

        this.categories.cards = [];

        this.categories.dom = {};

        this.categories.config = {};

        this.categories.state = {};

        this.emit(
            'tefco:categories:destroyed'
        );

    }

    /**
     * Rebuilds Categories.
     */
    rebuildCategories() {

        this.destroyCategories();

        this.initializeCategories();

        this.initializeCategoriesGrid();

        this.initializeCategoryHoverEffects();

        this.initializeCategoriesResponsive();

        this.initializeCategoriesLazyLoading();

        this.initializeCategoriesAccessibility();

        this.initializeCategoriesThemeEditor();

        this.emit(
            'tefco:categories:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getCategoriesLifecycleState() {

        return {

            initialized:
                this.categories?.initialized ?? false,

            destroyed:
                this.categories?.destroyed ?? false,

            valid:
                this.validateCategories(),

            diagnostics:
                this.runCategoriesDiagnostics()

        };

    }

    /**
     * Returns whether Categories
     * have been destroyed.
     *
     * @returns {boolean}
     */
    isCategoriesDestroyed() {

        return !!(
            this.categories?.destroyed
        );

    }
    /**
     * ========================================================================
     
    //----------------------------------------
    // PART 3 — Solutions
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Solutions
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Solutions
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Solutions
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Solutions
    //----------------------------------------

* Part 3B.1
     * Solutions Foundation
     * ------------------------------------------------------------------------
     * Enterprise Solutions Foundation
     * Initialization
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Solutions.
     */
    initializeSolutions() {

        this.solutions = {

            initialized: false,

            destroyed: false,

            events: []

        };

        this.cacheSolutionsDOM();

        if (!this.solutions.dom.section) {
            return;
        }

        this.initializeSolutionsConfig();

        this.initializeSolutionsState();

        this.collectSolutionCards();

        this.solutions.initialized = true;

        this.emit(
            'tefco:solutions:initialized'
        );

    }

    /**
     * Caches Solutions DOM.
     */
    cacheSolutionsDOM() {

        const section =
            document.querySelector(
                '[data-solutions]'
            );

        this.solutions.dom = {

            section,

            grid:
                section?.querySelector(
                    '[data-solutions-grid]'
                ) ?? null,

            cards: [],

            heading:
                section?.querySelector(
                    '[data-solutions-heading]'
                ) ?? null,

            description:
                section?.querySelector(
                    '[data-solutions-description]'
                ) ?? null,

            footer:
                section?.querySelector(
                    '[data-solutions-footer]'
                ) ?? null

        };

    }

    /**
     * Initializes Solutions configuration.
     */
    initializeSolutionsConfig() {

        const section =
            this.solutions.dom.section;

        const dataset =
            section?.dataset ?? {};

        this.solutions.config = {

            columnsDesktop:
                Number(
                    dataset.desktopColumns
                ) || 4,

            columnsTablet:
                Number(
                    dataset.tabletColumns
                ) || 2,

            columnsMobile:
                Number(
                    dataset.mobileColumns
                ) || 1,

            equalHeight:
                dataset.equalHeight !== 'false',

            hoverEffect:
                dataset.hoverEffect !== 'false',

            lazyLoad:
                dataset.lazyLoad !== 'false',

            animation:
                dataset.animation !== 'false',

            animationDelay:
                Number(
                    dataset.animationDelay
                ) || 100

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeSolutionsState() {

        this.solutions.state = {

            total: 0,

            visible: 0,

            columns: 0,

            hovered: null,

            active: null,

            animating: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects solution cards.
     */
    collectSolutionCards() {

        this.solutions.cards = Array.from(

            this.solutions.dom.grid
                ?.querySelectorAll(
                    '[data-solution-card]'
                ) ?? []

        );

        this.solutions.cards.forEach(
            (card, index) => {

                card.dataset.solutionIndex =
                    index;

            }
        );

        this.solutions.state.total =
            this.solutions.cards.length;

    }

    /**
     * Returns Solutions section.
     *
     * @returns {HTMLElement|null}
     */
    getSolutionsElement() {

        return this.solutions.dom.section;

    }

    /**
     * Returns Solutions grid.
     *
     * @returns {HTMLElement|null}
     */
    getSolutionsGrid() {

        return this.solutions.dom.grid;

    }

    /**
     * Returns solution cards.
     *
     * @returns {HTMLElement[]}
     */
    getSolutionCards() {

        return this.solutions.cards;

    }

    /**
     * Returns total solutions.
     *
     * @returns {number}
     */
    getSolutionCount() {

        return this.solutions.state.total;

    }

    /**
     * Returns Solutions configuration.
     *
     * @returns {Object}
     */
    getSolutionsConfig() {

        return {

            ...this.solutions.config

        };

    }

    /**
     * Returns Solutions state.
     *
     * @returns {Object}
     */
    getSolutionsState() {

        return {

            initialized:
                this.solutions.initialized,

            destroyed:
                this.solutions.destroyed,

            total:
                this.solutions.state.total,

            visible:
                this.solutions.state.visible,

            columns:
                this.solutions.state.columns,

            hovered:
                this.solutions.state.hovered,

            active:
                this.solutions.state.active,

            animating:
                this.solutions.state.animating

        };

    }

    /**
     * Returns active solution.
     *
     * @returns {number|null}
     */
    getActiveSolution() {

        return this.solutions.state.active;

    }

    /**
     * Sets active solution.
     *
     * @param {number|null} index
     */
    setActiveSolution(index) {

        this.solutions.state.active =
            index;

    }

    /**
     * Returns whether Solutions
     * are ready.
     *
     * @returns {boolean}
     */
    isSolutionsReady() {

        return !!(

            this.solutions &&
            this.solutions.initialized &&
            this.solutions.dom.section &&
            this.solutions.dom.grid &&
            this.solutions.state.total > 0

        );

    }
    /**
     * ========================================================================
     * Part 3B.2
     * Solutions Card Engine
     * ------------------------------------------------------------------------
     * Enterprise Card Engine
     * Card Management
     * Active State
     * Layout Synchronization
     * Runtime Updates
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Solutions Card Engine.
     */
    initializeSolutionsGrid() {

        if (!this.isSolutionsReady()) {
            return;
        }

        this.prepareSolutionCards();

        this.calculateSolutionsGrid();

        this.activateSolution(null);

        this.emit(
            'tefco:solutions:grid:initialized'
        );

    }

    /**
     * Prepares solution cards.
     */
    prepareSolutionCards() {

        this.solutions.cards.forEach(
            (card, index) => {

                card.dataset.solutionIndex =
                    index;

                card.classList.remove(
                    'is-active',
                    'is-hovered'
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Calculates grid layout.
     */
    calculateSolutionsGrid() {

        const columns =
            this.getCurrentSolutionColumns();

        this.solutions.state.columns =
            columns;

        const grid =
            this.solutions.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--solutions-columns',
            columns
        );

    }

    /**
     * Activates a solution.
     *
     * @param {number|null} index
     */
    activateSolution(index) {

        this.setActiveSolution(index);

        this.solutions.cards.forEach(
            (card, cardIndex) => {

                const active =
                    cardIndex === index;

                card.classList.toggle(
                    'is-active',
                    active
                );

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );

        this.emit(
            'tefco:solutions:activated',
            {
                active: index
            }
        );

    }

    /**
     * Activates solution by element.
     *
     * @param {HTMLElement} card
     */
    activateSolutionCard(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.solutionIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateSolution(index);

    }

    /**
     * Deactivates all solutions.
     */
    deactivateSolutions() {

        this.activateSolution(null);

    }

    /**
     * Updates grid.
     */
    updateSolutionsGrid() {

        this.calculateSolutionsGrid();

        this.updateSolutionVisibility();

        this.emit(
            'tefco:solutions:grid:updated'
        );

    }

    /**
     * Synchronizes grid state.
     */
    syncSolutionsGrid() {

        this.calculateSolutionsGrid();

        this.emit(
            'tefco:solutions:grid:synchronized'
        );

    }

    /**
     * Refreshes Solutions grid.
     */
    refreshSolutionsGrid() {

        this.collectSolutionCards();

        this.prepareSolutionCards();

        this.syncSolutionsGrid();

        this.emit(
            'tefco:solutions:grid:refreshed'
        );

    }

    /**
     * Updates visible solution cards.
     */
    updateSolutionVisibility() {

        let visible = 0;

        this.solutions.cards.forEach(card => {

            const hidden =
                card.offsetParent === null;

            card.classList.toggle(
                'is-visible',
                !hidden
            );

            if (!hidden) {
                visible++;
            }

        });

        this.solutions.state.visible =
            visible;

    }

    /**
     * Returns visible solution cards.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleSolutionCards() {

        return this.solutions.cards.filter(
            card =>
                card.classList.contains(
                    'is-visible'
                )
        );

    }

    /**
     * Returns current grid columns.
     *
     * @returns {number}
     */
    getCurrentSolutionColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.solutions.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.solutions.config.columnsTablet;

        }

        return this.solutions.config.columnsMobile;

    }

    /**
     * Returns Card Engine state.
     *
     * @returns {Object}
     */
    getSolutionsGridState() {

        return {

            active:
                this.solutions.state.active,

            total:
                this.solutions.state.total,

            visible:
                this.solutions.state.visible,

            columns:
                this.solutions.state.columns,

            animating:
                this.solutions.state.animating

        };

    }

    /**
     * Returns whether Card Engine
     * is ready.
     *
     * @returns {boolean}
     */
    isSolutionsGridReady() {

        return !!(

            this.isSolutionsReady() &&
            this.solutions.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3B.3
     * Hover / Animation
     * ------------------------------------------------------------------------
     * Enterprise Hover Engine
     * Card Hover Effects
     * Entrance Animations
     * Active State
     * Motion Preferences
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes hover and animations.
     */
    initializeSolutionHoverEffects() {

        if (
            !this.isSolutionsReady() ||
            !this.solutions.config.hoverEffect
        ) {
            return;
        }

        this.bindSolutionHoverEvents();

        if (
            this.solutions.config.animation
        ) {
            this.initializeSolutionAnimations();
        }

        this.emit(
            'tefco:solutions:hover:initialized'
        );

    }

    /**
     * Registers a Solutions event listener and tracks it for cleanup.
     */
    bindSolutionEvent(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        this.solutions.events = this.solutions.events || [];

        target.addEventListener(event, handler, options);

        this.solutions.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every event listener owned by Solutions.
     */
    cleanupSolutionEvents() {

        if (this.solutions?.resizeTimer !== null &&
            this.solutions?.resizeTimer !== undefined) {

            this.clearTimeout(
                this.solutions.resizeTimer
            );

            this.solutions.resizeTimer = null;
        }

        if (!this.solutions?.events) {
            return;
        }

        this.solutions.events.forEach(listener => {
            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );
        });

        this.solutions.events.length = 0;

    }

    /**
     * Binds hover events.
     */
    bindSolutionHoverEvents() {

        this.solutions.cards.forEach(card => {

            this.bindSolutionEvent(
                card,
                'mouseenter',
                () => this.handleSolutionHover(card)
            );

            this.bindSolutionEvent(
                card,
                'mouseleave',
                () => this.handleSolutionLeave(card)
            );

            this.bindSolutionEvent(
                card,
                'focus',
                () => this.handleSolutionHover(card)
            );

            this.bindSolutionEvent(
                card,
                'blur',
                () => this.handleSolutionLeave(card)
            );

        });

    }

    /**
     * Handles hover.
     *
     * @param {HTMLElement} card
     */
    handleSolutionHover(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.solutionIndex
            );

        this.solutions.state.hovered =
            index;

        this.highlightSolution(card);

        this.emit(
            'tefco:solutions:hover',
            {
                index,
                card
            }
        );

    }

    /**
     * Handles hover leave.
     *
     * @param {HTMLElement} card
     */
    handleSolutionLeave(card) {

        if (!card) {
            return;
        }

        card.classList.remove(
            'is-hovered'
        );

        this.solutions.state.hovered =
            null;

        this.emit(
            'tefco:solutions:hover:end'
        );

    }

    /**
     * Highlights solution.
     *
     * @param {HTMLElement} card
     */
    highlightSolution(card) {

        this.solutions.cards.forEach(item => {

            item.classList.remove(
                'is-hovered'
            );

        });

        card.classList.add(
            'is-hovered'
        );

    }

    /**
     * Initializes entrance animations.
     */
    initializeSolutionAnimations() {

        this.solutions.cards.forEach(
            (card, index) => {

                card.style.setProperty(
                    '--solution-delay',
                    `${index * this.solutions.config.animationDelay}ms`
                );

                card.classList.add(
                    'is-animatable'
                );

            }
        );

    }

    /**
     * Plays entrance animation.
     */
    playSolutionAnimations() {

        if (
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches
        ) {
            return;
        }

        this.solutions.state.animating =
            true;

        this.solutions.cards.forEach(card => {

            requestAnimationFrame(() => {

                card.classList.add(
                    'is-visible'
                );

            });

        });

        const lastCard =
            this.solutions.cards.at(-1);

        if (lastCard) {

            const onAnimationEnd = () => {

                this.solutions.state.animating =
                    false;

                lastCard.removeEventListener(
                    'animationend',
                    onAnimationEnd
                );

                this.emit(
                    'tefco:solutions:animation:complete'
                );

            };

            lastCard.addEventListener(
                'animationend',
                onAnimationEnd
            );

        }

    }

    /**
     * Resets animations.
     */
    resetSolutionAnimations() {

        this.solutions.cards.forEach(card => {

            card.classList.remove(
                'is-visible'
            );

        });

        this.solutions.state.animating =
            false;

    }

    /**
     * Refreshes hover effects.
     */
    refreshSolutionHoverEffects() {

        this.solutions.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.solutions.state.hovered =
            null;

        this.initializeSolutionAnimations();

        this.emit(
            'tefco:solutions:hover:refreshed'
        );

    }

    /**
     * Enables hover effects.
     */
    enableSolutionHoverEffects() {

        this.solutions.config.hoverEffect =
            true;

        this.emit(
            'tefco:solutions:hover:enabled'
        );

    }

    /**
     * Disables hover effects.
     */
    disableSolutionHoverEffects() {

        this.solutions.config.hoverEffect =
            false;

        this.solutions.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.solutions.state.hovered =
            null;

        this.emit(
            'tefco:solutions:hover:disabled'
        );

    }

    /**
     * Returns hover state.
     *
     * @returns {Object}
     */
    getSolutionHoverState() {

        return {

            enabled:
                this.solutions.config.hoverEffect,

            hovered:
                this.solutions.state.hovered,

            animating:
                this.solutions.state.animating

        };

    }

    /**
     * Returns whether hover engine
     * is ready.
     *
     * @returns {boolean}
     */
    isSolutionHoverReady() {

        return !!(

            this.isSolutionsReady() &&
            this.solutions.config.hoverEffect

        );

    }
    /**
     * ========================================================================
     * Part 3B.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Grid Layout
     * Breakpoints
     * Resize Handling
     * Dynamic Columns
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeSolutionsResponsive() {

        if (!this.isSolutionsReady()) {
            return;
        }

        this.bindSolutionsResize();

        this.updateSolutionBreakpoints();

        this.updateSolutionsLayout();

        this.emit(
            'tefco:solutions:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindSolutionsResize() {

        this.solutions.resizeTimer =
            this.solutions.resizeTimer ?? null;

        this.bindSolutionEvent(
            window,
            'resize',
            () => {

                if (this.solutions.resizeTimer !== null) {
                    this.clearTimeout(
                        this.solutions.resizeTimer
                    );
                }

                this.solutions.resizeTimer =
                    this.setTimeout(() => {

                        this.solutions.resizeTimer = null;

                        if (
                            !this.solutions ||
                            this.solutions.destroyed ||
                            !this.solutions.initialized
                        ) {
                            return;
                        }

                        this.updateSolutionBreakpoints();

                        this.updateSolutionsLayout();

                    }, 100);

            }
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateSolutionBreakpoints() {

        this.solutions.state.columns =
            this.calculateSolutionColumns();

        this.emit(
            'tefco:solutions:breakpoint:changed',
            {
                breakpoint:
                    this.getSolutionsBreakpoint(),

                columns:
                    this.solutions.state.columns
            }
        );

    }

    /**
     * Calculates columns.
     *
     * @returns {number}
     */
    calculateSolutionColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.solutions.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.solutions.config.columnsTablet;

        }

        return this.solutions.config.columnsMobile;

    }

    /**
     * Updates responsive layout.
     */
    updateSolutionsLayout() {

        const grid =
            this.solutions.dom.grid;

        if (!grid) {
            return;
        }

        const columns =
            this.solutions.state.columns;

        grid.style.setProperty(
            '--solutions-columns',
            columns
        );

        grid.setAttribute(
            'data-columns',
            columns
        );

        this.updateSolutionVisibility();

        this.emit(
            'tefco:solutions:layout:updated',
            {
                columns
            }
        );

    }

    /**
     * Refreshes responsive layout.
     */
    refreshSolutionsLayout() {

        this.updateSolutionBreakpoints();

        this.updateSolutionsLayout();

        this.emit(
            'tefco:solutions:layout:refreshed'
        );

    }

    /**
     * Recalculates layout.
     */
    recalculateSolutionsLayout() {

        this.collectSolutionCards();

        this.refreshSolutionsLayout();

        this.emit(
            'tefco:solutions:layout:recalculated'
        );

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getSolutionsBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getSolutionsResponsiveState() {

        return {

            breakpoint:
                this.getSolutionsBreakpoint(),

            columns:
                this.solutions.state.columns,

            total:
                this.solutions.state.total,

            visible:
                this.solutions.state.visible

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isSolutionsResponsiveReady() {

        return !!(

            this.isSolutionsReady() &&
            this.solutions.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3B.5
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeSolutionsLazyLoading() {

        if (
            !this.isSolutionsReady() ||
            !this.solutions.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.solutions.cards.forEach(card => {
                this.loadSolutionCard(card);
            });

            return;

        }

        this.solutions.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadSolutionCard(
                            entry.target
                        );

                        this.solutions.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05
                }

            );

        this.solutions.cards.forEach(card => {

            this.solutions.lazyObserver.observe(
                card
            );

        });

        this.preloadVisibleSolutions();

        this.emit(
            'tefco:solutions:lazy:initialized'
        );

    }

    /**
     * Loads solution assets.
     *
     * @param {HTMLElement} card
     */
    loadSolutionCard(card) {

        if (!card) {
            return;
        }

        if (
            card.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            card.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        card.dataset.loaded =
            'true';

        card.classList.add(
            'is-loaded'
        );

        this.emit(
            'tefco:solutions:card:loaded',
            {
                card
            }
        );

    }

    /**
     * Preloads visible solution cards.
     */
    preloadVisibleSolutions() {

        this.getVisibleSolutionCards()
            .forEach(card => {

                this.loadSolutionCard(
                    card
                );

            });

    }

    /**
     * Preloads adjacent solution cards.
     */
    preloadAdjacentSolutions() {

        const visible =
            this.getVisibleSolutionCards();

        visible.forEach(card => {

            const next =
                card.nextElementSibling;

            if (
                next?.matches(
                    '[data-solution-card]'
                )
            ) {

                this.loadSolutionCard(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshSolutionsLazyLoading() {

        this.disconnectSolutionsLazyObserver();

        this.initializeSolutionsLazyLoading();

        this.emit(
            'tefco:solutions:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectSolutionsLazyObserver() {

        if (
            this.solutions.lazyObserver
        ) {

            this.solutions.lazyObserver.disconnect();

            this.solutions.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded solution cards.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedSolutionCards() {

        return this.solutions.cards.filter(
            card =>
                card.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending solution cards.
     *
     * @returns {HTMLElement[]}
     */
    getPendingSolutionCards() {

        return this.solutions.cards.filter(
            card =>
                card.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getSolutionsLazyState() {

        return {

            enabled:
                this.solutions.config.lazyLoad,

            observer:
                !!this.solutions.lazyObserver,

            loaded:
                this.getLoadedSolutionCards()
                    .length,

            pending:
                this.getPendingSolutionCards()
                    .length,

            total:
                this.solutions.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * engine is ready.
     *
     * @returns {boolean}
     */
    isSolutionsLazyReady() {

        return !!(

            this.isSolutionsReady() &&
            this.solutions.config.lazyLoad

        );

    }
    /**
     * ========================================================================
     * Part 3B.6
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeSolutionsAccessibility() {

        if (!this.isSolutionsReady()) {
            return;
        }

        this.initializeSolutionsAccessibilityAttributes();

        this.bindSolutionsKeyboardNavigation();

        this.initializeSolutionsLiveRegion();

        this.initializeSolutionsReducedMotion();

        this.emit(
            'tefco:solutions:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeSolutionsAccessibilityAttributes() {

        const {
            section,
            grid
        } = this.solutions.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Solutions'
        );

        grid?.setAttribute(
            'role',
            'list'
        );

        this.solutions.cards.forEach(
            (card, index) => {

                card.setAttribute(
                    'role',
                    'listitem'
                );

                card.setAttribute(
                    'tabindex',
                    '0'
                );

                card.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                card.setAttribute(
                    'aria-setsize',
                    this.solutions.state.total
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindSolutionsKeyboardNavigation() {

        const grid =
            this.solutions.dom.grid;

        if (!grid) {
            return;
        }

        this.bindSolutionEvent(
            grid,
            'keydown',
            event => {

                const current =
                    this.getActiveSolution() ?? 0;

                let next =
                    current;

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        next =
                            Math.min(
                                current + 1,
                                this.solutions.state.total - 1
                            );

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        next =
                            Math.max(
                                current - 1,
                                0
                            );

                        break;

                    case 'ArrowDown':

                        event.preventDefault();

                        next =
                            Math.min(
                                current +
                                this.solutions.state.columns,
                                this.solutions.state.total - 1
                            );

                        break;

                    case 'ArrowUp':

                        event.preventDefault();

                        next =
                            Math.max(
                                current -
                                this.solutions.state.columns,
                                0
                            );

                        break;

                    case 'Home':

                        event.preventDefault();

                        next = 0;

                        break;

                    case 'End':

                        event.preventDefault();

                        next =
                            this.solutions.state.total - 1;

                        break;

                    case 'Enter':

                    case ' ':

                    case 'Spacebar':

                        event.preventDefault();

                        this.solutions.cards[
                            current
                        ]?.click();

                        return;

                    default:

                        return;

                }

                this.activateSolution(next);

                this.focusSolution(next);

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeSolutionsLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.solutions.liveRegion =
            liveRegion;

    }

    /**
     * Announces active solution.
     *
     * @param {number} index
     */
    announceSolution(index) {

        if (
            !this.solutions.liveRegion
        ) {
            return;
        }

        const total =
            this.solutions.state.total;

        this.solutions.liveRegion.textContent =
            `Solution ${index + 1} of ${total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeSolutionsReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.solutions.reducedMotion =
            media.matches;

        if (
            this.solutions.reducedMotion
        ) {

            this.solutions.cards.forEach(
                card => {

                    card.style.transition =
                        'none';

                    card.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses a solution card.
     *
     * @param {number} index
     */
    focusSolution(index) {

        this.solutions.cards[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility state.
     */
    updateSolutionsAccessibility() {

        this.solutions.cards.forEach(
            (card, index) => {

                const active =
                    index ===
                    this.solutions.state.active;

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

                card.setAttribute(
                    'tabindex',
                    active
                        ? '0'
                        : '-1'
                );

            }
        );

        if (
            this.solutions.state.active !== null
        ) {

            this.announceSolution(
                this.solutions.state.active
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getSolutionsAccessibilityState() {

        return {

            reducedMotion:
                this.solutions.reducedMotion,

            liveRegion:
                !!this.solutions.liveRegion,

            active:
                this.solutions.state.active,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isSolutionsAccessibilityReady() {

        return !!(

            this.isSolutionsReady() &&
            this.solutions.liveRegion

        );

    }
    /**
     * ========================================================================
     * Part 3B.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Enterprise Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Live Preview
     * Inspector Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeSolutionsThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.bindSolutionsThemeEditorEvents();

        this.emit(
            'tefco:solutions:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindSolutionsThemeEditorEvents() {

        this.bindSolutionEvent(
            document,
            'shopify:section:load',
            this.handleSolutionsSectionLoad.bind(this)
        );

        this.bindSolutionEvent(
            document,
            'shopify:section:unload',
            this.handleSolutionsSectionUnload.bind(this)
        );

        this.bindSolutionEvent(
            document,
            'shopify:section:select',
            this.handleSolutionsSectionSelect.bind(this)
        );

        this.bindSolutionEvent(
            document,
            'shopify:section:deselect',
            this.handleSolutionsSectionDeselect.bind(this)
        );

        this.bindSolutionEvent(
            document,
            'shopify:block:select',
            this.handleSolutionBlockSelect.bind(this)
        );

        this.bindSolutionEvent(
            document,
            'shopify:block:deselect',
            this.handleSolutionBlockDeselect.bind(this)
        );

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleSolutionsSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-solutions]'
            )
        ) {
            return;
        }

        // Shopify replaces the section DOM. Tear down listeners/observers
        // that still reference the old nodes, then rebuild the module runtime.
        this.cleanupSolutionEvents();
        this.cleanupSolutionsObservers();
        this.cleanupSolutionsAccessibility();

        this.cacheSolutionsDOM();
        this.initializeSolutionsConfig();
        this.initializeSolutionsState();
        this.collectSolutionCards();

        this.solutions.initialized = true;
        this.solutions.destroyed = false;

        this.initializeSolutionsGrid();
        this.initializeSolutionHoverEffects();
        this.initializeSolutionsResponsive();
        this.initializeSolutionsLazyLoading();
        this.initializeSolutionsAccessibility();

        // cleanupSolutionEvents() also removes Theme Editor listeners, so
        // restore one clean set after the section has been rebuilt.
        this.bindSolutionsThemeEditorEvents();

        this.emit(
            'tefco:solutions:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleSolutionsSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-solutions]'
            )
        ) {
            return;
        }

        this.cleanupSolutionEvents();
        this.cleanupSolutionsObservers();
        this.cleanupSolutionsAccessibility();

        this.emit(
            'tefco:solutions:section:unloaded'
        );

    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleSolutionsSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-solutions]'
            )
        ) {
            return;
        }

        this.refreshSolutionsLayout?.();

        this.emit(
            'tefco:solutions:section:selected'
        );

    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleSolutionsSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-solutions]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:solutions:section:deselected'
        );

    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleSolutionBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-solution-card]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.solutionIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateSolution(index);

        this.focusSolution?.(index);

        this.emit(
            'tefco:solutions:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleSolutionBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-solution-card]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:solutions:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshSolutionsThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.refreshSolutionsGrid();

        this.refreshSolutionsLayout?.();

        this.refreshSolutionsLazyLoading?.();

        this.emit(
            'tefco:solutions:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getSolutionsThemeEditorState() {

        return {

            enabled:
                this.isThemeEditor(),

            initialized:
                this.isThemeEditor(),

            activeSolution:
                this.solutions.state.active,

            totalSolutions:
                this.solutions.state.total

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isSolutionsThemeEditorReady() {

        return !!(

            this.isThemeEditor() &&
            this.isSolutionsReady()

        );

    }
    /**
     * ========================================================================
     * Part 3B.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Health
     * Validation
     * Cleanup
     * Destroy
     * Memory Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * ========================================================================
     * Part 3B.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runSolutionsDiagnostics() {

        return {

            ready:
                this.isSolutionsReady(),

            initialized:
                this.solutions?.initialized ?? false,

            destroyed:
                this.solutions?.destroyed ?? false,

            total:
                this.solutions?.state?.total ?? 0,

            visible:
                this.solutions?.state?.visible ?? 0,

            active:
                this.solutions?.state?.active,

            hovered:
                this.solutions?.state?.hovered,

            columns:
                this.solutions?.state?.columns ?? 0,

            animating:
                this.solutions?.state?.animating ?? false,

            lazyLoaded:
                this.getLoadedSolutionCards?.()
                    ?.length ?? 0,

            pendingLazy:
                this.getPendingSolutionCards?.()
                    ?.length ?? 0,

            responsive:
                this.isSolutionsResponsiveReady?.() ?? false,

            hover:
                this.isSolutionHoverReady?.() ?? false,

            accessibility:
                this.isSolutionsAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isSolutionsLazyReady?.() ?? false,

            themeEditor:
                this.isSolutionsThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Solutions.
     *
     * @returns {boolean}
     */
    validateSolutions() {

        if (!this.solutions) {
            return false;
        }

        const {
            dom,
            cards
        } = this.solutions;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.grid) {
            return false;
        }

        if (!Array.isArray(cards)) {
            return false;
        }

        if (!cards.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logSolutionsDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Solutions Diagnostics'
        );

        console.table(
            this.runSolutionsDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupSolutionsStyles() {

        const grid =
            this.solutions?.dom?.grid;

        grid?.style.removeProperty(
            '--solutions-columns'
        );

        this.solutions?.cards?.forEach(card => {

            card.style.removeProperty(
                '--solution-delay'
            );

            card.style.removeProperty(
                'transition'
            );

            card.style.removeProperty(
                'animation'
            );

            card.classList.remove(
                'is-active',
                'is-hovered',
                'is-visible',
                'is-loaded',
                'is-animatable'
            );

            card.removeAttribute(
                'aria-selected'
            );

            card.removeAttribute(
                'tabindex'
            );

        });

    }

    /**
     * Cleans runtime state.
     */
    cleanupSolutionsState() {

        if (!this.solutions) {
            return;
        }

        this.solutions.state = {

            total: 0,

            visible: 0,

            columns: 0,

            active: null,

            hovered: null,

            animating: false,

            initializedAt: 0

        };

    }

    /**
     * Cleans observers.
     */
    cleanupSolutionsObservers() {

        this.disconnectSolutionsLazyObserver?.();

    }

    /**
     * Cleans accessibility.
     */
    cleanupSolutionsAccessibility() {

        if (
            this.solutions?.liveRegion
        ) {

            this.solutions.liveRegion.remove();

            this.solutions.liveRegion =
                null;

        }

    }

    /**
     * Cleans DOM references.
     */
    cleanupSolutionsDOM() {

        if (!this.solutions) {
            return;
        }

        this.solutions.cards = [];

        this.solutions.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupSolutionsConfig() {

        if (!this.solutions) {
            return;
        }

        this.solutions.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupSolutions() {

        this.cleanupSolutionEvents();

        this.cleanupSolutionsObservers();

        this.cleanupSolutionsAccessibility();

        this.cleanupSolutionsStyles();

        this.cleanupSolutionsState();

        this.cleanupSolutionsDOM();

        this.cleanupSolutionsConfig();

        this.emit(
            'tefco:solutions:cleanup'
        );

    }

    /**
     * Destroys Solutions.
     */
    destroySolutions() {

        if (
            !this.solutions ||
            this.solutions.destroyed
        ) {
            return;
        }

        this.cleanupSolutions();

        this.solutions.initialized =
            false;

        this.solutions.destroyed =
            true;

        this.emit(
            'tefco:solutions:destroyed'
        );

    }

    /**
     * Rebuilds Solutions.
     */
    rebuildSolutions() {

        this.destroySolutions();

        this.initializeSolutions();

        this.initializeSolutionsGrid();

        this.initializeSolutionHoverEffects();

        this.initializeSolutionsResponsive();

        this.initializeSolutionsLazyLoading();

        this.initializeSolutionsAccessibility();

        this.initializeSolutionsThemeEditor();

        this.emit(
            'tefco:solutions:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getSolutionsLifecycleState() {

        return {

            initialized:
                this.solutions?.initialized ?? false,

            destroyed:
                this.solutions?.destroyed ?? false,

            valid:
                this.validateSolutions(),

            diagnostics:
                this.runSolutionsDiagnostics()

        };

    }

    /**
     * Returns whether destroyed.
     *
     * @returns {boolean}
     */
    isSolutionsDestroyed() {

        return !!(
            this.solutions?.destroyed
        );

    }
    /**
     * ========================================================================
     * Part 3C.1
     * Banner Grid Foundation
     * ------------------------------------------------------------------------
     * Enterprise Banner Grid Foundation
     * Initialization
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Banner Grid.
     */
    
    //----------------------------------------
    // PART 3 — Banner Grid
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Banner Grid
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Banner Grid
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Banner Grid
    //----------------------------------------


    //----------------------------------------
    // PART 3 — Banner Grid
    //----------------------------------------

initializeBannerGrid() {

        this.bannerGrid = {

            initialized: false,

            destroyed: false,

            events: [],

            resizeTimer: null

        };

        this.cacheBannerGridDOM();

        if (!this.bannerGrid.dom.section) {
            return;
        }

        this.initializeBannerGridConfig();

        this.initializeBannerGridState();

        this.collectBannerCards();

        this.bannerGrid.initialized = true;

        this.emit(
            'tefco:banner-grid:initialized'
        );

    }

    /**
     * Caches Banner Grid DOM.
     */
    cacheBannerGridDOM() {

        const section =
            document.querySelector(
                '[data-banner-grid]'
            );

        this.bannerGrid.dom = {

            section,

            grid:
                section?.querySelector(
                    '[data-banner-grid-container]'
                ) ?? null,

            cards: [],

            heading:
                section?.querySelector(
                    '[data-banner-grid-heading]'
                ) ?? null,

            description:
                section?.querySelector(
                    '[data-banner-grid-description]'
                ) ?? null,

            footer:
                section?.querySelector(
                    '[data-banner-grid-footer]'
                ) ?? null

        };

    }

    /**
     * Initializes Banner Grid configuration.
     */
    initializeBannerGridConfig() {

        const section =
            this.bannerGrid.dom.section;

        const dataset =
            section?.dataset ?? {};

        this.bannerGrid.config = {

            columnsDesktop:
                Number(
                    dataset.desktopColumns
                ) || 2,

            columnsTablet:
                Number(
                    dataset.tabletColumns
                ) || 2,

            columnsMobile:
                Number(
                    dataset.mobileColumns
                ) || 1,

            equalHeight:
                dataset.equalHeight !== 'false',

            hoverEffect:
                dataset.hoverEffect !== 'false',

            lazyLoad:
                dataset.lazyLoad !== 'false',

            animation:
                dataset.animation !== 'false',

            animationDelay:
                Number(
                    dataset.animationDelay
                ) || 120

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeBannerGridState() {

        this.bannerGrid.state = {

            total: 0,

            visible: 0,

            columns: 0,

            hovered: null,

            active: null,

            animating: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects banner cards.
     */
    collectBannerCards() {

        this.bannerGrid.cards = Array.from(

            this.bannerGrid.dom.grid
                ?.querySelectorAll(
                    '[data-banner-card]'
                ) ?? []

        );

        this.bannerGrid.cards.forEach(
            (card, index) => {

                card.dataset.bannerIndex =
                    index;

            }
        );

        this.bannerGrid.state.total =
            this.bannerGrid.cards.length;

    }

    /**
     * Returns Banner Grid section.
     *
     * @returns {HTMLElement|null}
     */
    getBannerGridElement() {

        return this.bannerGrid.dom.section;

    }

    /**
     * Returns Banner Grid container.
     *
     * @returns {HTMLElement|null}
     */
    getBannerGridContainer() {

        return this.bannerGrid.dom.grid;

    }

    /**
     * Returns banner cards.
     *
     * @returns {HTMLElement[]}
     */
    getBannerCards() {

        return this.bannerGrid.cards;

    }

    /**
     * Returns banner count.
     *
     * @returns {number}
     */
    getBannerCount() {

        return this.bannerGrid.state.total;

    }

    /**
     * Returns Banner Grid configuration.
     *
     * @returns {Object}
     */
    getBannerGridConfig() {

        return {

            ...this.bannerGrid.config

        };

    }

    /**
     * Returns Banner Grid state.
     *
     * @returns {Object}
     */
    getBannerGridState() {

        return {

            initialized:
                this.bannerGrid.initialized,

            destroyed:
                this.bannerGrid.destroyed,

            total:
                this.bannerGrid.state.total,

            visible:
                this.bannerGrid.state.visible,

            columns:
                this.bannerGrid.state.columns,

            hovered:
                this.bannerGrid.state.hovered,

            active:
                this.bannerGrid.state.active,

            animating:
                this.bannerGrid.state.animating

        };

    }

    /**
     * Returns active banner.
     *
     * @returns {number|null}
     */
    getActiveBanner() {

        return this.bannerGrid.state.active;

    }

    /**
     * Sets active banner.
     *
     * @param {number|null} index
     */
    setActiveBanner(index) {

        this.bannerGrid.state.active =
            index;

    }

    /**
     * Returns whether Banner Grid
     * is ready.
     *
     * @returns {boolean}
     */
    isBannerGridReady() {

        return !!(

            this.bannerGrid &&
            this.bannerGrid.initialized &&
            this.bannerGrid.dom.section &&
            this.bannerGrid.dom.grid &&
            this.bannerGrid.state.total > 0

        );

    }
    /**
     * ========================================================================
     * Part 3C.2
     * Banner Engine
     * ------------------------------------------------------------------------
     * Enterprise Banner Engine
     * Banner Management
     * Active State
     * Layout Synchronization
     * Runtime Updates
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Banner Engine.
     */
    initializeBannerGridEngine() {

        if (!this.isBannerGridReady()) {
            return;
        }

        this.prepareBannerCards();

        this.calculateBannerGrid();

        // Apply the Liquid equal_height contract after columns are known.
        this.updateBannerEqualHeight();

        this.activateBanner(null);

        this.emit(
            'tefco:banner-grid:engine:initialized'
        );

    }

    /**
     * Prepares banner cards.
     */
    prepareBannerCards() {

        this.bannerGrid.cards.forEach(
            (card, index) => {

                card.dataset.bannerIndex =
                    index;

                card.classList.remove(
                    'is-active',
                    'is-hovered'
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Calculates banner grid layout.
     */
    calculateBannerGrid() {

        const columns =
            this.getCurrentBannerColumns();

        this.bannerGrid.state.columns =
            columns;

        const grid =
            this.bannerGrid.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--banner-grid-columns',
            columns
        );

    }

    /**
     * Activates a banner.
     *
     * @param {number|null} index
     */
    activateBanner(index) {

        this.setActiveBanner(index);

        this.bannerGrid.cards.forEach(
            (card, cardIndex) => {

                const active =
                    cardIndex === index;

                card.classList.toggle(
                    'is-active',
                    active
                );

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );

        this.emit(
            'tefco:banner-grid:activated',
            {
                active: index
            }
        );

    }

    /**
     * Activates banner by element.
     *
     * @param {HTMLElement} card
     */
    activateBannerCard(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.bannerIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateBanner(index);

    }

    /**
     * Deactivates all banners.
     */
    deactivateBanners() {

        this.activateBanner(null);

    }

    /**
     * Updates banner grid.
     */
    updateBannerGrid() {

        this.calculateBannerGrid();

        this.updateBannerVisibility();

        this.emit(
            'tefco:banner-grid:updated'
        );

    }

    /**
     * Synchronizes banner grid.
     */
    syncBannerGrid() {

        this.calculateBannerGrid();

        this.emit(
            'tefco:banner-grid:synchronized'
        );

    }

    /**
     * Refreshes banner grid.
     */
    refreshBannerGrid() {

        this.collectBannerCards();

        this.prepareBannerCards();

        this.syncBannerGrid();

        this.emit(
            'tefco:banner-grid:refreshed'
        );

    }

    /**
     * Updates visible banners.
     */
    updateBannerVisibility() {

        let visible = 0;

        this.bannerGrid.cards.forEach(card => {

            const hidden =
                card.offsetParent === null;

            card.classList.toggle(
                'is-visible',
                !hidden
            );

            if (!hidden) {
                visible++;
            }

        });

        this.bannerGrid.state.visible =
            visible;

    }

    /**
     * Returns visible banner cards.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleBannerCards() {

        return this.bannerGrid.cards.filter(
            card =>
                card.classList.contains(
                    'is-visible'
                )
        );

    }

    /**
     * Returns current banner columns.
     *
     * @returns {number}
     */
    getCurrentBannerColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.bannerGrid.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.bannerGrid.config.columnsTablet;

        }

        return this.bannerGrid.config.columnsMobile;

    }

    /**
     * Returns Banner Engine state.
     *
     * @returns {Object}
     */
    getBannerGridEngineState() {

        return {

            active:
                this.bannerGrid.state.active,

            total:
                this.bannerGrid.state.total,

            visible:
                this.bannerGrid.state.visible,

            columns:
                this.bannerGrid.state.columns,

            animating:
                this.bannerGrid.state.animating

        };

    }

    /**
     * Returns whether Banner Engine
     * is ready.
     *
     * @returns {boolean}
     */
    isBannerGridEngineReady() {

        return !!(

            this.isBannerGridReady() &&
            this.bannerGrid.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3C.3
     * Hover Effects
     * ------------------------------------------------------------------------
     * Enterprise Hover Engine
     * Banner Hover Effects
     * Entrance Animations
     * Active State
     * Motion Preferences
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes hover effects.
     */
    initializeBannerHoverEffects() {

        if (!this.isBannerGridReady()) {
            return;
        }

        if (this.bannerGrid.config.hoverEffect) {

            this.bindBannerHoverEvents();

            this.emit(
                'tefco:banner-grid:hover:initialized'
            );

        }

        // Entrance animation is independent from hover effects.
        if (this.bannerGrid.config.animation) {

            this.initializeBannerAnimations();
            this.playBannerAnimations();

        }

    }

    /**
     * Registers a Banner Grid event listener and tracks it for cleanup.
     */
    bindBannerEvent(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        this.bannerGrid.events =
            this.bannerGrid.events || [];

        target.addEventListener(
            event,
            handler,
            options
        );

        this.bannerGrid.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every Banner Grid-owned event listener.
     */
    cleanupBannerEvents() {

        if (!this.bannerGrid?.events) {
            return;
        }

        this.bannerGrid.events.forEach(listener => {

            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );

        });

        this.bannerGrid.events = [];

    }

    /**
     * Binds hover events.
     */
    bindBannerHoverEvents() {

        this.bannerGrid.cards.forEach(card => {

            this.bindBannerEvent(
                card,
                'mouseenter',
                () => this.handleBannerHover(card)
            );

            this.bindBannerEvent(
                card,
                'mouseleave',
                () => this.handleBannerLeave(card)
            );

            this.bindBannerEvent(
                card,
                'focus',
                () => this.handleBannerHover(card)
            );

            this.bindBannerEvent(
                card,
                'blur',
                () => this.handleBannerLeave(card)
            );

        });

    }

    /**
     * Handles hover.
     *
     * @param {HTMLElement} card
     */
    handleBannerHover(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.bannerIndex
            );

        this.bannerGrid.state.hovered =
            index;

        this.highlightBanner(card);

        this.emit(
            'tefco:banner-grid:hover',
            {
                index,
                card
            }
        );

    }

    /**
     * Handles hover leave.
     *
     * @param {HTMLElement} card
     */
    handleBannerLeave(card) {

        if (!card) {
            return;
        }

        card.classList.remove(
            'is-hovered'
        );

        this.bannerGrid.state.hovered =
            null;

        this.emit(
            'tefco:banner-grid:hover:end'
        );

    }

    /**
     * Highlights active banner.
     *
     * @param {HTMLElement} card
     */
    highlightBanner(card) {

        this.bannerGrid.cards.forEach(item => {

            item.classList.remove(
                'is-hovered'
            );

        });

        card.classList.add(
            'is-hovered'
        );

    }

    /**
     * Initializes entrance animations.
     */
    initializeBannerAnimations() {

        this.bannerGrid.cards.forEach(
            (card, index) => {

                card.style.setProperty(
                    '--banner-delay',
                    `${index * this.bannerGrid.config.animationDelay}ms`
                );

                card.classList.add(
                    'is-animatable'
                );

            }
        );

    }

    /**
     * Plays entrance animations.
     */
    playBannerAnimations() {

        if (
            !this.isBannerGridReady() ||
            !this.bannerGrid.config.animation
        ) {
            return;
        }

        const reducedMotion =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches;

        if (reducedMotion) {

            this.bannerGrid.cards.forEach(card => {

                card.classList.add(
                    'is-animated'
                );

            });

            this.bannerGrid.state.animating =
                false;

            this.emit(
                'tefco:banner-grid:animation:complete'
            );

            return;
        }

        this.bannerGrid.state.animating =
            true;

        this.bannerGrid.cards.forEach(card => {

            requestAnimationFrame(() => {

                card.classList.add(
                    'is-animated'
                );

            });

        });

        const lastCard =
            this.bannerGrid.cards.at(-1);

        if (!lastCard) {

            this.bannerGrid.state.animating =
                false;

            return;
        }

        const onTransitionEnd = event => {

            if (
                event.target !== lastCard ||
                (
                    event.propertyName !== 'opacity' &&
                    event.propertyName !== 'transform'
                )
            ) {
                return;
            }

            this.bannerGrid.state.animating =
                false;

            lastCard.removeEventListener(
                'transitionend',
                onTransitionEnd
            );

            this.emit(
                'tefco:banner-grid:animation:complete'
            );

        };

        this.bindBannerEvent(
            lastCard,
            'transitionend',
            onTransitionEnd
        );

    }

    /**
     * Resets animations.
     */
    resetBannerAnimations() {

        this.bannerGrid.cards.forEach(card => {

            card.classList.remove(
                'is-animated'
            );

        });

        this.bannerGrid.state.animating =
            false;

    }

    /**
     * Refreshes hover effects.
     */
    refreshBannerHoverEffects() {

        this.bannerGrid.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.bannerGrid.state.hovered =
            null;

        if (this.bannerGrid.config.animation) {
            this.initializeBannerAnimations();
            this.playBannerAnimations();
        }

        this.emit(
            'tefco:banner-grid:hover:refreshed'
        );

    }

    /**
     * Enables hover effects.
     */
    enableBannerHoverEffects() {

        this.bannerGrid.config.hoverEffect =
            true;

        this.emit(
            'tefco:banner-grid:hover:enabled'
        );

    }

    /**
     * Disables hover effects.
     */
    disableBannerHoverEffects() {

        this.bannerGrid.config.hoverEffect =
            false;

        this.bannerGrid.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.bannerGrid.state.hovered =
            null;

        this.emit(
            'tefco:banner-grid:hover:disabled'
        );

    }

    /**
     * Returns hover state.
     *
     * @returns {Object}
     */
    getBannerHoverState() {

        return {

            enabled:
                this.bannerGrid.config.hoverEffect,

            hovered:
                this.bannerGrid.state.hovered,

            animating:
                this.bannerGrid.state.animating

        };

    }

    /**
     * Returns whether hover engine
     * is ready.
     *
     * @returns {boolean}
     */
    isBannerHoverReady() {

        return !!(

            this.isBannerGridReady() &&
            this.bannerGrid.config.hoverEffect

        );

    }
    /**
     * ========================================================================
     * Part 3C.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Grid Layout
     * Breakpoints
     * Resize Handling
     * Dynamic Columns
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeBannerResponsive() {

        if (!this.isBannerGridReady()) {
            return;
        }

        this.bindBannerResize();

        this.updateBannerBreakpoints();

        this.updateBannerLayout();

        this.emit(
            'tefco:banner-grid:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindBannerResize() {

        const handleResize = () => {

            if (this.bannerGrid.resizeTimer !== null) {

                this.clearTimeout(
                    this.bannerGrid.resizeTimer
                );

                this.bannerGrid.resizeTimer = null;

            }

            this.bannerGrid.resizeTimer =
                this.setTimeout(() => {

                    this.bannerGrid.resizeTimer = null;

                    if (
                        !this.bannerGrid ||
                        !this.bannerGrid.initialized ||
                        this.bannerGrid.destroyed
                    ) {
                        return;
                    }

                    this.updateBannerBreakpoints();

                    this.updateBannerLayout();

                }, 100);

        };

        this.bindBannerEvent(
            window,
            'resize',
            handleResize
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateBannerBreakpoints() {

        this.bannerGrid.state.columns =
            this.calculateBannerColumns();

        this.emit(
            'tefco:banner-grid:breakpoint:changed',
            {
                breakpoint:
                    this.getBannerBreakpoint(),

                columns:
                    this.bannerGrid.state.columns
            }
        );

    }

    /**
     * Calculates columns.
     *
     * @returns {number}
     */
    calculateBannerColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.bannerGrid.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.bannerGrid.config.columnsTablet;

        }

        return this.bannerGrid.config.columnsMobile;

    }

    /**
     * Updates responsive layout.
     */
    updateBannerLayout() {

        const grid =
            this.bannerGrid.dom.grid;

        if (!grid) {
            return;
        }

        const columns =
            this.bannerGrid.state.columns;

        grid.style.setProperty(
            '--banner-grid-columns',
            columns
        );

        grid.setAttribute(
            'data-columns',
            columns
        );

        this.updateBannerVisibility();

        // Re-measure after every responsive column change. This also clears
        // stale inline heights when equal_height is disabled in Theme Editor.
        this.updateBannerEqualHeight();

        this.emit(
            'tefco:banner-grid:layout:updated',
            {
                columns
            }
        );

    }

    /**
     * Applies the Banner Grid equal-height contract.
     *
     * When equal_height is enabled, every visible banner is measured from its
     * natural height first and then receives the tallest measured height.
     * When disabled, all JS-owned heights are removed so CSS/Liquid can render
     * each banner naturally.
     */
    updateBannerEqualHeight() {

        if (!this.bannerGrid?.cards?.length) {
            return;
        }

        const cards = this.bannerGrid.cards;

        // Always clear previous measurements before reading layout. This is
        // essential after viewport changes and Theme Editor setting updates.
        cards.forEach(card => {
            card.style.removeProperty('height');
            card.style.removeProperty('--banner-equal-height');
        });

        if (!this.bannerGrid.config.equalHeight) {
            this.bannerGrid.dom.grid?.removeAttribute('data-equal-height');
            return;
        }

        const visibleCards = cards.filter(card =>
            card.offsetParent !== null
        );

        if (!visibleCards.length) {
            return;
        }

        const tallest = Math.max(
            ...visibleCards.map(card =>
                Math.ceil(card.getBoundingClientRect().height)
            )
        );

        if (!Number.isFinite(tallest) || tallest <= 0) {
            return;
        }

        visibleCards.forEach(card => {
            card.style.height = `${tallest}px`;
            card.style.setProperty(
                '--banner-equal-height',
                `${tallest}px`
            );
        });

        this.bannerGrid.dom.grid?.setAttribute(
            'data-equal-height',
            'true'
        );
    }

    /**
     * Refreshes responsive layout.
     */
    refreshBannerLayout() {

        this.updateBannerBreakpoints();

        this.updateBannerLayout();

        this.emit(
            'tefco:banner-grid:layout:refreshed'
        );

    }

    /**
     * Recalculates layout.
     */
    recalculateBannerLayout() {

        this.collectBannerCards();

        this.refreshBannerLayout();

        this.emit(
            'tefco:banner-grid:layout:recalculated'
        );

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getBannerBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getBannerResponsiveState() {

        return {

            breakpoint:
                this.getBannerBreakpoint(),

            columns:
                this.bannerGrid.state.columns,

            total:
                this.bannerGrid.state.total,

            visible:
                this.bannerGrid.state.visible

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBannerResponsiveReady() {

        return !!(

            this.isBannerGridReady() &&
            this.bannerGrid.state.columns > 0

        );

    }
    /**
     * ========================================================================
     * Part 3C.5
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeBannerLazyLoading() {

        if (
            !this.isBannerGridReady() ||
            !this.bannerGrid.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.bannerGrid.cards.forEach(card => {
                this.loadBannerCard(card);
            });

            return;

        }

        this.bannerGrid.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadBannerCard(
                            entry.target
                        );

                        this.bannerGrid.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05
                }

            );

        this.bannerGrid.cards.forEach(card => {

            this.bannerGrid.lazyObserver.observe(
                card
            );

        });

        this.preloadVisibleBanners();

        this.emit(
            'tefco:banner-grid:lazy:initialized'
        );

    }

    /**
     * Loads banner assets.
     *
     * @param {HTMLElement} card
     */
    loadBannerCard(card) {

        if (!card) {
            return;
        }

        if (
            card.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            card.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        card.dataset.loaded =
            'true';

        card.classList.add(
            'is-loaded'
        );

        this.emit(
            'tefco:banner-grid:card:loaded',
            {
                card
            }
        );

    }

    /**
     * Preloads visible banners.
     */
    preloadVisibleBanners() {

        this.getVisibleBannerCards()
            .forEach(card => {

                this.loadBannerCard(
                    card
                );

            });

    }

    /**
     * Preloads adjacent banners.
     */
    preloadAdjacentBanners() {

        const visible =
            this.getVisibleBannerCards();

        visible.forEach(card => {

            const next =
                card.nextElementSibling;

            if (
                next?.matches(
                    '[data-banner-card]'
                )
            ) {

                this.loadBannerCard(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshBannerLazyLoading() {

        this.disconnectBannerLazyObserver();

        this.initializeBannerLazyLoading();

        this.emit(
            'tefco:banner-grid:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectBannerLazyObserver() {

        if (
            this.bannerGrid.lazyObserver
        ) {

            this.bannerGrid.lazyObserver.disconnect();

            this.bannerGrid.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded banner cards.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedBannerCards() {

        return this.bannerGrid.cards.filter(
            card =>
                card.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending banner cards.
     *
     * @returns {HTMLElement[]}
     */
    getPendingBannerCards() {

        return this.bannerGrid.cards.filter(
            card =>
                card.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getBannerLazyState() {

        return {

            enabled:
                this.bannerGrid.config.lazyLoad,

            observer:
                !!this.bannerGrid.lazyObserver,

            loaded:
                this.getLoadedBannerCards()
                    .length,

            pending:
                this.getPendingBannerCards()
                    .length,

            total:
                this.bannerGrid.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBannerLazyReady() {

        return !!(

            this.isBannerGridReady() &&
            this.bannerGrid.config.lazyLoad

        );

    }
        /**
     * ========================================================================
     * Part 3C.6
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeBannerAccessibility() {

        if (!this.isBannerGridReady()) {
            return;
        }

        this.initializeBannerAccessibilityAttributes();

        this.bindBannerKeyboardNavigation();

        this.initializeBannerLiveRegion();

        this.initializeBannerReducedMotion();

        this.emit(
            'tefco:banner-grid:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeBannerAccessibilityAttributes() {

        const {
            section,
            grid
        } = this.bannerGrid.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Banner Grid'
        );

        grid?.setAttribute(
            'role',
            'list'
        );

        this.bannerGrid.cards.forEach(
            (card, index) => {

                card.setAttribute(
                    'role',
                    'listitem'
                );

                card.setAttribute(
                    'tabindex',
                    '0'
                );

                card.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                card.setAttribute(
                    'aria-setsize',
                    this.bannerGrid.state.total
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindBannerKeyboardNavigation() {

        const grid =
            this.bannerGrid.dom.grid;

        if (!grid) {
            return;
        }

        this.bindBannerEvent(
            grid,
            'keydown',
            event => {

                const current =
                    this.getActiveBanner() ?? 0;

                let next =
                    current;

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        next = Math.min(
                            current + 1,
                            this.bannerGrid.state.total - 1
                        );

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        next = Math.max(
                            current - 1,
                            0
                        );

                        break;

                    case 'ArrowDown':

                        event.preventDefault();

                        next = Math.min(
                            current +
                            this.bannerGrid.state.columns,
                            this.bannerGrid.state.total - 1
                        );

                        break;

                    case 'ArrowUp':

                        event.preventDefault();

                        next = Math.max(
                            current -
                            this.bannerGrid.state.columns,
                            0
                        );

                        break;

                    case 'Home':

                        event.preventDefault();

                        next = 0;

                        break;

                    case 'End':

                        event.preventDefault();

                        next =
                            this.bannerGrid.state.total - 1;

                        break;

                    case 'Enter':

                    case ' ':

                    case 'Spacebar':

                        event.preventDefault();

                        this.bannerGrid.cards[
                            current
                        ]?.click();

                        return;

                    default:

                        return;

                }

                this.activateBanner(next);

                this.focusBanner(next);

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeBannerLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.bannerGrid.liveRegion =
            liveRegion;

    }

    /**
     * Announces active banner.
     *
     * @param {number} index
     */
    announceBanner(index) {

        if (
            !this.bannerGrid.liveRegion
        ) {
            return;
        }

        const total =
            this.bannerGrid.state.total;

        this.bannerGrid.liveRegion.textContent =
            `Banner ${index + 1} of ${total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeBannerReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.bannerGrid.reducedMotion =
            media.matches;

        if (
            this.bannerGrid.reducedMotion
        ) {

            this.bannerGrid.cards.forEach(
                card => {

                    card.style.transition =
                        'none';

                    card.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses a banner.
     *
     * @param {number} index
     */
    focusBanner(index) {

        this.bannerGrid.cards[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility state.
     */
    updateBannerAccessibility() {

        this.bannerGrid.cards.forEach(
            (card, index) => {

                const active =
                    index ===
                    this.bannerGrid.state.active;

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

                card.setAttribute(
                    'tabindex',
                    active
                        ? '0'
                        : '-1'
                );

            }
        );

        if (
            this.bannerGrid.state.active !== null
        ) {

            this.announceBanner(
                this.bannerGrid.state.active
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getBannerAccessibilityState() {

        return {

            reducedMotion:
                this.bannerGrid.reducedMotion,

            liveRegion:
                !!this.bannerGrid.liveRegion,

            active:
                this.bannerGrid.state.active,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBannerAccessibilityReady() {

        return !!(

            this.isBannerGridReady() &&
            this.bannerGrid.liveRegion

        );

    }
    /**
     * ========================================================================
     * Part 3C.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Enterprise Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Live Preview
     * Inspector Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeBannerThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.bindBannerThemeEditorEvents();

        this.emit(
            'tefco:banner-grid:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindBannerThemeEditorEvents() {

        this.bindBannerEvent(
            document,
            'shopify:section:load',
            event => this.handleBannerSectionLoad(event)
        );

        this.bindBannerEvent(
            document,
            'shopify:section:unload',
            event => this.handleBannerSectionUnload(event)
        );

        this.bindBannerEvent(
            document,
            'shopify:section:select',
            event => this.handleBannerSectionSelect(event)
        );

        this.bindBannerEvent(
            document,
            'shopify:section:deselect',
            event => this.handleBannerSectionDeselect(event)
        );

        this.bindBannerEvent(
            document,
            'shopify:block:select',
            event => this.handleBannerBlockSelect(event)
        );

        this.bindBannerEvent(
            document,
            'shopify:block:deselect',
            event => this.handleBannerBlockDeselect(event)
        );

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleBannerSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-banner-grid]'
            )
        ) {
            return;
        }

        this.rebuildBannerGrid();

        this.emit(
            'tefco:banner-grid:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleBannerSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-banner-grid]'
            )
        ) {
            return;
        }

        this.destroyBannerGrid();

        this.emit(
            'tefco:banner-grid:section:unloaded'
        );

    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleBannerSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-banner-grid]'
            )
        ) {
            return;
        }

        this.refreshBannerLayout?.();

        this.emit(
            'tefco:banner-grid:section:selected'
        );

    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleBannerSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-banner-grid]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:banner-grid:section:deselected'
        );

    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleBannerBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-banner-card]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.bannerIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateBanner(index);

        this.focusBanner?.(index);

        this.emit(
            'tefco:banner-grid:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleBannerBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-banner-card]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:banner-grid:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshBannerThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.refreshBannerGrid();

        this.refreshBannerLayout?.();

        this.refreshBannerLazyLoading?.();

        this.emit(
            'tefco:banner-grid:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getBannerThemeEditorState() {

        return {

            enabled:
                this.isThemeEditor(),

            initialized:
                this.isThemeEditor(),

            activeBanner:
                this.bannerGrid.state.active,

            totalBanners:
                this.bannerGrid.state.total

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isBannerThemeEditorReady() {

        return !!(

            this.isThemeEditor() &&
            this.isBannerGridReady()

        );

    }
        /**
     * ========================================================================
     * Part 3C.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runBannerDiagnostics() {

        return {

            ready:
                this.isBannerGridReady(),

            initialized:
                this.bannerGrid?.initialized ?? false,

            destroyed:
                this.bannerGrid?.destroyed ?? false,

            total:
                this.bannerGrid?.state?.total ?? 0,

            visible:
                this.bannerGrid?.state?.visible ?? 0,

            active:
                this.bannerGrid?.state?.active,

            hovered:
                this.bannerGrid?.state?.hovered,

            columns:
                this.bannerGrid?.state?.columns ?? 0,

            animating:
                this.bannerGrid?.state?.animating ?? false,

            lazyLoaded:
                this.getLoadedBannerCards?.()
                    ?.length ?? 0,

            pendingLazy:
                this.getPendingBannerCards?.()
                    ?.length ?? 0,

            responsive:
                this.isBannerResponsiveReady?.() ?? false,

            hover:
                this.isBannerHoverReady?.() ?? false,

            accessibility:
                this.isBannerAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isBannerLazyReady?.() ?? false,

            themeEditor:
                this.isBannerThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Banner Grid.
     *
     * @returns {boolean}
     */
    validateBannerGrid() {

        if (!this.bannerGrid) {
            return false;
        }

        const {
            dom,
            cards
        } = this.bannerGrid;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.grid) {
            return false;
        }

        if (!Array.isArray(cards)) {
            return false;
        }

        if (!cards.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logBannerDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Banner Grid Diagnostics'
        );

        console.table(
            this.runBannerDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupBannerStyles() {

        const grid =
            this.bannerGrid?.dom?.grid;

        grid?.style.removeProperty(
            '--banner-grid-columns'
        );

        grid?.removeAttribute(
            'data-equal-height'
        );

        this.bannerGrid?.cards?.forEach(card => {

            card.style.removeProperty(
                '--banner-delay'
            );

            card.style.removeProperty(
                '--banner-equal-height'
            );

            card.style.removeProperty(
                'height'
            );

            card.style.removeProperty(
                'transition'
            );

            card.style.removeProperty(
                'animation'
            );

            card.classList.remove(
                'is-active',
                'is-hovered',
                'is-visible',
                'is-animated',
                'is-loaded',
                'is-animatable'
            );

            card.removeAttribute(
                'aria-selected'
            );

            card.removeAttribute(
                'tabindex'
            );

        });

    }

    /**
     * Cleans runtime state.
     */
    cleanupBannerState() {

        if (!this.bannerGrid) {
            return;
        }

        this.bannerGrid.state = {

            total: 0,

            visible: 0,

            columns: 0,

            active: null,

            hovered: null,

            animating: false,

            initializedAt: 0

        };

    }

    /**
     * Cleans observers.
     */
    cleanupBannerObservers() {

        this.disconnectBannerLazyObserver?.();

    }

    /**
     * Cleans accessibility.
     */
    cleanupBannerAccessibility() {

        if (
            this.bannerGrid?.liveRegion
        ) {

            this.bannerGrid.liveRegion.remove();

            this.bannerGrid.liveRegion =
                null;

        }

    }

    /**
     * Cleans DOM references.
     */
    cleanupBannerDOM() {

        if (!this.bannerGrid) {
            return;
        }

        this.bannerGrid.cards = [];

        this.bannerGrid.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupBannerConfig() {

        if (!this.bannerGrid) {
            return;
        }

        this.bannerGrid.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupBannerGrid() {

        if (this.bannerGrid?.resizeTimer !== null) {

            this.clearTimeout(
                this.bannerGrid.resizeTimer
            );

            this.bannerGrid.resizeTimer = null;

        }

        this.cleanupBannerEvents();

        this.cleanupBannerObservers();

        this.cleanupBannerAccessibility();

        this.cleanupBannerStyles();

        this.cleanupBannerState();

        this.cleanupBannerDOM();

        this.cleanupBannerConfig();

        this.emit(
            'tefco:banner-grid:cleanup'
        );

    }

    /**
     * Destroys Banner Grid.
     */
    destroyBannerGrid() {

        if (
            !this.bannerGrid ||
            this.bannerGrid.destroyed
        ) {
            return;
        }

        this.cleanupBannerGrid();

        this.bannerGrid.initialized =
            false;

        this.bannerGrid.destroyed =
            true;

        this.emit(
            'tefco:banner-grid:destroyed'
        );

    }

    /**
     * Rebuilds Banner Grid.
     */
    rebuildBannerGrid() {

        this.destroyBannerGrid();

        this.initializeBannerGrid();

        this.initializeBannerGridEngine();

        this.initializeBannerHoverEffects();

        this.initializeBannerResponsive();

        this.initializeBannerLazyLoading();

        this.initializeBannerAccessibility();

        this.initializeBannerThemeEditor();

        this.emit(
            'tefco:banner-grid:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getBannerLifecycleState() {

        return {

            initialized:
                this.bannerGrid?.initialized ?? false,

            destroyed:
                this.bannerGrid?.destroyed ?? false,

            valid:
                this.validateBannerGrid(),

            diagnostics:
                this.runBannerDiagnostics()

        };

    }

    /**
     * Returns whether destroyed.
     *
     * @returns {boolean}
     */
    isBannerDestroyed() {

        return !!(
            this.bannerGrid?.destroyed
        );

    }
        /**
     * ========================================================================
     * Part 4A.1
     * Featured Products Foundation
     * ------------------------------------------------------------------------
     * Enterprise Featured Products Engine
     * Foundation
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Featured Products.
     */
    
    //----------------------------------------
    // PART 4 — Featured Products
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Featured Products
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Featured Products
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Featured Products
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Featured Products
    //----------------------------------------

initializeFeaturedProducts() {

        this.cacheFeaturedProductsDOM();

        if (!this.featuredProducts.dom.section) {
            return;
        }

        this.initializeFeaturedProductsConfig();

        this.initializeFeaturedProductsState();

        this.collectFeaturedProductCards();

        this.featuredProducts.initialized = true;

        this.featuredProducts.destroyed = false;

        this.emit(
            'tefco:featured-products:initialized'
        );

    }

    /**
     * Caches DOM elements.
     */
    cacheFeaturedProductsDOM() {

        const section =
            document.querySelector(
                '[data-featured-products]'
            );

        this.featuredProducts = {

            ...this.featuredProducts,

            dom: {

                section,

                grid:
                    section?.querySelector(
                        '[data-products-grid]'
                    ) ?? null,

                wrapper:
                    section?.querySelector(
                        '[data-products-wrapper]'
                    ) ?? null,

                heading:
                    section?.querySelector(
                        '[data-products-heading]'
                    ) ?? null,

                cards:
                    section
                        ? Array.from(
                            section.querySelectorAll(
                                '[data-product-card]'
                            )
                        )
                        : []

            }

        };

    }

    /**
     * Initializes configuration.
     */
    initializeFeaturedProductsConfig() {

        const section =
            this.featuredProducts.dom.section;

        this.featuredProducts.config = {

            columnsDesktop:
                Number(
                    section?.dataset.columnsDesktop
                ) || 4,

            columnsTablet:
                Number(
                    section?.dataset.columnsTablet
                ) || 2,

            columnsMobile:
                Number(
                    section?.dataset.columnsMobile
                ) || 1,

            animation:
                section?.dataset.animation !== 'false',

            animationDelay:
                Number(
                    section?.dataset.animationDelay
                ) || 100,

            hoverEffect:
                section?.dataset.hover !== 'false',

            lazyLoad:
                section?.dataset.lazy !== 'false',

            quickView:
                section?.dataset.quickView !== 'false',

            wishlist:
                section?.dataset.wishlist !== 'false',

            compare:
                section?.dataset.compare !== 'false',

            autoplay:
                section?.dataset.autoplay === 'true'

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeFeaturedProductsState() {

        this.featuredProducts.state = {

            total: 0,

            visible: 0,

            columns:
                this.featuredProducts.config.columnsDesktop,

            active: null,

            hovered: null,

            selected: null,

            animating: false,

            loading: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects product cards.
     */
    collectFeaturedProductCards() {

        const cards =
            this.featuredProducts.dom.cards;

        this.featuredProducts.cards = cards;

        this.featuredProducts.state.total =
            cards.length;

        this.featuredProducts.state.visible =
            cards.filter(card =>
                card.offsetParent !== null
            ).length;

        cards.forEach(
            (card, index) => {

                card.dataset.productIndex =
                    index;

            }
        );

    }

    /**
     * Returns all product cards.
     *
     * @returns {HTMLElement[]}
     */
    getFeaturedProductCards() {

        return this.featuredProducts.cards;

    }

    /**
     * Returns active product.
     *
     * @returns {number|null}
     */
    getActiveFeaturedProduct() {

        return this.featuredProducts.state.active;

    }

    /**
     * Sets active product.
     *
     * @param {number|null} index
     */
    setActiveFeaturedProduct(index) {

        this.featuredProducts.state.active =
            index;

    }

    /**
     * Returns whether Featured Products
     * engine is initialized.
     *
     * @returns {boolean}
     */
    isFeaturedProductsReady() {

        return !!(

            this.featuredProducts &&
            this.featuredProducts.initialized &&
            !this.featuredProducts.destroyed &&
            this.featuredProducts.dom.section

        );

    }
        /**
     * ========================================================================
     * Part 4A.2
     * Featured Products Engine
     * ------------------------------------------------------------------------
     * Enterprise Product Engine
     * Product Activation
     * Product Selection
     * Grid Synchronization
     * Visibility Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Product Engine.
     */
    initializeFeaturedProductsEngine() {

        if (!this.isFeaturedProductsReady()) {
            return;
        }

        this.prepareFeaturedProductCards();

        this.calculateFeaturedProductsGrid();

        this.updateFeaturedProductsGrid();

        this.emit(
            'tefco:featured-products:engine:initialized'
        );

    }

    /**
     * Prepares product cards.
     */
    prepareFeaturedProductCards() {

        this.featuredProducts.cards.forEach(
            (card, index) => {

                card.dataset.productIndex = index;

                card.classList.remove(
                    'is-active',
                    'is-selected'
                );

                card.setAttribute(
                    'data-product-ready',
                    'true'
                );

            }
        );

    }

    /**
     * Calculates grid metrics.
     */
    calculateFeaturedProductsGrid() {

        const columns =
            this.featuredProducts.state.columns;

        const total =
            this.featuredProducts.cards.length;

        const rows =
            Math.ceil(
                total / columns
            );

        this.featuredProducts.state.total =
            total;

        this.featuredProducts.state.rows =
            rows;

        this.featuredProducts.state.visible =
            this.getVisibleFeaturedProductCards()
                .length;

    }

    /**
     * Activates product.
     *
     * @param {number} index
     */
    activateFeaturedProduct(index) {

        if (
            index < 0 ||
            index >= this.featuredProducts.cards.length
        ) {
            return;
        }

        this.deactivateFeaturedProducts();

        const card =
            this.featuredProducts.cards[index];

        if (!card) {
            return;
        }

        card.classList.add(
            'is-active'
        );

        this.featuredProducts.state.active =
            index;

        this.updateFeaturedProductsAccessibility?.();

        this.emit(
            'tefco:featured-products:activated',
            {
                index,
                card
            }
        );

    }

    /**
     * Activates product card.
     *
     * @param {HTMLElement} card
     */
    activateFeaturedProductCard(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.productIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateFeaturedProduct(
            index
        );

    }

    /**
     * Deactivates all products.
     */
    deactivateFeaturedProducts() {

        this.featuredProducts.cards.forEach(
            card => {

                card.classList.remove(
                    'is-active'
                );

            }
        );

        this.featuredProducts.state.active =
            null;

    }

    /**
     * Selects product.
     *
     * @param {number} index
     */
    selectFeaturedProduct(index) {

        if (
            index < 0 ||
            index >= this.featuredProducts.cards.length
        ) {
            return;
        }

        this.featuredProducts.cards.forEach(
            card => {

                card.classList.remove(
                    'is-selected'
                );

            }
        );

        const card =
            this.featuredProducts.cards[index];

        card.classList.add(
            'is-selected'
        );

        this.featuredProducts.state.selected =
            index;

        this.emit(
            'tefco:featured-products:selected',
            {
                index,
                card
            }
        );

    }

    /**
     * Updates product grid.
     */
    updateFeaturedProductsGrid() {

        const grid =
            this.featuredProducts.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--products-columns',
            this.featuredProducts.state.columns
        );

        grid.setAttribute(
            'data-columns',
            this.featuredProducts.state.columns
        );

        this.updateFeaturedProductsVisibility();

        this.emit(
            'tefco:featured-products:grid:updated'
        );

    }

    /**
     * Synchronizes product grid.
     */
    syncFeaturedProductsGrid() {

        this.calculateFeaturedProductsGrid();

        this.updateFeaturedProductsGrid();

        this.emit(
            'tefco:featured-products:grid:synced'
        );

    }

    /**
     * Refreshes product grid.
     */
    refreshFeaturedProductsGrid() {

        this.collectFeaturedProductCards();

        this.syncFeaturedProductsGrid();

        this.emit(
            'tefco:featured-products:grid:refreshed'
        );

    }

    /**
     * Updates product visibility.
     */
    updateFeaturedProductsVisibility() {

        this.featuredProducts.state.visible =
            this.getVisibleFeaturedProductCards()
                .length;

    }

    /**
     * Returns visible product cards.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleFeaturedProductCards() {

        return this.featuredProducts.cards.filter(
            card =>
                card.offsetParent !== null
        );

    }

    /**
     * Returns current columns.
     *
     * @returns {number}
     */
    getCurrentFeaturedProductColumns() {

        return this.featuredProducts.state.columns;

    }

    /**
     * Returns Product Engine state.
     *
     * @returns {Object}
     */
    getFeaturedProductsEngineState() {

        return {

            total:
                this.featuredProducts.state.total,

            visible:
                this.featuredProducts.state.visible,

            rows:
                this.featuredProducts.state.rows,

            columns:
                this.featuredProducts.state.columns,

            active:
                this.featuredProducts.state.active,

            selected:
                this.featuredProducts.state.selected

        };

    }

    /**
     * Returns whether Product Engine
     * is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductsEngineReady() {

        return !!(

            this.isFeaturedProductsReady() &&
            this.featuredProducts.cards.length > 0

        );

    }
        /**
     * ========================================================================
     * Part 4A.3
     * Product Interactions
     * ------------------------------------------------------------------------
     * Enterprise Product Interaction Engine
     * Hover Effects
     * Product Selection
     * Quick View
     * Wishlist
     * Compare
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes product interactions.
     */
    initializeFeaturedProductInteractions() {

        if (!this.isFeaturedProductsEngineReady()) {
            return;
        }

        this.bindFeaturedProductEvents();

        if (
            this.featuredProducts.config.animation
        ) {

            this.initializeFeaturedProductAnimations();

        }

        this.emit(
            'tefco:featured-products:interactions:initialized'
        );

    }

    /**
     * Registers a Featured Products event listener and tracks it for cleanup.
     */
    bindFeaturedProductEvent(target, event, handler, options = false) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        this.featuredProducts.events = this.featuredProducts.events || [];

        target.addEventListener(event, handler, options);

        this.featuredProducts.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every event listener and pending resize timeout owned by Featured Products.
     */
    cleanupFeaturedProductEvents() {

        if (this.featuredProducts?.resizeTimer !== null &&
            this.featuredProducts?.resizeTimer !== undefined) {

            this.clearTimeout(this.featuredProducts.resizeTimer);
            this.featuredProducts.resizeTimer = null;
        }

        if (!this.featuredProducts?.events) {
            return;
        }

        this.featuredProducts.events.forEach(listener => {
            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );
        });

        this.featuredProducts.events.length = 0;

    }

    /**
     * Binds product events.
     */
    bindFeaturedProductEvents() {

        this.featuredProducts.cards.forEach(card => {

            this.bindFeaturedProductEvent(
                card,
                'mouseenter',
                () => this.handleFeaturedProductHover(card)
            );

            this.bindFeaturedProductEvent(
                card,
                'mouseleave',
                () => this.handleFeaturedProductLeave(card)
            );

            this.bindFeaturedProductEvent(
                card,
                'focus',
                () => this.handleFeaturedProductHover(card)
            );

            this.bindFeaturedProductEvent(
                card,
                'blur',
                () => this.handleFeaturedProductLeave(card)
            );

            this.bindFeaturedProductEvent(
                card,
                'click',
                () => this.activateFeaturedProductCard(card)
            );

            const quickView =
                card.querySelector(
                    '[data-product-quick-view]'
                );

            if (quickView) {

                this.bindFeaturedProductEvent(
                    quickView,
                    'click',
                    event => {

                        event.preventDefault();

                        event.stopPropagation();

                        this.openFeaturedProductQuickView(card);

                    }
                );

            }

            const wishlist =
                card.querySelector(
                    '[data-product-wishlist]'
                );

            if (wishlist) {

                this.bindFeaturedProductEvent(
                    wishlist,
                    'click',
                    event => {

                        event.preventDefault();

                        event.stopPropagation();

                        this.toggleFeaturedProductWishlist(card);

                    }
                );

            }

            const compare =
                card.querySelector(
                    '[data-product-compare]'
                );

            if (compare) {

                this.bindFeaturedProductEvent(
                    compare,
                    'click',
                    event => {

                        event.preventDefault();

                        event.stopPropagation();

                        this.toggleFeaturedProductCompare(card);

                    }
                );

            }

        });

    }

    /**
     * Handles hover.
     *
     * @param {HTMLElement} card
     */
    handleFeaturedProductHover(card) {

        if (!card) {
            return;
        }

        const index =
            Number(
                card.dataset.productIndex
            );

        this.featuredProducts.state.hovered =
            index;

        this.highlightFeaturedProduct(card);

        this.emit(
            'tefco:featured-products:hover',
            {
                index,
                card
            }
        );

    }

    /**
     * Handles hover leave.
     *
     * @param {HTMLElement} card
     */
    handleFeaturedProductLeave(card) {

        if (!card) {
            return;
        }

        card.classList.remove(
            'is-hovered'
        );

        this.featuredProducts.state.hovered =
            null;

        this.emit(
            'tefco:featured-products:hover:end'
        );

    }

    /**
     * Highlights product.
     *
     * @param {HTMLElement} card
     */
    highlightFeaturedProduct(card) {

        this.featuredProducts.cards.forEach(item => {

            item.classList.remove(
                'is-hovered'
            );

        });

        card.classList.add(
            'is-hovered'
        );

    }

    /**
     * Initializes animations.
     */
    initializeFeaturedProductAnimations() {

        this.featuredProducts.cards.forEach(
            (card, index) => {

                card.style.setProperty(
                    '--product-delay',
                    `${index * this.featuredProducts.config.animationDelay}ms`
                );

                card.classList.add(
                    'is-animatable'
                );

            }
        );

    }

    /**
     * Plays animations.
     */
    playFeaturedProductAnimations() {

        if (
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            ).matches
        ) {
            return;
        }

        this.featuredProducts.state.animating =
            true;

        this.featuredProducts.cards.forEach(card => {

            requestAnimationFrame(() => {

                card.classList.add(
                    'is-visible'
                );

            });

        });

        const lastCard =
            this.featuredProducts.cards.at(-1);

        if (lastCard) {

            const onAnimationEnd = () => {

                this.featuredProducts.state.animating =
                    false;

                lastCard.removeEventListener(
                    'animationend',
                    onAnimationEnd
                );

                this.emit(
                    'tefco:featured-products:animation:complete'
                );

            };

            lastCard.addEventListener(
                'animationend',
                onAnimationEnd
            );

        }

    }

    /**
     * Resets animations.
     */
    resetFeaturedProductAnimations() {

        this.featuredProducts.cards.forEach(card => {

            card.classList.remove(
                'is-visible'
            );

        });

        this.featuredProducts.state.animating =
            false;

    }

    /**
     * Opens Quick View.
     *
     * @param {HTMLElement} card
     */
    openFeaturedProductQuickView(card) {

        if (
            !this.featuredProducts.config.quickView
        ) {
            return;
        }

        this.emit(
            'tefco:featured-products:quick-view',
            {
                card,
                productHandle:
                    card.dataset.productHandle
            }
        );

    }

    /**
     * Toggles Wishlist.
     *
     * @param {HTMLElement} card
     */
    toggleFeaturedProductWishlist(card) {

        if (
            !this.featuredProducts.config.wishlist
        ) {
            return;
        }

        card.classList.toggle(
            'is-in-wishlist'
        );

        this.emit(
            'tefco:featured-products:wishlist',
            {
                card,
                active:
                    card.classList.contains(
                        'is-in-wishlist'
                    )
            }
        );

    }

    /**
     * Toggles Compare.
     *
     * @param {HTMLElement} card
     */
    toggleFeaturedProductCompare(card) {

        if (
            !this.featuredProducts.config.compare
        ) {
            return;
        }

        card.classList.toggle(
            'is-in-compare'
        );

        this.emit(
            'tefco:featured-products:compare',
            {
                card,
                active:
                    card.classList.contains(
                        'is-in-compare'
                    )
            }
        );

    }

    /**
     * Refreshes interactions.
     */
    refreshFeaturedProductInteractions() {

        this.featuredProducts.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.featuredProducts.state.hovered =
            null;

        this.initializeFeaturedProductAnimations();

        this.emit(
            'tefco:featured-products:interactions:refreshed'
        );

    }

    /**
     * Enables interactions.
     */
    enableFeaturedProductInteractions() {

        this.featuredProducts.config.hoverEffect =
            true;

        this.emit(
            'tefco:featured-products:interactions:enabled'
        );

    }

    /**
     * Disables interactions.
     */
    disableFeaturedProductInteractions() {

        this.featuredProducts.config.hoverEffect =
            false;

        this.featuredProducts.cards.forEach(card => {

            card.classList.remove(
                'is-hovered'
            );

        });

        this.featuredProducts.state.hovered =
            null;

        this.emit(
            'tefco:featured-products:interactions:disabled'
        );

    }

    /**
     * Returns interaction state.
     *
     * @returns {Object}
     */
    getFeaturedProductInteractionState() {

        return {

            enabled:
                this.featuredProducts.config.hoverEffect,

            hovered:
                this.featuredProducts.state.hovered,

            animating:
                this.featuredProducts.state.animating,

            quickView:
                this.featuredProducts.config.quickView,

            wishlist:
                this.featuredProducts.config.wishlist,

            compare:
                this.featuredProducts.config.compare

        };

    }

    /**
     * Returns whether interaction
     * engine is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductInteractionReady() {

        return !!(

            this.isFeaturedProductsEngineReady() &&
            this.featuredProducts.config.hoverEffect

        );

    }
        /**
     * ========================================================================
     * Part 4A.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Product Grid
     * Breakpoints
     * Resize Handling
     * Dynamic Columns
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeFeaturedProductsResponsive() {

        if (!this.isFeaturedProductsEngineReady()) {
            return;
        }

        this.bindFeaturedProductsResize();

        this.updateFeaturedProductsBreakpoints();

        this.updateFeaturedProductsLayout();

        this.emit(
            'tefco:featured-products:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindFeaturedProductsResize() {

        this.featuredProducts.resizeTimer =
            this.featuredProducts.resizeTimer ?? null;

        this.bindFeaturedProductEvent(
            window,
            'resize',
            () => {

                if (this.featuredProducts.resizeTimer !== null) {
                    this.clearTimeout(this.featuredProducts.resizeTimer);
                }

                this.featuredProducts.resizeTimer =
                    this.setTimeout(() => {

                        this.featuredProducts.resizeTimer = null;

                        if (
                            !this.featuredProducts ||
                            this.featuredProducts.destroyed ||
                            !this.featuredProducts.initialized
                        ) {
                            return;
                        }

                        this.updateFeaturedProductsBreakpoints();
                        this.updateFeaturedProductsLayout();

                    }, 100);

            }
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateFeaturedProductsBreakpoints() {

        this.featuredProducts.state.columns =
            this.calculateFeaturedProductsColumns();

        this.emit(
            'tefco:featured-products:breakpoint:changed',
            {
                breakpoint:
                    this.getFeaturedProductsBreakpoint(),

                columns:
                    this.featuredProducts.state.columns
            }
        );

    }

    /**
     * Calculates columns.
     *
     * @returns {number}
     */
    calculateFeaturedProductsColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.featuredProducts.config.columnsDesktop;

        }

        if (width >= 768) {

            return this.featuredProducts.config.columnsTablet;

        }

        return this.featuredProducts.config.columnsMobile;

    }

    /**
     * Updates responsive layout.
     */
    updateFeaturedProductsLayout() {

        const grid =
            this.featuredProducts.dom.grid;

        if (!grid) {
            return;
        }

        const columns =
            this.featuredProducts.state.columns;

        grid.style.setProperty(
            '--products-columns',
            columns
        );

        grid.setAttribute(
            'data-columns',
            columns
        );

        this.updateFeaturedProductsVisibility();

        this.emit(
            'tefco:featured-products:layout:updated',
            {
                columns
            }
        );

    }

    /**
     * Refreshes responsive layout.
     */
    refreshFeaturedProductsLayout() {

        this.updateFeaturedProductsBreakpoints();

        this.updateFeaturedProductsLayout();

        this.emit(
            'tefco:featured-products:layout:refreshed'
        );

    }

    /**
     * Recalculates layout.
     */
    recalculateFeaturedProductsLayout() {

        this.collectFeaturedProductCards();

        this.refreshFeaturedProductsLayout();

        this.emit(
            'tefco:featured-products:layout:recalculated'
        );

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getFeaturedProductsBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getFeaturedProductsResponsiveState() {

        return {

            breakpoint:
                this.getFeaturedProductsBreakpoint(),

            columns:
                this.featuredProducts.state.columns,

            total:
                this.featuredProducts.state.total,

            visible:
                this.featuredProducts.state.visible

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductsResponsiveReady() {

        return !!(

            this.isFeaturedProductsEngineReady() &&
            this.featuredProducts.state.columns > 0

        );

    }
        /**
     * ========================================================================
     * Part 4A.5
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeFeaturedProductsLazyLoading() {

        if (
            !this.isFeaturedProductsEngineReady() ||
            !this.featuredProducts.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.featuredProducts.cards.forEach(card => {

                this.loadFeaturedProductCard(card);

            });

            return;

        }

        this.featuredProducts.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadFeaturedProductCard(
                            entry.target
                        );

                        this.featuredProducts.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05

                }

            );

        this.featuredProducts.cards.forEach(card => {

            this.featuredProducts.lazyObserver.observe(
                card
            );

        });

        this.preloadVisibleFeaturedProducts();

        this.emit(
            'tefco:featured-products:lazy:initialized'
        );

    }

    /**
     * Loads product assets.
     *
     * @param {HTMLElement} card
     */
    loadFeaturedProductCard(card) {

        if (!card) {
            return;
        }

        if (
            card.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            card.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        card.dataset.loaded =
            'true';

        card.classList.add(
            'is-loaded'
        );

        this.emit(
            'tefco:featured-products:card:loaded',
            {
                card
            }
        );

    }

    /**
     * Preloads visible products.
     */
    preloadVisibleFeaturedProducts() {

        this.getVisibleFeaturedProductCards()
            .forEach(card => {

                this.loadFeaturedProductCard(
                    card
                );

            });

    }

    /**
     * Preloads adjacent products.
     */
    preloadAdjacentFeaturedProducts() {

        const visible =
            this.getVisibleFeaturedProductCards();

        visible.forEach(card => {

            const next =
                card.nextElementSibling;

            if (
                next?.matches(
                    '[data-product-card]'
                )
            ) {

                this.loadFeaturedProductCard(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshFeaturedProductsLazyLoading() {

        this.disconnectFeaturedProductsLazyObserver();

        this.initializeFeaturedProductsLazyLoading();

        this.emit(
            'tefco:featured-products:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectFeaturedProductsLazyObserver() {

        if (
            this.featuredProducts.lazyObserver
        ) {

            this.featuredProducts.lazyObserver.disconnect();

            this.featuredProducts.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded product cards.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedFeaturedProductCards() {

        return this.featuredProducts.cards.filter(
            card =>
                card.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending product cards.
     *
     * @returns {HTMLElement[]}
     */
    getPendingFeaturedProductCards() {

        return this.featuredProducts.cards.filter(
            card =>
                card.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getFeaturedProductsLazyState() {

        return {

            enabled:
                this.featuredProducts.config.lazyLoad,

            observer:
                !!this.featuredProducts.lazyObserver,

            loaded:
                this.getLoadedFeaturedProductCards()
                    .length,

            pending:
                this.getPendingFeaturedProductCards()
                    .length,

            total:
                this.featuredProducts.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * engine is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductsLazyReady() {

        return !!(

            this.isFeaturedProductsEngineReady() &&
            this.featuredProducts.config.lazyLoad

        );

    }
        /**
     * ========================================================================
     * Part 4A.6
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeFeaturedProductsAccessibility() {

        if (!this.isFeaturedProductsEngineReady()) {
            return;
        }

        this.initializeFeaturedProductsAccessibilityAttributes();

        this.bindFeaturedProductsKeyboardNavigation();

        this.initializeFeaturedProductsLiveRegion();

        this.initializeFeaturedProductsReducedMotion();

        this.emit(
            'tefco:featured-products:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeFeaturedProductsAccessibilityAttributes() {

        const {
            section,
            grid
        } = this.featuredProducts.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Featured Products'
        );

        grid?.setAttribute(
            'role',
            'list'
        );

        this.featuredProducts.cards.forEach(
            (card, index) => {

                card.setAttribute(
                    'role',
                    'listitem'
                );

                card.setAttribute(
                    'tabindex',
                    '0'
                );

                card.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                card.setAttribute(
                    'aria-setsize',
                    this.featuredProducts.state.total
                );

                card.setAttribute(
                    'aria-selected',
                    'false'
                );

            }
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindFeaturedProductsKeyboardNavigation() {

        const grid =
            this.featuredProducts.dom.grid;

        if (!grid) {
            return;
        }

        grid.addEventListener(
            'keydown',
            event => {

                const current =
                    this.getActiveFeaturedProduct() ?? 0;

                let next =
                    current;

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        next = Math.min(
                            current + 1,
                            this.featuredProducts.state.total - 1
                        );

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        next = Math.max(
                            current - 1,
                            0
                        );

                        break;

                    case 'ArrowDown':

                        event.preventDefault();

                        next = Math.min(
                            current +
                            this.featuredProducts.state.columns,
                            this.featuredProducts.state.total - 1
                        );

                        break;

                    case 'ArrowUp':

                        event.preventDefault();

                        next = Math.max(
                            current -
                            this.featuredProducts.state.columns,
                            0
                        );

                        break;

                    case 'Home':

                        event.preventDefault();

                        next = 0;

                        break;

                    case 'End':

                        event.preventDefault();

                        next =
                            this.featuredProducts.state.total - 1;

                        break;

                    case 'Enter':

                    case ' ':

                    case 'Spacebar':

                        event.preventDefault();

                        this.featuredProducts.cards[
                            current
                        ]?.click();

                        return;

                    default:

                        return;

                }

                this.activateFeaturedProduct(next);

                this.focusFeaturedProduct(next);

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeFeaturedProductsLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.featuredProducts.liveRegion =
            liveRegion;

    }

    /**
     * Announces active product.
     *
     * @param {number} index
     */
    announceFeaturedProduct(index) {

        if (
            !this.featuredProducts.liveRegion
        ) {
            return;
        }

        const total =
            this.featuredProducts.state.total;

        this.featuredProducts.liveRegion.textContent =
            `Product ${index + 1} of ${total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeFeaturedProductsReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.featuredProducts.reducedMotion =
            media.matches;

        if (
            this.featuredProducts.reducedMotion
        ) {

            this.featuredProducts.cards.forEach(
                card => {

                    card.style.transition =
                        'none';

                    card.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses product.
     *
     * @param {number} index
     */
    focusFeaturedProduct(index) {

        this.featuredProducts.cards[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility state.
     */
    updateFeaturedProductsAccessibility() {

        this.featuredProducts.cards.forEach(
            (card, index) => {

                const active =
                    index ===
                    this.featuredProducts.state.active;

                card.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

                card.setAttribute(
                    'tabindex',
                    active
                        ? '0'
                        : '-1'
                );

            }
        );

        if (
            this.featuredProducts.state.active !== null
        ) {

            this.announceFeaturedProduct(
                this.featuredProducts.state.active
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getFeaturedProductsAccessibilityState() {

        return {

            reducedMotion:
                this.featuredProducts.reducedMotion,

            liveRegion:
                !!this.featuredProducts.liveRegion,

            active:
                this.featuredProducts.state.active,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductsAccessibilityReady() {

        return !!(

            this.isFeaturedProductsEngineReady() &&
            this.featuredProducts.liveRegion

        );

    }
        /**
     * ========================================================================
     * Part 4A.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Enterprise Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Live Preview
     * Inspector Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Theme Editor integration.
     */
    initializeFeaturedProductsThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.bindFeaturedProductsThemeEditorEvents();

        this.emit(
            'tefco:featured-products:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindFeaturedProductsThemeEditorEvents() {

        this.bindFeaturedProductEvent(
            document,
            'shopify:section:load',
            this.handleFeaturedProductsSectionLoad.bind(this)
        );

        this.bindFeaturedProductEvent(
            document,
            'shopify:section:unload',
            this.handleFeaturedProductsSectionUnload.bind(this)
        );

        this.bindFeaturedProductEvent(
            document,
            'shopify:section:select',
            this.handleFeaturedProductsSectionSelect.bind(this)
        );

        this.bindFeaturedProductEvent(
            document,
            'shopify:section:deselect',
            this.handleFeaturedProductsSectionDeselect.bind(this)
        );

        this.bindFeaturedProductEvent(
            document,
            'shopify:block:select',
            this.handleFeaturedProductsBlockSelect.bind(this)
        );

        this.bindFeaturedProductEvent(
            document,
            'shopify:block:deselect',
            this.handleFeaturedProductsBlockDeselect.bind(this)
        );

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsSectionLoad(event) {

        const section = event.target;

        if (!section?.matches('[data-featured-products]')) {
            return;
        }

        // Shopify replaces the section DOM. Remove resources that still point
        // at the old nodes, then initialize one clean runtime on the new DOM.
        this.cleanupFeaturedProductEvents();
        this.cleanupFeaturedProductsObservers();
        this.cleanupFeaturedProductsAccessibility();

        this.cacheFeaturedProductsDOM();
        this.initializeFeaturedProductsConfig();
        this.initializeFeaturedProductsState();
        this.collectFeaturedProductCards();

        this.featuredProducts.initialized = true;
        this.featuredProducts.destroyed = false;

        this.initializeFeaturedProductsEngine();
        this.initializeFeaturedProductInteractions();
        this.initializeFeaturedProductsResponsive();
        this.initializeFeaturedProductsLazyLoading();
        this.initializeFeaturedProductsAccessibility();

        // Event cleanup also removes this module's Theme Editor listeners.
        // Restore exactly one clean set for the rebuilt section.
        this.bindFeaturedProductsThemeEditorEvents();

        this.emit('tefco:featured-products:section:loaded');

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsSectionUnload(event) {

        const section = event.target;

        if (!section?.matches('[data-featured-products]')) {
            return;
        }

        this.cleanupFeaturedProductEvents();
        this.cleanupFeaturedProductsObservers();
        this.cleanupFeaturedProductsAccessibility();

        this.emit('tefco:featured-products:section:unloaded');

    }

    /**
     * Handles section selection.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-featured-products]'
            )
        ) {
            return;
        }

        this.refreshFeaturedProductsLayout?.();

        this.emit(
            'tefco:featured-products:section:selected'
        );

    }

    /**
     * Handles section deselection.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-featured-products]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:featured-products:section:deselected'
        );

    }

    /**
     * Handles block selection.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-product-card]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.productIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateFeaturedProduct(
            index
        );

        this.focusFeaturedProduct?.(
            index
        );

        this.emit(
            'tefco:featured-products:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselection.
     *
     * @param {CustomEvent} event
     */
    handleFeaturedProductsBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-product-card]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:featured-products:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor state.
     */
    refreshFeaturedProductsThemeEditor() {

        if (!this.isThemeEditor()) {
            return;
        }

        this.refreshFeaturedProductsGrid();

        this.refreshFeaturedProductsLayout?.();

        this.refreshFeaturedProductsLazyLoading?.();

        this.emit(
            'tefco:featured-products:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getFeaturedProductsThemeEditorState() {

        return {

            enabled:
                this.isThemeEditor(),

            initialized:
                this.isThemeEditor(),

            activeProduct:
                this.featuredProducts.state.active,

            totalProducts:
                this.featuredProducts.state.total

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isFeaturedProductsThemeEditorReady() {

        return !!(

            this.isThemeEditor() &&
            this.isFeaturedProductsEngineReady()

        );

    }
        /**
     * ========================================================================
     * Part 4A.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runFeaturedProductsDiagnostics() {

        return {

            ready:
                this.isFeaturedProductsReady(),

            initialized:
                this.featuredProducts?.initialized ?? false,

            destroyed:
                this.featuredProducts?.destroyed ?? false,

            total:
                this.featuredProducts?.state?.total ?? 0,

            visible:
                this.featuredProducts?.state?.visible ?? 0,

            active:
                this.featuredProducts?.state?.active,

            selected:
                this.featuredProducts?.state?.selected,

            hovered:
                this.featuredProducts?.state?.hovered,

            columns:
                this.featuredProducts?.state?.columns ?? 0,

            animating:
                this.featuredProducts?.state?.animating ?? false,

            lazyLoaded:
                this.getLoadedFeaturedProductCards?.()
                    ?.length ?? 0,

            pendingLazy:
                this.getPendingFeaturedProductCards?.()
                    ?.length ?? 0,

            responsive:
                this.isFeaturedProductsResponsiveReady?.() ?? false,

            interactions:
                this.isFeaturedProductInteractionReady?.() ?? false,

            accessibility:
                this.isFeaturedProductsAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isFeaturedProductsLazyReady?.() ?? false,

            themeEditor:
                this.isFeaturedProductsThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Featured Products.
     *
     * @returns {boolean}
     */
    validateFeaturedProducts() {

        if (!this.featuredProducts) {
            return false;
        }

        const {
            dom,
            cards
        } = this.featuredProducts;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.grid) {
            return false;
        }

        if (!Array.isArray(cards)) {
            return false;
        }

        if (!cards.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logFeaturedProductsDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Featured Products Diagnostics'
        );

        console.table(
            this.runFeaturedProductsDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupFeaturedProductsStyles() {

        const grid =
            this.featuredProducts?.dom?.grid;

        grid?.style.removeProperty(
            '--products-columns'
        );

        this.featuredProducts?.cards?.forEach(card => {

            card.style.removeProperty(
                '--product-delay'
            );

            card.style.removeProperty(
                'transition'
            );

            card.style.removeProperty(
                'animation'
            );

            card.classList.remove(
                'is-active',
                'is-selected',
                'is-hovered',
                'is-visible',
                'is-loaded',
                'is-animatable',
                'is-in-wishlist',
                'is-in-compare'
            );

            card.removeAttribute(
                'aria-selected'
            );

            card.removeAttribute(
                'tabindex'
            );

            card.removeAttribute(
                'data-product-ready'
            );

        });

    }

    /**
     * Cleans runtime state.
     */
    cleanupFeaturedProductsState() {

        if (!this.featuredProducts) {
            return;
        }

        this.featuredProducts.state = {

            total: 0,

            visible: 0,

            rows: 0,

            columns: 0,

            active: null,

            selected: null,

            hovered: null,

            animating: false,

            loading: false,

            initializedAt: 0

        };

    }

    /**
     * Cleans observers.
     */
    cleanupFeaturedProductsObservers() {

        this.disconnectFeaturedProductsLazyObserver?.();

    }

    /**
     * Cleans accessibility.
     */
    cleanupFeaturedProductsAccessibility() {

        if (
            this.featuredProducts?.liveRegion
        ) {

            this.featuredProducts.liveRegion.remove();

            this.featuredProducts.liveRegion =
                null;

        }

    }

    /**
     * Cleans DOM references.
     */
    cleanupFeaturedProductsDOM() {

        if (!this.featuredProducts) {
            return;
        }

        this.featuredProducts.cards = [];

        this.featuredProducts.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupFeaturedProductsConfig() {

        if (!this.featuredProducts) {
            return;
        }

        this.featuredProducts.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupFeaturedProducts() {

        this.cleanupFeaturedProductEvents();

        this.cleanupFeaturedProductsObservers();

        this.cleanupFeaturedProductsAccessibility();

        this.cleanupFeaturedProductsStyles();

        this.cleanupFeaturedProductsState();

        this.cleanupFeaturedProductsDOM();

        this.cleanupFeaturedProductsConfig();

        this.emit(
            'tefco:featured-products:cleanup'
        );

    }

    /**
     * Destroys Featured Products.
     */
    destroyFeaturedProducts() {

        if (
            !this.featuredProducts ||
            this.featuredProducts.destroyed
        ) {
            return;
        }

        this.cleanupFeaturedProducts();

        this.featuredProducts.initialized =
            false;

        this.featuredProducts.destroyed =
            true;

        this.emit(
            'tefco:featured-products:destroyed'
        );

    }

    /**
     * Rebuilds Featured Products.
     */
    rebuildFeaturedProducts() {

        this.destroyFeaturedProducts();

        this.initializeFeaturedProducts();

        this.initializeFeaturedProductsEngine();

        this.initializeFeaturedProductInteractions();

        this.initializeFeaturedProductsResponsive();

        this.initializeFeaturedProductsLazyLoading();

        this.initializeFeaturedProductsAccessibility();

        this.initializeFeaturedProductsThemeEditor();

        this.emit(
            'tefco:featured-products:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getFeaturedProductsLifecycleState() {

        return {

            initialized:
                this.featuredProducts?.initialized ?? false,

            destroyed:
                this.featuredProducts?.destroyed ?? false,

            valid:
                this.validateFeaturedProducts(),

            diagnostics:
                this.runFeaturedProductsDiagnostics()

        };

    }

    /**
     * Returns whether destroyed.
     *
     * @returns {boolean}
     */
    isFeaturedProductsDestroyed() {

        return !!(
            this.featuredProducts?.destroyed
        );

    }
        /**
     * ========================================================================
     * Part 4B.1
     * Testimonials Foundation
     * ------------------------------------------------------------------------
     * Enterprise Testimonials Engine
     * Foundation
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Testimonials.
     */
    
    //----------------------------------------
    // PART 4 — Testimonials
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Testimonials
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Testimonials
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Testimonials
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Testimonials
    //----------------------------------------

initializeTestimonials() {

        this.cacheTestimonialsDOM();

        if (!this.testimonials.dom.section) {
            return;
        }

        this.initializeTestimonialsConfig();

        this.initializeTestimonialsState();

        this.collectTestimonialSlides();

        // Runtime-owned resources. These registries make section rebuilds
        // idempotent and allow destroy() to release every listener/timer.
        this.testimonials.events = [];
        this.testimonials.themeEditorEvents =
            this.testimonials.themeEditorEvents ?? [];
        this.testimonials.resizeTimer = null;

        this.testimonials.initialized = true;

        this.testimonials.destroyed = false;

        this.emit(
            'tefco:testimonials:initialized'
        );

    }

    /**
     * Caches DOM elements.
     */
    cacheTestimonialsDOM() {

        const section =
            document.querySelector(
                '[data-testimonials]'
            );

        this.testimonials = {

            ...this.testimonials,

            dom: {

                section,

                slider:
                    section?.querySelector(
                        '[data-testimonials-slider]'
                    ) ?? null,

                track:
                    section?.querySelector(
                        '[data-testimonials-track]'
                    ) ?? null,

                wrapper:
                    section?.querySelector(
                        '[data-testimonials-wrapper]'
                    ) ?? null,

                navigation:
                    section?.querySelector(
                        '[data-testimonials-navigation]'
                    ) ?? null,

                pagination:
                    section?.querySelector(
                        '[data-testimonials-pagination]'
                    ) ?? null,

                previousButton:
                    section?.querySelector(
                        '[data-testimonials-prev]'
                    ) ?? null,

                nextButton:
                    section?.querySelector(
                        '[data-testimonials-next]'
                    ) ?? null,

                slides:
                    section
                        ? Array.from(
                            section.querySelectorAll(
                                '[data-testimonial-slide]'
                            )
                        )
                        : []

            }

        };

    }

    /**
     * Initializes configuration.
     */
    initializeTestimonialsConfig() {

        const section =
            this.testimonials.dom.section;

        this.testimonials.config = {

            autoplay:
                section?.dataset.autoplay === 'true',

            autoplaySpeed:
                Number(
                    section?.dataset.autoplaySpeed
                ) || 5000,

            loop:
                section?.dataset.loop !== 'false',

            animation:
                section?.dataset.animation !== 'false',

            animationDuration:
                Number(
                    section?.dataset.animationDuration
                ) || 500,

            animationDelay:
                Number(
                    section?.dataset.animationDelay
                ) || 100,

            pauseOnHover:
                section?.dataset.pauseOnHover !== 'false',

            draggable:
                section?.dataset.draggable !== 'false',

            touch:
                section?.dataset.touch !== 'false',

            lazyLoad:
                section?.dataset.lazy !== 'false'

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeTestimonialsState() {

        this.testimonials.state = {

            total: 0,

            visible: 0,

            current: 0,

            previous: null,

            hovered: false,

            dragging: false,

            autoplayRunning: false,

            animating: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects testimonial slides.
     */
    collectTestimonialSlides() {

        const slides =
            this.testimonials.dom.slides;

        this.testimonials.slides =
            slides;

        this.testimonials.state.total =
            slides.length;

        this.testimonials.state.visible =
            slides.filter(slide =>
                slide.offsetParent !== null
            ).length;

        slides.forEach(
            (slide, index) => {

                slide.dataset.testimonialIndex =
                    index;

            }
        );

    }

    /**
     * Returns testimonial slides.
     *
     * @returns {HTMLElement[]}
     */
    getTestimonialSlides() {

        return this.testimonials.slides;

    }

    /**
     * Returns current slide.
     *
     * @returns {number}
     */
    getCurrentTestimonial() {

        return this.testimonials.state.current;

    }

    /**
     * Sets current slide.
     *
     * @param {number} index
     */
    setCurrentTestimonial(index) {

        this.testimonials.state.current =
            index;

    }

    /**
     * Returns whether Testimonials
     * engine is initialized.
     *
     * @returns {boolean}
     */
    isTestimonialsReady() {

        return !!(

            this.testimonials &&
            this.testimonials.initialized &&
            !this.testimonials.destroyed &&
            this.testimonials.dom.section

        );

    }
        /**
     * ========================================================================
     * Part 4B.2
     * Testimonials Slider Engine
     * ------------------------------------------------------------------------
     * Enterprise Slider Engine
     * Slide Activation
     * Navigation
     * Loop Support
     * Track Synchronization
     * Visibility Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes slider engine.
     */
    initializeTestimonialsSlider() {

        if (!this.isTestimonialsReady()) {
            return;
        }

        this.prepareTestimonialSlides();

        this.calculateTestimonialsSlider();

        this.updateTestimonialsSlider();

        this.emit(
            'tefco:testimonials:slider:initialized'
        );

    }


    /**
     * Synchronizes the current Testimonials slider state and presentation.
     */
    updateTestimonialsSlider() {
        if (!this.testimonials || !this.testimonials.state) {
            return;
        }

        this.deactivateTestimonials();

        const total = this.testimonials.state.total || 0;
        if (!total) {
            return;
        }

        const current = Math.max(
            0,
            Math.min(this.testimonials.state.current || 0, total - 1)
        );

        this.testimonials.state.current = current;

        const slide = this.testimonials.slides[current];
        slide?.classList.add('is-active');

        this.updateAdjacentTestimonials();
        this.updateTestimonialsTrack();
        this.updateTestimonialsPagination?.();
        this.updateTestimonialsAccessibility?.();
    }

    /**
     * Prepares testimonial slides.
     */
    prepareTestimonialSlides() {

        this.testimonials.slides.forEach(
            (slide, index) => {

                slide.dataset.testimonialIndex =
                    index;

                slide.classList.remove(
                    'is-active',
                    'is-previous',
                    'is-next'
                );

                slide.setAttribute(
                    'data-slide-ready',
                    'true'
                );

            }
        );

    }

    /**
     * Calculates slider metrics.
     */
    calculateTestimonialsSlider() {

        this.testimonials.state.total =
            this.testimonials.slides.length;

        this.testimonials.state.visible =
            this.getVisibleTestimonialSlides()
                .length;

        if (
            this.testimonials.state.current >=
            this.testimonials.state.total
        ) {

            this.testimonials.state.current = 0;

        }

    }

    /**
     * Activates testimonial.
     *
     * @param {number} index
     */
    activateTestimonial(index) {

        const total =
            this.testimonials.state.total;

        if (!total) {
            return;
        }


        const slidesPerView = Math.max(
            1,
            Number(
                this.testimonials.state.slidesPerView
            ) || 1
        );


        const maxStartIndex = Math.max(
            0,
            total - slidesPerView
        );


        if (
            this.testimonials.config.loop
        ) {

            if (index < 0) {
                index = maxStartIndex;
            }

            if (index > maxStartIndex) {
                index = 0;
            }

        } else {

            index = Math.max(
                0,
                Math.min(
                    index,
                    maxStartIndex
                )
            );

        }


        this.deactivateTestimonials();


        const slide =
            this.testimonials.slides[index];


        if (!slide) {
            return;
        }


        this.testimonials.state.previous =
            this.testimonials.state.current;


        this.testimonials.state.current =
            index;


        slide.classList.add(
            'is-active'
        );


        this.updateAdjacentTestimonials();

        this.updateTestimonialsTrack();

        this.updateTestimonialsPagination?.();

        this.updateTestimonialsAccessibility?.();


        this.emit(
            'tefco:testimonials:activated',
            {
                index,
                slide
            }
        );

    }

    /**
     * Activates testimonial slide.
     *
     * @param {HTMLElement} slide
     */
    activateTestimonialSlide(slide) {

        if (!slide) {
            return;
        }

        const index =
            Number(
                slide.dataset.testimonialIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateTestimonial(
            index
        );

    }

    /**
     * Deactivates testimonials.
     */
    deactivateTestimonials() {

        this.testimonials.slides.forEach(
            slide => {

                slide.classList.remove(
                    'is-active',
                    'is-previous',
                    'is-next'
                );

            }
        );

    }

    /**
     * Updates adjacent slides.
     */
    updateAdjacentTestimonials() {

        const current =
            this.testimonials.state.current;

        const total =
            this.testimonials.state.total;

        const previous =
            current === 0
                ? total - 1
                : current - 1;

        const next =
            current === total - 1
                ? 0
                : current + 1;

        this.testimonials.slides[
            previous
        ]?.classList.add(
            'is-previous'
        );

        this.testimonials.slides[
            next
        ]?.classList.add(
            'is-next'
        );

    }

    /**
     * Updates slider track.
     */
    updateTestimonialsTrack() {

        const track =
            this.testimonials.dom.track;

        if (!track) {
            return;
        }

        const total =
            this.testimonials.state.total ||
            this.testimonials.slides?.length ||
            0;

        const slidesPerView = Math.max(
            1,
            Number(
                this.testimonials.state.slidesPerView
            ) || 1
        );

        const maxStartIndex = Math.max(
            0,
            total - slidesPerView
        );

        const current = Math.max(
            0,
            Math.min(
                Number(
                    this.testimonials.state.current
                ) || 0,
                maxStartIndex
            )
        );

        this.testimonials.state.current =
            current;

        const translate =
            current * (100 / slidesPerView);

        track.style.transform =
            `translate3d(-${translate}%,0,0)`;

        track.setAttribute(
            'data-active-slide',
            current
        );

        this.emit(
            'tefco:testimonials:track:updated',
            {
                current,
                slidesPerView,
                maxStartIndex,
                translate
            }
        );

    }

    /**
     * Synchronizes slider.
     */
    syncTestimonialsSlider() {

        this.calculateTestimonialsSlider();

        this.updateTestimonialsTrack();

        this.emit(
            'tefco:testimonials:slider:synced'
        );

    }

    /**
     * Refreshes slider.
     */
    refreshTestimonialsSlider() {

        this.collectTestimonialSlides();

        this.syncTestimonialsSlider();

        this.emit(
            'tefco:testimonials:slider:refreshed'
        );

    }

    /**
     * Updates visible slides.
     */
    updateTestimonialsVisibility() {

        this.testimonials.state.visible =
            this.getVisibleTestimonialSlides()
                .length;

    }

    /**
     * Returns visible slides.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleTestimonialSlides() {

        return this.testimonials.slides.filter(
            slide =>
                slide.offsetParent !== null
        );

    }

    /**
     * Goes to next testimonial.
     */
    nextTestimonial() {

        this.activateTestimonial(
            this.testimonials.state.current + 1
        );

    }

    /**
     * Goes to previous testimonial.
     */
    previousTestimonial() {

        this.activateTestimonial(
            this.testimonials.state.current - 1
        );

    }

    /**
     * Returns slider state.
     *
     * @returns {Object}
     */
    getTestimonialsSliderState() {

        return {

            total:
                this.testimonials.state.total,

            visible:
                this.testimonials.state.visible,

            current:
                this.testimonials.state.current,

            previous:
                this.testimonials.state.previous,

            loop:
                this.testimonials.config.loop

        };

    }

    /**
     * Returns whether slider
     * engine is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsSliderReady() {

        return !!(

            this.isTestimonialsReady() &&
            this.testimonials.slides.length > 0

        );

    }
        /**
     * ========================================================================
     * Part 4B.3
     * Navigation & Autoplay
     * ------------------------------------------------------------------------
     * Enterprise Navigation Engine
     * Previous / Next Controls
     * Pagination
     * Autoplay
     * Pause / Resume
     * Hover Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes navigation.
     */
    initializeTestimonialsNavigation() {

        if (!this.isTestimonialsSliderReady()) {
            return;
        }

        this.bindTestimonialsNavigationEvents();

        this.initializeTestimonialsPagination();

        if (
            this.testimonials.config.autoplay
        ) {

            this.startTestimonialsAutoplay();

        }

        this.emit(
            'tefco:testimonials:navigation:initialized'
        );

    }

    /**
     * Binds a Testimonials runtime-owned event.
     */
    bindTestimonialsEvent(target, event, handler, options) {

        if (!target || typeof handler !== 'function') {
            return null;
        }

        target.addEventListener(event, handler, options);

        this.testimonials.events ??= [];
        this.testimonials.events.push({
            target,
            event,
            handler,
            options
        });

        return handler;

    }

    /**
     * Removes every Testimonials runtime-owned event listener.
     */
    cleanupTestimonialsEvents() {

        this.testimonials?.events?.forEach(listener => {
            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );
        });

        if (this.testimonials) {
            this.testimonials.events = [];
        }

    }

    /**
     * Binds navigation events.
     */
    bindTestimonialsNavigationEvents() {

        const {
            previousButton,
            nextButton,
            slider
        } = this.testimonials.dom;

        if (previousButton) {
            this.bindTestimonialsEvent(
                previousButton,
                'click',
                () => this.previousTestimonial()
            );
        }

        if (nextButton) {
            this.bindTestimonialsEvent(
                nextButton,
                'click',
                () => this.nextTestimonial()
            );
        }

        if (
            slider &&
            this.testimonials.config.pauseOnHover
        ) {

            this.bindTestimonialsEvent(
                slider,
                'mouseenter',
                () => this.pauseTestimonialsAutoplay()
            );

            this.bindTestimonialsEvent(
                slider,
                'mouseleave',
                () => this.resumeTestimonialsAutoplay()
            );

        }

    }

    /**
     * Initializes pagination.
     */
    initializeTestimonialsPagination() {

        const pagination =
            this.testimonials.dom.pagination;

        if (!pagination) {
            return;
        }

        /*
         * Support both pagination contracts:
         *
         * JS-generated:
         * .tefco-testimonials-dot
         * data-testimonial-index
         *
         * Liquid-rendered:
         * .tefco-testimonials__pagination-item
         * data-slide-index
         *
         * Event delegation keeps one managed listener while pagination
         * can be rebuilt responsively without rebinding click handlers.
         */
        this.bindTestimonialsEvent(
            pagination,
            'click',
            event => {

                const button =
                    event.target.closest(
                        '.tefco-testimonials-dot, ' +
                        '.tefco-testimonials__pagination-item'
                    );

                if (
                    !button ||
                    !pagination.contains(button)
                ) {
                    return;
                }

                const rawIndex =
                    button.dataset.testimonialIndex ??
                    button.dataset.slideIndex;

                const index =
                    Number(rawIndex);

                if (
                    rawIndex === undefined ||
                    rawIndex === null ||
                    rawIndex === '' ||
                    Number.isNaN(index)
                ) {
                    return;
                }

                this.activateTestimonial(index);

            }
        );

        this.updateTestimonialsPagination();

    }

    /**
     * Updates pagination.
     */
    updateTestimonialsPagination() {

        const pagination =
            this.testimonials.dom.pagination;

        if (!pagination) {
            return;
        }

        const total =
            this.testimonials.state.total || 0;

        const slidesPerView = Math.max(
            1,
            Number(
                this.testimonials.state.slidesPerView
            ) || 1
        );

        const maxStartIndex = Math.max(
            0,
            total - slidesPerView
        );

        const positionCount =
            total > 0
                ? maxStartIndex + 1
                : 0;

        while (
            pagination.children.length >
            positionCount
        ) {
            pagination.lastElementChild?.remove();
        }

        while (
            pagination.children.length <
            positionCount
        ) {

            const index =
                pagination.children.length;

            const button =
                document.createElement(
                    'button'
                );

            button.type = 'button';

            button.className =
                'tefco-testimonials-dot';

            button.dataset.testimonialIndex =
                index;

            pagination.appendChild(button);

        }

        Array.from(
            pagination.children
        ).forEach(
            (button, index) => {

                const active =
                    index ===
                    this.testimonials.state.current;

                button.dataset.testimonialIndex =
                    index;

                button.classList.toggle(
                    'is-active',
                    active
                );

                button.setAttribute(
                    'aria-label',
                    `Go to testimonial position ${index + 1} of ${positionCount}`
                );

                button.setAttribute(
                    'aria-current',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );

    }

    /**
     * Starts autoplay.
     */
    startTestimonialsAutoplay() {

        this.stopTestimonialsAutoplay();

        this.testimonials.autoplayTimer =
            window.setInterval(() => {

                this.nextTestimonial();

                this.updateTestimonialsPagination();

            },
            this.testimonials.config.autoplaySpeed);

        this.testimonials.state.autoplayRunning =
            true;

        this.emit(
            'tefco:testimonials:autoplay:started'
        );

    }

    /**
     * Stops autoplay.
     */
    stopTestimonialsAutoplay() {

        if (
            this.testimonials.autoplayTimer
        ) {

            clearInterval(
                this.testimonials.autoplayTimer
            );

            this.testimonials.autoplayTimer =
                null;

        }

        this.testimonials.state.autoplayRunning =
            false;

        this.emit(
            'tefco:testimonials:autoplay:stopped'
        );

    }

    /**
     * Pauses autoplay.
     */
    pauseTestimonialsAutoplay() {

        if (
            !this.testimonials.state.autoplayRunning
        ) {
            return;
        }

        this.stopTestimonialsAutoplay();

        this.emit(
            'tefco:testimonials:autoplay:paused'
        );

    }

    /**
     * Resumes autoplay.
     */
    resumeTestimonialsAutoplay() {

        if (
            !this.testimonials.config.autoplay
        ) {
            return;
        }

        this.startTestimonialsAutoplay();

        this.emit(
            'tefco:testimonials:autoplay:resumed'
        );

    }

    /**
     * Toggles autoplay.
     */
    toggleTestimonialsAutoplay() {

        if (
            this.testimonials.state.autoplayRunning
        ) {

            this.stopTestimonialsAutoplay();

        } else {

            this.startTestimonialsAutoplay();

        }

    }

    /**
     * Refreshes navigation.
     */
    refreshTestimonialsNavigation() {

        this.updateTestimonialsPagination();

        if (
            this.testimonials.config.autoplay
        ) {

            this.startTestimonialsAutoplay();

        }

        this.emit(
            'tefco:testimonials:navigation:refreshed'
        );

    }

    /**
     * Returns navigation state.
     *
     * @returns {Object}
     */
    getTestimonialsNavigationState() {

        return {

            current:
                this.testimonials.state.current,

            autoplay:
                this.testimonials.state.autoplayRunning,

            pauseOnHover:
                this.testimonials.config.pauseOnHover,

            total:
                this.testimonials.state.total

        };

    }

    /**
     * Returns whether navigation
     * engine is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsNavigationReady() {

        return !!(

            this.isTestimonialsSliderReady() &&

            this.testimonials.dom
                .previousButton &&

            this.testimonials.dom
                .nextButton

        );

    }
        /**
     * ========================================================================
     * Part 4B.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Testimonial Slider
     * Breakpoints
     * Resize Handling
     * Dynamic Layout
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeTestimonialsResponsive() {

        if (!this.isTestimonialsSliderReady()) {
            return;
        }

        this.bindTestimonialsResize();

        this.updateTestimonialsBreakpoints();

        this.updateTestimonialsLayout();

        this.emit(
            'tefco:testimonials:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindTestimonialsResize() {

        this.bindTestimonialsEvent(
            window,
            'resize',
            () => {

                if (this.testimonials.resizeTimer) {
                    clearTimeout(
                        this.testimonials.resizeTimer
                    );
                }

                this.testimonials.resizeTimer =
                    window.setTimeout(() => {

                        this.testimonials.resizeTimer = null;

                        if (
                            !this.testimonials?.initialized ||
                            this.testimonials?.destroyed
                        ) {
                            return;
                        }

                        this.updateTestimonialsBreakpoints();

                        this.updateTestimonialsLayout();

                    }, 100);

            }
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateTestimonialsBreakpoints() {

        this.testimonials.state.slidesPerView =
            this.calculateTestimonialsSlidesPerView();

        this.emit(
            'tefco:testimonials:breakpoint:changed',
            {
                breakpoint:
                    this.getTestimonialsBreakpoint(),

                slidesPerView:
                    this.testimonials.state.slidesPerView
            }
        );

    }

    /**
     * Calculates slides per view.
     *
     * @returns {number}
     */
    calculateTestimonialsSlidesPerView() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return 3;

        }

        if (width >= 768) {

            return 2;

        }

        return 1;

    }

    /**
     * Updates responsive layout.
     */
    updateTestimonialsLayout() {

        const track =
            this.testimonials.dom.track;

        if (!track) {
            return;
        }

        const slidesPerView =
            this.testimonials.state.slidesPerView;

        track.style.setProperty(
            '--testimonials-slides-per-view',
            slidesPerView
        );

        track.setAttribute(
            'data-slides-per-view',
            slidesPerView
        );

        this.updateTestimonialsVisibility();

        this.updateTestimonialsTrack();

        this.updateTestimonialsPagination?.();

        this.updateTestimonialsAccessibility?.();

        this.emit(
            'tefco:testimonials:layout:updated',
            {
                slidesPerView
            }
        );

    }

    /**
     * Refreshes responsive layout.
     */
    refreshTestimonialsLayout() {

        this.updateTestimonialsBreakpoints();

        this.updateTestimonialsLayout();

        this.emit(
            'tefco:testimonials:layout:refreshed'
        );

    }

    /**
     * Recalculates layout.
     */
    recalculateTestimonialsLayout() {

        this.collectTestimonialSlides();

        this.refreshTestimonialsLayout();

        this.emit(
            'tefco:testimonials:layout:recalculated'
        );

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getTestimonialsBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getTestimonialsResponsiveState() {

        return {

            breakpoint:
                this.getTestimonialsBreakpoint(),

            slidesPerView:
                this.testimonials.state.slidesPerView,

            total:
                this.testimonials.state.total,

            visible:
                this.testimonials.state.visible,

            current:
                this.testimonials.state.current

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsResponsiveReady() {

        return !!(

            this.isTestimonialsSliderReady() &&
            this.testimonials.state.slidesPerView > 0

        );

    }
        /**
     * ========================================================================
     * Part 4B.5
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeTestimonialsLazyLoading() {

        if (
            !this.isTestimonialsSliderReady() ||
            !this.testimonials.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.testimonials.slides.forEach(slide => {

                this.loadTestimonialSlide(slide);

            });

            return;

        }

        this.testimonials.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadTestimonialSlide(
                            entry.target
                        );

                        this.testimonials.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {
                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05

                }

            );

        this.testimonials.slides.forEach(slide => {

            this.testimonials.lazyObserver.observe(
                slide
            );

        });

        this.preloadVisibleTestimonials();

        this.emit(
            'tefco:testimonials:lazy:initialized'
        );

    }

    /**
     * Loads testimonial assets.
     *
     * @param {HTMLElement} slide
     */
    loadTestimonialSlide(slide) {

        if (!slide) {
            return;
        }

        if (
            slide.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            slide.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        slide.dataset.loaded =
            'true';

        slide.classList.add(
            'is-loaded'
        );

        this.emit(
            'tefco:testimonials:slide:loaded',
            {
                slide
            }
        );

    }

    /**
     * Preloads visible testimonials.
     */
    preloadVisibleTestimonials() {

        this.getVisibleTestimonialSlides()
            .forEach(slide => {

                this.loadTestimonialSlide(
                    slide
                );

            });

    }

    /**
     * Preloads adjacent testimonials.
     */
    preloadAdjacentTestimonials() {

        const visible =
            this.getVisibleTestimonialSlides();

        visible.forEach(slide => {

            const next =
                slide.nextElementSibling;

            if (
                next?.matches(
                    '[data-testimonial-slide]'
                )
            ) {

                this.loadTestimonialSlide(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshTestimonialsLazyLoading() {

        this.disconnectTestimonialsLazyObserver();

        this.initializeTestimonialsLazyLoading();

        this.emit(
            'tefco:testimonials:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectTestimonialsLazyObserver() {

        if (
            this.testimonials.lazyObserver
        ) {

            this.testimonials.lazyObserver.disconnect();

            this.testimonials.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded testimonial slides.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedTestimonialSlides() {

        return this.testimonials.slides.filter(
            slide =>
                slide.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending testimonial slides.
     *
     * @returns {HTMLElement[]}
     */
    getPendingTestimonialSlides() {

        return this.testimonials.slides.filter(
            slide =>
                slide.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getTestimonialsLazyState() {

        return {

            enabled:
                this.testimonials.config.lazyLoad,

            observer:
                !!this.testimonials.lazyObserver,

            loaded:
                this.getLoadedTestimonialSlides()
                    .length,

            pending:
                this.getPendingTestimonialSlides()
                    .length,

            total:
                this.testimonials.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * engine is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsLazyReady() {

        return !!(

            this.isTestimonialsSliderReady() &&
            this.testimonials.config.lazyLoad

        );

    }
        /**
     * ========================================================================
     * Part 4B.6
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeTestimonialsAccessibility() {

        if (!this.isTestimonialsSliderReady()) {
            return;
        }

        this.initializeTestimonialsAccessibilityAttributes();

        this.bindTestimonialsKeyboardNavigation();

        this.initializeTestimonialsLiveRegion();

        this.initializeTestimonialsReducedMotion();

        this.emit(
            'tefco:testimonials:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeTestimonialsAccessibilityAttributes() {

        const {
            section,
            slider,
            track
        } = this.testimonials.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Testimonials'
        );

        slider?.setAttribute(
            'role',
            'group'
        );

        slider?.setAttribute(
            'aria-roledescription',
            'carousel'
        );

        track?.setAttribute(
            'role',
            'list'
        );

        this.testimonials.slides.forEach(
            (slide, index) => {

                slide.setAttribute(
                    'role',
                    'listitem'
                );

                slide.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                slide.setAttribute(
                    'aria-setsize',
                    this.testimonials.state.total
                );

            }
        );

        this.updateTestimonialsAccessibility();

    }

    /**
     * Binds keyboard navigation.
     */
    bindTestimonialsKeyboardNavigation() {

        const slider =
            this.testimonials.dom.slider;

        if (!slider) {
            return;
        }

        this.bindTestimonialsEvent(
            slider,
            'keydown',
            event => {

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        this.nextTestimonial();

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        this.previousTestimonial();

                        break;

                    case 'Home':

                        event.preventDefault();

                        this.activateTestimonial(
                            0
                        );

                        break;

                    case 'End': {

                        event.preventDefault();

                        const total =
                            this.testimonials.state.total || 0;

                        const slidesPerView = Math.max(
                            1,
                            Number(
                                this.testimonials.state.slidesPerView
                            ) || 1
                        );

                        const maxStartIndex = Math.max(
                            0,
                            total - slidesPerView
                        );

                        this.activateTestimonial(
                            maxStartIndex
                        );

                        break;
                    }

                    default:

                        return;

                }

                this.focusTestimonial(
                    this.testimonials.state.current
                );

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeTestimonialsLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.testimonials.liveRegion =
            liveRegion;

    }

    /**
     * Announces active testimonial.
     *
     * @param {number} index
     */
    announceTestimonial(index) {

        if (
            !this.testimonials.liveRegion
        ) {
            return;
        }

        const total =
            this.testimonials.state.total;

        this.testimonials.liveRegion.textContent =
            `Testimonial ${index + 1} of ${total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeTestimonialsReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.testimonials.reducedMotion =
            media.matches;

        if (
            this.testimonials.reducedMotion
        ) {

            this.testimonials.slides.forEach(
                slide => {

                    slide.style.transition =
                        'none';

                    slide.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses testimonial.
     *
     * @param {number} index
     */
    focusTestimonial(index) {

        this.testimonials.slides[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility state.
     */
    updateTestimonialsAccessibility() {

        const current =
            this.testimonials.state.current || 0;

        const slidesPerView = Math.max(
            1,
            Number(
                this.testimonials.state.slidesPerView
            ) || 1
        );

        const visibleEnd =
            current + slidesPerView;

        this.testimonials.slides.forEach(
            (slide, index) => {

                const visible =
                    index >= current &&
                    index < visibleEnd;

                slide.setAttribute(
                    'aria-hidden',
                    visible
                        ? 'false'
                        : 'true'
                );

                slide.setAttribute(
                    'tabindex',
                    visible
                        ? '0'
                        : '-1'
                );

            }
        );

        this.announceTestimonial(
            current
        );

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getTestimonialsAccessibilityState() {

        return {

            reducedMotion:
                this.testimonials.reducedMotion,

            liveRegion:
                !!this.testimonials.liveRegion,

            current:
                this.testimonials.state.current,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsAccessibilityReady() {

        return !!(

            this.isTestimonialsSliderReady() &&
            this.testimonials.liveRegion

        );

    }
        /**
     * ========================================================================
     * Part 4B.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Dynamic Refresh
     * OS 2.0 Compatible
     * ========================================================================
     */

    /**
     * Initializes Theme Editor support.
     */
    initializeTestimonialsThemeEditor() {

        if (
            typeof Shopify === 'undefined' ||
            !Shopify.designMode
        ) {
            return;
        }

        if (this.testimonials?.themeEditorEvents?.length) {
            return;
        }

        this.bindTestimonialsThemeEditorEvents();

        this.emit(
            'tefco:testimonials:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindTestimonialsThemeEditorEvents() {

        const bindings = [
            ['shopify:section:load', 'handleTestimonialsSectionLoad'],
            ['shopify:section:unload', 'handleTestimonialsSectionUnload'],
            ['shopify:section:select', 'handleTestimonialsSectionSelect'],
            ['shopify:section:deselect', 'handleTestimonialsSectionDeselect'],
            ['shopify:block:select', 'handleTestimonialsBlockSelect'],
            ['shopify:block:deselect', 'handleTestimonialsBlockDeselect']
        ];

        this.testimonials.themeEditorEvents ??= [];

        bindings.forEach(([event, method]) => {
            const handler = this[method].bind(this);
            document.addEventListener(event, handler);
            this.testimonials.themeEditorEvents.push({
                target: document,
                event,
                handler
            });
        });

    }

    /**
     * Removes Testimonials Theme Editor listeners.
     */
    cleanupTestimonialsThemeEditorEvents() {

        this.testimonials?.themeEditorEvents?.forEach(listener => {
            listener.target?.removeEventListener(
                listener.event,
                listener.handler
            );
        });

        if (this.testimonials) {
            this.testimonials.themeEditorEvents = [];
        }

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-testimonials]'
            )
        ) {
            return;
        }

        // Shopify replaces the section DOM before this event fires. Release
        // stale runtime resources while keeping the document-level editor
        // listeners alive so future load/unload events remain observable.
        this.destroyTestimonials({ preserveThemeEditor: true });

        this.initializeTestimonials();

        this.initializeTestimonialsSlider();

        this.initializeTestimonialsNavigation();

        this.initializeTestimonialsResponsive();

        this.initializeTestimonialsLazyLoading();

        this.initializeTestimonialsAccessibility();

        this.emit(
            'tefco:testimonials:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-testimonials]'
            )
        ) {
            return;
        }

        this.destroyTestimonials({ preserveThemeEditor: true });

        this.emit(
            'tefco:testimonials:section:unloaded'
        );

    }

    /**
     * Handles section select.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-testimonials]'
            )
        ) {
            return;
        }

        this.refreshTestimonialsThemeEditor();

        this.emit(
            'tefco:testimonials:section:selected'
        );

    }

    /**
     * Handles section deselect.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-testimonials]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:testimonials:section:deselected'
        );

    }

    /**
     * Handles block select.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-testimonial-slide]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.testimonialIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateTestimonial(index);

        this.focusTestimonial(index);

        this.emit(
            'tefco:testimonials:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselect.
     *
     * @param {CustomEvent} event
     */
    handleTestimonialsBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-testimonial-slide]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:testimonials:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor.
     */
    refreshTestimonialsThemeEditor() {

        this.collectTestimonialSlides();

        this.calculateTestimonialsSlider();

        this.updateTestimonialsTrack();

        this.updateTestimonialsPagination?.();

        this.updateTestimonialsAccessibility?.();

        this.emit(
            'tefco:testimonials:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getTestimonialsThemeEditorState() {

        return {

            enabled:
                typeof Shopify !== 'undefined' &&
                Shopify.designMode,

            initialized:
                this.isTestimonialsThemeEditorReady(),

            current:
                this.testimonials?.state?.current ?? 0,

            total:
                this.testimonials?.state?.total ?? 0,

            visible:
                this.testimonials?.state?.visible ?? 0

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isTestimonialsThemeEditorReady() {

        return !!(

            typeof Shopify !== 'undefined' &&
            Shopify.designMode &&
            this.isTestimonialsSliderReady()

        );

    }
        /**
     * ========================================================================
     * Part 4B.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runTestimonialsDiagnostics() {

        return {

            ready:
                this.isTestimonialsReady(),

            initialized:
                this.testimonials?.initialized ?? false,

            destroyed:
                this.testimonials?.destroyed ?? false,

            total:
                this.testimonials?.state?.total ?? 0,

            visible:
                this.testimonials?.state?.visible ?? 0,

            current:
                this.testimonials?.state?.current ?? 0,

            previous:
                this.testimonials?.state?.previous,

            hovered:
                this.testimonials?.state?.hovered ?? false,

            dragging:
                this.testimonials?.state?.dragging ?? false,

            autoplay:
                this.testimonials?.state?.autoplayRunning ?? false,

            animating:
                this.testimonials?.state?.animating ?? false,

            slidesPerView:
                this.testimonials?.state?.slidesPerView ?? 1,

            lazyLoaded:
                this.getLoadedTestimonialSlides?.()
                    ?.length ?? 0,

            pendingLazy:
                this.getPendingTestimonialSlides?.()
                    ?.length ?? 0,

            responsive:
                this.isTestimonialsResponsiveReady?.() ?? false,

            navigation:
                this.isTestimonialsNavigationReady?.() ?? false,

            accessibility:
                this.isTestimonialsAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isTestimonialsLazyReady?.() ?? false,

            themeEditor:
                this.isTestimonialsThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Testimonials.
     *
     * @returns {boolean}
     */
    validateTestimonials() {

        if (!this.testimonials) {
            return false;
        }

        const {
            dom,
            slides
        } = this.testimonials;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.track) {
            return false;
        }

        if (!Array.isArray(slides)) {
            return false;
        }

        if (!slides.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logTestimonialsDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Testimonials Diagnostics'
        );

        console.table(
            this.runTestimonialsDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupTestimonialsStyles() {

        const track =
            this.testimonials?.dom?.track;

        track?.style.removeProperty(
            'transform'
        );

        track?.style.removeProperty(
            '--testimonials-slides-per-view'
        );

        this.testimonials?.slides?.forEach(
            slide => {

                slide.style.removeProperty(
                    'transition'
                );

                slide.style.removeProperty(
                    'animation'
                );

                slide.classList.remove(
                    'is-active',
                    'is-previous',
                    'is-next',
                    'is-loaded'
                );

                slide.removeAttribute(
                    'tabindex'
                );

                slide.removeAttribute(
                    'aria-hidden'
                );

                slide.removeAttribute(
                    'aria-posinset'
                );

                slide.removeAttribute(
                    'aria-setsize'
                );

                slide.removeAttribute(
                    'data-slide-ready'
                );

                slide.removeAttribute(
                    'data-loaded'
                );

            }
        );

    }

    /**
     * Cleans runtime state.
     */
    cleanupTestimonialsState() {

        if (!this.testimonials) {
            return;
        }

        this.testimonials.state = {

            total: 0,

            visible: 0,

            current: 0,

            previous: null,

            hovered: false,

            dragging: false,

            autoplayRunning: false,

            animating: false,

            slidesPerView: 1,

            initializedAt: 0

        };

    }

    /**
     * Cleans observers.
     */
    cleanupTestimonialsObservers() {

        this.disconnectTestimonialsLazyObserver?.();

    }

    /**
     * Cleans timers.
     */
    cleanupTestimonialsTimers() {

        this.stopTestimonialsAutoplay?.();

        if (this.testimonials?.resizeTimer) {
            clearTimeout(this.testimonials.resizeTimer);
            this.testimonials.resizeTimer = null;
        }

    }

    /**
     * Cleans accessibility.
     */
    cleanupTestimonialsAccessibility() {

        if (
            this.testimonials?.liveRegion
        ) {

            this.testimonials.liveRegion.remove();

            this.testimonials.liveRegion =
                null;

        }

    }

    /**
     * Cleans DOM references.
     */
    cleanupTestimonialsDOM() {

        if (!this.testimonials) {
            return;
        }

        this.testimonials.slides = [];

        this.testimonials.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupTestimonialsConfig() {

        if (!this.testimonials) {
            return;
        }

        this.testimonials.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupTestimonials(options = {}) {

        const { preserveThemeEditor = false } = options;

        this.cleanupTestimonialsTimers();

        this.cleanupTestimonialsEvents();

        if (!preserveThemeEditor) {
            this.cleanupTestimonialsThemeEditorEvents();
        }

        this.cleanupTestimonialsObservers();

        this.cleanupTestimonialsAccessibility();

        this.cleanupTestimonialsStyles();

        this.cleanupTestimonialsState();

        this.cleanupTestimonialsDOM();

        this.cleanupTestimonialsConfig();

        this.emit(
            'tefco:testimonials:cleanup'
        );

    }

    /**
     * Destroys Testimonials.
     */
    destroyTestimonials(options = {}) {

        if (!this.testimonials) {
            return;
        }

        const { preserveThemeEditor = false } = options;

        // Even an already-destroyed section may still own Theme Editor
        // listeners that must be released during the global destroy path.
        if (this.testimonials.destroyed) {
            if (!preserveThemeEditor) {
                this.cleanupTestimonialsThemeEditorEvents?.();
            }
            return;
        }

        this.cleanupTestimonials({ preserveThemeEditor });

        this.testimonials.initialized =
            false;

        this.testimonials.destroyed =
            true;

        this.emit(
            'tefco:testimonials:destroyed'
        );

    }

    /**
     * Rebuilds Testimonials.
     */
    rebuildTestimonials() {

        this.destroyTestimonials();

        this.initializeTestimonials();

        this.initializeTestimonialsSlider();

        this.initializeTestimonialsNavigation();

        this.initializeTestimonialsResponsive();

        this.initializeTestimonialsLazyLoading();

        this.initializeTestimonialsAccessibility();

        this.initializeTestimonialsThemeEditor();

        this.emit(
            'tefco:testimonials:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getTestimonialsLifecycleState() {

        return {

            initialized:
                this.testimonials?.initialized ?? false,

            destroyed:
                this.testimonials?.destroyed ?? false,

            valid:
                this.validateTestimonials(),

            diagnostics:
                this.runTestimonialsDiagnostics()

        };

    }

    /**
     * Returns whether destroyed.
     *
     * @returns {boolean}
     */
    isTestimonialsDestroyed() {

        return !!(
            this.testimonials?.destroyed
        );

    }
        /**
     * ========================================================================
     * Part 4C.1
     * Blog Foundation
     * ------------------------------------------------------------------------
     * Enterprise Blog Engine
     * Foundation
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Blog.
     */
    
    //----------------------------------------
    // PART 4 — Blog
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Blog
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Blog
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Blog
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Blog
    //----------------------------------------

initializeBlog() {

        this.cacheBlogDOM();

        if (!this.blog.dom.section) {
            return;
        }

        this.initializeBlogConfig();

        this.initializeBlogState();

        this.collectBlogArticles();

        this.blog.initialized = true;

        this.blog.destroyed = false;

        this.emit(
            'tefco:blog:initialized'
        );

    }

    /**
     * Caches DOM elements.
     */
    cacheBlogDOM() {

        const section =
            document.querySelector(
                '[data-blog]'
            );

        this.blog = {

            ...this.blog,

            dom: {

                section,

                grid:
                    section?.querySelector(
                        '[data-blog-grid]'
                    ) ?? null,

                wrapper:
                    section?.querySelector(
                        '[data-blog-wrapper]'
                    ) ?? null,

                articles:
                    section
                        ? Array.from(
                            section.querySelectorAll(
                                '[data-blog-article]'
                            )
                        )
                        : [],

                featured:
                    section?.querySelector(
                        '[data-blog-featured]'
                    ) ?? null,

                pagination:
                    section?.querySelector(
                        '[data-blog-pagination]'
                    ) ?? null,

                filters:
                    section?.querySelector(
                        '[data-blog-filters]'
                    ) ?? null,

                search:
                    section?.querySelector(
                        '[data-blog-search]'
                    ) ?? null

            }

        };

    }

    /**
     * Initializes configuration.
     */
    initializeBlogConfig() {

        const section =
            this.blog.dom.section;

        this.blog.config = {

            columns:
                Number(
                    section?.dataset.columns
                ) || 3,

            animation:
                section?.dataset.animation !== 'false',

            animationDuration:
                Number(
                    section?.dataset.animationDuration
                ) || 500,

            animationDelay:
                Number(
                    section?.dataset.animationDelay
                ) || 100,

            lazyLoad:
                section?.dataset.lazy !== 'false',

            equalHeight:
                section?.dataset.equalHeight !== 'false',

            featuredEnabled:
                section?.dataset.featured !== 'false',

            pagination:
                section?.dataset.pagination !== 'false',

            filters:
                section?.dataset.filters === 'true',

            search:
                section?.dataset.search === 'true'

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeBlogState() {

        this.blog.state = {

            total: 0,

            visible: 0,

            featured: null,

            selected: null,

            hovered: null,

            columns:
                this.blog.config.columns,

            animating: false,

            loading: false,

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects blog articles.
     */
    collectBlogArticles() {

        const articles =
            this.blog.dom.articles;

        this.blog.articles =
            articles;

        this.blog.state.total =
            articles.length;

        this.blog.state.visible =
            articles.filter(article =>
                article.offsetParent !== null
            ).length;

        articles.forEach(
            (article, index) => {

                article.dataset.articleIndex =
                    index;

            }
        );

    }

    /**
     * Returns blog articles.
     *
     * @returns {HTMLElement[]}
     */
    getBlogArticles() {

        return this.blog.articles;

    }

    /**
     * Returns featured article.
     *
     * @returns {HTMLElement|null}
     */
    getFeaturedBlogArticle() {

        return this.blog.dom.featured;

    }

    /**
     * Sets featured article.
     *
     * @param {HTMLElement} article
     */
    setFeaturedBlogArticle(article) {

        this.blog.state.featured =
            article;

    }

    /**
     * Returns whether Blog
     * engine is initialized.
     *
     * @returns {boolean}
     */
    isBlogReady() {

        return !!(

            this.blog &&
            this.blog.initialized &&
            !this.blog.destroyed &&
            this.blog.dom.section

        );

    }
        /**
     * ========================================================================
     * Part 4C.2
     * Blog Engine
     * ------------------------------------------------------------------------
     * Enterprise Blog Engine
     * Card Management
     * Featured Article
     * Grid Synchronization
     * Visibility Management
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Blog engine.
     */
    initializeBlogEngine() {

        if (!this.isBlogReady()) {
            return;
        }

        this.prepareBlogArticles();

        this.calculateBlogGrid();

        this.updateBlogGrid();

        this.updateBlogEqualHeight();

        this.emit(
            'tefco:blog:engine:initialized'
        );

    }

    /**
     * Prepares blog articles.
     */
    prepareBlogArticles() {

        this.blog.articles.forEach(
            (article, index) => {

                article.dataset.articleIndex =
                    index;

                article.classList.remove(
                    'is-active',
                    'is-featured',
                    'is-hovered'
                );

                article.setAttribute(
                    'data-article-ready',
                    'true'
                );

            }
        );

    }

    /**
     * Calculates blog grid.
     */
    calculateBlogGrid() {

        this.blog.state.total =
            this.blog.articles.length;

        this.blog.state.visible =
            this.getVisibleBlogArticles()
                .length;

        this.blog.state.columns =
            this.getCurrentBlogColumns();

    }

    /**
     * Activates article.
     *
     * @param {number} index
     */
    activateBlogArticle(index) {

        const article =
            this.blog.articles[index];

        if (!article) {
            return;
        }

        this.deactivateBlogArticles();

        this.blog.state.selected =
            index;

        article.classList.add(
            'is-active'
        );

        this.updateFeaturedBlogArticle(
            article
        );

        this.updateBlogAccessibility?.();

        this.emit(
            'tefco:blog:article:activated',
            {
                index,
                article
            }
        );

    }

    /**
     * Activates article element.
     *
     * @param {HTMLElement} article
     */
    activateBlogCard(article) {

        if (!article) {
            return;
        }

        const index =
            Number(
                article.dataset.articleIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateBlogArticle(
            index
        );

    }

    /**
     * Deactivates all articles.
     */
    deactivateBlogArticles() {

        this.blog.articles.forEach(
            article => {

                article.classList.remove(
                    'is-active',
                    'is-featured'
                );

            }
        );

    }

    /**
     * Updates featured article.
     *
     * @param {HTMLElement} article
     */
    updateFeaturedBlogArticle(article) {

        if (
            !this.blog.config.featuredEnabled
        ) {
            return;
        }

        this.blog.state.featured =
            article;

        article.classList.add(
            'is-featured'
        );

    }

    /**
     * Updates blog grid.
     */
    updateBlogGrid() {

        const grid =
            this.blog.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--blog-columns',
            this.blog.state.columns
        );

        grid.setAttribute(
            'data-columns',
            this.blog.state.columns
        );

        this.updateBlogVisibility();

        this.emit(
            'tefco:blog:grid:updated'
        );

    }

    /**
     * Synchronizes blog grid.
     */
    syncBlogGrid() {

        this.calculateBlogGrid();

        this.updateBlogGrid();

        this.emit(
            'tefco:blog:grid:synced'
        );

    }

    /**
     * Refreshes blog grid.
     */
    refreshBlogGrid() {

        this.collectBlogArticles();

        this.syncBlogGrid();

        this.emit(
            'tefco:blog:grid:refreshed'
        );

    }

    /**
     * Updates visible articles.
     */
    updateBlogVisibility() {

        this.blog.state.visible =
            this.getVisibleBlogArticles()
                .length;

    }

    /**
     * Returns visible articles.
     *
     * @returns {HTMLElement[]}
     */
    getVisibleBlogArticles() {

        return this.blog.articles.filter(
            article =>
                article.offsetParent !== null
        );

    }

    /**
     * Returns current columns.
     *
     * @returns {number}
     */
    getCurrentBlogColumns() {

        return this.blog.state.columns ||
            this.blog.config.columns;

    }

    /**
     * Returns engine state.
     *
     * @returns {Object}
     */
    getBlogEngineState() {

        return {

            total:
                this.blog.state.total,

            visible:
                this.blog.state.visible,

            selected:
                this.blog.state.selected,

            featured:
                this.blog.state.featured,

            columns:
                this.blog.state.columns

        };

    }

    /**
     * Returns whether Blog
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBlogEngineReady() {

        return !!(

            this.isBlogReady() &&
            this.blog.articles.length > 0

        );

    }
        /**
     * ========================================================================
     * Part 4C.3
     * Blog Interactions
     * ------------------------------------------------------------------------
     * Enterprise Interaction Engine
     * Hover Effects
     * Card Animations
     * Featured Selection
     * User Interactions
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes blog interactions.
     */
    initializeBlogInteractions() {

        if (!this.isBlogEngineReady()) {
            return;
        }

        this.bindBlogEvents();

        this.initializeBlogAnimations();

        this.enableBlogInteractions();

        this.emit(
            'tefco:blog:interactions:initialized'
        );

    }

    /**
     * Binds interaction events.
     */
    bindBlogEvents() {

        this.cleanupBlogEvents?.();

        this.blog.eventListeners = [];

        const bind = (target, event, handler, options = false) => {

            if (!target || typeof handler !== 'function') {
                return;
            }

            target.addEventListener(event, handler, options);

            this.blog.eventListeners.push({
                target,
                event,
                handler,
                options
            });

        };

        this.blog.articles.forEach(article => {

            bind(
                article,
                'mouseenter',
                () => this.handleBlogHover(article)
            );

            bind(
                article,
                'mouseleave',
                () => this.handleBlogLeave(article)
            );

            bind(
                article,
                'click',
                () => this.activateBlogCard(article)
            );

        });

    }

    /**
     * Handles hover.
     *
     * @param {HTMLElement} article
     */
    handleBlogHover(article) {

        if (!article) {
            return;
        }

        this.blog.state.hovered =
            Number(
                article.dataset.articleIndex
            );

        article.classList.add(
            'is-hovered'
        );

        this.highlightBlogArticle(
            article
        );

        this.emit(
            'tefco:blog:hover',
            {
                article
            }
        );

    }

    /**
     * Handles hover leave.
     *
     * @param {HTMLElement} article
     */
    handleBlogLeave(article) {

        if (!article) {
            return;
        }

        this.blog.state.hovered =
            null;

        article.classList.remove(
            'is-hovered'
        );

        this.resetBlogAnimations(
            article
        );

        this.emit(
            'tefco:blog:hover:end',
            {
                article
            }
        );

    }

    /**
     * Highlights article.
     *
     * @param {HTMLElement} article
     */
    highlightBlogArticle(article) {

        if (!article) {
            return;
        }

        article.classList.add(
            'is-highlighted'
        );

        this.playBlogAnimations(
            article
        );

    }

    /**
     * Initializes animations.
     */
    initializeBlogAnimations() {

        if (!this.blog?.articles?.length) {
            return;
        }

        this.blog.articles.forEach(
            (article, index) => {

                /*
                 * Keep CSS animation variables synchronized with the
                 * Liquid -> JS configuration contract.
                 */
                article.style.setProperty(
                    '--blog-animation-duration',
                    `${this.blog.config.animationDuration}ms`
                );

                article.style.setProperty(
                    '--blog-animation-delay',
                    `${index * this.blog.config.animationDelay}ms`
                );

                /* Remove the retired pre-contract variable. */
                article.style.removeProperty(
                    '--blog-delay'
                );

                if (!this.blog.config.animation) {
                    article.classList.remove(
                        'is-animatable',
                        'is-animated'
                    );
                    return;
                }

                article.classList.add(
                    'is-animatable'
                );

            }
        );

    }

    /**
     * Plays animations.
     *
     * @param {HTMLElement} article
     */
    playBlogAnimations(article) {

        if (
            !this.blog.config.animation ||
            !article
        ) {
            return;
        }

        article.classList.add(
            'is-animated'
        );

    }

    /**
     * Resets animations.
     *
     * @param {HTMLElement} article
     */
    resetBlogAnimations(article) {

        if (!article) {
            return;
        }

        article.classList.remove(
            'is-highlighted',
            'is-animated'
        );

    }

    /**
     * Refreshes interactions.
     */
    refreshBlogInteractions() {

        this.initializeBlogAnimations();

        this.emit(
            'tefco:blog:interactions:refreshed'
        );

    }

    /**
     * Enables interactions.
     */
    enableBlogInteractions() {

        this.blog.state.interactions =
            true;

    }

    /**
     * Disables interactions.
     */
    disableBlogInteractions() {

        this.blog.state.interactions =
            false;

    }

    /**
     * Returns interaction state.
     *
     * @returns {Object}
     */
    getBlogInteractionState() {

        return {

            enabled:
                this.blog.state.interactions,

            hovered:
                this.blog.state.hovered,

            selected:
                this.blog.state.selected,

            animation:
                this.blog.config.animation,

            animating:
                this.blog.state.animating

        };

    }

    /**
     * Returns whether interaction
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBlogInteractionReady() {

        return !!(

            this.isBlogEngineReady() &&
            this.blog.state.interactions

        );

    }
        /**
     * ========================================================================
     * Part 4C.4
     * Responsive Layout
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Blog Grid
     * Breakpoints
     * Resize Handling
     * Dynamic Columns
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive layout.
     */
    initializeBlogResponsive() {

        if (!this.isBlogEngineReady()) {
            return;
        }

        this.bindBlogResize();

        this.updateBlogBreakpoints();

        this.updateBlogLayout();

        this.emit(
            'tefco:blog:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindBlogResize() {

        if (this.blog.resizeHandler) {
            window.removeEventListener(
                'resize',
                this.blog.resizeHandler
            );
        }

        if (this.blog.resizeTimer) {
            this.clearTimeout(this.blog.resizeTimer);
            this.blog.resizeTimer = null;
        }

        this.blog.resizeHandler = () => {

            if (this.blog.resizeTimer) {
                this.clearTimeout(this.blog.resizeTimer);
            }

            this.blog.resizeTimer =
                this.setTimeout(() => {

                    this.blog.resizeTimer = null;

                    if (!this.isBlogReady()) {
                        return;
                    }

                    this.updateBlogBreakpoints();
                    this.updateBlogLayout();

                }, 100);

        };

        window.addEventListener(
            'resize',
            this.blog.resizeHandler
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateBlogBreakpoints() {

        this.blog.state.columns =
            this.calculateBlogColumns();

        this.emit(
            'tefco:blog:breakpoint:changed',
            {
                breakpoint:
                    this.getBlogBreakpoint(),

                columns:
                    this.blog.state.columns
            }
        );

    }

    /**
     * Calculates responsive columns.
     *
     * @returns {number}
     */
    calculateBlogColumns() {

        const width =
            window.innerWidth;

        if (width >= 1200) {

            return this.blog.config.columns;

        }

        if (width >= 768) {

            return Math.min(
                2,
                this.blog.config.columns
            );

        }

        return 1;

    }

    /**
     * Applies the Blog equal-height contract.
     *
     * Equal height is measured per visible grid row so cards in the same row
     * match without forcing unrelated rows to the height of the tallest card
     * on the entire page. When disabled, all JS-owned heights are removed.
     */
    updateBlogEqualHeight() {

        if (!this.blog?.articles?.length) {
            return;
        }

        const cards =
            this.blog.articles
                .map(article => ({
                    article,
                    link:
                        article.querySelector(
                            '.tefco-blog__article-link'
                        )
                }))
                .filter(item =>
                    item.link
                );

        cards.forEach(({ link }) => {
            link.style.removeProperty('height');
            link.style.removeProperty('min-height');
        });

        const grid =
            this.blog.dom.grid;

        if (!this.blog.config.equalHeight) {
            grid?.removeAttribute(
                'data-equal-height'
            );
            return;
        }

        grid?.setAttribute(
            'data-equal-height',
            'true'
        );

        const visibleCards =
            cards.filter(({ article }) =>
                article.offsetParent !== null
            );

        if (visibleCards.length < 2) {
            return;
        }

        const rows = new Map();

        visibleCards.forEach(item => {

            const top =
                Math.round(
                    item.article.getBoundingClientRect().top
                );

            if (!rows.has(top)) {
                rows.set(top, []);
            }

            rows.get(top).push(item);

        });

        rows.forEach(row => {

            if (row.length < 2) {
                return;
            }

            const tallest =
                Math.max(
                    ...row.map(({ link }) =>
                        Math.ceil(
                            link.getBoundingClientRect().height
                        )
                    )
                );

            if (
                !Number.isFinite(tallest) ||
                tallest <= 0
            ) {
                return;
            }

            row.forEach(({ link }) => {
                link.style.height =
                    `${tallest}px`;
            });

        });

    }

    /**
     * Updates responsive layout.
     */
    updateBlogLayout() {

        const grid =
            this.blog.dom.grid;

        if (!grid) {
            return;
        }

        grid.style.setProperty(
            '--blog-columns',
            this.blog.state.columns
        );

        grid.setAttribute(
            'data-columns',
            this.blog.state.columns
        );

        this.updateBlogVisibility();

        this.updateBlogEqualHeight();

        this.emit(
            'tefco:blog:layout:updated',
            {
                columns:
                    this.blog.state.columns
            }
        );

    }

    /**
     * Refreshes responsive layout.
     */
    refreshBlogLayout() {

        this.updateBlogBreakpoints();

        this.updateBlogLayout();

        this.emit(
            'tefco:blog:layout:refreshed'
        );

    }

    /**
     * Recalculates layout.
     */
    recalculateBlogLayout() {

        this.collectBlogArticles();

        this.refreshBlogLayout();

        this.emit(
            'tefco:blog:layout:recalculated'
        );

    }

    /**
     * Returns current breakpoint.
     *
     * @returns {string}
     */
    getBlogBreakpoint() {

        const width =
            window.innerWidth;

        if (width >= 1200) {
            return 'desktop';
        }

        if (width >= 768) {
            return 'tablet';
        }

        return 'mobile';

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getBlogResponsiveState() {

        return {

            breakpoint:
                this.getBlogBreakpoint(),

            columns:
                this.blog.state.columns,

            total:
                this.blog.state.total,

            visible:
                this.blog.state.visible,

            selected:
                this.blog.state.selected

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBlogResponsiveReady() {

        return !!(

            this.isBlogEngineReady() &&
            this.blog.state.columns > 0

        );

    }
        /**
     * ========================================================================
     * Part 4C.5
     * Lazy Loading
     * ------------------------------------------------------------------------
     * Enterprise Lazy Loading Engine
     * Intersection Observer
     * Image Optimization
     * Intelligent Preloading
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes lazy loading.
     */
    initializeBlogLazyLoading() {

        if (
            !this.isBlogEngineReady() ||
            !this.blog.config.lazyLoad
        ) {
            return;
        }

        if (!('IntersectionObserver' in window)) {

            this.blog.articles.forEach(article => {

                this.loadBlogArticle(article);

            });

            return;

        }

        this.blog.lazyObserver =
            new IntersectionObserver(

                entries => {

                    entries.forEach(entry => {

                        if (!entry.isIntersecting) {
                            return;
                        }

                        this.loadBlogArticle(
                            entry.target
                        );

                        this.blog.lazyObserver.unobserve(
                            entry.target
                        );

                    });

                },

                {

                    root: null,

                    rootMargin: '150px',

                    threshold: 0.05

                }

            );

        this.blog.articles.forEach(article => {

            this.blog.lazyObserver.observe(
                article
            );

        });

        this.preloadVisibleBlogArticles();

        this.emit(
            'tefco:blog:lazy:initialized'
        );

    }

    /**
     * Loads article assets.
     *
     * @param {HTMLElement} article
     */
    loadBlogArticle(article) {

        if (!article) {
            return;
        }

        if (
            article.dataset.loaded === 'true'
        ) {
            return;
        }

        const images =
            article.querySelectorAll(
                'img[data-src]'
            );

        images.forEach(image => {

            if (image.dataset.src) {

                image.src =
                    image.dataset.src;

            }

            if (image.dataset.srcset) {

                image.srcset =
                    image.dataset.srcset;

            }

            image.loading =
                'eager';

            image.decoding =
                'async';

            image.decode?.()
                .catch(() => {})
                .finally(() => {

                    image.removeAttribute(
                        'data-src'
                    );

                    image.removeAttribute(
                        'data-srcset'
                    );

                });

        });

        article.dataset.loaded =
            'true';

        article.classList.add(
            'is-loaded'
        );

        this.updateBlogEqualHeight();

        this.emit(
            'tefco:blog:article:loaded',
            {
                article
            }
        );

    }

    /**
     * Preloads visible articles.
     */
    preloadVisibleBlogArticles() {

        this.getVisibleBlogArticles()
            .forEach(article => {

                this.loadBlogArticle(
                    article
                );

            });

    }

    /**
     * Preloads adjacent articles.
     */
    preloadAdjacentBlogArticles() {

        const visible =
            this.getVisibleBlogArticles();

        visible.forEach(article => {

            const next =
                article.nextElementSibling;

            if (
                next?.matches(
                    '[data-blog-article]'
                )
            ) {

                this.loadBlogArticle(
                    next
                );

            }

        });

    }

    /**
     * Refreshes lazy loading.
     */
    refreshBlogLazyLoading() {

        this.disconnectBlogLazyObserver();

        this.initializeBlogLazyLoading();

        this.emit(
            'tefco:blog:lazy:refreshed'
        );

    }

    /**
     * Disconnects lazy observer.
     */
    disconnectBlogLazyObserver() {

        if (
            this.blog.lazyObserver
        ) {

            this.blog.lazyObserver.disconnect();

            this.blog.lazyObserver =
                null;

        }

    }

    /**
     * Returns loaded articles.
     *
     * @returns {HTMLElement[]}
     */
    getLoadedBlogArticles() {

        return this.blog.articles.filter(
            article =>
                article.dataset.loaded === 'true'
        );

    }

    /**
     * Returns pending articles.
     *
     * @returns {HTMLElement[]}
     */
    getPendingBlogArticles() {

        return this.blog.articles.filter(
            article =>
                article.dataset.loaded !== 'true'
        );

    }

    /**
     * Returns lazy loading state.
     *
     * @returns {Object}
     */
    getBlogLazyState() {

        return {

            enabled:
                this.blog.config.lazyLoad,

            observer:
                !!this.blog.lazyObserver,

            loaded:
                this.getLoadedBlogArticles()
                    .length,

            pending:
                this.getPendingBlogArticles()
                    .length,

            total:
                this.blog.state.total

        };

    }

    /**
     * Returns whether lazy loading
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBlogLazyReady() {

        return !!(

            this.isBlogEngineReady() &&
            this.blog.config.lazyLoad

        );

    }
        /**
     * ========================================================================
     * Part 4C.6
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Focus Management
     * Screen Reader Support
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeBlogAccessibility() {

        if (!this.isBlogEngineReady()) {
            return;
        }

        this.initializeBlogAccessibilityAttributes();

        this.bindBlogKeyboardNavigation();

        this.initializeBlogLiveRegion();

        this.initializeBlogReducedMotion();

        this.emit(
            'tefco:blog:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeBlogAccessibilityAttributes() {

        const {
            section,
            grid
        } = this.blog.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Latest Articles'
        );

        grid?.setAttribute(
            'role',
            'list'
        );

        this.blog.articles.forEach(
            (article, index) => {

                article.setAttribute(
                    'role',
                    'listitem'
                );

                article.setAttribute(
                    'tabindex',
                    '0'
                );

                article.setAttribute(
                    'aria-posinset',
                    index + 1
                );

                article.setAttribute(
                    'aria-setsize',
                    this.blog.state.total
                );

                article.setAttribute(
                    'aria-selected',
                    index ===
                    this.blog.state.selected
                        ? 'true'
                        : 'false'
                );

            }
        );

    }

    /**
     * Binds keyboard navigation.
     */
    bindBlogKeyboardNavigation() {

        const grid =
            this.blog.dom.grid;

        if (!grid) {
            return;
        }

        grid.addEventListener(
            'keydown',
            event => {

                const current =
                    this.blog.state.selected ?? 0;

                switch (event.key) {

                    case 'ArrowRight':

                        event.preventDefault();

                        this.activateBlogArticle(
                            Math.min(
                                current + 1,
                                this.blog.state.total - 1
                            )
                        );

                        break;

                    case 'ArrowLeft':

                        event.preventDefault();

                        this.activateBlogArticle(
                            Math.max(
                                current - 1,
                                0
                            )
                        );

                        break;

                    case 'Home':

                        event.preventDefault();

                        this.activateBlogArticle(
                            0
                        );

                        break;

                    case 'End':

                        event.preventDefault();

                        this.activateBlogArticle(
                            this.blog.state.total - 1
                        );

                        break;

                    case 'Enter':

                    case ' ':

                        event.preventDefault();

                        this.blog.articles[
                            current
                        ]?.querySelector(
                            'a'
                        )?.click();

                        break;

                    default:

                        return;

                }

                this.focusBlogArticle(
                    this.blog.state.selected
                );

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeBlogLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        document.body.appendChild(
            liveRegion
        );

        this.blog.liveRegion =
            liveRegion;

    }

    /**
     * Announces active article.
     *
     * @param {number} index
     */
    announceBlogArticle(index) {

        if (!this.blog.liveRegion) {
            return;
        }

        this.blog.liveRegion.textContent =
            `Article ${index + 1} of ${this.blog.state.total}`;

    }

    /**
     * Initializes reduced motion.
     */
    initializeBlogReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.blog.reducedMotion =
            media.matches;

        if (
            this.blog.reducedMotion
        ) {

            this.blog.articles.forEach(
                article => {

                    article.style.transition =
                        'none';

                    article.style.animation =
                        'none';

                }
            );

        }

    }

    /**
     * Focuses article.
     *
     * @param {number} index
     */
    focusBlogArticle(index) {

        this.blog.articles[
            index
        ]?.focus();

    }

    /**
     * Updates accessibility.
     */
    updateBlogAccessibility() {

        this.blog.articles.forEach(
            (article, index) => {

                const active =
                    index ===
                    this.blog.state.selected;

                article.setAttribute(
                    'tabindex',
                    active
                        ? '0'
                        : '-1'
                );

                article.setAttribute(
                    'aria-selected',
                    active
                        ? 'true'
                        : 'false'
                );

            }
        );

        if (
            this.blog.state.selected !== null
        ) {

            this.announceBlogArticle(
                this.blog.state.selected
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getBlogAccessibilityState() {

        return {

            reducedMotion:
                this.blog.reducedMotion,

            liveRegion:
                !!this.blog.liveRegion,

            selected:
                this.blog.state.selected,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isBlogAccessibilityReady() {

        return !!(

            this.isBlogEngineReady() &&
            this.blog.liveRegion

        );

    }
        /**
     * ========================================================================
     * Part 4C.7
     * Theme Editor
     * ------------------------------------------------------------------------
     * Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Dynamic Refresh
     * OS 2.0 Compatible
     * ========================================================================
     */

    /**
     * Initializes Theme Editor support.
     */
    initializeBlogThemeEditor() {

        if (
            typeof Shopify === 'undefined' ||
            !Shopify.designMode
        ) {
            return;
        }

        this.bindBlogThemeEditorEvents();

        this.emit(
            'tefco:blog:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindBlogThemeEditorEvents() {

        if (this.blogThemeEditorBound) {
            return;
        }

        this.blogThemeEditorHandlers = {
            sectionLoad:
                this.handleBlogSectionLoad.bind(this),
            sectionUnload:
                this.handleBlogSectionUnload.bind(this),
            sectionSelect:
                this.handleBlogSectionSelect.bind(this),
            sectionDeselect:
                this.handleBlogSectionDeselect.bind(this),
            blockSelect:
                this.handleBlogBlockSelect.bind(this),
            blockDeselect:
                this.handleBlogBlockDeselect.bind(this)
        };

        Object.entries({
            'shopify:section:load': 'sectionLoad',
            'shopify:section:unload': 'sectionUnload',
            'shopify:section:select': 'sectionSelect',
            'shopify:section:deselect': 'sectionDeselect',
            'shopify:block:select': 'blockSelect',
            'shopify:block:deselect': 'blockDeselect'
        }).forEach(([event, key]) => {
            this.on(
                document,
                event,
                this.blogThemeEditorHandlers[key]
            );
        });

        this.blogThemeEditorBound = true;

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleBlogSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-blog]'
            )
        ) {
            return;
        }

        if (this.blog && !this.blog.destroyed) {
            this.destroyBlog();
        }

        this.initializeBlog();
        this.initializeBlogEngine();
        this.initializeBlogInteractions();
        this.initializeBlogResponsive();
        this.initializeBlogLazyLoading();
        this.initializeBlogAccessibility();

        this.emit(
            'tefco:blog:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleBlogSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-blog]'
            )
        ) {
            return;
        }

        this.destroyBlog();

        this.emit(
            'tefco:blog:section:unloaded'
        );

    }

    /**
     * Handles section select.
     *
     * @param {CustomEvent} event
     */
    handleBlogSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-blog]'
            )
        ) {
            return;
        }

        this.refreshBlogThemeEditor();

        this.emit(
            'tefco:blog:section:selected'
        );

    }

    /**
     * Handles section deselect.
     *
     * @param {CustomEvent} event
     */
    handleBlogSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-blog]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:blog:section:deselected'
        );

    }

    /**
     * Handles block select.
     *
     * @param {CustomEvent} event
     */
    handleBlogBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-blog-article]'
            )
        ) {
            return;
        }

        const index =
            Number(
                block.dataset.articleIndex
            );

        if (
            Number.isNaN(index)
        ) {
            return;
        }

        this.activateBlogArticle(
            index
        );

        this.focusBlogArticle(
            index
        );

        this.emit(
            'tefco:blog:block:selected',
            {
                index
            }
        );

    }

    /**
     * Handles block deselect.
     *
     * @param {CustomEvent} event
     */
    handleBlogBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.matches(
                '[data-blog-article]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:blog:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor.
     */
    refreshBlogThemeEditor() {

        this.collectBlogArticles();

        this.calculateBlogGrid();

        this.updateBlogGrid();

        this.updateBlogAccessibility?.();

        this.emit(
            'tefco:blog:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getBlogThemeEditorState() {

        return {

            enabled:
                typeof Shopify !== 'undefined' &&
                Shopify.designMode,

            initialized:
                this.isBlogThemeEditorReady(),

            total:
                this.blog?.state?.total ?? 0,

            visible:
                this.blog?.state?.visible ?? 0,

            selected:
                this.blog?.state?.selected,

            featured:
                this.blog?.state?.featured

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isBlogThemeEditorReady() {

        return !!(

            typeof Shopify !== 'undefined' &&
            Shopify.designMode &&
            this.isBlogEngineReady()

        );

    }
        /**
     * ========================================================================
     * Part 4C.8
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runBlogDiagnostics() {

        return {

            ready:
                this.isBlogReady(),

            initialized:
                this.blog?.initialized ?? false,

            destroyed:
                this.blog?.destroyed ?? false,

            total:
                this.blog?.state?.total ?? 0,

            visible:
                this.blog?.state?.visible ?? 0,

            selected:
                this.blog?.state?.selected,

            featured:
                this.blog?.state?.featured,

            hovered:
                this.blog?.state?.hovered,

            columns:
                this.blog?.state?.columns ?? 0,

            animating:
                this.blog?.state?.animating ?? false,

            lazyLoaded:
                this.getLoadedBlogArticles?.()
                    ?.length ?? 0,

            pendingLazy:
                this.getPendingBlogArticles?.()
                    ?.length ?? 0,

            responsive:
                this.isBlogResponsiveReady?.() ?? false,

            interactions:
                this.isBlogInteractionReady?.() ?? false,

            accessibility:
                this.isBlogAccessibilityReady?.() ?? false,

            lazyLoading:
                this.isBlogLazyReady?.() ?? false,

            themeEditor:
                this.isBlogThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Blog.
     *
     * @returns {boolean}
     */
    validateBlog() {

        if (!this.blog) {
            return false;
        }

        const {
            dom,
            articles
        } = this.blog;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.grid) {
            return false;
        }

        if (!Array.isArray(articles)) {
            return false;
        }

        if (!articles.length) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logBlogDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Blog Diagnostics'
        );

        console.table(
            this.runBlogDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupBlogStyles() {

        const grid =
            this.blog?.dom?.grid;

        grid?.style.removeProperty(
            '--blog-columns'
        );

        grid?.removeAttribute(
            'data-equal-height'
        );

        this.blog?.articles?.forEach(
            article => {

                article.style.removeProperty(
                    '--blog-delay'
                );

                article.style.removeProperty(
                    '--blog-animation-duration'
                );

                article.style.removeProperty(
                    '--blog-animation-delay'
                );

                const articleLink =
                    article.querySelector(
                        '.tefco-blog__article-link'
                    );

                articleLink?.style.removeProperty(
                    'height'
                );

                articleLink?.style.removeProperty(
                    'min-height'
                );

                article.style.removeProperty(
                    'transition'
                );

                article.style.removeProperty(
                    'animation'
                );

                article.classList.remove(
                    'is-active',
                    'is-featured',
                    'is-hovered',
                    'is-highlighted',
                    'is-loaded',
                    'is-animated',
                    'is-animatable'
                );

                article.removeAttribute(
                    'tabindex'
                );

                article.removeAttribute(
                    'aria-selected'
                );

                article.removeAttribute(
                    'aria-posinset'
                );

                article.removeAttribute(
                    'aria-setsize'
                );

                article.removeAttribute(
                    'data-article-ready'
                );

                article.removeAttribute(
                    'data-loaded'
                );

            }
        );

    }

    /**
     * Cleans runtime state.
     */
    cleanupBlogState() {

        if (!this.blog) {
            return;
        }

        this.blog.state = {

            total: 0,

            visible: 0,

            featured: null,

            selected: null,

            hovered: null,

            columns: 0,

            animating: false,

            loading: false,

            interactions: false,

            initializedAt: 0

        };

    }

    /**
     * Cleans Blog-owned DOM and resize events.
     */
    cleanupBlogEvents() {

        if (!this.blog) {
            return;
        }

        this.blog.eventListeners?.forEach(listener => {
            listener.target?.removeEventListener(
                listener.event,
                listener.handler,
                listener.options
            );
        });

        this.blog.eventListeners = [];

        if (this.blog.resizeHandler) {
            window.removeEventListener(
                'resize',
                this.blog.resizeHandler
            );
            this.blog.resizeHandler = null;
        }

        if (this.blog.resizeTimer) {
            this.clearTimeout(this.blog.resizeTimer);
            this.blog.resizeTimer = null;
        }

    }

    /**
     * Cleans observers.
     */
    cleanupBlogObservers() {

        this.disconnectBlogLazyObserver?.();

    }

    /**
     * Cleans accessibility.
     */
    cleanupBlogAccessibility() {

        if (
            this.blog?.liveRegion
        ) {

            this.blog.liveRegion.remove();

            this.blog.liveRegion =
                null;

        }

    }

    /**
     * Cleans DOM references.
     */
    cleanupBlogDOM() {

        if (!this.blog) {
            return;
        }

        this.blog.articles = [];

        this.blog.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupBlogConfig() {

        if (!this.blog) {
            return;
        }

        this.blog.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupBlog() {

        this.cleanupBlogEvents();

        this.cleanupBlogObservers();

        this.cleanupBlogAccessibility();

        this.cleanupBlogStyles();

        this.cleanupBlogState();

        this.cleanupBlogDOM();

        this.cleanupBlogConfig();

        this.emit(
            'tefco:blog:cleanup'
        );

    }

    /**
     * Destroys Blog.
     */
    destroyBlog() {

        if (
            !this.blog ||
            this.blog.destroyed
        ) {
            return;
        }

        this.cleanupBlog();

        this.blog.initialized =
            false;

        this.blog.destroyed =
            true;

        this.emit(
            'tefco:blog:destroyed'
        );

    }

    /**
     * Rebuilds Blog.
     */
    rebuildBlog() {

        this.destroyBlog();

        this.initializeBlog();

        this.initializeBlogEngine();

        this.initializeBlogInteractions();

        this.initializeBlogResponsive();

        this.initializeBlogLazyLoading();

        this.initializeBlogAccessibility();

        this.initializeBlogThemeEditor();

        this.emit(
            'tefco:blog:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getBlogLifecycleState() {

        return {

            initialized:
                this.blog?.initialized ?? false,

            destroyed:
                this.blog?.destroyed ?? false,

            valid:
                this.validateBlog(),

            diagnostics:
                this.runBlogDiagnostics()

        };

    }

    /**
     * Returns whether destroyed.
     *
     * @returns {boolean}
     */
    isBlogDestroyed() {

        return !!(
            this.blog?.destroyed
        );

    }
        /**
     * ========================================================================
     * Part 4D.1
     * Newsletter Foundation
     * ------------------------------------------------------------------------
     * Enterprise Newsletter Engine
     * Foundation
     * DOM Cache
     * Configuration
     * Runtime State
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Newsletter.
     */
    
    //----------------------------------------
    // PART 4 — Newsletter
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Newsletter
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Newsletter
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Newsletter
    //----------------------------------------


    //----------------------------------------
    // PART 4 — Newsletter
    //----------------------------------------

initializeNewsletter() {

        this.cacheNewsletterDOM();

        if (!this.newsletter.dom.section) {
            return;
        }

        this.initializeNewsletterConfig();

        this.initializeNewsletterState();

        this.collectNewsletterFields();

        this.newsletter.initialized = true;

        this.newsletter.destroyed = false;

        this.newsletter.eventListeners =
            this.newsletter.eventListeners || [];

        this.newsletter.resizeHandler =
            this.newsletter.resizeHandler || null;

        this.emit(
            'tefco:newsletter:initialized'
        );

    }

    /**
     * Caches DOM elements.
     */
    cacheNewsletterDOM() {

        const section =
            document.querySelector(
                '[data-newsletter]'
            );

        this.newsletter = {

            ...this.newsletter,

            dom: {

                section,

                wrapper:
                    section?.querySelector(
                        '[data-newsletter-wrapper]'
                    ) ?? null,

                form:
                    section?.querySelector('form[action*="/contact"]') ?? null,

                email:
                    section?.querySelector(
                        '[data-newsletter-email]'
                    ) ?? null,

                submit:
                    section?.querySelector(
                        '[data-newsletter-submit]'
                    ) ?? null,

                message:
                    section?.querySelector(
                        '[data-newsletter-message]'
                    ) ?? null,

                consent:
                    section?.querySelector(
                        '[data-newsletter-consent]'
                    ) ?? null,

                hiddenFields:
                    section
                        ? Array.from(
                            section.querySelectorAll(
                                '[data-newsletter-hidden]'
                            )
                        )
                        : []

            }

        };

    }

    /**
     * Initializes configuration.
     */
    initializeNewsletterConfig() {

        const section =
            this.newsletter.dom.section;

        this.newsletter.config = {

            animation:
                section?.dataset.animation !== 'false',

            animationDuration:
                Number(
                    section?.dataset.animationDuration
                ) || 500,

            animationDelay:
                Number(
                    section?.dataset.animationDelay
                ) || 100,

            validateOnInput:
                section?.dataset.validateOnInput !== 'false',

            validateOnBlur:
                section?.dataset.validateOnBlur !== 'false',

            requireConsent:
                section?.dataset.requireConsent === 'true',

            disableWhileSubmitting:
                section?.dataset.disableSubmit !== 'false',

            resetOnSuccess:
                section?.dataset.resetOnSuccess !== 'false',

            showSuccessMessage:
                section?.dataset.showSuccess !== 'false'

        };

    }

    /**
     * Initializes runtime state.
     */
    initializeNewsletterState() {

        this.newsletter.state = {

            submitting: false,

            submitted: false,

            valid: false,

            focused: false,

            dirty: false,

            touched: false,

            errors: [],

            initializedAt:
                performance.now()

        };

    }

    /**
     * Collects form fields.
     */
    collectNewsletterFields() {

        const form =
            this.newsletter.dom.form;

        this.newsletter.fields =
            form
                ? Array.from(
                    form.querySelectorAll(
                        'input, select, textarea'
                    )
                )
                : [];

        this.newsletter.state.totalFields =
            this.newsletter.fields.length;

    }

    /**
     * Returns newsletter form.
     *
     * @returns {HTMLFormElement|null}
     */
    getNewsletterForm() {

        return this.newsletter.dom.form;

    }

    /**
     * Returns newsletter fields.
     *
     * @returns {HTMLElement[]}
     */
    getNewsletterFields() {

        return this.newsletter.fields;

    }

    /**
     * Returns email input.
     *
     * @returns {HTMLInputElement|null}
     */
    getNewsletterEmailField() {

        return this.newsletter.dom.email;

    }

    /**
     * Returns submit button.
     *
     * @returns {HTMLButtonElement|null}
     */
    getNewsletterSubmitButton() {

        return this.newsletter.dom.submit;

    }

    /**
     * Returns whether Newsletter
     * engine is initialized.
     *
     * @returns {boolean}
     */
    isNewsletterReady() {

        return !!(

            this.newsletter &&
            this.newsletter.initialized &&
            !this.newsletter.destroyed &&
            this.newsletter.dom.section

        );

    }
    /**
     * Binds an event owned by Newsletter so it can be removed on rebuild.
     */
    bindNewsletterOwnedEvent(
        target,
        event,
        handler,
        options = false
    ) {

        if (
            !target ||
            typeof handler !== 'function'
        ) {
            return;
        }

        target.addEventListener(
            event,
            handler,
            options
        );

        this.newsletter.eventListeners =
            this.newsletter.eventListeners || [];

        this.newsletter.eventListeners.push({
            target,
            event,
            handler,
            options
        });

    }

        /**
     * ========================================================================
     * Part 4D.2
     * Newsletter Form Engine
     * ------------------------------------------------------------------------
     * Enterprise Form Engine
     * Submission Lifecycle
     * State Management
     * Success / Error Handling
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes form engine.
     */
    initializeNewsletterFormEngine() {

        if (!this.isNewsletterReady()) {
            return;
        }

        this.prepareNewsletterForm();

        this.initializeNewsletterMessage();

        this.bindNewsletterFormEvents();

        this.emit(
            'tefco:newsletter:engine:initialized'
        );

    }

    /**
     * Prepares newsletter form.
     */
    prepareNewsletterForm() {

        const form =
            this.newsletter.dom.form;

        if (!form) {
            return;
        }

        form.setAttribute(
            'novalidate',
            'novalidate'
        );

        this.newsletter.fields.forEach(
            field => {

                field.dataset.newsletterReady =
                    'true';

            }
        );

    }

    /**
     * Initializes message container.
     */
    initializeNewsletterMessage() {

        const message =
            this.newsletter.dom.message;

        if (!message) {
            return;
        }

        // Preserve a server-rendered message. After a successful native
        // {% form 'customer' %} submission Shopify reloads the page with
        // ?customer_posted=true and the section renders the success/error state
        // server-side (class "is-success is-visible" / "is-error is-visible",
        // and the container is NOT hidden). Blanket-clearing it here is what
        // swallowed the "Thank you for subscribing." message on load, so if a
        // server-rendered state is present we keep it visible and leave early.
        if (
            message.classList.contains('is-success') ||
            message.classList.contains('is-error')
        ) {
            message.hidden = false;
            message.classList.add('is-visible');
            return;
        }

        message.hidden = true;

        message.textContent = '';

        message.classList.remove(
            'is-success',
            'is-error',
            'is-visible'
        );

    }

    /**
     * Binds form events.
     */
    bindNewsletterFormEvents() {
 
        const form =
            this.newsletter.dom.form;
 
        if (
            !form ||
            this.newsletter.formEventsBound
        ) {
            return;
        }
 
        const handler = event => {

            // Run TEFCO client-side validation (keeps inline a11y messaging and
            // animation). Only block submission when the form is INVALID.
            //
            // When the form is valid we deliberately let the native Shopify
            // `{% form 'customer' %}` submission proceed instead of re-submitting
            // it with fetch(). The previous fetch()-based path bypassed Shopify's
            // native navigation, which broke hCaptcha / bot-protection on the
            // customer form and never produced the native success/error state
            // (form.posted_successfully? / form.errors) that this section renders.
            if (
                typeof this.validateNewsletterForm === 'function' &&
                !this.validateNewsletterForm()
            ) {
                event.preventDefault();
                event.stopImmediatePropagation();
                return;
            }

            // Valid: optionally disable the submit control to prevent a double
            // submit, then allow the native form navigation to happen.
            if (this.newsletter?.config?.disableWhileSubmitting !== false) {
                this.toggleNewsletterSubmit(false);
            }

        };

        this.bindNewsletterOwnedEvent(
            form,
            'submit',
            handler,
            {
                capture: true
            }
        );

        this.newsletter.formEventsBound = true;
 
    }

    /**
     * Submits newsletter form.
     */
    async submitNewsletterForm() {
 
        if (
            this.newsletter.state.submitting
        ) {
            return;
        }
 
        this.prepareNewsletterSubmission();
 
        if (
            !this.validateNewsletterForm?.()
        ) {
            return;
        }
 
        this.newsletter.state.submitting =
            true;
 
        this.toggleNewsletterSubmit(
            false
        );
 
        this.clearNewsletterMessage();
 
        this.emit(
            'tefco:newsletter:submit:start'
        );
 
        try {
 
            const form =
                this.newsletter.dom.form;
 
            if (!form) {
                throw new Error(
                    'Newsletter form not found.'
                );
            }
 
            const formData =
                new FormData(form);
 
            const response =
                await fetch(
                    form.action,
                    {
                        method:
                            form.method || 'POST',
 
                        body:
                            formData,
 
                        headers: {
                            Accept:
                                'application/json'
                        }
                    }
                );
 
            const result =
                await this.parseNewsletterResponse(
                    response
                );
 
            if (!result.ok) {
                throw new Error(
                    result.message ||
                    'Newsletter submission failed.'
                );
            }
 
            this.handleNewsletterSuccess(
                result
            );
 
        } catch (error) {
 
            this.handleNewsletterError(
                error
            );
 
        } finally {
 
            this.newsletter.state.submitting =
                false;
 
            this.toggleNewsletterSubmit(
                true
            );
 
            this.emit(
                'tefco:newsletter:submit:complete'
            );
 
        }
 
    }

    /**
     * Handles successful submission.
     */
    handleNewsletterSuccess(result = {}) {
 
        this.newsletter.state.submitted =
            true;
 
        this.newsletter.state.errors = [];
 
        this.newsletter.state.valid = true;
 
        if (
            this.newsletter.config.resetOnSuccess
        ) {
 
            this.newsletter.dom.form?.reset();
 
            this.resetNewsletterValidation();
 
        }
 
        if (
            this.newsletter.config.showSuccessMessage
        ) {
 
            this.showNewsletterMessage(
                'success',
                result.message ||
                    'Thank you for subscribing.'
            );
 
        }
 
        this.emit(
            'tefco:newsletter:submit:success',
            {
                response: result.response,
                data: result.data
            }
        );
 
    }

    /**
     * Handles submission error.
     *
     * @param {Error} error
     */
    handleNewsletterError(error) {
 
        this.newsletter.state.submitted =
            false;
 
        this.newsletter.state.valid =
            false;
 
        this.showNewsletterMessage(
            'error',
            error?.message ||
            'Unable to subscribe.'
        );

        this.emit(
            'tefco:newsletter:submit:error',
            {
                error
            }
        );

    }

    /**
     * Shows message.
     *
     * @param {string} type
     * @param {string} message
     */
    showNewsletterMessage(
        type,
        message
    ) {

        const container =
            this.newsletter.dom.message;

        if (!container) {
            return;
        }

        container.hidden = false;

        container.textContent =
            message;

        container.classList.remove(
            'is-success',
            'is-error'
        );

        container.classList.add(
            'is-visible',
            `is-${type}`
        );

    }

    /**
     * Clears message.
     */
    clearNewsletterMessage() {

        const container =
            this.newsletter.dom.message;

        if (!container) {
            return;
        }

        container.hidden = true;

        container.textContent = '';

        container.classList.remove(
            'is-success',
            'is-error',
            'is-visible'
        );

    }

    /**
     * Enables / disables submit button.
     *
     * @param {boolean} enabled
     */
    toggleNewsletterSubmit(enabled) {

        const button =
            this.newsletter.dom.submit;

        if (!button) {
            return;
        }

        if (
            this.newsletter.config
                .disableWhileSubmitting
        ) {

            button.disabled =
                !enabled;

        }

    }

    /**
     * Refreshes form engine.
     */
    refreshNewsletterFormEngine() {

        this.prepareNewsletterForm();

        this.initializeNewsletterMessage();

        this.emit(
            'tefco:newsletter:engine:refreshed'
        );

    }

    /**
     * Returns form engine state.
     *
     * @returns {Object}
     */
    getNewsletterFormEngineState() {

        return {

            submitting:
                this.newsletter.state.submitting,

            submitted:
                this.newsletter.state.submitted,

            valid:
                this.newsletter.state.valid,

            totalFields:
                this.newsletter.state.totalFields,

            disabled:
                this.newsletter.dom.submit
                    ?.disabled ?? false

        };

    }

    /**
     * Returns whether form engine
     * is ready.
     *
     * @returns {boolean}
     */
    isNewsletterFormEngineReady() {

        return !!(

            this.isNewsletterReady() &&
            this.newsletter.dom.form

        );

    }
        /**
     * ========================================================================
     * Part 4D.3
     * Validation & Submission
     * ------------------------------------------------------------------------
     * Enterprise Validation Engine
     * Email Validation
     * Consent Validation
     * Error Handling
     * Submission Preparation
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes validation.
     */
    initializeNewsletterValidation() {

        if (!this.isNewsletterFormEngineReady()) {
            return;
        }

        this.bindNewsletterValidationEvents();

        this.emit(
            'tefco:newsletter:validation:initialized'
        );

    }

    /**
     * Binds validation events.
     */
    bindNewsletterValidationEvents() {
 
        const email =
            this.newsletter.dom.email;
 
        if (
            !email ||
            this.newsletter.validationEventsBound
        ) {
            return;
        }

        if (
            this.newsletter.config.validateOnInput
        ) {

            this.bindNewsletterOwnedEvent(
                email,
                'input',
                () => {

                    this.validateNewsletterEmail();

                }
            );

        }

        if (
            this.newsletter.config.validateOnBlur
        ) {

            this.bindNewsletterOwnedEvent(
                email,
                'blur',
                () => {

                    this.validateNewsletterEmail();

                }
            );

        }

        const consent =
            this.newsletter.dom.consent;
 
        if (consent) {
 
            this.bindNewsletterOwnedEvent(
                consent,
                'change',
                () => {
 
                    this.validateNewsletterConsent();
 
                }
            );
 
        }
 
        this.newsletter.validationEventsBound = true;
 
    }

    /**
     * Validates entire form.
     *
     * @returns {boolean}
     */
    validateNewsletterForm() {

        this.newsletter.state.errors = [];

        const emailValid =
            this.validateNewsletterEmail();

        const consentValid =
            this.validateNewsletterConsent();

        this.newsletter.state.valid =
            emailValid &&
            consentValid;

        this.emit(
            'tefco:newsletter:validation',
            {
                valid:
                    this.newsletter.state.valid,

                errors:
                    this.newsletter.state.errors
            }
        );

        return this.newsletter.state.valid;

    }

    /**
     * Validates email field.
     *
     * @returns {boolean}
     */
    validateNewsletterEmail() {
 
        const input =
            this.newsletter.dom.email;
 
        if (!input) {
            return false;
        }
 
        const value =
            this.sanitizeNewsletterEmail();

        const pattern =
            /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

        if (!value.length) {

            this.setNewsletterFieldError(
                input,
                'Email address is required.'
            );

            return false;

        }

        if (
            !pattern.test(value)
        ) {

            this.setNewsletterFieldError(
                input,
                'Please enter a valid email address.'
            );

            return false;

        }

        this.clearNewsletterFieldError(
            input
        );

        return true;

    }

    /**
     * Validates consent.
     *
     * @returns {boolean}
     */
    validateNewsletterConsent() {

        if (
            !this.newsletter.config
                .requireConsent
        ) {
            return true;
        }

        const consent =
            this.newsletter.dom.consent;

        if (
            !consent?.checked
        ) {

            this.newsletter.state.errors.push(
                'Consent is required.'
            );

            consent?.setAttribute(
                'aria-invalid',
                'true'
            );

            return false;

        }

        consent?.removeAttribute(
            'aria-invalid'
        );

        return true;

    }

    /**
     * Sets field error.
     *
     * @param {HTMLElement} field
     * @param {string} message
     */
    setNewsletterFieldError(
        field,
        message
    ) {

        field.setAttribute(
            'aria-invalid',
            'true'
        );

        field.classList.add(
            'is-invalid'
        );

        this.newsletter.state.errors.push(
            message
        );

        this.showNewsletterMessage(
            'error',
            message
        );

    }

    /**
     * Clears field error.
     *
     * @param {HTMLElement} field
     */
    clearNewsletterFieldError(
        field
    ) {

        field.removeAttribute(
            'aria-invalid'
        );

        field.classList.remove(
            'is-invalid'
        );

        if (
            !this.newsletter.state.errors.length
        ) {

            this.clearNewsletterMessage();

        }

    }

    /**
     * Sanitizes email.
     *
     * @returns {string}
     */
    sanitizeNewsletterEmail() {

        const email =
            this.newsletter.dom.email;

        if (!email) {
            return '';
        }

        const sanitized =
            email.value
                .trim()
                .toLowerCase();

        email.value =
            sanitized;

        return sanitized;

    }

    /**
     * Prepares submission.
     */
    prepareNewsletterSubmission() {

        this.sanitizeNewsletterEmail();

        this.newsletter.state.dirty =
            false;

        this.newsletter.state.touched =
            true;

        this.emit(
            'tefco:newsletter:submission:prepared'
        );

    }

    /**
     * Resets validation.
     */
    resetNewsletterValidation() {
 
        this.newsletter.state.errors = [];
 
        this.newsletter.state.valid =
            false;
 
        this.newsletter.state.dirty =
            false;
 
        this.newsletter.state.touched =
            false;
 
        this.newsletter.fields.forEach(
            field => {
 
                field.classList.remove(
                    'is-invalid'
                );
 
                field.removeAttribute(
                    'aria-invalid'
                );
 
            }
        );
 
        this.clearNewsletterMessage();
 
    }
 
    /**
     * Parses newsletter response.
     *
     * @param {Response} response
     * @returns {Promise<Object>}
     */
    async parseNewsletterResponse(response) {
 
        const contentType =
            (response.headers.get(
                'content-type'
            ) || '').toLowerCase();
 
        let body = null;
        let text = '';
        const result = {
 
            ok: response.ok,
 
            success: false,
 
            message: '',
 
            data: null,
 
            response
 
        };
 
        if (
            contentType.includes(
                'application/json'
            )
        ) {
            try {
                body = await response.json();
            } catch (error) {
                body = null;
            }
        } else {
            text = await response.text();
        }
 
        result.data = body ?? text;
 
        const extractErrors = data => {
            const errors = [];
 
            if (!data) {
                return errors;
            }
 
            if (typeof data === 'string') {
                const value = data.trim();
 
                if (value.length) {
                    errors.push(value);
                }
 
                return errors;
            }
 
            if (Array.isArray(data)) {
                data.forEach(item => {
                    if (item) {
                        errors.push(
                            String(item)
                        );
                    }
                });
 
                return errors;
            }
 
            if (typeof data === 'object') {
                if (data.errors) {
                    if (Array.isArray(data.errors)) {
                        data.errors.forEach(error => {
                            if (error) {
                                errors.push(
                                    String(error)
                                );
                            }
                        });
                    } else if (typeof data.errors === 'object') {
                        Object.values(data.errors).forEach(value => {
                            if (Array.isArray(value)) {
                                value.forEach(item => {
                                    if (item) {
                                        errors.push(
                                            String(item)
                                        );
                                    }
                                });
                            } else if (value) {
                                errors.push(
                                    String(value)
                                );
                            }
                        });
                    } else {
                        errors.push(
                            String(data.errors)
                        );
                    }
                }
 
                if (data.error) {
                    errors.push(
                        String(data.error)
                    );
                }
 
                if (
                    data.message &&
                    !data.success
                ) {
                    errors.push(
                        String(data.message)
                    );
                }
            }
 
            return errors;
        };
 
        const errors =
            extractErrors(body ?? text);
 
        if (!response.ok) {
            result.ok = false;
            result.message =
                errors.join(' ') ||
                `Request failed with status ${response.status}.`;
            return result;
        }
 
        if (typeof body === 'object' && body !== null) {
            if (
                body.customer_posted ||
                body.contact_posted ||
                body.success === true
            ) {
                result.ok = true;
                result.success = true;
                result.message =
                    body.message ||
                    '';
                return result;
            }
 
            if (
                body.status === 422 ||
                body.status === '422' ||
                errors.length
            ) {
                result.ok = false;
                result.message =
                    errors.join(' ') ||
                    'Newsletter submission failed.';
                return result;
            }
 
            result.ok = true;
            result.success = true;
            result.message =
                body.message ||
                '';
            return result;
        }
 
        const normalizedText =
            (text || '').toLowerCase();
 
        if (
            normalizedText.includes(
                'customer_posted=true'
            ) ||
            normalizedText.includes(
                'contact_posted=true'
            )
        ) {
            result.ok = true;
            result.success = true;
            return result;
        }
 
        if (
            normalizedText.includes(
                'form.errors'
            ) ||
            normalizedText.includes(
                'errors['
            ) ||
            normalizedText.includes(
                'errors:'
            ) ||
            normalizedText.includes(
                'error:'
            )
        ) {
            result.ok = false;
            result.message =
                errors.join(' ') ||
                'Newsletter submission failed.';
            return result;
        }
 
        result.ok = true;
        result.success = true;
        return result;
    }
 
    /**
     * Returns validation state.
     *
     * @returns {Object}
     */
    getNewsletterValidationState() {

        return {

            valid:
                this.newsletter.state.valid,

            errors:
                [
                    ...this.newsletter.state.errors
                ],

            touched:
                this.newsletter.state.touched,

            dirty:
                this.newsletter.state.dirty

        };

    }

    /**
     * Returns whether validation
     * engine is ready.
     *
     * @returns {boolean}
     */
    isNewsletterValidationReady() {

        return !!(

            this.isNewsletterFormEngineReady() &&
            this.newsletter.dom.email

        );

    }
        /**
     * ========================================================================
     * Part 4D.4
     * Responsive
     * ------------------------------------------------------------------------
     * Enterprise Responsive Engine
     * Breakpoint Management
     * Layout Optimization
     * Form Adaptation
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes responsive engine.
     */
    initializeNewsletterResponsive() {

        if (!this.isNewsletterValidationReady()) {
            return;
        }

        this.updateNewsletterBreakpoints();

        this.updateNewsletterLayout();

        this.bindNewsletterResize();

        this.emit(
            'tefco:newsletter:responsive:initialized'
        );

    }

    /**
     * Binds resize events.
     */
    bindNewsletterResize() {

        if (this.newsletter.resizeHandler) {
            window.removeEventListener(
                'resize',
                this.newsletter.resizeHandler
            );
        }

        this.newsletter.resizeHandler =
            () => {

                if (
                    !this.newsletter ||
                    this.newsletter.destroyed
                ) {
                    return;
                }

                this.refreshNewsletterLayout();

            };

        window.addEventListener(
            'resize',
            this.newsletter.resizeHandler
        );

    }

    /**
     * Updates active breakpoint.
     */
    updateNewsletterBreakpoints() {

        const width =
            window.innerWidth;

        let breakpoint =
            'desktop';

        if (width < 576) {

            breakpoint = 'mobile';

        } else if (width < 992) {

            breakpoint = 'tablet';

        }

        this.newsletter.state.breakpoint =
            breakpoint;

    }

    /**
     * Updates layout.
     */
    updateNewsletterLayout() {

        const wrapper =
            this.newsletter.dom.wrapper;

        if (!wrapper) {
            return;
        }

        wrapper.dataset.breakpoint =
            this.newsletter.state.breakpoint;

        switch (
            this.newsletter.state.breakpoint
        ) {

            case 'mobile':

                wrapper.classList.add(
                    'is-mobile'
                );

                wrapper.classList.remove(
                    'is-tablet',
                    'is-desktop'
                );

                break;

            case 'tablet':

                wrapper.classList.add(
                    'is-tablet'
                );

                wrapper.classList.remove(
                    'is-mobile',
                    'is-desktop'
                );

                break;

            default:

                wrapper.classList.add(
                    'is-desktop'
                );

                wrapper.classList.remove(
                    'is-mobile',
                    'is-tablet'
                );

        }

        this.updateNewsletterFieldLayout();

    }

    /**
     * Updates field layout.
     */
    updateNewsletterFieldLayout() {

        const email =
            this.newsletter.dom.email;

        const submit =
            this.newsletter.dom.submit;

        if (!email || !submit) {
            return;
        }

        const mobile =
            this.newsletter.state.breakpoint ===
            'mobile';

        email.setAttribute(
            'data-full-width',
            mobile
                ? 'true'
                : 'false'
        );

        submit.setAttribute(
            'data-full-width',
            mobile
                ? 'true'
                : 'false'
        );

    }

    /**
     * Refreshes layout.
     */
    refreshNewsletterLayout() {

        const previous =
            this.newsletter.state.breakpoint;

        this.updateNewsletterBreakpoints();

        if (
            previous !==
            this.newsletter.state.breakpoint
        ) {

            this.updateNewsletterLayout();

            this.emit(
                'tefco:newsletter:breakpoint:changed',
                {
                    breakpoint:
                        this.newsletter.state.breakpoint
                }
            );

        }

    }

    /**
     * Forces layout recalculation.
     */
    recalculateNewsletterLayout() {

        this.updateNewsletterBreakpoints();

        this.updateNewsletterLayout();

        this.emit(
            'tefco:newsletter:layout:recalculated'
        );

    }

    /**
     * Returns active breakpoint.
     *
     * @returns {string}
     */
    getNewsletterBreakpoint() {

        return (
            this.newsletter.state.breakpoint ||
            'desktop'
        );

    }

    /**
     * Returns responsive state.
     *
     * @returns {Object}
     */
    getNewsletterResponsiveState() {

        return {

            breakpoint:
                this.getNewsletterBreakpoint(),

            width:
                window.innerWidth,

            mobile:
                this.getNewsletterBreakpoint() ===
                'mobile',

            tablet:
                this.getNewsletterBreakpoint() ===
                'tablet',

            desktop:
                this.getNewsletterBreakpoint() ===
                'desktop'

        };

    }

    /**
     * Returns whether responsive
     * engine is ready.
     *
     * @returns {boolean}
     */
    isNewsletterResponsiveReady() {

        return !!(

            this.isNewsletterValidationReady() &&
            this.newsletter.state.breakpoint

        );

    }
        /**
     * ========================================================================
     * Part 4D.5
     * Accessibility
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Engine
     * WCAG 2.2 AA
     * Keyboard Navigation
     * Screen Reader Support
     * Focus Management
     * Reduced Motion
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes accessibility.
     */
    initializeNewsletterAccessibility() {

        if (!this.isNewsletterResponsiveReady()) {
            return;
        }

        this.initializeNewsletterAccessibilityAttributes();

        this.bindNewsletterKeyboardNavigation();

        this.initializeNewsletterLiveRegion();

        this.initializeNewsletterReducedMotion();

        this.emit(
            'tefco:newsletter:a11y:initialized'
        );

    }

    /**
     * Initializes accessibility attributes.
     */
    initializeNewsletterAccessibilityAttributes() {

        const {
            section,
            form,
            email,
            submit,
            consent,
            message
        } = this.newsletter.dom;

        section?.setAttribute(
            'role',
            'region'
        );

        section?.setAttribute(
            'aria-label',
            'Newsletter Subscription'
        );

        form?.setAttribute(
            'novalidate',
            'novalidate'
        );

        email?.setAttribute(
            'autocomplete',
            'email'
        );

        email?.setAttribute(
            'aria-required',
            'true'
        );

        submit?.setAttribute(
            'type',
            'submit'
        );

        if (consent) {

            consent.setAttribute(
                'aria-required',
                this.newsletter.config.requireConsent
                    ? 'true'
                    : 'false'
            );

        }

        if (message) {

            message.setAttribute(
                'role',
                'status'
            );

            message.setAttribute(
                'aria-live',
                'polite'
            );

            message.setAttribute(
                'aria-atomic',
                'true'
            );

        }

    }

    /**
     * Binds keyboard navigation.
     */
    bindNewsletterKeyboardNavigation() {

        const form =
            this.newsletter.dom.form;

        if (!form) {
            return;
        }

        this.bindNewsletterOwnedEvent(
            form,
            'keydown',
            event => {

                switch (event.key) {

                    case 'Escape':

                        this.newsletter.dom.email?.blur();

                        break;

                    case 'Enter':

                        if (
                            event.target ===
                            this.newsletter.dom.email
                        ) {

                            event.preventDefault();

                            this.submitNewsletterForm();

                        }

                        break;

                    default:

                        return;

                }

            }
        );

    }

    /**
     * Initializes live region.
     */
    initializeNewsletterLiveRegion() {

        const liveRegion =
            document.createElement(
                'div'
            );

        liveRegion.className =
            'visually-hidden';

        liveRegion.setAttribute(
            'aria-live',
            'polite'
        );

        liveRegion.setAttribute(
            'aria-atomic',
            'true'
        );

        this.newsletter.dom.section?.appendChild(
            liveRegion
        );

        this.newsletter.liveRegion =
            liveRegion;

    }

    /**
     * Announces message.
     *
     * @param {string} message
     */
    announceNewsletterMessage(
        message
    ) {

        if (
            !this.newsletter.liveRegion
        ) {
            return;
        }

        this.newsletter.liveRegion.textContent =
            message;

    }

    /**
     * Initializes reduced motion.
     */
    initializeNewsletterReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.newsletter.reducedMotion =
            media.matches;

        if (
            this.newsletter.reducedMotion
        ) {

            this.newsletter.dom.wrapper
                ?.classList.add(
                    'reduced-motion'
                );

        }

    }

    /**
     * Focuses email field.
     */
    focusNewsletterEmail() {

        this.newsletter.dom.email?.focus();

    }

    /**
     * Focuses first invalid field.
     */
    focusFirstNewsletterError() {

        const invalid =
            this.newsletter.fields.find(
                field =>
                    field.hasAttribute(
                        'aria-invalid'
                    )
            );

        invalid?.focus();

    }

    /**
     * Updates accessibility.
     */
    updateNewsletterAccessibility() {

        const message =
            this.newsletter.dom.message;

        if (
            message &&
            !message.hidden
        ) {

            this.announceNewsletterMessage(
                message.textContent
            );

        }

    }

    /**
     * Returns accessibility state.
     *
     * @returns {Object}
     */
    getNewsletterAccessibilityState() {

        return {

            reducedMotion:
                this.newsletter.reducedMotion,

            liveRegion:
                !!this.newsletter.liveRegion,

            emailFocused:
                document.activeElement ===
                this.newsletter.dom.email,

            keyboardEnabled:
                true

        };

    }

    /**
     * Returns whether accessibility
     * engine is ready.
     *
     * @returns {boolean}
     */
    isNewsletterAccessibilityReady() {

        return !!(

            this.isNewsletterResponsiveReady() &&
            this.newsletter.liveRegion

        );

    }
    /**
     * Initializes Newsletter entrance animation contract.
     */
    initializeNewsletterAnimations() {

        const wrapper =
            this.newsletter?.dom?.wrapper;

        if (!wrapper) {
            return;
        }

        wrapper.style.setProperty(
            '--newsletter-animation-duration',
            `${this.newsletter.config.animationDuration}ms`
        );

        wrapper.style.setProperty(
            '--newsletter-animation-delay',
            `${this.newsletter.config.animationDelay}ms`
        );

        wrapper.classList.remove(
            'is-animatable',
            'is-animated'
        );

        if (
            !this.newsletter.config.animation ||
            this.newsletter.reducedMotion
        ) {
            return;
        }

        wrapper.classList.add(
            'is-animatable'
        );

        requestAnimationFrame(() => {

            if (
                !this.newsletter ||
                this.newsletter.destroyed ||
                this.newsletter.dom.wrapper !== wrapper
            ) {
                return;
            }

            requestAnimationFrame(() => {

                if (
                    !this.newsletter ||
                    this.newsletter.destroyed ||
                    this.newsletter.dom.wrapper !== wrapper
                ) {
                    return;
                }

                wrapper.classList.add(
                    'is-animated'
                );

            });

        });

    }

        /**
     * ========================================================================
     * Part 4D.6
     * Theme Editor
     * ------------------------------------------------------------------------
     * Shopify Theme Editor Integration
     * Section Lifecycle
     * Block Lifecycle
     * Dynamic Refresh
     * OS 2.0 Compatible
     * ========================================================================
     */

    /**
     * Initializes Theme Editor support.
     */
    initializeNewsletterThemeEditor() {

        if (
            typeof Shopify === 'undefined' ||
            !Shopify.designMode
        ) {
            return;
        }

        this.bindNewsletterThemeEditorEvents();

        this.emit(
            'tefco:newsletter:theme-editor:initialized'
        );

    }

    /**
     * Binds Theme Editor events.
     */
    bindNewsletterThemeEditorEvents() {

        if (this.newsletterThemeEditorBound) {
            return;
        }

        this.newsletterThemeEditorHandlers = {
            sectionLoad:
                this.handleNewsletterSectionLoad.bind(this),
            sectionUnload:
                this.handleNewsletterSectionUnload.bind(this),
            sectionSelect:
                this.handleNewsletterSectionSelect.bind(this),
            sectionDeselect:
                this.handleNewsletterSectionDeselect.bind(this),
            blockSelect:
                this.handleNewsletterBlockSelect.bind(this),
            blockDeselect:
                this.handleNewsletterBlockDeselect.bind(this)
        };

        Object.entries({
            'shopify:section:load': 'sectionLoad',
            'shopify:section:unload': 'sectionUnload',
            'shopify:section:select': 'sectionSelect',
            'shopify:section:deselect': 'sectionDeselect',
            'shopify:block:select': 'blockSelect',
            'shopify:block:deselect': 'blockDeselect'
        }).forEach(([event, key]) => {

            this.on(
                document,
                event,
                this.newsletterThemeEditorHandlers[key]
            );

        });

        this.newsletterThemeEditorBound = true;

    }

    /**
     * Handles section load.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterSectionLoad(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        if (
            this.newsletter &&
            !this.newsletter.destroyed
        ) {
            this.destroyNewsletter();
        }

        this.initializeNewsletter();

        this.initializeNewsletterFormEngine();

        this.initializeNewsletterValidation();

        this.initializeNewsletterResponsive();

        this.initializeNewsletterAccessibility();

        this.initializeNewsletterAnimations();

        this.emit(
            'tefco:newsletter:section:loaded'
        );

    }

    /**
     * Handles section unload.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterSectionUnload(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        this.destroyNewsletter();

        this.emit(
            'tefco:newsletter:section:unloaded'
        );

    }

    /**
     * Handles section select.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterSectionSelect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        this.refreshNewsletterThemeEditor();

        this.newsletter.dom.email?.focus();

        this.emit(
            'tefco:newsletter:section:selected'
        );

    }

    /**
     * Handles section deselect.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterSectionDeselect(event) {

        const section =
            event.target;

        if (
            !section?.matches(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        this.emit(
            'tefco:newsletter:section:deselected'
        );

    }

    /**
     * Handles block select.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterBlockSelect(event) {

        const block =
            event.target;

        if (
            !block?.closest(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        block.scrollIntoView({

            behavior: 'smooth',

            block: 'center'

        });

        block.classList.add(
            'is-selected'
        );

        this.emit(
            'tefco:newsletter:block:selected',
            {
                block
            }
        );

    }

    /**
     * Handles block deselect.
     *
     * @param {CustomEvent} event
     */
    handleNewsletterBlockDeselect(event) {

        const block =
            event.target;

        if (
            !block?.closest(
                '[data-newsletter]'
            )
        ) {
            return;
        }

        block.classList.remove(
            'is-selected'
        );

        this.emit(
            'tefco:newsletter:block:deselected'
        );

    }

    /**
     * Refreshes Theme Editor.
     */
    refreshNewsletterThemeEditor() {

        this.collectNewsletterFields();

        this.refreshNewsletterFormEngine();

        this.recalculateNewsletterLayout();

        this.updateNewsletterAccessibility?.();

        this.initializeNewsletterAnimations?.();

        this.emit(
            'tefco:newsletter:theme-editor:refreshed'
        );

    }

    /**
     * Returns Theme Editor state.
     *
     * @returns {Object}
     */
    getNewsletterThemeEditorState() {

        return {

            enabled:
                typeof Shopify !== 'undefined' &&
                !!Shopify.designMode,

            initialized:
                this.isNewsletterThemeEditorReady(),

            totalFields:
                this.newsletter?.state?.totalFields ?? 0,

            valid:
                this.newsletter?.state?.valid ?? false,

            submitting:
                this.newsletter?.state?.submitting ?? false,

            submitted:
                this.newsletter?.state?.submitted ?? false

        };

    }

    /**
     * Returns whether Theme Editor
     * integration is ready.
     *
     * @returns {boolean}
     */
    isNewsletterThemeEditorReady() {

        return !!(

            typeof Shopify !== 'undefined' &&
            Shopify.designMode &&
            this.isNewsletterFormEngineReady()

        );

    }
        /**
     * ========================================================================
     * Part 4D.7
     * Diagnostics / Cleanup / Destroy
     * ------------------------------------------------------------------------
     * Enterprise Diagnostics
     * Runtime Validation
     * Memory Cleanup
     * Resource Management
     * Destroy Lifecycle
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Runs diagnostics.
     *
     * @returns {Object}
     */
    runNewsletterDiagnostics() {

        return {

            ready:
                this.isNewsletterReady(),

            initialized:
                this.newsletter?.initialized ?? false,

            destroyed:
                this.newsletter?.destroyed ?? false,

            submitting:
                this.newsletter?.state?.submitting ?? false,

            submitted:
                this.newsletter?.state?.submitted ?? false,

            valid:
                this.newsletter?.state?.valid ?? false,

            dirty:
                this.newsletter?.state?.dirty ?? false,

            touched:
                this.newsletter?.state?.touched ?? false,

            totalFields:
                this.newsletter?.state?.totalFields ?? 0,

            errors:
                this.newsletter?.state?.errors?.length ?? 0,

            responsive:
                this.isNewsletterResponsiveReady?.() ?? false,

            accessibility:
                this.isNewsletterAccessibilityReady?.() ?? false,

            validation:
                this.isNewsletterValidationReady?.() ?? false,

            formEngine:
                this.isNewsletterFormEngineReady?.() ?? false,

            themeEditor:
                this.isNewsletterThemeEditorReady?.() ?? false,

            timestamp:
                performance.now()

        };

    }

    /**
     * Validates Newsletter.
     *
     * @returns {boolean}
     */
    validateNewsletter() {

        if (!this.newsletter) {
            return false;
        }

        const {
            dom,
            fields
        } = this.newsletter;

        if (!dom?.section) {
            return false;
        }

        if (!dom?.form) {
            return false;
        }

        if (!dom?.email) {
            return false;
        }

        if (!Array.isArray(fields)) {
            return false;
        }

        return true;

    }

    /**
     * Logs diagnostics.
     */
    logNewsletterDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Newsletter Diagnostics'
        );

        console.table(
            this.runNewsletterDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Cleans styles.
     */
    cleanupNewsletterStyles() {

        const {
            wrapper,
            message
        } = this.newsletter.dom;

        wrapper?.classList.remove(
            'is-mobile',
            'is-tablet',
            'is-desktop',
            'reduced-motion',
            'is-animatable',
            'is-animated'
        );

        wrapper?.style.removeProperty(
            '--newsletter-animation-duration'
        );

        wrapper?.style.removeProperty(
            '--newsletter-animation-delay'
        );

        wrapper?.removeAttribute(
            'data-breakpoint'
        );

        message?.classList.remove(
            'is-success',
            'is-error',
            'is-visible'
        );

        this.newsletter.fields.forEach(
            field => {

                field.classList.remove(
                    'is-invalid'
                );

                field.removeAttribute(
                    'aria-invalid'
                );

                field.removeAttribute(
                    'data-newsletter-ready'
                );

            }
        );

    }

    /**
     * Cleans runtime state.
     */
    cleanupNewsletterState() {

        if (!this.newsletter) {
            return;
        }

        this.newsletter.state = {

            submitting: false,

            submitted: false,

            valid: false,

            focused: false,

            dirty: false,

            touched: false,

            totalFields: 0,

            breakpoint: null,

            errors: [],

            initializedAt: 0

        };

    }

    /**
     * Cleans Newsletter-owned DOM and resize events.
     */
    cleanupNewsletterEvents() {

        if (!this.newsletter) {
            return;
        }

        this.newsletter.eventListeners?.forEach(
            listener => {
 
                listener.target?.removeEventListener(
                    listener.event,
                    listener.handler,
                    listener.options
                );
 
            }
        );
 
        this.newsletter.eventListeners = [];
        this.newsletter.formEventsBound = false;
        this.newsletter.validationEventsBound = false;

        if (this.newsletter.resizeHandler) {

            window.removeEventListener(
                'resize',
                this.newsletter.resizeHandler
            );

            this.newsletter.resizeHandler = null;

        }

    }

    /**
     * Cleans accessibility.
     */
    cleanupNewsletterAccessibility() {

        if (
            this.newsletter?.liveRegion
        ) {

            this.newsletter.liveRegion.remove();

            this.newsletter.liveRegion =
                null;

        }

    }

    /**
     * Cleans form state.
     */
    cleanupNewsletterForm() {

        this.newsletter.dom.form?.reset();

        this.clearNewsletterMessage?.();

    }

    /**
     * Cleans DOM references.
     */
    cleanupNewsletterDOM() {

        if (!this.newsletter) {
            return;
        }

        this.newsletter.fields = [];

        this.newsletter.dom = {};

    }

    /**
     * Cleans configuration.
     */
    cleanupNewsletterConfig() {

        if (!this.newsletter) {
            return;
        }

        this.newsletter.config = {};

    }

    /**
     * Performs cleanup.
     */
    cleanupNewsletter() {

        this.cleanupNewsletterEvents();

        this.cleanupNewsletterAccessibility();

        this.cleanupNewsletterForm();

        this.cleanupNewsletterStyles();

        this.cleanupNewsletterState();

        this.cleanupNewsletterDOM();

        this.cleanupNewsletterConfig();

        this.emit(
            'tefco:newsletter:cleanup'
        );

    }

    /**
     * Destroys Newsletter.
     */
    destroyNewsletter() {

        if (
            !this.newsletter ||
            this.newsletter.destroyed
        ) {
            return;
        }

        this.cleanupNewsletter();

        this.newsletter.initialized =
            false;

        this.newsletter.destroyed =
            true;

        this.emit(
            'tefco:newsletter:destroyed'
        );

    }

    /**
     * Rebuilds Newsletter.
     */
    rebuildNewsletter() {

        this.destroyNewsletter();

        this.initializeNewsletter();

        this.initializeNewsletterFormEngine();

        this.initializeNewsletterValidation();

        this.initializeNewsletterResponsive();

        this.initializeNewsletterAccessibility();

        this.initializeNewsletterAnimations();

        this.initializeNewsletterThemeEditor();

        this.emit(
            'tefco:newsletter:rebuilt'
        );

    }

    /**
     * Returns lifecycle state.
     *
     * @returns {Object}
     */
    getNewsletterLifecycleState() {

        return {

            initialized:
                this.newsletter?.initialized ?? false,

            destroyed:
                this.newsletter?.destroyed ?? false,

            valid:
                this.validateNewsletter(),

            diagnostics:
                this.runNewsletterDiagnostics()

        };

    }

    /**
     * Returns whether Newsletter
     * has been destroyed.
     *
     * @returns {boolean}
     */
    isNewsletterDestroyed() {

        return !!(
            this.newsletter?.destroyed
        );

    }
        /**
     * ========================================================================
     * Part 5.1
     * Global Cleanup Manager
     * ------------------------------------------------------------------------
     * Enterprise Cleanup Manager
     * Shared Resource Cleanup
     * Memory Management
     * DOM Cleanup
     * Runtime Cleanup
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Cleanup Manager.
     */
    initializeCleanupManager() {

        this.cleanup = {

            initialized: true,

            modules: new Map(),

            callbacks: [],

            metrics: {

                cleanedModules: 0,

                cleanedCallbacks: 0,

                lastCleanup: 0

            }

        };

        this.registerDefaultCleanupModules();

        this.emit(
            'tefco:cleanup:initialized'
        );

    }

    /**
     * Registers default modules.
     */
    registerDefaultCleanupModules() {

        [

            'hero',

            'brandSlider',

            'categories',

            'solutions',

            'featuredProducts',

            'bannerGrid',

            'testimonials',

            'blog',

            'newsletter'

        ].forEach(name => {

            this.registerCleanupModule(
                name
            );

        });

    }

    /**
     * Registers cleanup module.
     *
     * @param {string} name
     * @param {Function} callback
     */
    registerCleanupModule(
        name,
        callback = null
    ) {

        if (!name) {
            return;
        }

        this.cleanup.modules.set(
            name,
            {

                callback,

                cleaned: false,

                timestamp: 0

            }
        );

    }

    /**
     * Registers cleanup callback.
     *
     * @param {Function} callback
     */
    registerCleanupCallback(
        callback
    ) {

        if (
            typeof callback !==
            'function'
        ) {
            return;
        }

        this.cleanup.callbacks.push(
            callback
        );

    }

    /**
     * Cleans module.
     *
     * @param {string} name
     */
    cleanupModule(name) {

        const module =
            this.cleanup.modules.get(
                name
            );

        if (!module) {
            return;
        }

        try {

            module.callback?.call(
                this
            );

            module.cleaned =
                true;

            module.timestamp =
                performance.now();

            this.cleanup.metrics.cleanedModules++;

            this.emit(
                'tefco:cleanup:module',
                {
                    name
                }
            );

        } catch (error) {

            console.error(
                `[TEFCO] Cleanup failed: ${name}`,
                error
            );

        }

    }

    /**
     * Cleans all modules.
     */
    cleanupModules() {

        this.cleanup.modules.forEach(
            (
                _,
                name
            ) => {

                this.cleanupModule(
                    name
                );

            }
        );

    }

    /**
     * Executes registered callbacks.
     */
    cleanupCallbacks() {

        this.cleanup.callbacks.forEach(
            callback => {

                try {

                    callback.call(
                        this
                    );

                    this.cleanup.metrics
                        .cleanedCallbacks++;

                } catch (error) {

                    console.error(
                        '[TEFCO] Cleanup callback failed.',
                        error
                    );

                }

            }
        );

        this.cleanup.callbacks =
            [];

    }

    /**
     * Cleans DOM.
     */
    cleanupDOM() {

        document
            .querySelectorAll(
                '[data-tefco-temp]'
            )
            .forEach(node => {

                node.remove();

            });

        document
            .querySelectorAll(
                '.is-loading,.is-loaded,.is-active,.is-hovered,.is-selected,.is-visible'
            )
            .forEach(node => {

                node.classList.remove(

                    'is-loading',

                    'is-loaded',

                    'is-active',

                    'is-hovered',

                    'is-selected',

                    'is-visible'

                );

            });

    }

    /**
     * Cleans runtime caches.
     */
    cleanupRuntime() {

        if (
            this.domCache
        ) {

            Object.keys(
                this.domCache
            ).forEach(key => {

                this.domCache[key] =
                    null;

            });

        }

        if (
            this.performance
        ) {

            this.performance.metrics =
                {};

        }

    }

    /**
     * Cleans memory.
     */
    cleanupMemory() {

        [

            'hero',

            'brandSlider',

            'categories',

            'solutions',

            'featuredProducts',

            'bannerGrid',

            'testimonials',

            'blog',

            'newsletter'

        ].forEach(name => {

            const module =
                this[name];

            if (!module) {
                return;
            }

            Object.keys(
                module
            ).forEach(key => {

                const value =
                    module[key];

                if (
                    Array.isArray(value)
                ) {

                    value.length = 0;

                }

                else if (
                    value instanceof Map
                ) {

                    value.clear();

                }

                else if (
                    value instanceof Set
                ) {

                    value.clear();

                }

            });

        });

    }

    /**
     * Cleans managers.
     */
    cleanupManagers() {

        this.eventManager
            ?.removeAll?.();

        this.observerManager
            ?.disconnectAll?.();

        this.visibilityManager
            ?.disconnect?.();

        this.scheduler
            ?.clear?.();

    }

    /**
     * Performs full cleanup.
     */
    cleanupAll() {

        this.cleanupModules();

        this.cleanupCallbacks();

        this.cleanupManagers();

        this.cleanupDOM();

        this.cleanupRuntime();

        this.cleanupMemory();

        this.cleanup.metrics.lastCleanup =
            performance.now();

        this.emit(
            'tefco:cleanup:complete'
        );

    }

    /**
     * Returns cleanup state.
     *
     * @returns {Object}
     */
    getCleanupState() {

        return {

            initialized:
                this.cleanup
                    ?.initialized ?? false,

            modules:
                this.cleanup
                    ?.modules
                    ?.size ?? 0,

            callbacks:
                this.cleanup
                    ?.callbacks
                    ?.length ?? 0,

            cleanedModules:
                this.cleanup
                    ?.metrics
                    ?.cleanedModules ?? 0,

            cleanedCallbacks:
                this.cleanup
                    ?.metrics
                    ?.cleanedCallbacks ?? 0,

            lastCleanup:
                this.cleanup
                    ?.metrics
                    ?.lastCleanup ?? 0

        };

    }

    /**
     * Returns whether Cleanup
     * Manager is ready.
     *
     * @returns {boolean}
     */
    isCleanupManagerReady() {

        return !!(

            this.cleanup &&
            this.cleanup.initialized

        );

    }
        /**
     * ========================================================================
     * Part 5.2
     * Global Destroy Manager
     * ------------------------------------------------------------------------
     * Enterprise Destroy Manager
     * Shared Lifecycle Management
     * Resource Release
     * Module Shutdown
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Destroy Manager.
     */
    initializeDestroyManager() {

        this.destroyManager = {

            initialized: true,

            destroying: false,

            destroyed: false,

            modules: new Map(),

            metrics: {

                destroyedModules: 0,

                lastDestroy: 0

            }

        };

        this.registerDestroyModules();

        this.emit(
            'tefco:destroy:initialized'
        );

    }

    /**
     * Registers destroyable modules.
     */
    registerDestroyModules() {

        [

            ['hero', this.destroyHero],

            ['brandSlider', this.destroyBrandSlider],

            ['categories', this.destroyCategories],

            ['solutions', this.destroySolutions],

            ['featuredProducts', this.destroyFeaturedProducts],

            ['bannerGrid', this.destroyBannerGrid],

            ['testimonials', this.destroyTestimonials],

            ['blog', this.destroyBlog],

            ['newsletter', this.destroyNewsletter]

        ].forEach(
            ([name, handler]) => {

                this.registerDestroyModule(
                    name,
                    handler
                );

            }
        );

    }

    /**
     * Registers destroy module.
     *
     * @param {string} name
     * @param {Function} handler
     */
    registerDestroyModule(
        name,
        handler
    ) {

        if (
            !name ||
            typeof handler !== 'function'
        ) {
            return;
        }

        this.destroyManager.modules.set(
            name,
            {

                handler,

                destroyed: false,

                timestamp: 0

            }
        );

    }

    /**
     * Destroys module.
     *
     * @param {string} name
     */
    destroyModule(name) {

        const module =
            this.destroyManager.modules.get(
                name
            );

        if (
            !module ||
            module.destroyed
        ) {
            return;
        }

        try {

            module.handler.call(
                this
            );

            module.destroyed =
                true;

            module.timestamp =
                performance.now();

            this.destroyManager.metrics
                .destroyedModules++;

            this.emit(
                'tefco:destroy:module',
                {
                    module: name
                }
            );

        } catch (error) {

            console.error(
                `[TEFCO] Destroy failed: ${name}`,
                error
            );

        }

    }

    /**
     * Stops runtime updates.
     */
    stopRuntime() {

        this.scheduler
            ?.pause?.();

        this.performanceMonitor
            ?.stop?.();

        this.animationFrame &&
            cancelAnimationFrame(
                this.animationFrame
            );

        this.idleCallback &&
            cancelIdleCallback?.(
                this.idleCallback
            );

        this.emit(
            'tefco:runtime:stopped'
        );

    }

    /**
     * Stops managers.
     */
    stopManagers() {

        this.eventManager
            ?.removeAll?.();

        this.observerManager
            ?.disconnectAll?.();

        this.visibilityManager
            ?.disconnect?.();

        this.resizeManager
            ?.disconnect?.();

        this.emit(
            'tefco:managers:stopped'
        );

    }

    /**
     * Destroys all modules.
     */
    destroyModules() {

        this.destroyManager.modules.forEach(
            (
                _,
                name
            ) => {

                this.destroyModule(
                    name
                );

            }
        );

    }

    /**
     * Releases shared resources.
     */
    releaseResources() {

        this.cleanupAll?.();

        this.domCache = {};

        this.config = {};

        this.state = {};

        this.emit(
            'tefco:resources:released'
        );

    }

    /**
     * Destroys managers.
     */
    destroyManagers() {

        [

            'eventManager',

            'observerManager',

            'visibilityManager',

            'resizeManager',

            'scheduler',

            'performanceMonitor'

        ].forEach(
            manager => {

                if (
                    this[manager]
                ) {

                    this[manager] =
                        null;

                }

            }
        );

    }

    /**
     * Performs full destroy.
     */
    destroyAll() {

        if (
            this.destroyManager.destroying ||
            this.destroyManager.destroyed
        ) {
            return;
        }

        this.destroyManager.destroying =
            true;

        this.emit(
            'tefco:destroy:start'
        );

        this.stopRuntime();

        this.stopManagers();

        this.destroyModules();

        this.releaseResources();

        this.destroyManagers();

        this.destroyManager.destroying =
            false;

        this.destroyManager.destroyed =
            true;

        this.destroyManager.metrics.lastDestroy =
            performance.now();

        this.emit(
            'tefco:destroy:complete'
        );

    }

    /**
     * Rebuilds application.
     */
    rebuildApplication() {

        this.destroyAll();

        this.initialize();

        this.emit(
            'tefco:application:rebuilt'
        );

    }

    /**
     * Returns destroy state.
     *
     * @returns {Object}
     */
    getDestroyState() {

        return {

            initialized:
                this.destroyManager
                    ?.initialized ?? false,

            destroying:
                this.destroyManager
                    ?.destroying ?? false,

            destroyed:
                this.destroyManager
                    ?.destroyed ?? false,

            modules:
                this.destroyManager
                    ?.modules
                    ?.size ?? 0,

            destroyedModules:
                this.destroyManager
                    ?.metrics
                    ?.destroyedModules ?? 0,

            lastDestroy:
                this.destroyManager
                    ?.metrics
                    ?.lastDestroy ?? 0

        };

    }

    /**
     * Returns whether Destroy
     * Manager is ready.
     *
     * @returns {boolean}
     */
    isDestroyManagerReady() {

        return !!(

            this.destroyManager &&
            this.destroyManager.initialized &&
            !this.destroyManager.destroyed

        );

    }
        /**
     * ========================================================================
     * Part 5.3
     * Global Accessibility Manager
     * ------------------------------------------------------------------------
     * Enterprise Accessibility Manager
     * WCAG 2.2 AA
     * Shared Focus Management
     * Keyboard Navigation
     * Screen Reader Support
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Accessibility Manager.
     */
    initializeAccessibilityManager() {

        this.accessibility = {

            initialized: true,

            enabled: true,

            focusStack: [],

            liveRegion: null,

            trappedElement: null,

            reducedMotion: false,

            rtl: false,

            keyboardEnabled: true,

            metrics: {

                announcements: 0,

                focusRestores: 0,

                keyboardEvents: 0

            }

        };

        this.initializeLiveRegion();

        this.initializeKeyboardManager();

        this.initializeReducedMotion();

        this.initializeRTL();

        this.emit(
            'tefco:a11y:initialized'
        );

    }

    /**
     * Initializes live region.
     */
    initializeLiveRegion() {

        const region =
            document.createElement(
                'div'
            );

        region.className =
            'visually-hidden';

        region.setAttribute(
            'aria-live',
            'polite'
        );

        region.setAttribute(
            'aria-atomic',
            'true'
        );

        region.setAttribute(
            'role',
            'status'
        );

        document.body.appendChild(
            region
        );

        this.accessibility.liveRegion =
            region;

    }

    /**
     * Announces message.
     *
     * @param {string} message
     */
    announce(message) {

        if (
            !this.accessibility.liveRegion ||
            !message
        ) {
            return;
        }

        this.accessibility.liveRegion.textContent =
            '';

        requestAnimationFrame(() => {

            this.accessibility.liveRegion.textContent =
                message;

        });

        this.accessibility.metrics
            .announcements++;

        this.emit(
            'tefco:a11y:announce',
            {
                message
            }
        );

    }

    /**
     * Saves current focus.
     */
    saveFocus() {

        const active =
            document.activeElement;

        if (
            active &&
            active !== document.body
        ) {

            this.accessibility.focusStack.push(
                active
            );

        }

    }

    /**
     * Restores previous focus.
     */
    restoreFocus() {

        const element =
            this.accessibility.focusStack.pop();

        element?.focus?.();

        this.accessibility.metrics
            .focusRestores++;

    }

    /**
     * Traps keyboard focus.
     *
     * @param {HTMLElement} container
     */
    trapFocus(container) {

        if (!container) {
            return;
        }

        this.accessibility.trappedElement =
            container;

        const focusable =
            container.querySelectorAll(

                'a,button,input,textarea,select,[tabindex]:not([tabindex="-1"])'

            );

        if (!focusable.length) {
            return;
        }

        focusable[0].focus();

    }

    /**
     * Releases trapped focus.
     */
    releaseFocus() {

        this.accessibility.trappedElement =
            null;

        this.restoreFocus();

    }

    /**
     * Initializes keyboard manager.
     */
    initializeKeyboardManager() {

        document.addEventListener(
            'keydown',
            event => {

                this.handleKeyboardEvent(
                    event
                );

            }
        );

    }

    /**
     * Handles keyboard events.
     *
     * @param {KeyboardEvent} event
     */
    handleKeyboardEvent(event) {

        this.accessibility.metrics
            .keyboardEvents++;

        switch (event.key) {

            case 'Escape':

                this.releaseFocus();

                break;

            case 'Home':

                window.scrollTo({

                    top: 0,

                    behavior: 'smooth'

                });

                break;

            case 'End':

                window.scrollTo({

                    top:
                        document.body.scrollHeight,

                    behavior: 'smooth'

                });

                break;

            default:

                return;

        }

        this.emit(
            'tefco:a11y:keyboard',
            {
                key:
                    event.key
            }
        );

    }

    /**
     * Initializes reduced motion.
     */
    initializeReducedMotion() {

        const media =
            window.matchMedia(
                '(prefers-reduced-motion: reduce)'
            );

        this.accessibility.reducedMotion =
            media.matches;

        if (
            media.addEventListener
        ) {

            media.addEventListener(
                'change',
                event => {

                    this.accessibility.reducedMotion =
                        event.matches;

                    this.emit(
                        'tefco:a11y:motion',
                        {
                            reduced:
                                event.matches
                        }
                    );

                }
            );

        }

    }

    /**
     * Initializes RTL support.
     */
    initializeRTL() {

        this.accessibility.rtl =
            document.documentElement.dir ===
            'rtl';

    }

    /**
     * Updates ARIA attribute.
     *
     * @param {HTMLElement} element
     * @param {string} attribute
     * @param {string} value
     */
    updateARIA(
        element,
        attribute,
        value
    ) {

        if (!element) {
            return;
        }

        element.setAttribute(
            attribute,
            value
        );

    }

    /**
     * Announces loading.
     *
     * @param {string} label
     */
    announceLoading(label) {

        this.announce(
            `${label} loading`
        );

    }

    /**
     * Announces success.
     *
     * @param {string} label
     */
    announceSuccess(label) {

        this.announce(
            `${label} completed`
        );

    }

    /**
     * Announces error.
     *
     * @param {string} label
     */
    announceError(label) {

        this.announce(
            `${label} failed`
        );

    }

    /**
     * Runs accessibility diagnostics.
     *
     * @returns {Object}
     */
    runAccessibilityDiagnostics() {

        return {

            initialized:
                this.accessibility
                    ?.initialized ?? false,

            enabled:
                this.accessibility
                    ?.enabled ?? false,

            rtl:
                this.accessibility
                    ?.rtl ?? false,

            reducedMotion:
                this.accessibility
                    ?.reducedMotion ?? false,

            liveRegion:
                !!this.accessibility
                    ?.liveRegion,

            trapped:
                !!this.accessibility
                    ?.trappedElement,

            announcements:
                this.accessibility
                    ?.metrics
                    ?.announcements ?? 0,

            keyboardEvents:
                this.accessibility
                    ?.metrics
                    ?.keyboardEvents ?? 0,

            focusRestores:
                this.accessibility
                    ?.metrics
                    ?.focusRestores ?? 0

        };

    }

    /**
     * Destroys Accessibility Manager.
     */
    destroyAccessibilityManager() {

        this.accessibility
            ?.liveRegion
            ?.remove();

        this.accessibility =
            null;

        this.emit(
            'tefco:a11y:destroyed'
        );

    }

    /**
     * Returns whether Accessibility
     * Manager is ready.
     *
     * @returns {boolean}
     */
    isAccessibilityManagerReady() {

        return !!(

            this.accessibility &&
            this.accessibility.initialized

        );

    }
        /**
     * ========================================================================
     * Part 5.4
     * Performance Optimizations
     * ------------------------------------------------------------------------
     * Enterprise Performance Layer
     * Scheduler Integration
     * DOM Optimization
     * Lazy Initialization
     * Runtime Optimization
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes Performance Layer.
     */
    initializePerformanceOptimizations() {

        this.performanceOptimizer = {

            initialized: true,

            paused: false,

            visible: true,

            queue: [],

            idleQueue: [],

            frameQueue: [],

            metrics: {

                domReads: 0,

                domWrites: 0,

                rafTasks: 0,

                idleTasks: 0,

                schedulerTasks: 0,

                visibleModules: 0,

                hiddenModules: 0,

                optimizationRuns: 0,

                lastOptimization: 0

            }

        };

        this.initializeDOMBatching();

        this.initializeVisibilityOptimization();

        this.initializeAnimationOptimization();

        this.initializeIdleProcessing();

        this.emit(
            'tefco:performance:initialized'
        );

    }

    /**
     * Initializes DOM batching.
     */
    initializeDOMBatching() {

        this.domRead = callback => {

            if (
                typeof callback !== 'function'
            ) {
                return;
            }

            this.performanceOptimizer
                .metrics
                .domReads++;

            return callback();

        };

        this.domWrite = callback => {

            if (
                typeof callback !== 'function'
            ) {
                return;
            }

            this.performanceOptimizer
                .metrics
                .domWrites++;

            requestAnimationFrame(() => {

                callback();

            });

        };

    }

    /**
     * Schedules task.
     *
     * @param {Function} callback
     */
    scheduleTask(callback) {

        if (
            typeof callback !== 'function'
        ) {
            return;
        }

        this.scheduler?.schedule?.(
            callback
        );

        this.performanceOptimizer
            .metrics
            .schedulerTasks++;

    }

    /**
     * Schedules animation frame.
     *
     * @param {Function} callback
     */
    scheduleFrame(callback) {

        if (
            typeof callback !== 'function'
        ) {
            return;
        }

        const id =
            requestAnimationFrame(() => {

                callback();

            });

        this.performanceOptimizer
            .frameQueue
            .push(id);

        this.performanceOptimizer
            .metrics
            .rafTasks++;

    }

    /**
     * Schedules idle work.
     *
     * @param {Function} callback
     */
    scheduleIdle(callback) {

        if (
            typeof callback !== 'function'
        ) {
            return;
        }

        const execute = () => {

            callback();

        };

        if (
            'requestIdleCallback' in window
        ) {

            const id =
                requestIdleCallback(
                    execute
                );

            this.performanceOptimizer
                .idleQueue
                .push(id);

        } else {

            const id =
                setTimeout(
                    execute,
                    1
                );

            this.performanceOptimizer
                .idleQueue
                .push(id);

        }

        this.performanceOptimizer
            .metrics
            .idleTasks++;

    }

    /**
     * Initializes visibility optimization.
     */
    initializeVisibilityOptimization() {

        document.addEventListener(
            'visibilitychange',
            () => {

                this.performanceOptimizer.visible =
                    !document.hidden;

                if (
                    document.hidden
                ) {

                    this.pauseOptimizations();

                } else {

                    this.resumeOptimizations();

                }

            }
        );

    }

    /**
     * Pauses optimizations.
     */
    pauseOptimizations() {

        this.performanceOptimizer.paused =
            true;

        this.scheduler?.pause?.();

        this.emit(
            'tefco:performance:paused'
        );

    }

    /**
     * Resumes optimizations.
     */
    resumeOptimizations() {

        this.performanceOptimizer.paused =
            false;

        this.scheduler?.resume?.();

        this.emit(
            'tefco:performance:resumed'
        );

    }

    /**
     * Initializes animation optimization.
     */
    initializeAnimationOptimization() {

        if (
            this.accessibility
                ?.reducedMotion
        ) {

            document.documentElement
                .classList.add(
                    'reduced-motion'
                );

        }

    }

    /**
     * Initializes idle processing.
     */
    initializeIdleProcessing() {

        this.scheduleIdle(() => {

            this.cleanupRuntime?.();

            this.emit(
                'tefco:performance:idle'
            );

        });

    }

    /**
     * Optimizes module.
     *
     * @param {string} name
     */
    optimizeModule(name) {

        if (!name) {
            return;
        }

        this.scheduleTask(() => {

            const refresh =
                this[
                    `refresh${name.charAt(0).toUpperCase()}${name.slice(1)}`
                ];

            refresh?.call(this);

        });

    }

    /**
     * Optimizes all modules.
     */
    optimizeModules() {

        [

            'hero',

            'brandSlider',

            'categories',

            'solutions',

            'featuredProducts',

            'bannerGrid',

            'testimonials',

            'blog',

            'newsletter'

        ].forEach(name => {

            this.optimizeModule(
                name
            );

        });

        this.performanceOptimizer
            .metrics
            .optimizationRuns++;

        this.performanceOptimizer
            .metrics
            .lastOptimization =
                performance.now();

    }

    /**
     * Flushes pending work.
     */
    flushPerformanceQueue() {

        this.performanceOptimizer.queue
            .forEach(task => {

                task();

            });

        this.performanceOptimizer.queue =
            [];

        this.emit(
            'tefco:performance:flushed'
        );

    }

    /**
     * Cancels scheduled work.
     */
    cancelPerformanceTasks() {

        this.performanceOptimizer
            .frameQueue
            .forEach(id => {

                cancelAnimationFrame(
                    id
                );

            });

        this.performanceOptimizer
            .idleQueue
            .forEach(id => {

                if (
                    'cancelIdleCallback' in window
                ) {

                    cancelIdleCallback(
                        id
                    );

                } else {

                    clearTimeout(id);

                }

            });

        this.performanceOptimizer
            .frameQueue = [];

        this.performanceOptimizer
            .idleQueue = [];

    }

    /**
     * Returns performance metrics.
     *
     * @returns {Object}
     */
    getPerformanceMetrics() {

        return {

            initialized:
                this.performanceOptimizer
                    ?.initialized ?? false,

            paused:
                this.performanceOptimizer
                    ?.paused ?? false,

            visible:
                this.performanceOptimizer
                    ?.visible ?? true,

            domReads:
                this.performanceOptimizer
                    ?.metrics
                    ?.domReads ?? 0,

            domWrites:
                this.performanceOptimizer
                    ?.metrics
                    ?.domWrites ?? 0,

            schedulerTasks:
                this.performanceOptimizer
                    ?.metrics
                    ?.schedulerTasks ?? 0,

            rafTasks:
                this.performanceOptimizer
                    ?.metrics
                    ?.rafTasks ?? 0,

            idleTasks:
                this.performanceOptimizer
                    ?.metrics
                    ?.idleTasks ?? 0,

            optimizationRuns:
                this.performanceOptimizer
                    ?.metrics
                    ?.optimizationRuns ?? 0,

            lastOptimization:
                this.performanceOptimizer
                    ?.metrics
                    ?.lastOptimization ?? 0

        };

    }

    /**
     * Returns whether Performance
     * Optimizer is ready.
     *
     * @returns {boolean}
     */
    isPerformanceOptimizerReady() {

        return !!(

            this.performanceOptimizer &&
            this.performanceOptimizer.initialized

        );

    }
        /**
     * ========================================================================
     * Part 5.5
     * Production Finalization
     * ------------------------------------------------------------------------
     * Enterprise Production Layer
     * Bootstrap Validation
     * Dependency Validation
     * Module Registry
     * Diagnostics
     * Production Mode
     * Shopify OS 2.0
     * ========================================================================
     */

    /**
     * Initializes production layer.
     */
    /**
     * Normalizes manager aliases used by production validation.
     */
    normalizeProductionDependencies() {

        this.scheduler =
            this.scheduler ||
            this.taskScheduler;

        this.visibilityManager =
            this.visibilityManager ||
            this.visibility;

        this.eventManager =
            this.eventManager ||
            {
                initialized: true,
                listeners: this.events
            };

    }

    initializeProduction() {

        this.production = {

            initialized: true,

            ready: false,

            version: '1.0.0',

            build: Date.now(),

            environment: 'production',

            debug: false,

            registry: new Map(),

            metrics: {

                startup: performance.now(),

                initializedModules: 0,

                initializedManagers: 0

            }

        };

        this.normalizeProductionDependencies?.();

        this.buildModuleRegistry();

        this.freezeConfiguration();

        this.validateDependencies();

        this.validateManagers();

        this.validateModules();

        this.production.ready =
            this.isProductionReady();

        this.emit(
            'tefco:production:initialized'
        );

    }

    /**
     * Builds module registry.
     */
    buildModuleRegistry() {

        [

            'hero',

            'brandSlider',

            'categories',

            'solutions',

            'featuredProducts',

            'bannerGrid',

            'testimonials',

            'blog',

            'newsletter'

        ].forEach(name => {

            this.production.registry.set(
                name,
                {

                    initialized:
                        !!this[name]?.initialized,

                    destroyed:
                        !!this[name]?.destroyed,

                    ready:
                        this[
                            `is${name.charAt(0).toUpperCase()}${name.slice(1)}Ready`
                        ]?.call(this) ?? false

                }
            );

        });

    }

    /**
     * Freezes configuration.
     */
    freezeConfiguration() {

        [

            'config',

            'defaults',

            'constants'

        ].forEach(object => {

            if (
                this[object] &&
                typeof this[object] ===
                'object'
            ) {

                Object.freeze(
                    this[object]
                );

            }

        });

    }

    /**
     * Validates dependencies.
     *
     * @returns {boolean}
     */
    validateDependencies() {

        const required = [

            'scheduler',

            'eventManager',

            'observerManager',

            'visibilityManager',

            'performanceOptimizer',

            'cleanup',

            'destroyManager',

            'accessibility'

        ];

        const valid =
            required.every(
                dependency =>
                    !!this[
                        dependency
                    ]
            );

        if (!valid) {

            console.warn(
                '[TEFCO] Missing dependencies.'
            );

        }

        return valid;

    }

    /**
     * Validates managers.
     *
     * @returns {boolean}
     */
    validateManagers() {

        const managers = [

            this.isCleanupManagerReady?.(),

            this.isDestroyManagerReady?.(),

            this.isAccessibilityManagerReady?.(),

            this.isPerformanceOptimizerReady?.()

        ];

        this.production.metrics
            .initializedManagers =
                managers.filter(Boolean)
                    .length;

        return managers.every(
            Boolean
        );

    }

    /**
     * Validates modules.
     *
     * @returns {boolean}
     */
    validateModules() {

        let ready = 0;

        this.production.registry.forEach(
            module => {

                if (
                    module.ready
                ) {

                    ready++;

                }

            }
        );

        this.production.metrics
            .initializedModules =
                ready;

        return ready ===
            this.production.registry.size;

    }

    /**
     * Runs production diagnostics.
     *
     * @returns {Object}
     */
    runProductionDiagnostics() {

        return {

            version:
                this.production.version,

            build:
                this.production.build,

            environment:
                this.production.environment,

            ready:
                this.production.ready,

            modules:
                this.production.metrics
                    .initializedModules,

            managers:
                this.production.metrics
                    .initializedManagers,

            cleanup:
                this.getCleanupState?.(),

            destroy:
                this.getDestroyState?.(),

            accessibility:
                this.runAccessibilityDiagnostics?.(),

            performance:
                this.getPerformanceMetrics?.(),

            timestamp:
                performance.now()

        };

    }

    /**
     * Logs diagnostics.
     */
    logProductionDiagnostics() {

        console.groupCollapsed(
            '[TEFCO] Production Diagnostics'
        );

        console.table(
            this.runProductionDiagnostics()
        );

        console.groupEnd();

    }

    /**
     * Starts production mode.
     */
    startProduction() {

        if (
            !this.production.ready
        ) {

            throw new Error(
                'Production validation failed.'
            );

        }

        this.emit(
            'tefco:production:start'
        );

    }

    /**
     * Stops production mode.
     */
    stopProduction() {

        this.destroyAll?.();

        this.emit(
            'tefco:production:stop'
        );

    }

    /**
     * Returns version.
     *
     * @returns {Object}
     */
    getVersion() {

        return {

            name:
                'TEFCO Home',

            version:
                this.production.version,

            build:
                this.production.build,

            environment:
                this.production.environment

        };

    }

    /**
     * Returns whether production
     * layer is ready.
     *
     * @returns {boolean}
     */
    isProductionReady() {

        return (

            this.validateDependencies() &&
            this.validateManagers() &&
            this.validateModules()

        );

    }

    /**
     * Boots entire application.
     */
    start() {

        this.initialize();

        this.initializeCleanupManager();

        this.initializeDestroyManager();

        this.initializeAccessibilityManager();

        this.initializePerformanceOptimizations();

        this.initializeProduction();

        this.startProduction();

        this.emit(
            'tefco:application:started'
        );

    }

    /**
     * Gracefully shuts down
     * application.
     */
    shutdown() {

        this.stopProduction();

        this.emit(
            'tefco:application:shutdown'
        );

    }


}

document.addEventListener(
    'DOMContentLoaded',
    () => {
        new TefcoHome();

    }
);

})();